import React, { useEffect, useState, useRef } from 'react';
import { Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Divider, FormControl, FormControlLabel, Grid, IconButton, InputAdornment, InputLabel, MenuItem, Popover, Select, Slider, Stack, Switch, Tab, Tabs, TextField, Typography, Collapse } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import CropSquareOutlinedIcon from '@mui/icons-material/CropSquareOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import AnimationIcon from '@mui/icons-material/Animation';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import SettingsIcon from '@mui/icons-material/Settings';
import TableChartIcon from '@mui/icons-material/TableChart';
import SearchIcon from '@mui/icons-material/Search';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import ViewCarouselIcon from '@mui/icons-material/ViewCarousel';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import HomeIcon from '@mui/icons-material/Home';
import PublicIcon from '@mui/icons-material/Public';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { supabase } from '../../lib/supabase';
import { useTheme } from '@mui/material/styles';
import toast from 'react-hot-toast';
import { useAppMode } from '../../contexts/AppModeContext';
import { AdminKeyService } from '../../services/adminKeyService';
import CategoriesPickerDialog from './CategoriesPickerDialog';
import Counter from '../Counter/Counter';
import { useWebsiteSettings } from '../../contexts/WebsiteSettingsContext';
import { ListBuilder } from './ListBuilder';
import IconGalleryDialog from './IconGalleryDialog';
import ImageGalleryDialog from './ImageGalleryDialog';
import GridEditor from './GridEditor';
import MediaPickerModal from '../MediaPickerModal';
import type { MediaItem } from '../../lib/media';
import PageHeroSection from '../page-hero-section';
import PageGallerySection from '../page-gallery-section';
import PageCtaSection from '../page-cta-section';
import PageReviewsSection from '../page-reviews-section';
import PageMediaTextSection from '../page-media-text-section';
import PageScrollListSection from '../page-scroll-list-section';
import PageHeroCarouselSection from '../page-hero-carousel-section';
import PageProductsStripSection from '../page-products-strip-section';
import PageSideNavigationSection from '../page-side-navigation-section';
import PageCounterSection from '../page-counter-section';
import PageParagraphSection from '../page-paragraph-section';
import PageCardStripSection from '../page-card-strip-section';
import { getLocalizedString, setLocalizedString, type LocalizedString } from '../../utils/localizedText';

const HomepageSettingsTab: React.FC = () => {
  const theme = useTheme();
  const { mode } = useAppMode();
  const { settings } = useWebsiteSettings();
  const [secret, setSecret] = useState<string | null>(null);
  const [heroEnabled, setHeroEnabled] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [mediaPath, setMediaPath] = useState<string>('');
  const [mediaType, setMediaType] = useState<'image' | 'video' | null>(null);
  const [originalMediaPath, setOriginalMediaPath] = useState<string>('');
  const [originalMediaType, setOriginalMediaType] = useState<'image' | 'video' | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [file, setFile] = useState<File | null>(null);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [gallery, setGallery] = useState<string[]>([]);
  const [originalGallery, setOriginalGallery] = useState<string[]>([]);
  const [galleryFiles, setGalleryFiles] = useState<File[]>([]);
  const [galleryObjectUrls, setGalleryObjectUrls] = useState<string[]>([]);
  const [uploadingGallery, setUploadingGallery] = useState<boolean>(false);
  const [deletingPath, setDeletingPath] = useState<string | null>(null);
  const [galleryPickerOpen, setGalleryPickerOpen] = useState<boolean>(false);
  const [selectedGalleryPath, setSelectedGalleryPath] = useState<string | null>(null);
  const [slideshow, setSlideshow] = useState<string[]>([]);
  const [originalSlideshow, setOriginalSlideshow] = useState<string[]>([]);
  const [slideshowFiles, setSlideshowFiles] = useState<File[]>([]);
  const [slideshowObjectUrls, setSlideshowObjectUrls] = useState<string[]>([]);
  const [uploadingSlideshow, setUploadingSlideshow] = useState<boolean>(false);
  const [slideshowPickerOpen, setSlideshowPickerOpen] = useState<boolean>(false);
  const [slideshowSelection, setSlideshowSelection] = useState<Set<string>>(new Set());
  // Promo popup
  const [promoEnabled, setPromoEnabled] = useState<boolean>(false);
  const [promoOnce, setPromoOnce] = useState<boolean>(true);
  const [promoTitle, setPromoTitle] = useState<string>('');
  const [promoMessage, setPromoMessage] = useState<string>('');
  const [promoCtaText, setPromoCtaText] = useState<string>('');
  const [promoCtaUrl, setPromoCtaUrl] = useState<string>('');
  const [promoImagePath, setPromoImagePath] = useState<string>('');
  const [promoImages, setPromoImages] = useState<string[]>([]);
  const [promoImageInterval, setPromoImageInterval] = useState<number>(5);
  const [promoImagesLinks, setPromoImagesLinks] = useState<Record<string, string>>({});
  const [promoImageFile, setPromoImageFile] = useState<File | null>(null);
  const [promoObjectUrl, setPromoObjectUrl] = useState<string | null>(null);
  const [promoPicking, setPromoPicking] = useState<boolean>(false);
  const [promoSelection, setPromoSelection] = useState<Set<string>>(new Set());
  const [savingPromo, setSavingPromo] = useState<boolean>(false);
  const [promoDisplayMode, setPromoDisplayMode] = useState<'modal'|'drawer'>('modal');
  const [promoDrawerSide, setPromoDrawerSide] = useState<'left'|'right'|'top'|'bottom'>('right');
  const [promoImagesLayout, setPromoImagesLayout] = useState<'slideshow'|'list'>('slideshow');
  const [promoImgEditor, setPromoImgEditor] = useState<{ open: boolean; path: string; url: string }>({ open: false, path: '', url: '' });
  // Circles section
  const [circlesEnabled, setCirclesEnabled] = useState<boolean>(false);
  const [circlesTitle, setCirclesTitle] = useState<string>('');
  const [circlesItems, setCirclesItems] = useState<Array<{ label: string; url: string; image: string }>>([]);
  const [circlePickerOpen, setCirclePickerOpen] = useState<{ open: boolean; index: number | null }>({ open: false, index: null });
  const [savingCircles, setSavingCircles] = useState<boolean>(false);
  const [bulkCirclesOpen, setBulkCirclesOpen] = useState<boolean>(false);
  const [bulkSelection, setBulkSelection] = useState<Set<string>>(new Set());
  const [bulkCount, setBulkCount] = useState<number>(6);
  const [bulkLabelPrefix, setBulkLabelPrefix] = useState<string>('Kategorie');
  const [bulkUrlPrefix, setBulkUrlPrefix] = useState<string>('');
  // Circles link modes (not persisted, only helps editing UX)
  const [circleLinkModes, setCircleLinkModes] = useState<Record<number, 'custom' | 'category'>>({});
  const [categoryPicker, setCategoryPicker] = useState<{ open: boolean; index: number | null }>({ open: false, index: null });
  // Counter section (global, reusable)
  const [counterEnabled, setCounterEnabled] = useState<boolean>(false);
  const [counterName, setCounterName] = useState<string>('');
  const [counterPrefix, setCounterPrefix] = useState<string>('');
  const [counterSuffix, setCounterSuffix] = useState<string>('');
  const [counterPrefixNewline, setCounterPrefixNewline] = useState<boolean>(false);
  const [counterSuffixNewline, setCounterSuffixNewline] = useState<boolean>(false);
  const [counterBg, setCounterBg] = useState<string>('rgba(255,255,255,0.9)');
  const [counterTextColor, setCounterTextColor] = useState<string>('#111827');
  const [counterRadius, setCounterRadius] = useState<number>(8);
  const [counterScale, setCounterScale] = useState<number>(1);
  const [savingCounter, setSavingCounter] = useState<boolean>(false);
  const [counterPreviewValue, setCounterPreviewValue] = useState<number>(1234);
  const [counterVariant, setCounterVariant] = useState<'classic'|'radial'|'glass'>('classic');
  const [counterFontFamily, setCounterFontFamily] = useState<string>('');
  const [counterValueFontSize, setCounterValueFontSize] = useState<number>(28);
  const [counterPrefixFontSize, setCounterPrefixFontSize] = useState<number>(12);
  const [counterSuffixFontSize, setCounterSuffixFontSize] = useState<number>(12);
  const [counterGradientFrom, setCounterGradientFrom] = useState<string>('#60a5fa');
  const [counterGradientTo, setCounterGradientTo] = useState<string>('#22c55e');
  const [counterRingWidth, setCounterRingWidth] = useState<number>(10);
  const [counterShadow, setCounterShadow] = useState<'soft'|'medium'|'strong'|'glow'|'none'>('soft');
  const [counterNumberLocale, setCounterNumberLocale] = useState<string>('de-DE');
  const [counterPreset, setCounterPreset] = useState<string>('');
  // Counter tabs (multiple counters)
  const [counterTabs, setCounterTabs] = useState<Array<any>>([]);
  const [counterActiveTab, setCounterActiveTab] = useState<number>(0);
  // Lists section state
  const [listTabs, setListTabs] = useState<Array<{ label: string; title?: string; items: Array<{ text: string; subtext?: string; href?: string; icon?: string; tag?: string; }> }>>([]);
  const [listActiveTab, setListActiveTab] = useState<number>(0);
  const [listTitle, setListTitle] = useState<string>('');
  const [listItems, setListItems] = useState<Array<{ text: string; subtext?: string; href?: string; icon?: string; iconName?: string; image?: string; mediaType?: 'icon'|'image'; tag?: string }>>([]);
  const [listIconPicker, setListIconPicker] = useState<{ open: boolean; index: number | null }>({ open: false, index: null });
  const [listImagePicker, setListImagePicker] = useState<{ open: boolean; index: number | null }>({ open: false, index: null });
  const [listItemSize, setListItemSize] = useState<number>(40);
  const [listIconColor, setListIconColor] = useState<string>('');
  const [listIconBg, setListIconBg] = useState<string>('');
  const [gridOpen, setGridOpen] = useState<boolean>(false);
  const [circlesOpen, setCirclesOpen] = useState<boolean>(false);
  const [featureOpen, setFeatureOpen] = useState<boolean>(false);
  const [promoOpen, setPromoOpen] = useState<boolean>(false);
  const [mediaOpen, setMediaOpen] = useState<boolean>(false);
  const [listsOpen, setListsOpen] = useState<boolean>(false);
  const [counterOpen, setCounterOpen] = useState<boolean>(false);
  const [heroSlidesOpen, setHeroSlidesOpen] = useState<boolean>(false);
  const [heroAreaOpen, setHeroAreaOpen] = useState<boolean>(false);
  const [pagesOpen, setPagesOpen] = useState<boolean>(false);
  const [pagesTabs, setPagesTabs] = useState<Array<{ label: string; url: string; displayFrom?: string }>>([
    { label: 'Startseite', url: '/' },
    { label: '404 Seite', url: '/404' },
  ]);
  const [pagesActiveTab, setPagesActiveTab] = useState<number>(() => {
    try {
      if (typeof window !== 'undefined') {
        const raw = window.localStorage.getItem('admin.pages.activeTab');
        const n = Number(raw);
        if (Number.isInteger(n) && n >= 0) return n;
      }
    } catch {}
    return 0;
  });
  const [pagesDialogOpen, setPagesDialogOpen] = useState<boolean>(false);
  const [pagesDialogName, setPagesDialogName] = useState<string>('');
  const [pagesDialogUrl, setPagesDialogUrl] = useState<string>('/');
  const [pagesSaving, setPagesSaving] = useState<boolean>(false);
  const [pageBuilderOpen, setPageBuilderOpen] = useState<boolean>(false);
  const [slugDialogOpen, setSlugDialogOpen] = useState<boolean>(false);
  const [slugDraft, setSlugDraft] = useState<string>('');
  const [slugTouched, setSlugTouched] = useState<boolean>(false);
  const [slugSaving, setSlugSaving] = useState<boolean>(false);
  const [pageDisplayDialogOpen, setPageDisplayDialogOpen] = useState<boolean>(false);
  const [pageDisplayMode, setPageDisplayMode] = useState<'own'|'reuse'>('own');
  const [pageDisplayFrom, setPageDisplayFrom] = useState<string>('');
  const [pageBuilderIndex, setPageBuilderIndex] = useState<number>(() => {
    try {
      if (typeof window !== 'undefined') {
        const raw = window.localStorage.getItem('admin.pageBuilder.index');
        const n = Number(raw);
        if (Number.isInteger(n) && n >= 0) return n;
      }
    } catch {}
    return 0;
  });
  const [addComponentOpen, setAddComponentOpen] = useState<boolean>(false);
  const [heroEditor, setHeroEditor] = useState<{ open: boolean; sectionId: string | null; enabled: boolean; title: LocalizedString; subtitle: LocalizedString; ctaText: LocalizedString; ctaUrl: string; backgroundImage: string; align: 'left'|'center'; minHeight: number }>({ open: false, sectionId: null, enabled: true, title: { de: 'Dein Marktplatz für besondere Funde' }, subtitle: { de: 'Entdecke großartige Produkte von Top-Marken und unabhängigen Verkäufern.' }, ctaText: { de: '' }, ctaUrl: '', backgroundImage: '', align: 'center', minHeight: 520 });
  const [heroLang, setHeroLang] = useState<string>('de');
  const [heroLangAnchor, setHeroLangAnchor] = useState<HTMLElement | null>(null);
  const [heroBgPickerOpen, setHeroBgPickerOpen] = useState<boolean>(false);
  const [heroCarouselEditor, setHeroCarouselEditor] = useState<{ open: boolean; sectionId: string | null; enabled: boolean; title: LocalizedString; subtitle: LocalizedString; ctaText: LocalizedString; ctaUrl: string; align: 'left'|'right'; minHeight: number; autoPlay: boolean; autoPlayInterval: number; showArrows: boolean; showDots: boolean; images: string[] }>({ open: false, sectionId: null, enabled: true, title: { de: 'Echt luxuriöse Uhren' }, subtitle: { de: 'Entdecke neue Lieblinge mit geprüfter Qualität.' }, ctaText: { de: 'Hier entlang' }, ctaUrl: '/', align: 'left', minHeight: 520, autoPlay: true, autoPlayInterval: 4500, showArrows: true, showDots: true, images: [] });
  const [heroCarouselPickerOpen, setHeroCarouselPickerOpen] = useState<boolean>(false);
  const [heroCarouselLang, setHeroCarouselLang] = useState<string>('de');
  const [heroCarouselLangAnchor, setHeroCarouselLangAnchor] = useState<HTMLElement | null>(null);

  const [sidenavEditor, setSidenavEditor] = useState<{ open: boolean; sectionId: string | null; enabled: boolean; buttonTooltip: LocalizedString; triggerRadius: number; popupRadius: number; popoverTitle: LocalizedString; popoverSubtitle: LocalizedString; popoverHint: LocalizedString; tabs: Array<{ label: LocalizedString; url: string }> }>({ open: false, sectionId: null, enabled: true, buttonTooltip: { de: 'Menü' }, triggerRadius: 999, popupRadius: 16, popoverTitle: { de: 'Navigation' }, popoverSubtitle: { de: 'Schnellzugriff' }, popoverHint: { de: 'Tippe einen Eintrag, um direkt zu öffnen.' }, tabs: [{ label: { de: 'Kontakt' }, url: '/kontakt' }, { label: { de: 'Über uns' }, url: '/ueber-uns' }] });
  const [sidenavLang, setSidenavLang] = useState<string>('de');
  const [sidenavLangAnchor, setSidenavLangAnchor] = useState<HTMLElement | null>(null);

  const heroCarouselFlag = React.useCallback((code: string) => {
    const c = String(code || '').trim().toLowerCase();
    if (c === 'de') return '🇩🇪';
    if (c === 'en') return '🇬🇧';
    if (c === 'tr') return '🇹🇷';
    if (c === 'fr') return '🇫🇷';
    if (c === 'es') return '🇪🇸';
    if (c === 'it') return '🇮🇹';
    if (c === 'pt') return '🇵🇹';
    if (c === 'ar') return '🇸🇦';
    if (c === 'hi') return '🇮🇳';
    if (c === 'zh') return '🇨🇳';
    return '🏳️';
  }, []);

  const removeSectionById = React.useCallback((sections: any[], id: string) => {
    let removed: any = null;
    const next: any[] = [];

    for (const s of sections || []) {
      if (!s) continue;
      if (String(s.id) === id) {
        removed = s;
        continue;
      }
      if (s.type === 'row' && Array.isArray((s as any).columns)) {
        const cols = (s as any).columns as any[];
        let changed = false;
        const nextCols = cols.map((c: any) => {
          if (c && String(c.id) === id) {
            removed = c;
            changed = true;
            return null;
          }
          return c;
        });
        if (changed) {
          const filled = nextCols.filter(Boolean);
          if (filled.length === 0) {
            continue;
          }
          if (filled.length === 1) {
            next.push(filled[0]);
            continue;
          }
          next.push({ ...s, columns: nextCols });
          continue;
        }
      }
      next.push(s);
    }

    return { next, removed };
  }, []);

  const updateSectionById = React.useCallback((sections: any[], id: string, updater: (sec: any) => any) => {
    let changed = false;
    const next = sections.map((s: any) => {
      if (!s || typeof s !== 'object') return s;
      if (String(s?.id || '') === String(id)) {
        changed = true;
        try { return updater(s); } catch { return s; }
      }
      if (String(s?.type || '') === 'row') {
        const colsRaw = Array.isArray((s as any).columns) ? (s as any).columns : [];
        const cols = [colsRaw[0] || null, colsRaw[1] || null];
        let innerChanged = false;
        const nextCols = cols.map((c: any) => {
          if (!c || typeof c !== 'object') return c;
          if (String(c?.id || '') === String(id)) {
            innerChanged = true;
            try { return updater(c); } catch { return c; }
          }
          return c;
        });
        if (innerChanged) {
          changed = true;
          return { ...s, columns: nextCols };
        }
      }
      return s;
    });
    return { next, changed };
  }, []);

  const [productsEditor, setProductsEditor] = useState<{ open: boolean; sectionId: string | null; enabled: boolean; heading: string; subtitle: string; badgeText: string; radius: number; limit: number; sortBy: 'created_at'|'favorite_count'|'view_count'|'price'; sortOrder: 'asc'|'desc'; productIdsText: string; tagsText: string; keywordsText: string }>({ open: false, sectionId: null, enabled: true, heading: 'Deals des Tages', subtitle: '', badgeText: '', radius: 16, limit: 12, sortBy: 'created_at', sortOrder: 'desc', productIdsText: '', tagsText: '', keywordsText: '' });
  const [cardStripEditor, setCardStripEditor] = useState<{ open: boolean; sectionId: string | null; enabled: boolean; heading: string; text: string; productIdsText: string; perRow: number; variant: 'default'|'compact'; showSeller: boolean }>({ open: false, sectionId: null, enabled: true, heading: 'Highlights', text: 'Ein paar ausgewählte Angebote – direkt für dich.', productIdsText: '', perRow: 3, variant: 'default', showSeller: false });
  const [ctaEditor, setCtaEditor] = useState<{ open: boolean; sectionId: string | null; enabled: boolean; title: LocalizedString; text: LocalizedString; buttonText: LocalizedString; buttonUrl: string; backgroundImage: string; align: 'left'|'center'; variant: 'gradient'|'soft' }>({ open: false, sectionId: null, enabled: true, title: { de: 'Bereit loszulegen?' }, text: { de: 'Entdecke unsere Highlights oder starte direkt mit deinem nächsten Fund.' }, buttonText: { de: 'Jetzt entdecken' }, buttonUrl: '/', backgroundImage: '', align: 'center', variant: 'gradient' });
  const [ctaLang, setCtaLang] = useState<string>('de');
  const [ctaLangAnchor, setCtaLangAnchor] = useState<HTMLElement | null>(null);
  const [ctaBgPickerOpen, setCtaBgPickerOpen] = useState<boolean>(false);
  const [reviewsEditor, setReviewsEditor] = useState<{ open: boolean; sectionId: string | null; enabled: boolean; title: LocalizedString; subtitle: LocalizedString; variant: 'glass'|'soft'; autoplay: boolean; intervalMs: number; showArrows: boolean; showDots: boolean; items: Array<{ name: LocalizedString; role?: LocalizedString; text: LocalizedString; rating?: number; avatar?: string }> }>({ open: false, sectionId: null, enabled: true, title: { de: 'Was unsere Kunden sagen' }, subtitle: { de: 'Echte Stimmen. Echte Erfahrungen. 100% Vertrauen.' }, variant: 'glass', autoplay: true, intervalMs: 5500, showArrows: true, showDots: true, items: [{ name: { de: 'Sophie' }, role: { de: 'Käuferin' }, text: { de: 'Super schneller Versand und tolle Qualität. Ich bestelle definitiv wieder!' }, rating: 5, avatar: '' }, { name: { de: 'Jonas' }, role: { de: 'Stammkunde' }, text: { de: 'Sehr schöne Auswahl und richtig faire Preise. Mega!' }, rating: 5, avatar: '' }] });
  const [reviewsLang, setReviewsLang] = useState<string>('de');
  const [reviewsLangAnchor, setReviewsLangAnchor] = useState<HTMLElement | null>(null);
  const [counterEditor, setCounterEditor] = useState<{ open: boolean; sectionId: string | null; enabled: boolean; topText: LocalizedString; bottomText: LocalizedString; type: 'radial'|'classic'; align: 'left'|'center'|'right'; perRow: number; size: number; textScale: number; showPercent: boolean; bgMode: 'theme'|'custom'|'transparent'; bgColor: string; textColor: string; ringBgColor: string; gradientFrom: string; gradientTo: string; animate: boolean; animationDurationMs: number; blocks: Array<any> }>({ open: false, sectionId: null, enabled: true, topText: { de: 'Unsere Zahlen' }, bottomText: { de: 'Was uns ausmacht – auf einen Blick.' }, type: 'radial', align: 'center', perRow: 3, size: 1, textScale: 1, showPercent: false, bgMode: 'theme', bgColor: '#ffffff', textColor: '', ringBgColor: '', gradientFrom: '', gradientTo: '', animate: true, animationDurationMs: 1400, blocks: [{ kind: 'counter', value: 1200, max: 2000, decimals: 0, prefix: { de: '' }, suffix: { de: '+' }, labelTop: { de: 'Kunden' }, labelBottom: { de: 'Zufriedene Käufer' } }, { kind: 'counter', value: 4.9, max: 5, decimals: 1, prefix: { de: '' }, suffix: { de: '★' }, labelTop: { de: 'Bewertung' }, labelBottom: { de: 'Durchschnittlich' } }, { kind: 'counter', value: 24, max: 48, decimals: 0, prefix: { de: '' }, suffix: { de: 'h' }, labelTop: { de: 'Versand' }, labelBottom: { de: 'Schnell geliefert' } }] });
  const [counterLang, setCounterLang] = useState<string>('de');
  const [counterLangAnchor, setCounterLangAnchor] = useState<HTMLElement | null>(null);
  const [paragraphEditor, setParagraphEditor] = useState<{ open: boolean; sectionId: string | null; enabled: boolean; text: LocalizedString; align: 'left'|'center'|'right'; textColor: string; fontSize: number; maxWidth: 'sm'|'md'|'lg'; variant: 'plain'|'card'; bgColor: string }>({ open: false, sectionId: null, enabled: true, text: { de: 'Hier ist ein Textabschnitt. Du kannst ihn im Admin bearbeiten, mehrsprachig machen und das Styling anpassen.' }, align: 'center', textColor: '', fontSize: 18, maxWidth: 'lg', variant: 'plain', bgColor: '' });
  const [paragraphLang, setParagraphLang] = useState<string>('de');
  const [paragraphLangAnchor, setParagraphLangAnchor] = useState<HTMLElement | null>(null);
  const [reviewAvatarPicker, setReviewAvatarPicker] = useState<{ open: boolean; index: number | null }>({ open: false, index: null });
  const [mediaTextEditor, setMediaTextEditor] = useState<{ open: boolean; sectionId: string | null; enabled: boolean; eyebrow: LocalizedString; title: LocalizedString; text: LocalizedString; ctaText: LocalizedString; ctaUrl: string; mediaSide: 'left'|'right'; imageHeight: number; autoplay: boolean; autoPlayInterval: number; showArrows: boolean; showDots: boolean; images: string[] }>({ open: false, sectionId: null, enabled: true, eyebrow: { de: 'Über uns' }, title: { de: 'Unsere Story' }, text: { de: 'Erzähle hier deine Geschichte, deine Mission oder warum Kunden dir vertrauen sollten. Kurz, klar, überzeugend.' }, ctaText: { de: '' }, ctaUrl: '', mediaSide: 'left', imageHeight: 420, autoplay: false, autoPlayInterval: 4500, showArrows: true, showDots: true, images: [] });
  const [mediaTextLang, setMediaTextLang] = useState<string>('de');
  const [mediaTextLangAnchor, setMediaTextLangAnchor] = useState<HTMLElement | null>(null);
  const [mediaTextPickerOpen, setMediaTextPickerOpen] = useState<boolean>(false);
  const [scrollListEditor, setScrollListEditor] = useState<{ open: boolean; sectionId: string | null; enabled: boolean; heading: LocalizedString; roundness: number; itemSize: number; gap: number; items: Array<{ image: string; title: LocalizedString; linkUrl?: string }> }>({ open: false, sectionId: null, enabled: true, heading: { de: 'Neues Jahr, neue Lieblinge' }, roundness: 50, itemSize: 112, gap: 18, items: [] });
  const [scrollListItemPicker, setScrollListItemPicker] = useState<{ open: boolean; index: number | null }>({ open: false, index: null });
  const [scrollListLang, setScrollListLang] = useState<string>('de');
  const [scrollListLangAnchor, setScrollListLangAnchor] = useState<HTMLElement | null>(null);
  const [galleryEditor, setGalleryEditor] = useState<{ open: boolean; sectionId: string | null; enabled: boolean; title: string; subtitle: string; layout: 'grid'|'masonry'; aspectRatio: string; xsCols: number; smCols: number; mdCols: number; gap: number; radius: number; shadow: 'none'|'soft'|'medium'; lightboxEnabled: boolean; items: Array<{ image: string; caption?: string; linkUrl?: string }> }>({ open: false, sectionId: null, enabled: true, title: 'Galerie', subtitle: '', layout: 'grid', aspectRatio: '1 / 1', xsCols: 2, smCols: 3, mdCols: 4, gap: 12, radius: 16, shadow: 'soft', lightboxEnabled: true, items: [] });
  const [galleryItemPickerOpen, setGalleryItemPickerOpen] = useState<boolean>(false);
  const [lsDialogOpen, setLsDialogOpen] = useState<boolean>(false);
  const [lsDump, setLsDump] = useState<string>('');
  const refreshLsDump = () => {
    try {
      if (typeof window === 'undefined') { setLsDump(''); return; }
      const keys = Object.keys(window.localStorage).sort();
      const lines = keys.map(k => `${k}: ${String(window.localStorage.getItem(k))}`);
      setLsDump(lines.join('\n'));
    } catch { setLsDump(''); }
  };
  // Page Builder: multiple sections (for now only type 'grid'). Keep per-section grid data for non-homepage pages.
  const [builderSections, setBuilderSections] = useState<Array<any>>([]);
  const builderSectionsRef = React.useRef<Array<any>>([]);
  const [builderGridOpen, setBuilderGridOpen] = useState<boolean>(true);
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const restoredRef = React.useRef<boolean>(false);

  useEffect(() => {
    builderSectionsRef.current = Array.isArray(builderSections) ? builderSections : [];
  }, [builderSections]);

  const [pageBuilderDirty, setPageBuilderDirty] = useState<boolean>(false);
  const [dirtySectionIds, setDirtySectionIds] = useState<Set<string>>(new Set());
  const [pageBuilderSaving, setPageBuilderSaving] = useState<boolean>(false);

  const markDirty = (sectionId?: string) => {
    setPageBuilderDirty(true);
    if (sectionId) {
      setDirtySectionIds((prev) => {
        const next = new Set(prev);
        next.add(String(sectionId));
        return next;
      });
    }
  };

  const clearDirty = (sectionId?: string) => {
    if (!sectionId) {
      setPageBuilderDirty(false);
      setDirtySectionIds(new Set());
      return;
    }
    setDirtySectionIds((prev) => {
      const next = new Set(prev);
      next.delete(String(sectionId));
      return next;
    });
  };

  async function persistPageSections(nextSections: any[], sectionId?: string) {
    const url = pagesTabs[pageBuilderIndex]?.url || '/';
    const endpoint = (process.env.REACT_APP_WEBSITE_SETTINGS_ENDPOINT as string | undefined) || '/api/update-website-settings.php';
    let tokenLocal = secret;
    if (!tokenLocal) {
      const s = await AdminKeyService.getSecret();
      setSecret(s);
      tokenLocal = s;
    }
    if (!tokenLocal) {
      toast.error('Missing admin key.');
      return;
    }
    const payload: any = (url === '/')
      ? { mode, pageUrl: '/', pageFile: 'homepage', pageSections: nextSections, pageSectionId: sectionId, token: tokenLocal }
      : { mode, pageUrl: url, pageSections: nextSections, pageSectionId: sectionId, token: tokenLocal };
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Auth-Token': tokenLocal, 'Authorization': `Bearer ${tokenLocal}` },
        body: JSON.stringify(payload),
      });
      const json: any = await res.json().catch(() => null);
      if (!res.ok || (json && json.ok === false)) {
        const msg = String(json?.error || json?.message || res.statusText || 'Save failed');
        throw new Error(msg);
      }
    } catch (e: any) {
      throw e;
    }
  }

  async function savePageBuilderAll() {
    if (pageBuilderSaving) return;
    setPageBuilderSaving(true);
    try {
      await persistPageSections(builderSectionsRef.current);
      clearDirty();
      toast.success('Seite gespeichert', { duration: 1200 });
    } catch (e: any) {
      toast.error(String(e?.message || 'Speichern fehlgeschlagen'));
    } finally {
      setPageBuilderSaving(false);
    }
  }

  async function savePageBuilderSection(sectionId: string) {
    if (pageBuilderSaving) return;
    setPageBuilderSaving(true);
    try {
      await persistPageSections(builderSectionsRef.current, sectionId);
      clearDirty(sectionId);
      toast.success('Komponente gespeichert', { duration: 1200 });
    } catch (e: any) {
      toast.error(String(e?.message || 'Speichern fehlgeschlagen'));
    } finally {
      setPageBuilderSaving(false);
    }
  }

  async function savePages(
    tabs: Array<{ label: string; url: string; displayFrom?: string }>,
    createFiles?: Array<{ label: string; url: string }>
  ): Promise<boolean> {
    const endpoint = (process.env.REACT_APP_WEBSITE_SETTINGS_ENDPOINT as string | undefined) || '/api/update-website-settings.php';
    let token = secret;
    if (!token) {
      const s = await AdminKeyService.getSecret();
      setSecret(s);
      token = s;
    }
    if (!token) {
      toast.error('Missing admin key.');
      return false;
    }
    const loadingId = toast.loading('Seiten speichern...');
    try {
      const keepUrl = (() => {
        try {
          if (typeof window !== 'undefined') {
            const u = String(window.localStorage.getItem('admin.pageBuilder.pageUrl') || '').trim();
            if (u) return u;
          }
        } catch {}
        const cur = (pagesTabs && pagesTabs[pageBuilderIndex]) ? (pagesTabs[pageBuilderIndex].url || '') : '';
        return cur || '/';
      })();

      const payload: any = {
        mode,
        homepage: {
          pages: tabs,
          ...(Array.isArray(createFiles) && createFiles.length > 0 ? { createPageFiles: createFiles } : {}),
        },
        token,
      };
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Auth-Token': token, 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const raw = await res.text();
      let json: any = null;
      try { json = raw ? JSON.parse(raw) : null; } catch {}
      if (!res.ok || !json?.ok) {
        const detail = json?.error || raw || 'No response body';
        throw new Error(`${res.status} ${res.statusText} - ${detail}`);
      }
      if (Array.isArray(json?.homepage?.pages)) {
        const arr = (json.homepage.pages as any[]).map((p: any, i: number) => ({
          label: typeof p?.label === 'string' && p.label ? p.label : (i === 0 ? 'Startseite' : `Seite ${i + 1}`),
          url: typeof p?.url === 'string' && p.url ? p.url : (i === 0 ? '/' : `/seite-${i + 1}`),
          displayFrom: (typeof p?.displayFrom === 'string' && p.displayFrom) ? p.displayFrom : undefined,
        }));
        setPagesTabs(arr);
        const found = arr.findIndex((pp) => String(pp?.url || '').trim().toLowerCase() === String(keepUrl || '').trim().toLowerCase());
        if (found >= 0) {
          setPagesActiveTab(found);
          setPageBuilderIndex(found);
          try {
            if (typeof window !== 'undefined') {
              window.localStorage.setItem('admin.pageBuilder.pageUrl', arr[found]?.url || '/');
              window.localStorage.setItem('admin.pageBuilder.index', String(found));
              window.localStorage.setItem('admin.pages.activeTab', String(found));
            }
          } catch {}
        }
      }
      toast.success('Seiten gespeichert', { id: loadingId });
      return true;
    } catch (e: any) {
      toast.error(`Speichern fehlgeschlagen: ${e?.message || 'Unknown error'}`, { id: loadingId });
      return false;
    }
  }

  const reservedPageSlugs = React.useMemo(() => {
    return {
      exact: new Set<string>([
        '/',
        '/404',
        '/page',
        '/products',
        '/all-products',
        '/all-jobs',
        '/cart',
        '/checkout',
        '/login',
        '/register',
        '/seller',
        '/profile',
        '/orders',
        '/dashboard',
        '/upload-product',
        '/upload-job',
        '/user-profile',
        '/admin',
        '/admin/media-gallery',
        '/admin/component-editor',
        '/admin/page-preview',
        '/quill-admin/page-preview',
        '/pages-editor',
        '/post-editor',
        '/categories',
        '/job-categories',
        '/members',
        '/companies',
        '/companies/create',
        '/payment-success',
        '/payment-cancelled',
        '/kundencenter',
        '/datenschutz',
        '/privacy',
        '/agb',
        '/terms',
        '/regeln',
        '/rules',
      ]),
      prefix: [
        '/admin',
        '/product/',
        '/seller/',
        '/edit-product/',
        '/user-profile/',
        '/companies/',
        '/page/',
      ],
    };
  }, []);

  const normalizePageUrl = React.useCallback((raw: string) => {
    let v = String(raw || '').trim().toLowerCase();
    v = v.replace(/\s+/g, '-');
    v = v.replace(/[^a-z0-9\-\/]+/g, '');
    v = v.replace(/\/{2,}/g, '/');
    if (!v.startsWith('/')) v = `/${v}`;
    if (v !== '/' && v.endsWith('/')) v = v.replace(/\/+$/, '');
    if (v === '') v = '/';
    return v;
  }, []);

  const getSlugError = React.useCallback((url: string, index: number) => {
    const u = normalizePageUrl(url);
    if (!/^\/[a-z0-9\-\/]*$/.test(u)) return 'Ungültige URL. Erlaubt: a-z, 0-9, Bindestrich (-) und /.';
    if (u.includes('//')) return 'Ungültige URL: doppelte "/" sind nicht erlaubt.';
    if (reservedPageSlugs.exact.has(u)) return 'Diese URL ist reserviert.';
    if (reservedPageSlugs.prefix.some((p) => u.startsWith(p))) return 'Diese URL ist reserviert.';
    const existsUrl = pagesTabs.some((p, idx) => idx !== index && (p?.url || '').trim().toLowerCase() === u);
    if (existsUrl) return 'Diese URL wird bereits von einer anderen Seite verwendet.';
    return '';
  }, [normalizePageUrl, pagesTabs, reservedPageSlugs]);

  const suggestUniquePageUrl = React.useCallback((rawUrl: string) => {
    const base = normalizePageUrl(rawUrl);
    if (!pagesTabs.some((p) => normalizePageUrl(p?.url || '/') === base)) return base;
    const trimmed = base === '/' ? '/seite' : base;
    // If it already ends with -<n>, remove it for base.
    const m = trimmed.match(/^(.*?)(?:-(\d+))?$/);
    const stem = (m && m[1]) ? m[1] : trimmed;
    for (let i = 2; i <= 99; i += 1) {
      const candidate = normalizePageUrl(`${stem}-${i}`);
      const exists = pagesTabs.some((p) => normalizePageUrl(p?.url || '/') === candidate);
      if (!exists) return candidate;
    }
    return normalizePageUrl(`${stem}-${Date.now()}`);
  }, [normalizePageUrl, pagesTabs]);

  const normalizePickedUrl = (url: string) => {
    const u = String(url || '');
    if (!u) return '';
    const i = u.indexOf('/uploads/');
    if (i >= 0) return u.slice(i);
    return u;
  };

  const addHeroSection = () => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setBuilderSections((prev) => {
      const next = [...prev, { id, type: 'hero', title: 'Hero', config: { enabled: true, title: 'Dein Marktplatz für besondere Funde', subtitle: 'Entdecke großartige Produkte von Top-Marken und unabhängigen Verkäufern.', ctaText: '', ctaUrl: '', backgroundImage: '', align: 'center', minHeight: 520 } }];
      return next;
    });
    markDirty(id);
    toast.success('Hero hinzugefügt', { duration: 1200 });
  };

  const addHeroCarouselSection = () => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setBuilderSections((prev) => {
      const next = [...prev, { id, type: 'herocarousel', title: 'Hero Carousel', config: { enabled: true, title: 'Echt luxuriöse Uhren', subtitle: 'Entdecke neue Lieblinge mit geprüfter Qualität.', ctaText: 'Hier entlang', ctaUrl: '/', align: 'left', minHeight: 520, autoPlay: true, autoPlayInterval: 4500, showArrows: true, showDots: true, images: [] } }];
      return next;
    });
    markDirty(id);
    toast.success('Hero Carousel hinzugefügt', { duration: 1200 });
  };

  const addProductsSection = () => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setBuilderSections((prev) => {
      const next = [...prev, { id, type: 'products', title: 'Products', config: { enabled: true, heading: 'Deals des Tages', subtitle: '', limit: 12, sortBy: 'created_at', sortOrder: 'desc', badgeText: '', radius: 16, productIds: [], tags: [], nameKeywords: [] } }];
      return next;
    });
    markDirty(id);
    toast.success('Produkte hinzugefügt', { duration: 1200 });
  };

  const addCtaSection = () => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setBuilderSections((prev) => {
      const next = [...prev, { id, type: 'cta', title: 'CTA', config: { enabled: true, title: 'Bereit loszulegen?', text: 'Entdecke unsere Highlights oder starte direkt mit deinem nächsten Fund.', buttonText: 'Jetzt entdecken', buttonUrl: '/', backgroundImage: '', align: 'center', variant: 'gradient' } }];
      return next;
    });
    markDirty(id);
    toast.success('CTA hinzugefügt', { duration: 1200 });
  };

  const addReviewsSection = () => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setBuilderSections((prev) => {
      const next = [...prev, { id, type: 'reviews', title: 'Reviews', config: { enabled: true, title: { de: 'Was unsere Kunden sagen' }, subtitle: { de: 'Echte Stimmen. Echte Erfahrungen. 100% Vertrauen.' }, autoplay: true, intervalMs: 5500, showArrows: true, showDots: true, variant: 'glass', items: [{ name: { de: 'Sophie' }, role: { de: 'Käuferin' }, text: { de: 'Super schneller Versand und tolle Qualität. Ich bestelle definitiv wieder!' }, rating: 5, avatar: '' }, { name: { de: 'Jonas' }, role: { de: 'Stammkunde' }, text: { de: 'Sehr schöne Auswahl und richtig faire Preise. Mega!' }, rating: 5, avatar: '' }] } }];
      return next;
    });
    markDirty(id);
    toast.success('Reviews hinzugefügt', { duration: 1200 });
  };

  const addCounterSection = () => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setBuilderSections((prev) => {
      const next = [...prev, {
        id,
        type: 'counter',
        title: 'Counter',
        config: {
          enabled: true,
          topText: { de: 'Unsere Zahlen' },
          bottomText: { de: 'Was uns ausmacht – auf einen Blick.' },
          type: 'radial',
          align: 'center',
          perRow: 3,
          textScale: 1,
          bgMode: 'theme',
          bgColor: '#ffffff',
          textColor: '',
          ringBgColor: '',
          gradientFrom: '',
          gradientTo: '',
          animate: true,
          animationDurationMs: 1400,
          items: [
            { value: 1200, max: 2000, decimals: 0, prefix: { de: '' }, suffix: { de: '+' }, labelTop: { de: 'Kunden' }, labelBottom: { de: 'Zufriedene Käufer' } },
            { value: 4.9, max: 5, decimals: 1, prefix: { de: '' }, suffix: { de: '★' }, labelTop: { de: 'Bewertung' }, labelBottom: { de: 'Durchschnittlich' } },
            { value: 24, max: 48, decimals: 0, prefix: { de: '' }, suffix: { de: 'h' }, labelTop: { de: 'Versand' }, labelBottom: { de: 'Schnell geliefert' } },
          ],
        },
      }];
      return next;
    });
    markDirty(id);
    toast.success('Counter hinzugefügt', { duration: 1200 });
  };

  const addParagraphSection = () => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setBuilderSections((prev) => {
      const next = [...prev, {
        id,
        type: 'paragraph',
        title: 'Paragraph',
        config: {
          enabled: true,
          text: { de: 'Hier ist ein Textabschnitt. Du kannst ihn im Admin bearbeiten, mehrsprachig machen und das Styling anpassen.' },
          align: 'center',
          textColor: '',
          fontSize: 18,
          maxWidth: 'lg',
          variant: 'plain',
          bgColor: '',
        },
      }];
      return next;
    });
    markDirty(id);
    toast.success('Paragraph hinzugefügt', { duration: 1200 });
  };

  const addMediaTextSection = () => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setBuilderSections((prev) => {
      const next = [...prev, { id, type: 'mediatext', title: 'Media + Text', config: { enabled: true, eyebrow: { de: 'Über uns' }, title: { de: 'Unsere Story' }, text: { de: 'Erzähle hier deine Geschichte, deine Mission oder warum Kunden dir vertrauen sollten. Kurz, klar, überzeugend.' }, images: [], mediaSide: 'left', imageHeight: 420, autoPlay: false, autoPlayInterval: 4500, showArrows: true, showDots: true, ctaText: { de: '' }, ctaUrl: '' } }];
      return next;
    });
    markDirty(id);
    toast.success('Media + Text hinzugefügt', { duration: 1200 });
  };

  const addScrollListSection = () => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setBuilderSections((prev) => {
      const next = [...prev, { id, type: 'scrolllist', title: 'Scroll List', config: { enabled: true, heading: 'Neues Jahr, neue Lieblinge', roundness: 50, itemSize: 112, gap: 18, items: [] } }];
      return next;
    });
    markDirty(id);
    toast.success('Scroll Liste hinzugefügt', { duration: 1200 });
  };

  const addSideNavSection = () => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setBuilderSections((prev) => {
      const next = [...prev, { id, type: 'sidenav', title: 'Side navigation', config: { enabled: true, buttonTooltip: { de: 'Menü' }, triggerRadius: 999, tabs: [{ label: { de: 'Kontakt' }, url: '/kontakt' }, { label: { de: 'Über uns' }, url: '/ueber-uns' }] } }];
      return next;
    });
    markDirty(id);
    toast.success('Side navigation hinzugefügt', { duration: 1200 });
  };

  const addGridSection = () => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setBuilderSections((prev) => {
      const next = [...prev, { id, type: 'grid', title: 'Grid', open: true, gridTabs: [], gridTabsDisplay: undefined }];
      return next;
    });
    setActiveSectionId(id);
    markDirty(id);
    toast.success('Grid hinzugefügt', { duration: 1200 });
  };

  const addGallerySection = () => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setBuilderSections((prev) => {
      const next = [...prev, { id, type: 'gallery', title: 'Gallery', config: { enabled: true, title: 'Galerie', subtitle: '', layout: 'grid', aspectRatio: '1 / 1', gap: 12, radius: 16, shadow: 'soft', lightboxEnabled: true, columns: { xs: 2, sm: 3, md: 4 }, items: [] } }];
      return next;
    });
    markDirty(id);
    toast.success('Galerie hinzugefügt', { duration: 1200 });
  };

  const [dndState, setDndState] = useState<{ kind: 'section' | 'palette' | null; sectionId?: string; paletteType?: string; overIndex: number | null; overSide?: { targetId: string; side: 'left' | 'right' } | null }>({ kind: null, overIndex: null, overSide: null });

  const createSectionByType = React.useCallback((tp: string) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const t = String(tp || '').toLowerCase();
    if (t === 'hero') return { id, type: 'hero', title: 'Hero', config: { enabled: true, title: 'Dein Marktplatz für besondere Funde', subtitle: 'Entdecke großartige Produkte von Top-Marken und unabhängigen Verkäufern.', ctaText: '', ctaUrl: '', backgroundImage: '', align: 'center', minHeight: 520 } };
    if (t === 'herocarousel') return { id, type: 'herocarousel', title: 'Hero Carousel', config: { enabled: true, title: 'Echt luxuriöse Uhren', subtitle: 'Entdecke neue Lieblinge mit geprüfter Qualität.', ctaText: 'Hier entlang', ctaUrl: '/', align: 'left', minHeight: 520, autoPlay: true, autoPlayInterval: 4500, showArrows: true, showDots: true, images: [] } };
    if (t === 'products') return { id, type: 'products', title: 'Products', config: { enabled: true, heading: 'Deals des Tages', subtitle: '', limit: 12, sortBy: 'created_at', sortOrder: 'desc', badgeText: '', radius: 16, productIds: [], tags: [], nameKeywords: [] } };
    if (t === 'cardstrip') return { id, type: 'cardstrip', title: 'Cards', config: { enabled: true, heading: 'Highlights', text: 'Ein paar ausgewählte Angebote – direkt für dich.', productIds: [], perRow: 3, variant: 'default', showSeller: false } };
    if (t === 'cta') return { id, type: 'cta', title: 'CTA', config: { enabled: true, title: 'Bereit loszulegen?', text: 'Entdecke unsere Highlights oder starte direkt mit deinem nächsten Fund.', buttonText: 'Jetzt entdecken', buttonUrl: '/', backgroundImage: '', align: 'center', variant: 'gradient' } };
    if (t === 'reviews') return { id, type: 'reviews', title: 'Reviews', config: { enabled: true, title: 'Was unsere Kunden sagen', subtitle: 'Echte Stimmen. Echte Erfahrungen. 100% Vertrauen.', autoplay: true, intervalMs: 5500, showArrows: true, showDots: true, variant: 'glass', items: [{ name: 'Sophie', role: 'Käuferin', text: 'Super schneller Versand und tolle Qualität. Ich bestelle definitiv wieder!', rating: 5, avatar: '' }, { name: 'Jonas', role: 'Stammkunde', text: 'Sehr schöne Auswahl und richtig faire Preise. Mega!', rating: 5, avatar: '' }] } };
    if (t === 'counter') return { id, type: 'counter', title: 'Counter', config: { enabled: true, topText: { de: 'Unsere Zahlen' }, bottomText: { de: 'Was uns ausmacht – auf einen Blick.' }, type: 'radial', align: 'center', perRow: 3, textScale: 1, bgMode: 'theme', bgColor: '#ffffff', textColor: '', ringBgColor: '', gradientFrom: '', gradientTo: '', animate: true, animationDurationMs: 1400, items: [{ value: 1200, max: 2000, decimals: 0, prefix: { de: '' }, suffix: { de: '+' }, labelTop: { de: 'Kunden' }, labelBottom: { de: 'Zufriedene Käufer' } }, { value: 4.9, max: 5, decimals: 1, prefix: { de: '' }, suffix: { de: '★' }, labelTop: { de: 'Bewertung' }, labelBottom: { de: 'Durchschnittlich' } }, { value: 24, max: 48, decimals: 0, prefix: { de: '' }, suffix: { de: 'h' }, labelTop: { de: 'Versand' }, labelBottom: { de: 'Schnell geliefert' } }] } };
    if (t === 'paragraph') return { id, type: 'paragraph', title: 'Paragraph', config: { enabled: true, text: { de: 'Hier ist ein Textabschnitt. Du kannst ihn im Admin bearbeiten, mehrsprachig machen und das Styling anpassen.' }, align: 'center', textColor: '', fontSize: 18, maxWidth: 'lg', variant: 'plain', bgColor: '' } };
    if (t === 'mediatext') return { id, type: 'mediatext', title: 'Media + Text', config: { enabled: true, eyebrow: { de: 'Über uns' }, title: { de: 'Unsere Story' }, text: { de: 'Erzähle hier deine Geschichte, deine Mission oder warum Kunden dir vertrauen sollten. Kurz, klar, überzeugend.' }, images: [], mediaSide: 'left', imageHeight: 420, autoPlay: false, autoPlayInterval: 4500, showArrows: true, showDots: true, ctaText: { de: '' }, ctaUrl: '' } };
    if (t === 'scrolllist') return { id, type: 'scrolllist', title: 'Scroll List', config: { enabled: true, heading: 'Neues Jahr, neue Lieblinge', roundness: 50, itemSize: 112, gap: 18, items: [] } };
    if (t === 'gallery') return { id, type: 'gallery', title: 'Gallery', config: { enabled: true, title: 'Galerie', subtitle: '', layout: 'grid', aspectRatio: '1 / 1', gap: 12, radius: 16, shadow: 'soft', lightboxEnabled: true, columns: { xs: 2, sm: 3, md: 4 }, items: [] } };
    if (t === 'grid') return { id, type: 'grid', title: 'Grid', open: true, gridTabs: [], gridTabsDisplay: undefined };
    if (t === 'sidenav') return { id, type: 'sidenav', title: 'Side navigation', config: { enabled: true, buttonTooltip: { de: 'Menü' }, triggerRadius: 999, popupRadius: 16, popoverTitle: { de: 'Navigation' }, popoverSubtitle: { de: 'Schnellzugriff' }, popoverHint: { de: 'Tippe einen Eintrag, um direkt zu öffnen.' }, tabs: [{ label: { de: 'Kontakt' }, url: '/kontakt' }, { label: { de: 'Über uns' }, url: '/ueber-uns' }] } };
    return { id, type: t || 'section', title: 'Sektion', config: { enabled: true } };
  }, []);

  const handleDropAtIndex = React.useCallback((dropIndex: number) => {
    setBuilderSections((prev) => {
      const over = Math.max(0, Math.min(dropIndex, prev.length));
      if (dndState.kind === 'section' && dndState.sectionId) {
        const from = prev.findIndex((s: any) => String(s?.id) === String(dndState.sectionId));
        if (from < 0) return prev;
        let to = over;
        const next = [...prev];
        const [moved] = next.splice(from, 1);
        if (from < to) to = Math.max(0, to - 1);
        next.splice(to, 0, moved);
        try {
          markDirty(String(moved?.id));
        } catch {}
        return next;
      }
      if (dndState.kind === 'palette' && dndState.paletteType) {
        const created: any = createSectionByType(dndState.paletteType);
        const next = [...prev.slice(0, over), created, ...prev.slice(over)];
        try {
          markDirty(String(created?.id));
        } catch {}
        if (created?.type === 'grid') {
          try { setActiveSectionId(String(created.id)); } catch {}
        }
        return next;
      }
      return prev;
    });
    setDndState({ kind: null, overIndex: null, overSide: null });
  }, [createSectionByType, dndState.kind, dndState.paletteType, dndState.sectionId, markDirty]);

  const parseDndPayload = React.useCallback((e: any) => {
    try {
      const raw = e?.dataTransfer?.getData?.('text/plain');
      const parsed: any = raw ? JSON.parse(raw) : null;
      if (parsed?.kind === 'palette') {
        return { kind: 'palette' as const, paletteType: String(parsed.paletteType || '') };
      }
      if (parsed?.kind === 'section') {
        return { kind: 'section' as const, sectionId: String(parsed.sectionId || '') };
      }
    } catch {}
    return null;
  }, []);

  const renderDropZone = React.useCallback((index: number) => {
    return (
      <Box
        key={`pb-drop-${index}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDndState((p) => ({ ...p, overIndex: index }));
          try { e.dataTransfer.dropEffect = (dndState.kind === 'palette' ? 'copy' : 'move'); } catch {}
        }}
        onDrop={(e) => {
          e.preventDefault();
          const payload = parseDndPayload(e);
          if (payload?.kind === 'palette') setDndState({ kind: 'palette', paletteType: payload.paletteType, overIndex: index, overSide: null });
          if (payload?.kind === 'section') setDndState({ kind: 'section', sectionId: payload.sectionId, overIndex: index, overSide: null });
          handleDropAtIndex(index);
        }}
        sx={{
          height: 12,
          borderRadius: 999,
          mb: 2.5,
          mt: -1.25,
          transition: 'all .15s',
          background: (dndState.overIndex === index ? 'linear-gradient(90deg, rgba(59,130,246,0.55), rgba(59,130,246,0.05))' : 'transparent'),
          border: (dndState.overIndex === index ? '1px dashed rgba(59,130,246,0.9)' : '1px dashed transparent'),
        }}
      />
    );
  }, [dndState.kind, dndState.overIndex, handleDropAtIndex, parseDndPayload]);

  const handleDropOnSide = React.useCallback((targetId: string, side: 'left' | 'right', payload: any | null) => {
    if (!targetId) return;
    setBuilderSections((prev) => {
      const overTargetId = String(targetId);
      const sourceKind = payload?.kind || dndState.kind;
      const sourceSectionId = payload?.sectionId || dndState.sectionId;
      const sourcePaletteType = payload?.paletteType || dndState.paletteType;

      let working = [...prev];
      let moved: any = null;

      if (sourceKind === 'section' && sourceSectionId) {
        const res = removeSectionById(working, String(sourceSectionId));
        working = res.next;
        moved = res.removed;
        if (!moved) return prev;
      } else if (sourceKind === 'palette' && sourcePaletteType) {
        moved = createSectionByType(String(sourcePaletteType));
      } else {
        return prev;
      }

      const idx = working.findIndex((s: any) => String(s?.id) === overTargetId);
      if (idx < 0) return prev;
      const target = working[idx];

      // If target is already a row, try to fill/shift within row.
      if (target?.type === 'row') {
        const colsRaw = Array.isArray((target as any).columns) ? (target as any).columns : [];
        const cols = [colsRaw[0] || null, colsRaw[1] || null];
        const desired = side === 'left' ? 0 : 1;

        if (!cols[desired]) {
          cols[desired] = moved;
          const nextRow = { ...target, columns: cols };
          const next = [...working];
          next[idx] = nextRow;
          try { markDirty(String(nextRow?.id || moved?.id)); } catch {}
          return next;
        }
        if (!cols[1 - desired]) {
          // shift existing to empty slot
          cols[1 - desired] = cols[desired];
          cols[desired] = moved;
          const nextRow = { ...target, columns: cols };
          const next = [...working];
          next[idx] = nextRow;
          try { markDirty(String(nextRow?.id || moved?.id)); } catch {}
          return next;
        }

        // both filled: replace desired and push displaced after row
        const displaced = cols[desired];
        cols[desired] = moved;
        const nextRow = { ...target, columns: cols };
        const next = [...working];
        next[idx] = nextRow;
        next.splice(idx + 1, 0, displaced);
        try { markDirty(String(nextRow?.id || moved?.id)); } catch {}
        return next;
      }

      // Create a new row replacing the target
      const rowId = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      const cols = side === 'left' ? [moved, target] : [target, moved];
      const row = { id: rowId, type: 'row', title: 'Row', columns: cols };
      const next = [...working];
      next[idx] = row;
      try { markDirty(String(rowId)); } catch {}
      return next;
    });
    setDndState({ kind: null, overIndex: null, overSide: null });
  }, [createSectionByType, dndState.kind, dndState.paletteType, dndState.sectionId, markDirty, removeSectionById]);

  const renderSideDropZone = React.useCallback((targetId: string, side: 'left' | 'right') => {
    const isOver = dndState.overSide?.targetId === String(targetId) && dndState.overSide?.side === side;
    return (
      <Box
        key={`pb-side-${targetId}-${side}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDndState((p) => ({ ...p, overSide: { targetId: String(targetId), side } }));
          try { e.dataTransfer.dropEffect = (dndState.kind === 'palette' ? 'copy' : 'move'); } catch {}
        }}
        onDragLeave={() => setDndState((p) => ({ ...p, overSide: null }))}
        onDrop={(e) => {
          e.preventDefault();
          const payload = parseDndPayload(e);
          handleDropOnSide(String(targetId), side, payload);
        }}
        sx={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          width: 34,
          zIndex: 4,
          ...(side === 'left' ? { left: 0 } : { right: 0 }),
          borderRadius: side === 'left' ? '12px 0 0 12px' : '0 12px 12px 0',
          transition: 'all .12s ease',
          background: isOver ? 'linear-gradient(180deg, rgba(59,130,246,0.35), rgba(59,130,246,0.08))' : 'transparent',
          border: isOver ? '1px dashed rgba(59,130,246,0.95)' : '1px dashed transparent',
          pointerEvents: 'auto',
        }}
      />
    );
  }, [dndState.kind, dndState.overSide, handleDropOnSide, parseDndPayload]);

  const moveBuilderSection = React.useCallback((sectionId: string, dir: -1 | 1) => {
    setBuilderSections((prev) => {
      const idx = prev.findIndex((s: any) => String(s?.id) === String(sectionId));
      if (idx < 0) return prev;
      const nextIdx = idx + dir;
      if (nextIdx < 0 || nextIdx >= prev.length) return prev;
      const arr = [...prev];
      const a = arr[idx];
      const b = arr[nextIdx];
      arr[idx] = b;
      arr[nextIdx] = a;
      return arr;
    });
    markDirty(sectionId);
  }, [markDirty]);

  // Persist popup state and selection across reloads (skip first run to avoid clobbering restore flags)
  const persistInitRef = React.useRef<boolean>(false);
  useEffect(() => {
    if (!persistInitRef.current) { persistInitRef.current = true; return; }
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('admin.pageBuilder.open', pageBuilderOpen ? '1' : '0');
        window.localStorage.setItem('admin.pageBuilder.index', String(pageBuilderIndex));
        const urlToStore = (pagesTabs && pagesTabs[pageBuilderIndex]) ? (pagesTabs[pageBuilderIndex].url || '') : '';
        // Don't overwrite remembered pageUrl with empty/mismatched value during hydration.
        if (urlToStore) {
          window.localStorage.setItem('admin.pageBuilder.pageUrl', urlToStore);
        }
      }
    } catch {}
  }, [pagesTabs, pagesActiveTab, pageBuilderIndex, savePages]);

  const openEditorForSection = React.useCallback((section: any) => {
    const tp = String(section?.type || '').toLowerCase();
    const cfg: any = (section && typeof section.config === 'object') ? section.config : {};

    if (tp === 'products') {
      setProductsEditor({
        open: true,
        sectionId: section.id,
        enabled: cfg?.enabled !== false,
        heading: String(cfg?.heading || 'Deals des Tages'),
        subtitle: String(cfg?.subtitle || ''),
        badgeText: String(cfg?.badgeText || ''),
        radius: Math.max(0, Math.min(40, Number(cfg?.radius) || 16)),
        limit: Math.max(1, Math.min(40, Number(cfg?.limit) || 12)),
        sortBy: (cfg?.sortBy === 'favorite_count' || cfg?.sortBy === 'view_count' || cfg?.sortBy === 'price' ? cfg.sortBy : 'created_at'),
        sortOrder: (cfg?.sortOrder === 'asc' ? 'asc' : 'desc'),
        productIdsText: Array.isArray(cfg?.productIds) ? cfg.productIds.map((x: any) => String(x || '')).filter(Boolean).join(', ') : String(cfg?.productIdsText || ''),
        tagsText: Array.isArray(cfg?.tags) ? cfg.tags.map((x: any) => String(x || '')).filter(Boolean).join(', ') : String(cfg?.tagsText || ''),
        keywordsText: Array.isArray(cfg?.nameKeywords) ? cfg.nameKeywords.map((x: any) => String(x || '')).filter(Boolean).join(', ') : String(cfg?.keywordsText || ''),
      });
      return;
    }

    if (tp === 'cardstrip') {
      setCardStripEditor({
        open: true,
        sectionId: section.id,
        enabled: cfg?.enabled !== false,
        heading: String(cfg?.heading || 'Highlights'),
        text: String(cfg?.text || ''),
        productIdsText: Array.isArray(cfg?.productIds) ? cfg.productIds.map((x: any) => String(x || '')).filter(Boolean).join(', ') : String(cfg?.productIdsText || ''),
        perRow: Math.max(1, Math.min(6, Number(cfg?.perRow) || 3)),
        variant: (cfg?.variant === 'compact' ? 'compact' : 'default'),
        showSeller: cfg?.showSeller === true,
      });
      return;
    }

    if (tp === 'cta') {
      const titleVal: LocalizedString = (typeof cfg?.title === 'string')
        ? ({ de: String(cfg?.title || 'Bereit loszulegen?') } as any)
        : ((cfg?.title && typeof cfg?.title === 'object') ? cfg.title : ({ de: 'Bereit loszulegen?' } as any));
      const textVal: LocalizedString = (typeof cfg?.text === 'string')
        ? ({ de: String(cfg?.text || '') } as any)
        : ((cfg?.text && typeof cfg?.text === 'object') ? cfg.text : ({ de: '' } as any));
      const buttonTextVal: LocalizedString = (typeof cfg?.buttonText === 'string')
        ? ({ de: String(cfg?.buttonText || '') } as any)
        : ((cfg?.buttonText && typeof cfg?.buttonText === 'object') ? cfg.buttonText : ({ de: '' } as any));
      try {
        const keys = (titleVal && typeof titleVal === 'object') ? Object.keys(titleVal as any).filter(Boolean) : [];
        const nextLang = keys.includes('de') ? 'de' : (keys[0] || 'de');
        setCtaLang(nextLang);
      } catch { setCtaLang('de'); }
      setCtaEditor({
        open: true,
        sectionId: section.id,
        enabled: cfg?.enabled !== false,
        title: titleVal,
        text: textVal,
        buttonText: buttonTextVal,
        buttonUrl: String(cfg?.buttonUrl || ''),
        backgroundImage: String(cfg?.backgroundImage || ''),
        align: (cfg?.align === 'left' ? 'left' : 'center'),
        variant: (cfg?.variant === 'soft' ? 'soft' : 'gradient'),
      });
      return;
    }

    if (tp === 'reviews') {
      const items = Array.isArray(cfg?.items) ? cfg.items : [];
      setReviewsEditor({
        open: true,
        sectionId: section.id,
        enabled: cfg?.enabled !== false,
        title: String(cfg?.title || 'Was unsere Kunden sagen'),
        subtitle: String(cfg?.subtitle || ''),
        variant: (cfg?.variant === 'soft' ? 'soft' : 'glass'),
        autoplay: cfg?.autoplay !== false,
        intervalMs: Math.max(2000, Math.min(15000, Number(cfg?.intervalMs) || 5500)),
        showArrows: cfg?.showArrows !== false,
        showDots: cfg?.showDots !== false,
        items: items.map((it: any) => ({
          name: String(it?.name || ''),
          role: String(it?.role || ''),
          text: String(it?.text || ''),
          rating: (typeof it?.rating === 'number' ? it.rating : undefined),
          avatar: String(it?.avatar || ''),
        })),
      });
      return;
    }

    if (tp === 'scrolllist') {
      const items = Array.isArray(cfg?.items) ? cfg.items : [];
      const headingVal: LocalizedString = (typeof cfg?.heading === 'string')
        ? ({ de: String(cfg?.heading || 'Neues Jahr, neue Lieblinge') } as any)
        : ((cfg?.heading && typeof cfg?.heading === 'object') ? cfg.heading : ({ de: 'Neues Jahr, neue Lieblinge' } as any));
      try {
        const keys = (headingVal && typeof headingVal === 'object') ? Object.keys(headingVal as any).filter(Boolean) : [];
        const nextLang = keys.includes('de') ? 'de' : (keys[0] || 'de');
        setScrollListLang(nextLang);
      } catch { setScrollListLang('de'); }
      setScrollListEditor({
        open: true,
        sectionId: section.id,
        enabled: cfg?.enabled !== false,
        heading: headingVal,
        roundness: Math.max(0, Math.min(120, Number(cfg?.roundness) || 50)),
        itemSize: Math.max(60, Math.min(200, Number(cfg?.itemSize) || 112)),
        gap: Math.max(0, Math.min(60, Number(cfg?.gap) || 18)),
        items: items.map((it: any) => ({
          image: String(it?.image || ''),
          title: (typeof it?.title === 'string') ? ({ de: String(it?.title || '') } as any) : ((it?.title && typeof it?.title === 'object') ? it.title : ({ de: '' } as any)),
          linkUrl: String(it?.linkUrl || ''),
        })),
      });
      return;
    }

    if (tp === 'gallery') {
      const items = Array.isArray(cfg?.items) ? cfg.items : [];
      const cols = (cfg && typeof cfg.columns === 'object') ? cfg.columns : {};
      setGalleryEditor({
        open: true,
        sectionId: section.id,
        enabled: cfg?.enabled !== false,
        title: String(cfg?.title || 'Galerie'),
        subtitle: String(cfg?.subtitle || ''),
        layout: (cfg?.layout === 'masonry' ? 'masonry' : 'grid'),
        aspectRatio: String(cfg?.aspectRatio || '1 / 1'),
        xsCols: Math.max(1, Math.min(6, Number(cols?.xs) || Number(cfg?.xsCols) || 2)),
        smCols: Math.max(1, Math.min(6, Number(cols?.sm) || Number(cfg?.smCols) || 3)),
        mdCols: Math.max(1, Math.min(8, Number(cols?.md) || Number(cfg?.mdCols) || 4)),
        gap: Math.max(0, Math.min(40, Number(cfg?.gap) || 12)),
        radius: Math.max(0, Math.min(40, Number(cfg?.radius) || 16)),
        shadow: (cfg?.shadow === 'none' || cfg?.shadow === 'medium' ? cfg.shadow : 'soft'),
        lightboxEnabled: cfg?.lightboxEnabled !== false,
        items: items.map((it: any) => ({ image: String(it?.image || ''), caption: String(it?.caption || ''), linkUrl: String(it?.linkUrl || '') })),
      });
      return;
    }

    if (tp === 'sidenav') {
      const tooltipVal: LocalizedString = (typeof cfg?.buttonTooltip === 'string')
        ? ({ de: String(cfg?.buttonTooltip || 'Menü') } as any)
        : ((cfg?.buttonTooltip && typeof cfg?.buttonTooltip === 'object') ? cfg.buttonTooltip : ({ de: 'Menü' } as any));
      const popTitleVal: LocalizedString = (typeof cfg?.popoverTitle === 'string')
        ? ({ de: String(cfg?.popoverTitle || 'Navigation') } as any)
        : ((cfg?.popoverTitle && typeof cfg?.popoverTitle === 'object') ? cfg.popoverTitle : ({ de: 'Navigation' } as any));
      const popSubtitleVal: LocalizedString = (typeof cfg?.popoverSubtitle === 'string')
        ? ({ de: String(cfg?.popoverSubtitle || 'Schnellzugriff') } as any)
        : ((cfg?.popoverSubtitle && typeof cfg?.popoverSubtitle === 'object') ? cfg.popoverSubtitle : ({ de: 'Schnellzugriff' } as any));
      const popHintVal: LocalizedString = (typeof cfg?.popoverHint === 'string')
        ? ({ de: String(cfg?.popoverHint || 'Tippe einen Eintrag, um direkt zu öffnen.') } as any)
        : ((cfg?.popoverHint && typeof cfg?.popoverHint === 'object') ? cfg.popoverHint : ({ de: 'Tippe einen Eintrag, um direkt zu öffnen.' } as any));
      const triggerRadius = (() => {
        const raw = (cfg as any)?.triggerRadius;
        const n = typeof raw === 'number' ? raw : (raw === null || raw === undefined ? NaN : Number(raw));
        const v = Number.isFinite(n) ? n : 999;
        return Math.max(0, Math.min(999, v));
      })();
      const popupRadius = (() => {
        const raw = (cfg as any)?.popupRadius;
        const n = typeof raw === 'number' ? raw : (raw === null || raw === undefined ? NaN : Number(raw));
        const v = Number.isFinite(n) ? n : 16;
        return Math.max(0, Math.min(64, v));
      })();
      const tabs = Array.isArray(cfg?.tabs) ? cfg.tabs : [];
      const tabsVal = tabs.map((t: any) => ({
        label: (typeof t?.label === 'string') ? ({ de: String(t?.label || '') } as any) : ((t?.label && typeof t?.label === 'object') ? t.label : ({ de: '' } as any)),
        url: String(t?.url || ''),
      }));
      try {
        const keys = (tooltipVal && typeof tooltipVal === 'object') ? Object.keys(tooltipVal as any).filter(Boolean) : [];
        const nextLang = keys.includes('de') ? 'de' : (keys[0] || 'de');
        setSidenavLang(nextLang);
      } catch { setSidenavLang('de'); }
      setSidenavEditor({
        open: true,
        sectionId: section.id,
        enabled: cfg?.enabled !== false,
        buttonTooltip: tooltipVal,
        triggerRadius,
        popupRadius,
        popoverTitle: popTitleVal,
        popoverSubtitle: popSubtitleVal,
        popoverHint: popHintVal,
        tabs: tabsVal,
      });
      return;
    }
  }, [setCardStripEditor, setCtaEditor, setCtaLang, setGalleryEditor, setProductsEditor, setReviewsEditor, setScrollListEditor, setScrollListLang, setSidenavEditor, setSidenavLang]);

  useEffect(() => {
    if (!pageBuilderOpen) return;
    if (!Array.isArray(pagesTabs) || pagesTabs.length === 0) return;
    const idx = Math.max(0, Math.min(pageBuilderIndex, pagesTabs.length - 1));
    const url = normalizePageUrl(pagesTabs[idx]?.url || '/');
    const slug = (() => {
      if (url === '/') return 'homepage';
      const cleaned = url.replace(/^\/+/, '').replace(/\/+$/, '');
      const parts = cleaned.split('/').filter(Boolean);
      const last = parts.length > 0 ? parts[parts.length - 1] : '';
      const s = String(last || 'seite').toLowerCase().replace(/[^a-z0-9\-]+/g, '');
      return s || 'seite';
    })();

    let aborted = false;
    (async () => {
      try {
        const res = await fetch(`/pages/${slug}.json?t=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) {
          if (!aborted) {
            setBuilderSections([]);
            setActiveSectionId(null);
          }
          return;
        }
        const data: any = await res.json();
        const content: any = data && typeof data === 'object' ? data.content : null;
        const sectionsRaw: any[] = Array.isArray(content?.sections) ? content.sections : [];

        const normalizeType = (tp: any) => {
          const t = String(tp || '').toLowerCase();
          if (t === 'hero-carousel' || t === 'herocarousel' || t === 'herocarousel') return 'herocarousel';
          if (t === 'hero') return 'hero';
          if (t === 'products') return 'products';
          if (t === 'cardstrip' || t === 'cards') return 'cardstrip';
          if (t === 'gallery') return 'gallery';
          if (t === 'cta') return 'cta';
          if (t === 'reviews') return 'reviews';
          if (t === 'counter') return 'counter';
          if (t === 'paragraph' || t === 'text' || t === 'textblock') return 'paragraph';
          if (t === 'media-text' || t === 'mediatext' || t === 'mediatext') return 'mediatext';
          if (t === 'scroll-list' || t === 'scrolllist' || t === 'scrolllist') return 'scrolllist';
          if (t === 'grid') return 'grid';
          if (t === 'row') return 'row';
          if (t === 'sidenav' || t === 'side-navigation' || t === 'side_navigation') return 'sidenav';
          return t;
        };

        const normalizeSection = (s: any): any => {
          if (!s || typeof s !== 'object') return null;
          const type = normalizeType((s as any).type);
          const id = (typeof (s as any).id === 'string' && (s as any).id) ? (s as any).id : `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
          const title = (typeof (s as any).title === 'string' && (s as any).title) ? (s as any).title : (type === 'hero' ? 'Hero' : type === 'grid' ? 'Grid' : type === 'row' ? 'Row' : 'Sektion');
          if (type === 'grid') {
            return {
              id,
              type: 'grid',
              title,
              open: true,
              grid: (s && typeof (s as any).grid === 'object') ? (s as any).grid : null,
              gridTabs: Array.isArray((s as any).gridTabs) ? (s as any).gridTabs : [],
              gridTabsDisplay: (s && typeof (s as any).gridTabsDisplay === 'object') ? (s as any).gridTabsDisplay : undefined,
            };
          }
          if (type === 'row') {
            const colsRaw = Array.isArray((s as any).columns) ? (s as any).columns : [];
            const col0 = colsRaw[0] ? normalizeSection(colsRaw[0]) : null;
            const col1 = colsRaw[1] ? normalizeSection(colsRaw[1]) : null;
            return {
              id,
              type: 'row',
              title,
              columns: [col0, col1],
            };
          }
          return {
            id,
            type,
            title,
            config: (s && typeof (s as any).config === 'object') ? (s as any).config : {},
          };
        };

        const mapped = sectionsRaw
          .map((s: any) => normalizeSection(s))
          .filter(Boolean);

        if (aborted) return;
        setBuilderSections(mapped);
        try {
          if (typeof window !== 'undefined') {
            const stored = String(window.localStorage.getItem('admin.pageBuilder.activeSectionId') || '');
            const found = mapped.find((m: any) => String(m?.id) === stored);
            setActiveSectionId(found ? stored : null);
          }
        } catch {
          setActiveSectionId(null);
        }
      } catch {
        if (!aborted) {
          setBuilderSections([]);
          setActiveSectionId(null);
        }
      }
    })();

    return () => { aborted = true; };
  }, [pageBuilderOpen, pageBuilderIndex, pagesTabs, normalizePageUrl]);

  const openPageDisplayDialog = React.useCallback(() => {
    const idx = Math.max(0, Math.min(pageBuilderIndex, pagesTabs.length - 1));
    const u = normalizePageUrl(pagesTabs[idx]?.url || '/');
    const curDf = typeof pagesTabs[idx]?.displayFrom === 'string' ? String(pagesTabs[idx].displayFrom) : '';
    // Only for Startseite and 404
    if (u !== '/' && u !== '/404') return;
    if (curDf) {
      setPageDisplayMode('reuse');
      setPageDisplayFrom(curDf);
    } else {
      setPageDisplayMode('own');
      setPageDisplayFrom('');
    }
    setPageDisplayDialogOpen(true);
  }, [pageBuilderIndex, pagesTabs, normalizePageUrl]);

  // When tabs list changes (hydration or savePages response), keep the active tab stable by URL.
  useEffect(() => {
    try {
      if (typeof window === 'undefined') return;
      if (!Array.isArray(pagesTabs) || pagesTabs.length === 0) return;
      const storedUrl = String(window.localStorage.getItem('admin.pageBuilder.pageUrl') || '').trim().toLowerCase();
      const currentUrl = String(pagesTabs[Math.max(0, Math.min(pageBuilderIndex, pagesTabs.length - 1))]?.url || '').trim().toLowerCase();
      const wantUrl = storedUrl || currentUrl;
      if (!wantUrl) return;
      const found = pagesTabs.findIndex((p) => String(p?.url || '').trim().toLowerCase() === wantUrl);
      if (found >= 0 && found !== pageBuilderIndex) {
        setPageBuilderIndex(found);
        setPagesActiveTab(found);
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagesTabs]);

  const deletePageTab = React.useCallback(async (indexToDelete: number) => {
    const idx = Math.max(0, Math.min(indexToDelete, pagesTabs.length - 1));
    const url = normalizePageUrl(pagesTabs[idx]?.url || '/');
    if (idx === 0 || url === '/' || url === '/404') {
      toast.error('Diese Seite kann nicht gelöscht werden.');
      return;
    }

    const next = pagesTabs.filter((_, i) => i !== idx);
    const nextIndex = (() => {
      const cur = Math.max(0, Math.min(pageBuilderIndex, pagesTabs.length - 1));
      if (cur === idx) return Math.max(0, idx - 1);
      if (cur > idx) return cur - 1;
      return cur;
    })();
    const nextActive = (() => {
      const cur = Math.max(0, Math.min(pagesActiveTab, pagesTabs.length - 1));
      if (cur === idx) return Math.max(0, idx - 1);
      if (cur > idx) return cur - 1;
      return cur;
    })();

    setPagesTabs(next);
    setPagesActiveTab(nextActive);
    setPageBuilderIndex(nextIndex);
    setActiveSectionId(null);
    setBuilderSections([]);

    try {
      if (typeof window !== 'undefined') {
        const nextUrl = (next[nextIndex]?.url || '/').trim() || '/';
        window.localStorage.setItem('admin.pageBuilder.pageUrl', nextUrl);
        window.localStorage.setItem('admin.pageBuilder.index', String(nextIndex));
        window.localStorage.setItem('admin.pages.activeTab', String(nextActive));
        window.localStorage.setItem('admin.pageBuilder.open', '1');
      }
    } catch {}

    try {
      const ok = await savePages(next, []);
      if (ok) toast.success('Seite gelöscht', { duration: 1200 });
    } catch {}
  }, [normalizePageUrl, pageBuilderIndex, pagesActiveTab, pagesTabs, savePages]);

  // Hydrate page tabs from website settings (authoritative), fallback to localStorage.
  useEffect(() => {
    try {
      const hp: any = (settings as any)?.homepage;
      let tabsIn: Array<{ label: string; url: string; displayFrom?: string }> | null = null;
      if (hp && typeof hp === 'object' && Array.isArray(hp.pages)) {
        tabsIn = hp.pages
          .filter((p: any) => p && typeof p === 'object')
          .map((p: any) => ({
            label: typeof p.label === 'string' ? p.label : '',
            url: normalizePageUrl(typeof p.url === 'string' ? p.url : '/'),
            displayFrom: typeof p.displayFrom === 'string' ? p.displayFrom : undefined,
          }));
      }
      if (!tabsIn) {
        try {
          if (typeof window !== 'undefined') {
            const raw = window.localStorage.getItem('admin.pages.tabs');
            if (raw) {
              const parsed = JSON.parse(raw);
              if (Array.isArray(parsed)) {
                tabsIn = parsed
                  .filter((p: any) => p && typeof p === 'object')
                  .map((p: any) => ({
                    label: typeof p.label === 'string' ? p.label : '',
                    url: normalizePageUrl(typeof p.url === 'string' ? p.url : '/'),
                    displayFrom: typeof p.displayFrom === 'string' ? p.displayFrom : undefined,
                  }));
              }
            }
          }
        } catch {}
      }

      const ensure = (arr: Array<{ label: string; url: string; displayFrom?: string }>) => {
        const out: Array<{ label: string; url: string; displayFrom?: string }> = [];
        const seen = new Set<string>();
        const push = (label: string, url: string, displayFrom?: string) => {
          const u = normalizePageUrl(url);
          if (seen.has(u)) return;
          seen.add(u);
          out.push({ label: label || (u === '/' ? 'Startseite' : (u === '/404' ? '404 Seite' : 'Seite')), url: u, displayFrom });
        };
        push('Startseite', '/');
        push('404 Seite', '/404');
        (arr || []).forEach((p) => {
          const u = normalizePageUrl(p?.url || '/');
          if (u === '/' || u === '/404') {
            // keep the default labels for special pages
            return;
          }
          push(p?.label || '', u, p?.displayFrom);
        });
        return out;
      };

      const normalized = ensure(tabsIn || []);
      setPagesTabs((prev) => {
        const prevNorm = ensure(prev || []);
        if (JSON.stringify(prevNorm) === JSON.stringify(normalized)) return prev;
        return normalized;
      });
    } catch {}
  }, [settings, normalizePageUrl]);

  // Persist tabs to localStorage for quick restore.
  useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('admin.pages.tabs', JSON.stringify(pagesTabs));
      }
    } catch {}
  }, [pagesTabs]);

  // Initialize grid row height from settings on load
  useEffect(() => {
    const hp: any = (settings as any)?.homepage;
    if (!hp || typeof hp !== 'object') return;
    if (Array.isArray(hp.gridTabs) && hp.gridTabs.length > 0) {
      const rh = hp.gridTabs[0]?.rowHeight;
      if (typeof rh !== 'undefined') setGridRowHeight(Math.max(80, Math.min(480, Number(rh) || 160)));
      const c0 = Math.max(1, Number(hp.gridTabs[0]?.cols) || 1);
      const cw = Array.isArray(hp.gridTabs[0]?.colWidths) ? hp.gridTabs[0].colWidths : null;
      if (cw && cw.length === c0) {
        setGridColWidths(cw.map((n: any) => Math.max(0.25, Number(n) || 1)));
      } else {
        setGridColWidths(Array.from({ length: c0 }).map(() => 1));
      }
    } else if (hp.grid && typeof hp.grid.rowHeight !== 'undefined') {
      setGridRowHeight(Math.max(80, Math.min(480, Number(hp.grid.rowHeight) || 160)));
      const c0 = Math.max(1, Number(hp.grid?.cols) || 1);
      const cw = Array.isArray(hp.grid?.colWidths) ? hp.grid.colWidths : null;
      if (cw && cw.length === c0) {
        setGridColWidths(cw.map((n: any) => Math.max(0.25, Number(n) || 1)));
      } else {
        setGridColWidths(Array.from({ length: c0 }).map(() => 1));
      }
    }
  }, [settings]);

  

  // Ensure at least one list tab exists for better UX
  useEffect(() => {
    if (listTabs.length === 0) {
      const first = { label: 'Liste 1', title: listTitle || '', items: Array.isArray(listItems) ? listItems : [] } as any;
      setListTabs([first]);
      setListActiveTab(0);
      try { if (typeof window !== 'undefined') window.localStorage.setItem('admin.lists.activeTab', '0'); } catch {}
    }
  }, [listTabs.length]);

  // Load lists from settings
  useEffect(() => {
    const hp: any = (settings as any)?.homepage || {};
    const tabsArr = Array.isArray(hp.listTabs) ? hp.listTabs.filter((x: any) => x && typeof x === 'object') : [];
    const use = tabsArr.length > 0 ? tabsArr : [];
    setListTabs(use.map((t: any, i: number) => ({
      label: typeof t?.label === 'string' && t.label ? t.label : `Liste ${i+1}`,
      title: typeof t?.title === 'string' ? t.title : '',
      itemSize: Math.max(20, Math.min(160, Number(t?.itemSize) || 40)),
      iconColor: typeof t?.iconColor === 'string' ? t.iconColor : '',
      iconBg: typeof t?.iconBg === 'string' ? t.iconBg : '',
      items: Array.isArray(t?.items) ? t.items.filter(Boolean).map((it: any) => ({
        text: typeof it?.text === 'string' ? it.text : '',
        subtext: typeof it?.subtext === 'string' ? it.subtext : '',
        href: typeof it?.href === 'string' ? it.href : '',
        icon: typeof it?.icon === 'string' ? it.icon : '',
        iconName: typeof it?.iconName === 'string' ? it.iconName : '',
        image: typeof it?.image === 'string' ? it.image : '',
        mediaType: (typeof it?.mediaType === 'string' && (it.mediaType === 'icon' || it.mediaType === 'image')) ? it.mediaType : 'icon',
        tag: typeof it?.tag === 'string' ? it.tag : '',
      })) : []
    })));
    const savedIdxRaw = typeof window !== 'undefined' ? window.localStorage.getItem('admin.lists.activeTab') : null;
    let idx = Number(savedIdxRaw);
    if (!Number.isInteger(idx) || idx < 0 || idx >= use.length) idx = 0;
    setListActiveTab(idx);
    const cur = use[idx];
    if (cur && typeof cur === 'object') {
      setListTitle(typeof cur.title === 'string' ? cur.title : '');
      setListItemSize(Math.max(20, Math.min(160, Number(cur.itemSize) || 40)));
      setListIconColor(typeof (cur as any).iconColor === 'string' ? (cur as any).iconColor : '');
      setListIconBg(typeof (cur as any).iconBg === 'string' ? (cur as any).iconBg : '');
      setListItems(Array.isArray(cur.items) ? cur.items.filter(Boolean).map((it: any) => ({
        text: typeof it?.text === 'string' ? it.text : '',
        subtext: typeof it?.subtext === 'string' ? it.subtext : '',
        href: typeof it?.href === 'string' ? it.href : '',
        icon: typeof it?.icon === 'string' ? it.icon : '',
        iconName: typeof it?.iconName === 'string' ? it.iconName : '',
        image: typeof it?.image === 'string' ? it.image : '',
        mediaType: (typeof it?.mediaType === 'string' && (it.mediaType === 'icon' || it.mediaType === 'image')) ? it.mediaType : 'icon',
        tag: typeof it?.tag === 'string' ? it.tag : '',
      })) : []);
    } else {
      setListTitle('');
      setListItems([]);
    }
  }, [settings]);

  // Load counter state from website-settings.json on mount/settings change
  useEffect(() => {
    const hp: any = (settings as any)?.homepage || {};
    const tabsArr = Array.isArray(hp.counterTabs) ? hp.counterTabs.filter((x: any) => x && typeof x === 'object') : [];
    const use = tabsArr.length > 0 ? tabsArr : (hp.counter ? [hp.counter] : []);
    if (use.length === 0) return;
    setCounterTabs(use.map((c: any, i: number) => ({
      label: typeof c?.name === 'string' && c.name ? c.name : `Counter ${i+1}`,
      enabled: c?.enabled !== false,
      name: typeof c?.name === 'string' ? c.name : '',
      prefix: typeof c?.prefix === 'string' ? c.prefix : '',
      suffix: typeof c?.suffix === 'string' ? c.suffix : '',
      prefixNewline: !!c?.prefixNewline,
      suffixNewline: !!c?.suffixNewline,
      variant: (typeof c?.variant === 'string' && ['classic','radial','glass'].includes(c.variant)) ? c.variant : 'classic',
      bg: typeof c?.bg === 'string' ? c.bg : 'rgba(255,255,255,0.9)',
      textColor: typeof c?.textColor === 'string' ? c.textColor : '#111827',
      radius: Math.max(0, Math.min(28, Number(c?.radius) || 8)),
      scale: Math.max(0.5, Math.min(3, Number(c?.scale) || 1)),
      fontFamily: typeof c?.fontFamily === 'string' ? c.fontFamily : '',
      valueFontSize: Math.max(10, Math.min(96, Number(c?.valueFontSize) || 28)),
      prefixFontSize: Math.max(8, Math.min(48, Number(c?.prefixFontSize) || 12)),
      suffixFontSize: Math.max(8, Math.min(48, Number(c?.suffixFontSize) || 12)),
      gradientFrom: typeof c?.gradientFrom === 'string' ? c.gradientFrom : '#60a5fa',
      gradientTo: typeof c?.gradientTo === 'string' ? c.gradientTo : '#22c55e',
      ringWidth: Math.max(2, Math.min(24, Number(c?.ringWidth) || 10)),
      shadow: (typeof c?.shadow === 'string' && ['soft','medium','strong','glow','none'].includes(c.shadow)) ? c.shadow : 'soft',
      numberLocale: typeof c?.numberLocale === 'string' ? c.numberLocale : 'de-DE',
      preset: typeof c?.preset === 'string' ? c.preset : ''
    })));
    const savedIdxRaw = typeof window !== 'undefined' ? window.localStorage.getItem('admin.counter.activeTab') : null;
    let idx = Number(savedIdxRaw);
    if (!Number.isInteger(idx) || idx < 0 || idx >= use.length) idx = 0;
    setCounterActiveTab(idx);
    const c = use[idx];
    if (!c || typeof c !== 'object') return;
    if (typeof c.enabled !== 'undefined') setCounterEnabled(!!c.enabled);
    if (typeof c.name === 'string') setCounterName(c.name);
    if (typeof c.prefix === 'string') setCounterPrefix(c.prefix);
    if (typeof c.suffix === 'string') setCounterSuffix(c.suffix);
    if (typeof c.prefixNewline !== 'undefined') setCounterPrefixNewline(!!c.prefixNewline);
    if (typeof c.suffixNewline !== 'undefined') setCounterSuffixNewline(!!c.suffixNewline);
    if (typeof c.bg === 'string') setCounterBg(c.bg);
    if (typeof c.textColor === 'string') setCounterTextColor(c.textColor);
    if (typeof c.radius !== 'undefined') setCounterRadius(Math.max(0, Math.min(28, Number(c.radius) || 8)));
    if (typeof c.scale !== 'undefined') setCounterScale(Math.max(0.5, Math.min(3, Number(c.scale) || 1)));
    const v = typeof c.variant === 'string' && ['classic','radial','glass'].includes(c.variant) ? (c.variant as any) : 'classic';
    setCounterVariant(v);
    if (typeof c.fontFamily === 'string') setCounterFontFamily(c.fontFamily);
    if (typeof c.valueFontSize !== 'undefined') setCounterValueFontSize(Math.max(10, Math.min(96, Number(c.valueFontSize) || 28)));
    if (typeof c.prefixFontSize !== 'undefined') setCounterPrefixFontSize(Math.max(8, Math.min(48, Number(c.prefixFontSize) || 12)));
    if (typeof c.suffixFontSize !== 'undefined') setCounterSuffixFontSize(Math.max(8, Math.min(48, Number(c.suffixFontSize) || 12)));
    if (typeof c.gradientFrom === 'string') setCounterGradientFrom(c.gradientFrom);
    if (typeof c.gradientTo === 'string') setCounterGradientTo(c.gradientTo);
    if (typeof c.ringWidth !== 'undefined') setCounterRingWidth(Math.max(2, Math.min(24, Number(c.ringWidth) || 10)));
    if (typeof c.shadow === 'string' && ['soft','medium','strong','glow','none'].includes(c.shadow)) setCounterShadow(c.shadow as any);
    if (typeof c.numberLocale === 'string') setCounterNumberLocale(c.numberLocale);
    if (typeof c.preset === 'string') setCounterPreset(c.preset);
  }, [settings]);
  // Featured section
  const [featureEnabled, setFeatureEnabled] = useState<boolean>(false);
  const [featureTitle, setFeatureTitle] = useState<string>('');
  const [featureSubtitle, setFeatureSubtitle] = useState<string>('');
  const [featureCtaText, setFeatureCtaText] = useState<string>('');
  const [featureCtaUrl, setFeatureCtaUrl] = useState<string>('');
  const [featureImagePath, setFeatureImagePath] = useState<string>('');
  const [featureImageFile, setFeatureImageFile] = useState<File | null>(null);
  const [featureObjectUrl, setFeatureObjectUrl] = useState<string | null>(null);
  const [featurePicking, setFeaturePicking] = useState<boolean>(false);
  const [savingFeature, setSavingFeature] = useState<boolean>(false);
  const [featureUseGradient, setFeatureUseGradient] = useState<boolean>(false);
  const [featureBgColor, setFeatureBgColor] = useState<string>('#ffffff');
  const [featureGradFrom, setFeatureGradFrom] = useState<string>('#0ea5e9');
  const [featureGradTo, setFeatureGradTo] = useState<string>('#22c55e');
  const [featureTextColor, setFeatureTextColor] = useState<string>('');
  const [featureBtnBg, setFeatureBtnBg] = useState<string>('');
  const [featureBtnText, setFeatureBtnText] = useState<string>('');

  // Event + Featured products section
  const [eventEnabled, setEventEnabled] = useState<boolean>(false);
  const [eventTitle, setEventTitle] = useState<string>('');
  const [eventSubtitle, setEventSubtitle] = useState<string>('');
  const [eventCtaText, setEventCtaText] = useState<string>('');
  const [eventCtaUrl, setEventCtaUrl] = useState<string>('');
  const [eventUseGradient, setEventUseGradient] = useState<boolean>(true);
  const [eventBgColor, setEventBgColor] = useState<string>('#f472b6');
  const [eventGradFrom, setEventGradFrom] = useState<string>('#ec4899');
  const [eventGradTo, setEventGradTo] = useState<string>('#f59e0b');
  const [eventTextColor, setEventTextColor] = useState<string>('');
  const [eventBtnBg, setEventBtnBg] = useState<string>('');
  const [eventBtnText, setEventBtnText] = useState<string>('');
  const [eventProductIdsText, setEventProductIdsText] = useState<string>('');
  // Grid section
  const [gridEnabled, setGridEnabled] = useState<boolean>(false);
  const [gridTitle, setGridTitle] = useState<string>('');
  const [gridRows, setGridRows] = useState<number>(2);
  const [gridCols, setGridCols] = useState<number>(3);
  const [gridRowHeight, setGridRowHeight] = useState<number>(160);
  const [gridTiles, setGridTiles] = useState<Array<{ title: string; subtitle?: string; image?: string; images?: string[]; imageInterval?: number; ctaText?: string; ctaUrl?: string; bgColor?: string; textColor?: string; enabled?: boolean; reserveSpace?: boolean; colSpan?: number; rowSpan?: number; hoverEffect?: string; ctaFree?: boolean; ctaX?: number; ctaY?: number; ctaScale?: number; ctaBtnBg?: string; ctaBtnTextColor?: string; ctaHoverEffect?: string; ctaFontSize?: number; ctaWidth?: number; showCounter?: boolean; counterFree?: boolean; counterTabIndex?: number; counterX?: number; counterY?: number; counterScale?: number; counterBg?: string; counterTextColor?: string; counterRadius?: number; counterPrefix?: string; counterSuffix?: string; counterPrefixNewline?: boolean; counterSuffixNewline?: boolean; showList?: boolean; listTabIndex?: number; listFullSize?: boolean; listScrollDir?: 'vertical' | 'horizontal' }>>([]);
  // Column sizing for live preview (md+). Unit: fr weights per column
  const [gridColWidths, setGridColWidths] = useState<number[]>([]);
  const gridPreviewRef = useRef<HTMLDivElement | null>(null);
  // Keep widths array length in sync with current gridCols
  useEffect(() => {
    const cols = Math.max(1, Number(gridCols) || 1);
    if (gridColWidths.length !== cols) {
      setGridColWidths(Array.from({ length: cols }).map(() => 1));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridCols]);
  // Tabs management for Grid
  const [gridTabs, setGridTabs] = useState<Array<{ label: string; enabled?: boolean; title?: string; rows?: number; cols?: number; rowHeight?: number; tiles?: any[] }>>([]);
  const [gridActiveTab, setGridActiveTab] = useState<number>(0);
  // Tabs display preferences
  const [gridTabsPosition, setGridTabsPosition] = useState<'above' | 'below'>('above');
  const [gridTabsStyle, setGridTabsStyle] = useState<'labels' | 'dots'>('labels');
  const [gridTabsArrows, setGridTabsArrows] = useState<boolean>(false);
  const [gridTabsTransitionType, setGridTabsTransitionType] = useState<'none' | 'slide' | 'tiles' | 'cube'>('none');
  // Persist current inner grid tab for the active section, scoped by page URL + section ID
  useEffect(() => {
    try {
      if (typeof window === 'undefined' || !pageBuilderOpen) return;
      const url = (pagesTabs && pagesTabs[pageBuilderIndex]) ? (pagesTabs[pageBuilderIndex].url || '/') : '/';
      if (!activeSectionId || !Array.isArray(pagesTabs) || pagesTabs.length === 0) return;
      window.localStorage.setItem(`admin.pageBuilder.gridActiveTab::${url}::${activeSectionId}` , String(gridActiveTab));
    } catch {}
  }, [gridActiveTab, pageBuilderOpen, pageBuilderIndex, activeSectionId, pagesTabs]);
  const [savingGrid, setSavingGrid] = useState<boolean>(false);
  const [deletingGrid, setDeletingGrid] = useState<boolean>(false);
  const [gridPickerOpen, setGridPickerOpen] = useState<{ open: boolean; index: number | null }>({ open: false, index: null });

  const buildHomepagePayload = React.useCallback(() => {
    const mergedCounterTabs = (counterTabs.length > 0 ? counterTabs : []).map((x, idx) => idx === counterActiveTab ? {
      ...x,
      label: counterName || x.label,
      enabled: counterEnabled,
      name: counterName,
      prefix: counterPrefix,
      suffix: counterSuffix,
      prefixNewline: counterPrefixNewline,
      suffixNewline: counterSuffixNewline,
      variant: counterVariant,
      bg: counterBg,
      textColor: counterTextColor,
      radius: counterRadius,
      scale: counterScale,
      fontFamily: counterFontFamily,
      valueFontSize: counterValueFontSize,
      prefixFontSize: counterPrefixFontSize,
      suffixFontSize: counterSuffixFontSize,
      gradientFrom: counterGradientFrom,
      gradientTo: counterGradientTo,
      ringWidth: counterRingWidth,
      shadow: counterShadow,
      numberLocale: counterNumberLocale,
      preset: counterPreset,
    } : x);

    const mergedListTabs = (listTabs.length > 0 ? listTabs : []).map((x, idx) => idx === listActiveTab ? {
      ...x,
      label: listTitle || x.label,
      title: listTitle,
      itemSize: listItemSize,
      iconColor: listIconColor,
      iconBg: listIconBg,
      items: listItems,
    } : x);

    const rowHeightPx = Math.max(80, Math.min(480, Number(gridRowHeight) || 160));
    const rows = Math.max(1, Number(gridRows) || 1);
    const cols = Math.max(1, Number(gridCols) || 1);
    const total = rows * cols;
    const paddedTiles = Array.from({ length: total }).map((_, i) => gridTiles[i] || {
      title: '', subtitle: '', image: '', images: [], imageInterval: undefined,
      ctaText: '', ctaUrl: '', bgColor: '', textColor: '', enabled: true,
      reserveSpace: false, colSpan: 1, rowSpan: 1, hoverEffect: 'none',
      ctaFree: false, ctaX: 15, ctaY: 80, ctaScale: 1,
      ctaBtnBg: '', ctaBtnTextColor: '', ctaHoverEffect: 'none', ctaFontSize: 14, ctaWidth: 140,
      showCounter: false, counterFree: false, counterTabIndex: -1, counterX: 85, counterY: 85, counterScale: 1,
      counterBg: 'rgba(255,255,255,0.9)', counterTextColor: '#111827', counterRadius: 8,
      counterPrefix: '', counterSuffix: '', counterPrefixNewline: false, counterSuffixNewline: false,
      showList: false, listTabIndex: -1, listFullSize: false, listScrollDir: 'vertical',
    });

    const tabsOut = (() => {
      const base = gridTabs.length > 0 ? [...gridTabs] : [{ label: 'Tab 1', enabled: gridEnabled, title: gridTitle, rows, cols, rowHeight: rowHeightPx, tiles: paddedTiles }];
      if (base[gridActiveTab]) {
        base[gridActiveTab] = { ...base[gridActiveTab], enabled: gridEnabled, title: gridTitle, rows, cols, rowHeight: rowHeightPx, tiles: paddedTiles, colWidths: gridColWidths } as any;
      }
      return base;
    })();

    const eventProductIds = (eventProductIdsText || '').split(',').map(s => s.trim()).filter(Boolean);

    const homepage: any = {
      heroEnabled,
      mediaPath,
      mediaType: mediaType || undefined,
      gallery,
      slideshow,
      gridTabs: tabsOut.map((tb: any, i: number) => ({
        label: typeof tb?.label === 'string' && tb.label ? tb.label : `Tab ${i + 1}`,
        enabled: tb?.enabled !== false,
        title: tb?.title || '',
        rows: Math.max(1, Number(tb?.rows) || 1),
        cols: Math.max(1, Number(tb?.cols) || 1),
        rowHeight: Math.max(80, Math.min(480, Number(tb?.rowHeight) || 160)),
        tiles: Array.isArray(tb?.tiles) ? tb.tiles : [],
        colWidths: Array.isArray((tb as any)?.colWidths) ? (tb as any).colWidths : undefined,
      })),
      gridTabsDisplay: { position: gridTabsPosition, style: gridTabsStyle, arrowsEnabled: gridTabsArrows, transitionType: gridTabsTransitionType },
      promo: {
        enabled: promoEnabled,
        showOncePerSession: promoOnce,
        title: promoTitle,
        message: promoMessage,
        ctaText: promoCtaText,
        ctaUrl: promoCtaUrl,
        image: promoImagePath,
        images: promoImages,
        imageInterval: promoImageInterval,
        imagesLayout: promoImagesLayout,
        imagesLinks: promoImagesLinks,
        displayMode: promoDisplayMode,
        drawerSide: promoDrawerSide,
      },
      feature: {
        enabled: featureEnabled,
        title: featureTitle,
        subtitle: featureSubtitle,
        ctaText: featureCtaText,
        ctaUrl: featureCtaUrl,
        image: featureImagePath,
        useGradient: featureUseGradient,
        bgColor: featureBgColor,
        gradientFrom: featureGradFrom,
        gradientTo: featureGradTo,
        textColor: featureTextColor,
        buttonBg: featureBtnBg,
        buttonText: featureBtnText,
      },
      eventFeatured: {
        enabled: eventEnabled,
        title: eventTitle,
        subtitle: eventSubtitle,
        ctaText: eventCtaText,
        ctaUrl: eventCtaUrl,
        useGradient: eventUseGradient,
        bgColor: eventBgColor,
        gradientFrom: eventGradFrom,
        gradientTo: eventGradTo,
        textColor: eventTextColor,
        buttonBg: eventBtnBg,
        buttonText: eventBtnText,
        productIds: eventProductIds,
      },
      circles: {
        enabled: circlesEnabled,
        title: circlesTitle,
        items: circlesItems,
      },
      counterTabs: mergedCounterTabs,
      listTabs: mergedListTabs,
      pages: pagesTabs,
    };

    return { homepage, mergedCounterTabs, mergedListTabs };
  }, [
    counterTabs,
    counterActiveTab,
    counterEnabled,
    counterName,
    counterPrefix,
    counterSuffix,
    counterPrefixNewline,
    counterSuffixNewline,
    counterVariant,
    counterBg,
    counterTextColor,
    counterRadius,
    counterScale,
    counterFontFamily,
    counterValueFontSize,
    counterPrefixFontSize,
    counterSuffixFontSize,
    counterGradientFrom,
    counterGradientTo,
    counterRingWidth,
    counterShadow,
    counterNumberLocale,
    counterPreset,
    listTabs,
    listActiveTab,
    listTitle,
    listItemSize,
    listIconColor,
    listIconBg,
    listItems,
    gridRowHeight,
    gridRows,
    gridCols,
    gridTiles,
    gridTabs,
    gridActiveTab,
    gridEnabled,
    gridTitle,
    gridColWidths,
    gridTabsPosition,
    gridTabsStyle,
    gridTabsArrows,
    gridTabsTransitionType,
    heroEnabled,
    mediaPath,
    mediaType,
    gallery,
    slideshow,
    promoEnabled,
    promoOnce,
    promoTitle,
    promoMessage,
    promoCtaText,
    promoCtaUrl,
    promoImagePath,
    promoImages,
    promoImageInterval,
    promoImagesLayout,
    promoImagesLinks,
    promoDisplayMode,
    promoDrawerSide,
    featureEnabled,
    featureTitle,
    featureSubtitle,
    featureCtaText,
    featureCtaUrl,
    featureImagePath,
    featureUseGradient,
    featureBgColor,
    featureGradFrom,
    featureGradTo,
    featureTextColor,
    featureBtnBg,
    featureBtnText,
    eventEnabled,
    eventTitle,
    eventSubtitle,
    eventCtaText,
    eventCtaUrl,
    eventUseGradient,
    eventBgColor,
    eventGradFrom,
    eventGradTo,
    eventTextColor,
    eventBtnBg,
    eventBtnText,
    eventProductIdsText,
    circlesEnabled,
    circlesTitle,
    circlesItems,
    pagesTabs,
  ]);

  const lastSavedRef = React.useRef<string>('');
  const [overallSaving, setOverallSaving] = React.useState<boolean>(false);
  const [overallDirty, setOverallDirty] = React.useState<boolean>(false);

  useEffect(() => {
    if (!lastSavedRef.current) {
      try {
        const currentHp = (settings as any)?.homepage || {};
        lastSavedRef.current = JSON.stringify(currentHp);
      } catch {
        lastSavedRef.current = '';
      }
    }
  }, [settings]);

  useEffect(() => {
    try {
      const next = buildHomepagePayload().homepage;
      setOverallDirty(JSON.stringify(next) !== lastSavedRef.current);
    } catch {
      setOverallDirty(true);
    }
  }, [buildHomepagePayload]);

  const saveHomepageAll = async () => {
    const endpoint = (process.env.REACT_APP_WEBSITE_SETTINGS_ENDPOINT as string | undefined) || '/api/update-website-settings.php';
    let token = secret;
    if (!token) {
      const s = await AdminKeyService.getSecret();
      setSecret(s);
      token = s;
    }
    if (!token) { toast.error('Missing admin key.'); return; }

    const loadingId = toast.loading('Startseite speichern...');
    setOverallSaving(true);
    try {
      const { homepage, mergedCounterTabs, mergedListTabs } = buildHomepagePayload();
      const payload: any = { mode, homepage, token };
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Auth-Token': token, 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const raw = await res.text();
      let json: any = null; try { json = raw ? JSON.parse(raw) : null; } catch {}
      if (!res.ok || !json?.ok) {
        const detail = json?.error || raw || 'No response body';
        throw new Error(`${res.status} ${res.statusText} - ${detail}`);
      }

      // Keep local state consistent with what we just saved
      setCounterTabs(mergedCounterTabs);
      setListTabs(mergedListTabs);

      try {
        lastSavedRef.current = JSON.stringify(homepage);
      } catch {
        lastSavedRef.current = '';
      }
      setOverallDirty(false);

      // Live update other parts of the app (including HomePage) without requiring refresh
      try {
        window.dispatchEvent(new CustomEvent('websiteSettingsUpdated', { detail: { homepage } }));
      } catch {
        // ignore
      }

      toast.success('Startseite gespeichert', { id: loadingId });
    } catch (e: any) {
      toast.error(`Speichern fehlgeschlagen: ${e?.message || 'Unknown error'}`, { id: loadingId });
    } finally {
      setOverallSaving(false);
    }
  };
  // Grid tile editor dialog state
  const [tileEditorOpen, setTileEditorOpen] = useState<boolean>(false);
  const [tileEditorIndex, setTileEditorIndex] = useState<number | null>(null);
  const [tileEditorFullScreen, setTileEditorFullScreen] = useState<boolean>(false);
  const [teTitle, setTeTitle] = useState<string>('');
  const [teSubtitle, setTeSubtitle] = useState<string>('');
  const [teCtaText, setTeCtaText] = useState<string>('');
  const [teCtaUrl, setTeCtaUrl] = useState<string>('');
  const [teShowCounter, setTeShowCounter] = useState<boolean>(false);
  const [teCounterFree, setTeCounterFree] = useState<boolean>(false);
  const [teCounterTabIndex, setTeCounterTabIndex] = useState<number>(-1);
  const [teCounterX, setTeCounterX] = useState<number>(85);
  const [teCounterY, setTeCounterY] = useState<number>(85);
  const [teCounterScale, setTeCounterScale] = useState<number>(1);
  const [teCounterDragging, setTeCounterDragging] = useState<boolean>(false);
  const [teCounterResizing, setTeCounterResizing] = useState<boolean>(false);
  const [teCounterSnapX, setTeCounterSnapX] = useState<number | null>(null);
  const [teCounterSnapY, setTeCounterSnapY] = useState<number | null>(null);
  const [teCounterSettingsAnchor, setTeCounterSettingsAnchor] = useState<HTMLElement | null>(null);
  const [teCounterBg, setTeCounterBg] = useState<string>('rgba(255,255,255,0.9)');
  const [teCounterTextColor, setTeCounterTextColor] = useState<string>('#111827');
  const [teCounterRadius, setTeCounterRadius] = useState<number>(8);
  const [teCounterPrefix, setTeCounterPrefix] = useState<string>('');
  const [teCounterSuffix, setTeCounterSuffix] = useState<string>('');
  const [teCounterPrefixNewline, setTeCounterPrefixNewline] = useState<boolean>(false);
  const [teCounterSuffixNewline, setTeCounterSuffixNewline] = useState<boolean>(false);
  const [teShowList, setTeShowList] = useState<boolean>(false);
  const [teListTabIndex, setTeListTabIndex] = useState<number>(-1);
  const [teListFullSize, setTeListFullSize] = useState<boolean>(false);
  const [listSettingsDialog, setListSettingsDialog] = useState<{ open: boolean; index: number | null }>({ open: false, index: null });
  // Counter data source (Supabase) - editor-only for now
  const [teCounterSchema, setTeCounterSchema] = useState<string>('public');
  const [teCounterTable, setTeCounterTable] = useState<string>('');
  const [teCounterColumn, setTeCounterColumn] = useState<string>('');
  const [teCounterTables, setTeCounterTables] = useState<string[]>([]);
  const [teCounterTablesLoading, setTeCounterTablesLoading] = useState<boolean>(false);
  const [teCounterTablesError, setTeCounterTablesError] = useState<string>('');
  const [teCounterSearch, setTeCounterSearch] = useState<string>('');
  const [teCounterColumns, setTeCounterColumns] = useState<string[]>([]);
  const [teCounterColumnsLoading, setTeCounterColumnsLoading] = useState<boolean>(false);
  const [teCounterColumnsError, setTeCounterColumnsError] = useState<string>('');
  const [teCounterCount, setTeCounterCount] = useState<number | null>(null);
  const [teCounterCountLoading, setTeCounterCountLoading] = useState<boolean>(false);
  const [teCounterCountError, setTeCounterCountError] = useState<string>('');

  // Helpers to load tables/columns from Supabase meta views
  const loadSupabaseTables = async (schema: string) => {
    try {
      setTeCounterTablesError(''); setTeCounterTablesLoading(true);
      const { data, error } = await (supabase as any).rpc('list_public_tables', { p_schema: schema });
      if (error) throw error;
      const list = Array.isArray(data) ? data.map((r:any)=> r.table_name || r).filter(Boolean).map(String).sort() : [];
      setTeCounterTables(list);
    } catch(e:any){ setTeCounterTablesError(e?.message||'Konnte Tabellen nicht laden'); setTeCounterTables([]); }
    finally { setTeCounterTablesLoading(false); }
  };

  const removeGridTab = async (idx: number) => {
    const endpoint = (process.env.REACT_APP_WEBSITE_SETTINGS_ENDPOINT as string | undefined) || '/api/update-website-settings.php';
    let token = secret; if (!token) { const s = await AdminKeyService.getSecret(); setSecret(s); token = s; }
    if (!token) { toast.error('Missing admin key.'); return; }
    const nextTabs = gridTabs.filter((_, i) => i !== idx);
    setGridTabs(nextTabs);
    const outTabs = nextTabs.map((tb, i) => ({
      label: tb.label || `Tab ${i+1}`,
      enabled: tb.enabled !== false,
      title: tb.title || '',
      rows: Math.max(1, Number(tb.rows) || 1),
      cols: Math.max(1, Number(tb.cols) || 1),
      rowHeight: Math.max(80, Math.min(480, Number(tb.rowHeight) || 160)),
      tiles: Array.isArray((tb as any).tiles) ? (tb as any).tiles : [],
      colWidths: Array.isArray((tb as any).colWidths) ? (tb as any).colWidths : undefined,
    }));
    const url = pagesTabs[pageBuilderIndex]?.url || '/';
    const isPage = url !== '/';
    let pageSections: any = undefined;
    if (isPage) {
      const display = { position: gridTabsPosition, style: gridTabsStyle, arrowsEnabled: gridTabsArrows, transitionType: gridTabsTransitionType } as any;
      const nextSections = builderSections.map((s: any) => s.id === activeSectionId ? { ...s, gridTabs: outTabs, gridTabsDisplay: display } : s);
      pageSections = nextSections;
      setBuilderSections(nextSections);
    }
    const payload: any = {
      mode,
      homepage: { gridTabs: outTabs, gridTabsDisplay: { position: gridTabsPosition, style: gridTabsStyle, arrowsEnabled: gridTabsArrows, transitionType: gridTabsTransitionType } },
      pageUrl: url,
      ...(isPage && { pageSections, pageSectionId: activeSectionId || undefined }),
      token,
    };
    try {
      const res = await fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Auth-Token': token, 'Authorization': `Bearer ${token}` }, body: JSON.stringify(payload) });
      const raw = await res.text(); let json: any = null; try { json = raw ? JSON.parse(raw) : null; } catch {}
      if (!res.ok || !json?.ok) { const detail = json?.error || raw || 'No response body'; throw new Error(`${res.status} ${res.statusText} - ${detail}`); }
      if (Array.isArray(json?.homepage?.gridTabs)) {
        const tabs = (json.homepage.gridTabs as any[]).map((tb: any, i2: number) => ({
          label: typeof tb?.label === 'string' && tb.label ? tb.label : `Tab ${i2+1}`,
          enabled: tb?.enabled !== false,
          title: tb?.title || '',
          rows: Math.max(1, Number(tb?.rows)||1),
          cols: Math.max(1, Number(tb?.cols)||1),
          rowHeight: Math.max(80, Math.min(480, Number(tb?.rowHeight) || 160)),
          tiles: Array.isArray(tb?.tiles) ? tb.tiles : [],
          colWidths: Array.isArray((tb as any).colWidths) ? (tb as any).colWidths : undefined,
        }));
        setGridTabs(tabs);
        setGridActiveTab(prev => Math.max(0, Math.min(prev > idx ? prev - 1 : prev, tabs.length - 1)));
      }
      toast.success('Tab gelöscht.');
    } catch (e: any) {
      toast.error('Löschen fehlgeschlagen.');
    }
  };

  // Save Counter section
  const saveCounter = async () => {
    const endpoint = (process.env.REACT_APP_WEBSITE_SETTINGS_ENDPOINT as string | undefined) || '/api/update-website-settings.php';
    let token = secret; if (!token) { const s = await AdminKeyService.getSecret(); setSecret(s); token = s; }
    if (!token) { toast.error('Missing admin key.'); return; }
    setSavingCounter(true);
    const loadingId = toast.loading('Counter speichern...');
    try {
      // Persist active tab edits into a local array and use it for saving
      const mergedTabs = (counterTabs.length > 0 ? counterTabs : []).map((x, idx) => idx === counterActiveTab ? {
        ...x,
        label: counterName || x.label,
        enabled: counterEnabled,
        name: counterName,
        prefix: counterPrefix,
        suffix: counterSuffix,
        prefixNewline: counterPrefixNewline,
        suffixNewline: counterSuffixNewline,
        variant: counterVariant,
        bg: counterBg,
        textColor: counterTextColor,
        radius: counterRadius,
        scale: counterScale,
        fontFamily: counterFontFamily,
        valueFontSize: counterValueFontSize,
        prefixFontSize: counterPrefixFontSize,
        suffixFontSize: counterSuffixFontSize,
        gradientFrom: counterGradientFrom,
        gradientTo: counterGradientTo,
        ringWidth: counterRingWidth,
        shadow: counterShadow,
        numberLocale: counterNumberLocale,
        preset: counterPreset,
      } : x);
      setCounterTabs(mergedTabs);

      const tabsOut = mergedTabs.length > 0 ? mergedTabs.map((c: any) => ({
        label: c.label || '', enabled: c.enabled !== false,
        name: c.name || '', prefix: c.prefix || '', suffix: c.suffix || '',
        prefixNewline: !!c.prefixNewline, suffixNewline: !!c.suffixNewline,
        variant: c.variant, bg: c.bg, textColor: c.textColor, radius: c.radius, scale: c.scale,
        fontFamily: c.fontFamily || '', valueFontSize: c.valueFontSize, prefixFontSize: c.prefixFontSize, suffixFontSize: c.suffixFontSize,
        gradientFrom: c.gradientFrom, gradientTo: c.gradientTo, ringWidth: c.ringWidth, shadow: c.shadow, numberLocale: c.numberLocale, preset: c.preset
      })) : [];

      const payload: any = {
        mode,
        homepage: {
          counter: {
            enabled: counterEnabled,
            name: counterName,
            prefix: counterPrefix,
            suffix: counterSuffix,
            prefixNewline: !!counterPrefixNewline,
            suffixNewline: !!counterSuffixNewline,
            variant: counterVariant,
            bg: counterBg,
            textColor: counterTextColor,
            radius: Math.max(0, Math.min(28, Number(counterRadius) || 8)),
            scale: Math.max(0.5, Math.min(3, Number(counterScale) || 1)),
            fontFamily: counterFontFamily,
            valueFontSize: Math.max(10, Math.min(96, Number(counterValueFontSize) || 28)),
            prefixFontSize: Math.max(8, Math.min(48, Number(counterPrefixFontSize) || 12)),
            suffixFontSize: Math.max(8, Math.min(48, Number(counterSuffixFontSize) || 12)),
            gradientFrom: counterGradientFrom,
            gradientTo: counterGradientTo,
            ringWidth: Math.max(2, Math.min(24, Number(counterRingWidth) || 10)),
            shadow: counterShadow,
            numberLocale: counterNumberLocale,
            preset: counterPreset,
          },
          counterTabs: tabsOut,
        },
        token,
      };
      const res = await fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Auth-Token': token, 'Authorization': `Bearer ${token}` }, body: JSON.stringify(payload) });
      const raw = await res.text(); let json: any = null; try { json = raw ? JSON.parse(raw) : null; } catch {}
      if (!res.ok || !json?.ok) { const detail = json?.error || raw || 'No response body'; throw new Error(`${res.status} ${res.statusText} - ${detail}`); }
      if (json?.homepage?.counter) {
        const c = json.homepage.counter as any;
        setCounterEnabled(!!c.enabled);
        setCounterName(typeof c.name === 'string' ? c.name : '');
        setCounterPrefix(typeof c.prefix === 'string' ? c.prefix : '');
        setCounterSuffix(typeof c.suffix === 'string' ? c.suffix : '');
        setCounterPrefixNewline(!!c.prefixNewline);
        setCounterSuffixNewline(!!c.suffixNewline);
        setCounterBg(typeof c.bg === 'string' ? c.bg : 'rgba(255,255,255,0.9)');
        setCounterTextColor(typeof c.textColor === 'string' ? c.textColor : '#111827');
        setCounterRadius(Math.max(0, Math.min(28, Number(c.radius) || 8)));
        setCounterScale(Math.max(0.5, Math.min(3, Number(c.scale) || 1)));
        const v = typeof c.variant === 'string' && ['classic','radial','glass'].includes(c.variant) ? (c.variant as any) : 'classic';
        setCounterVariant(v);
        setCounterFontFamily(typeof c.fontFamily === 'string' ? c.fontFamily : '');
        setCounterValueFontSize(Math.max(10, Math.min(96, Number(c.valueFontSize) || 28)));
        setCounterPrefixFontSize(Math.max(8, Math.min(48, Number(c.prefixFontSize) || 12)));
        setCounterSuffixFontSize(Math.max(8, Math.min(48, Number(c.suffixFontSize) || 12)));
        setCounterGradientFrom(typeof c.gradientFrom === 'string' ? c.gradientFrom : '#60a5fa');
        setCounterGradientTo(typeof c.gradientTo === 'string' ? c.gradientTo : '#22c55e');
        setCounterRingWidth(Math.max(2, Math.min(24, Number(c.ringWidth) || 10)));
        setCounterShadow((typeof c.shadow === 'string' && ['soft','medium','strong','glow','none'].includes(c.shadow)) ? (c.shadow as any) : 'soft');
        setCounterNumberLocale(typeof c.numberLocale === 'string' ? c.numberLocale : 'de-DE');
        setCounterPreset(typeof c.preset === 'string' ? c.preset : '');
      }
      if (Array.isArray(json?.homepage?.counterTabs)) {
        setCounterTabs((json.homepage.counterTabs as any[]).map((c: any, i: number) => ({
          label: typeof c?.label === 'string' && c.label ? c.label : `Counter ${i+1}`,
          enabled: c?.enabled !== false,
          name: typeof c?.name === 'string' ? c.name : '',
          prefix: typeof c?.prefix === 'string' ? c.prefix : '',
          suffix: typeof c?.suffix === 'string' ? c.suffix : '',
          prefixNewline: !!c?.prefixNewline,
          suffixNewline: !!c?.suffixNewline,
          variant: (typeof c?.variant === 'string' && ['classic','radial','glass'].includes(c.variant)) ? c.variant : 'classic',
          bg: typeof c?.bg === 'string' ? c.bg : 'rgba(255,255,255,0.9)',
          textColor: typeof c?.textColor === 'string' ? c.textColor : '#111827',
          radius: Math.max(0, Math.min(28, Number(c?.radius) || 8)),
          scale: Math.max(0.5, Math.min(3, Number(c?.scale) || 1)),
          fontFamily: typeof c?.fontFamily === 'string' ? c.fontFamily : '',
          valueFontSize: Math.max(10, Math.min(96, Number(c?.valueFontSize) || 28)),
          prefixFontSize: Math.max(8, Math.min(48, Number(c?.prefixFontSize) || 12)),
          suffixFontSize: Math.max(8, Math.min(48, Number(c?.suffixFontSize) || 12)),
          gradientFrom: typeof c?.gradientFrom === 'string' ? c.gradientFrom : '#60a5fa',
          gradientTo: typeof c?.gradientTo === 'string' ? c.gradientTo : '#22c55e',
          ringWidth: Math.max(2, Math.min(24, Number(c?.ringWidth) || 10)),
          shadow: (typeof c?.shadow === 'string' && ['soft','medium','strong','glow','none'].includes(c.shadow)) ? c.shadow : 'soft',
          numberLocale: typeof c?.numberLocale === 'string' ? c.numberLocale : 'de-DE',
          preset: typeof c?.preset === 'string' ? c.preset : ''
        })));
      }
      toast.success('Counter gespeichert', { id: loadingId });
    } catch (e: any) {
      toast.error(`Speichern fehlgeschlagen: ${e?.message || 'Unknown error'}`, { id: loadingId });
    } finally {
      setSavingCounter(false);
    }
  };
  const loadSupabaseColumns = async (schema: string, table: string) => {
    if (!table) { setTeCounterColumns([]); return; }
    try {
      setTeCounterColumnsError(''); setTeCounterColumnsLoading(true);
      const { data, error } = await (supabase as any).rpc('list_table_columns', { p_schema: schema, p_table: table });
      if (error) throw error;
      const list = Array.isArray(data) ? data.map((r:any)=> r.column_name || r).filter(Boolean).map(String).sort() : [];
      setTeCounterColumns(list);
    } catch(e:any){ setTeCounterColumnsError(e?.message||'Konnte Spalten nicht laden'); setTeCounterColumns([]); }
    finally { setTeCounterColumnsLoading(false); }
  };

  // Auto-load when popover opens
  useEffect(() => {
    if (teCounterSettingsAnchor) {
      loadSupabaseTables(teCounterSchema);
      if (teCounterTable) loadSupabaseColumns(teCounterSchema, teCounterTable);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teCounterSettingsAnchor]);
  // Reload tables when schema changes while open
  useEffect(() => {
    if (teCounterSettingsAnchor) { loadSupabaseTables(teCounterSchema); setTeCounterTable(''); setTeCounterColumns([]); setTeCounterColumn(''); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teCounterSchema]);
  // Load columns when table changes while open
  useEffect(() => {
    if (teCounterSettingsAnchor && teCounterTable) { loadSupabaseColumns(teCounterSchema, teCounterTable); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teCounterTable]);

  // Load live count when table changes
  useEffect(() => {
    const run = async () => {
      setTeCounterCountError(''); setTeCounterCount(null);
      if (!teCounterTable) return;
      try {
        setTeCounterCountLoading(true);
        const { data, error } = await (supabase as any).rpc('count_table_rows', { p_schema: teCounterSchema, p_table: teCounterTable });
        if (error) throw error;
        const n = typeof data === 'number' ? data : Number(data);
        setTeCounterCount(Number.isFinite(n) ? n : 0);
      } catch(e:any){ setTeCounterCountError(e?.message||'Zählen fehlgeschlagen'); setTeCounterCount(null); }
      finally { setTeCounterCountLoading(false); }
    };
    if (teCounterSettingsAnchor) run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teCounterSchema, teCounterTable, teCounterSettingsAnchor]);
  const [teCtaFree, setTeCtaFree] = useState<boolean>(false);
  const [teCtaX, setTeCtaX] = useState<number>(15);
  const [teCtaY, setTeCtaY] = useState<number>(80);
  const [teCtaScale, setTeCtaScale] = useState<number>(1);
  const [teCtaBtnBg, setTeCtaBtnBg] = useState<string>('');
  const [teCtaBtnTextColor, setTeCtaBtnTextColor] = useState<string>('');
  const [teCtaHoverEffect, setTeCtaHoverEffect] = useState<string>('none');
  const [teCtaFontSize, setTeCtaFontSize] = useState<number>(14);
  const [teCtaWidth, setTeCtaWidth] = useState<number>(140);
  const [teImages, setTeImages] = useState<string[]>([]);
  const [teImageInterval, setTeImageInterval] = useState<number>(5);
  const [teBgColor, setTeBgColor] = useState<string>('');
  const [teTextColor, setTeTextColor] = useState<string>('');
  const [teEnabled, setTeEnabled] = useState<boolean>(true);
  const [teReserveSpace, setTeReserveSpace] = useState<boolean>(false);
  const [teColSpan, setTeColSpan] = useState<number>(1);
  const [teRowSpan, setTeRowSpan] = useState<number>(1);
  const [teHoverEffect, setTeHoverEffect] = useState<string>('none');
  const [teGuideX, setTeGuideX] = useState<number | null>(null);
  const [teGuideY, setTeGuideY] = useState<number | null>(null);
  const [ctaSettingsOpen, setCtaSettingsOpen] = useState<boolean>(false);
  const [ctaSettingsFull, setCtaSettingsFull] = useState<boolean>(false);
  const [gridPickerSelection, setGridPickerSelection] = useState<Set<string>>(new Set());
  const [teImgDragIdx, setTeImgDragIdx] = useState<number | null>(null);
  const [teImgDropIndex, setTeImgDropIndex] = useState<number | null>(null);
  const [teImgDropSide, setTeImgDropSide] = useState<'left' | 'right' | null>(null);
  const [teImgDragging, setTeImgDragging] = useState<boolean>(false);
  // Tick to animate gallery thumbnails in admin preview
  const [gridTick, setGridTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setGridTick(t => (t + 1) % 1_000_000), 1000);
    return () => window.clearInterval(id);
  }, []);

  const toPublicUrl = (p?: string | null) => {
    if (!p) return '';
    if (/^https?:\/\//i.test(p)) return p;
    // For Admin UI, keep relative /uploads/* paths as-is to use current origin
    return p;
  };

  const persistEditingIntoActiveTab = () => {
    setGridTabs(prev => {
      const next = [...prev];
      const rows = Math.max(1, Number(gridRows) || 1);
      const cols = Math.max(1, Number(gridCols) || 1);
      const rowHeightPx = Math.max(80, Math.min(480, Number(gridRowHeight) || 160));
      const total = rows * cols;
      const padded = Array.from({ length: total }).map((_, i) => gridTiles[i] || {
        title: '', subtitle: '', image: '', images: [], imageInterval: undefined,
        ctaText: '', ctaUrl: '', bgColor: '', textColor: '', enabled: true, reserveSpace: false, colSpan: 1, rowSpan: 1, hoverEffect: 'none',
        ctaFree: false, ctaX: 15, ctaY: 80, ctaScale: 1, ctaBtnBg: '', ctaBtnTextColor: '', ctaHoverEffect: 'none', ctaFontSize: 14, ctaWidth: 140,
        showCounter: false, counterFree: false, counterX: 85, counterY: 85, counterScale: 1,
        counterBg: 'rgba(255,255,255,0.9)', counterTextColor: '#111827', counterRadius: 8, counterPrefix: '', counterSuffix: '', counterPrefixNewline: false, counterSuffixNewline: false,
        showList: false, listTabIndex: -1, listFullSize: false, listScrollDir: 'vertical'
      });
      if (next[gridActiveTab]) {
        next[gridActiveTab] = { ...next[gridActiveTab], enabled: gridEnabled, title: gridTitle, rows, cols, rowHeight: rowHeightPx, tiles: padded };
      }
      return next;
    });
  };
  const loadFromTab = (idx: number) => {
    const tab = gridTabs[idx];
    if (!tab) return;
    setGridEnabled(tab.enabled !== false);
    setGridTitle(tab.title || '');
    setGridRows(Math.max(1, Number(tab.rows) || 1));
    setGridCols(Math.max(1, Number(tab.cols) || 1));
    setGridRowHeight(Math.max(80, Math.min(480, Number((tab as any)?.rowHeight) || 160)));
    const tiles = Array.isArray(tab.tiles) ? tab.tiles : [];
    setGridTiles(tiles.map((it: any) => ({
      title: it?.title || '',
      subtitle: it?.subtitle || '',
      image: typeof it?.image === 'string' ? it.image : '',
      images: Array.isArray(it?.images) ? it.images.filter((s: any) => typeof s === 'string' && s) : undefined,
      imageInterval: Number(it?.imageInterval) || undefined,
      ctaText: it?.ctaText || '',
      ctaUrl: it?.ctaUrl || '',
      bgColor: it?.bgColor || '',
      textColor: it?.textColor || '',
      enabled: it?.enabled !== false,
      reserveSpace: it?.reserveSpace === true,
      colSpan: Math.max(1, Number(it?.colSpan)||1),
      hoverEffect: typeof it?.hoverEffect === 'string' ? it.hoverEffect : 'none',
      ctaFree: it?.ctaFree === true,
      ctaX: Math.max(0, Math.min(100, Number(it?.ctaX) || 15)),
      ctaY: Math.max(0, Math.min(100, Number(it?.ctaY) || 80)),
      ctaScale: Math.max(0.5, Math.min(3, Number(it?.ctaScale) || 1)),
      ctaBtnBg: typeof it?.ctaBtnBg === 'string' ? it.ctaBtnBg : '',
      ctaBtnTextColor: typeof it?.ctaBtnTextColor === 'string' ? it.ctaBtnTextColor : '',
      ctaHoverEffect: typeof it?.ctaHoverEffect === 'string' ? it.ctaHoverEffect : 'none',
      ctaFontSize: Math.max(10, Math.min(36, Number(it?.ctaFontSize) || 14)),
      ctaWidth: Math.max(80, Math.min(400, Number(it?.ctaWidth) || 140)),
      showCounter: it?.showCounter === true,
      counterFree: it?.counterFree === true,
      counterX: Math.max(0, Math.min(100, Number(it?.counterX) || 85)),
      counterY: Math.max(0, Math.min(100, Number(it?.counterY) || 85)),
      counterScale: Math.max(0.5, Math.min(3, Number(it?.counterScale) || 1)),
      counterBg: typeof it?.counterBg === 'string' ? it.counterBg : 'rgba(255,255,255,0.9)',
      counterTextColor: typeof it?.counterTextColor === 'string' ? it.counterTextColor : '#111827',
      counterRadius: Math.max(0, Math.min(28, Number(it?.counterRadius) || 8)),
      counterPrefix: typeof it?.counterPrefix === 'string' ? it.counterPrefix : '',
      counterSuffix: typeof it?.counterSuffix === 'string' ? it.counterSuffix : '',
      counterPrefixNewline: it?.counterPrefixNewline === true,
      counterSuffixNewline: it?.counterSuffixNewline === true,
      showList: it?.showList === true,
      listTabIndex: Number.isInteger(it?.listTabIndex) ? it.listTabIndex : -1,
      listFullSize: it?.listFullSize === true,
      listScrollDir: (it?.listScrollDir === 'horizontal') ? 'horizontal' : 'vertical',
    })));
    // Apply saved column widths (md+) if present, otherwise equal distribution
    const cols = Math.max(1, Number(tab.cols) || 1);
    const cw = Array.isArray((tab as any).colWidths) ? (tab as any).colWidths : null;
    if (cw && cw.length === cols) {
      setGridColWidths(cw.map((n: any) => Math.max(0.25, Number(n) || 1)));
    } else {
      setGridColWidths(Array.from({ length: cols }).map(() => 1));
    }
  };
  const addGridTab = () => {
    persistEditingIntoActiveTab();
    setGridTabs(prev => [...prev, { label: `Tab ${prev.length + 1}`, enabled: true, title: '', rows: 2, cols: 3, rowHeight: 160, tiles: [] }]);
    setGridActiveTab(v => {
      const next = gridTabs.length; // old length becomes new index
      // load defaults for new tab
      setTimeout(() => {
        setGridEnabled(true); setGridTitle(''); setGridRows(2); setGridCols(3); setGridTiles([]);
      }, 0);
      return next;
    });
  };
  const deleteGridSection = async () => {
    const endpoint = (process.env.REACT_APP_WEBSITE_SETTINGS_ENDPOINT as string | undefined) || '/api/update-website-settings.php';
    let token = secret; if (!token) { const s = await AdminKeyService.getSecret(); setSecret(s); token = s; }
    if (!token) { toast.error('Missing admin key.'); return; }
    const loadingId = toast.loading('Grid wird gelöscht...');
    setDeletingGrid(true);
    try {
      const payload = { mode, homepage: { deleteGrid: true }, token } as any;
      const res = await fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Auth-Token': token, 'Authorization': `Bearer ${token}` }, body: JSON.stringify(payload) });
      const raw = await res.text(); let json: any = null; try { json = raw ? JSON.parse(raw) : null; } catch {}
      if (!res.ok || !json?.ok) { const detail = json?.error || raw || 'No response body'; throw new Error(`${res.status} ${res.statusText} - ${detail}`); }
      // Reset local UI state
      setGridEnabled(false);
      setGridTitle('');
      setGridRows(1);
      setGridCols(1);
      setGridTiles([]);
      toast.success('Grid wurde gelöscht.');
    } catch (e: any) {
      toast.error(e?.message || 'Löschen fehlgeschlagen');
    } finally {
      setDeletingGrid(false);
      toast.dismiss(loadingId);
    }
  };
  const toggleTileEnabled = (idx: number) => {
    setGridTiles(prev => {
      const next = [...prev];
      while (next.length <= idx) next.push({ title: '', subtitle: '', image: '', ctaText: '', ctaUrl: '', bgColor: '', textColor: '', enabled: true, reserveSpace: false, colSpan: 1, hoverEffect: 'none', ctaFree: false, ctaX: 15, ctaY: 80, ctaScale: 1, ctaBtnBg: '', ctaBtnTextColor: '', ctaHoverEffect: 'none', ctaFontSize: 14, ctaWidth: 140, showCounter: false });
      const cur = next[idx] || { title: '', subtitle: '', image: '', ctaText: '', ctaUrl: '', bgColor: '', textColor: '', enabled: true, reserveSpace: false, colSpan: 1, hoverEffect: 'none', ctaFree: false, ctaX: 15, ctaY: 80, ctaScale: 1, ctaBtnBg: '', ctaBtnTextColor: '', ctaHoverEffect: 'none', ctaFontSize: 14, ctaWidth: 140, showCounter: false };
      next[idx] = { ...cur, enabled: cur.enabled === false ? true : false };
      return next;
    });
  };

  const expandTile = (idx: number, dir: 1 | -1) => {
    setGridTiles(prev => prev.map((x, i) => {
      if (i !== idx) return x;
      const cur = x || { title: '', subtitle: '', image: '', ctaText: '', ctaUrl: '', bgColor: '', textColor: '', enabled: true, reserveSpace: false, colSpan: 1, showCounter: false, counterFree: false, counterX: 85, counterY: 85, counterScale: 1 };
      const nextSpan = Math.max(1, Math.min(gridCols, (cur.colSpan || 1) + dir));
      return { ...cur, colSpan: nextSpan };
    }));
  };

  const expandTileVertical = (idx: number, dir: 1 | -1) => {
    setGridTiles(prev => prev.map((x, i) => {
      if (i !== idx) return x;
      const cur = x || { title: '', subtitle: '', image: '', ctaText: '', ctaUrl: '', bgColor: '', textColor: '', enabled: true, reserveSpace: false, rowSpan: 1, showCounter: false, counterFree: false, counterX: 85, counterY: 85, counterScale: 1 };
      const nextSpan = Math.max(1, Math.min(gridRows, (cur.rowSpan || 1) + dir));
      return { ...cur, rowSpan: nextSpan };
    }));
  };

  const openGridPicker = (idx: number) => { setGridPickerSelection(new Set()); setGridPickerOpen({ open: true, index: idx }); };
  const closeGridPicker = () => { setGridPickerOpen({ open: false, index: null }); setGridPickerSelection(new Set()); };
  const setGridTileImage = (path: string) => {
    if (gridPickerOpen.index == null) return;
    // If tile editor is open and we're editing, add to teImages; otherwise set single image
    if (tileEditorOpen && tileEditorIndex === gridPickerOpen.index) {
      setTeImages(prev => Array.from(new Set([...(prev || []), path])));
    } else {
      setGridTiles(prev => prev.map((it, i) => (i === gridPickerOpen.index ? { ...it, image: path } : it)));
    }
  };
  const addGridTile = () => setGridTiles(prev => [...prev, { title: '', subtitle: '', image: '', ctaText: '', ctaUrl: '', bgColor: '', textColor: '', enabled: true, reserveSpace: false, colSpan: 1, rowSpan: 1, hoverEffect: 'none', ctaFree: false, ctaX: 15, ctaY: 80, ctaScale: 1, ctaBtnBg: '', ctaBtnTextColor: '', ctaHoverEffect: 'none', ctaFontSize: 14, ctaWidth: 140, showCounter: false, counterFree: false, counterTabIndex: -1, counterX: 85, counterY: 85, counterScale: 1, showList: false, listTabIndex: -1, listFullSize: false, listScrollDir: 'vertical' }]);
  // ensure addGridTile includes counter placement defaults
  const addGridTile_original = addGridTile;
  // redeclare to include counter fields
  const addGridTile2 = () => setGridTiles(prev => [...prev, { title: '', subtitle: '', image: '', ctaText: '', ctaUrl: '', bgColor: '', textColor: '', enabled: true, reserveSpace: false, colSpan: 1, rowSpan: 1, hoverEffect: 'none', ctaFree: false, ctaX: 15, ctaY: 80, ctaScale: 1, ctaBtnBg: '', ctaBtnTextColor: '', ctaHoverEffect: 'none', ctaFontSize: 14, ctaWidth: 140, showCounter: false, counterFree: false, counterX: 85, counterY: 85, counterScale: 1, counterBg: 'rgba(255,255,255,0.9)', counterTextColor: '#111827', counterRadius: 8, counterPrefix: '', counterSuffix: '', counterPrefixNewline: false, counterSuffixNewline: false, showList: false, listTabIndex: -1, listFullSize: false, listScrollDir: 'vertical' }]);
  // keep for backward compatibility, enhanced defaults are applied on load/save
  const removeGridTile = (idx: number) => setGridTiles(prev => prev.filter((_, i) => i !== idx));
  const moveGridTile = (idx: number, dir: -1 | 1) => setGridTiles(prev => { const arr = [...prev]; const j = idx + dir; if (j < 0 || j >= arr.length) return arr; const tmp = arr[idx]; arr[idx] = arr[j]; arr[j] = tmp; return arr; });
  const clearGridTile = (idx: number) => setGridTiles(prev => prev.map((x, i) => {
    if (i !== idx) return x;
    const cur = x || { title: '', subtitle: '', image: '', ctaText: '', ctaUrl: '', bgColor: '', textColor: '', enabled: true, reserveSpace: false, colSpan: 1, hoverEffect: 'none', ctaFree: false, ctaX: 15, ctaY: 80, ctaScale: 1, ctaBtnBg: '', ctaBtnTextColor: '', ctaHoverEffect: 'none', ctaFontSize: 14, ctaWidth: 140, showCounter: false };
    return { ...cur, enabled: false, title: '', subtitle: '', image: '', images: [], imageInterval: undefined, ctaText: '', ctaUrl: '', bgColor: '', textColor: '', hoverEffect: 'none', ctaFree: false, ctaX: 15, ctaY: 80, ctaScale: 1, ctaBtnBg: '', ctaBtnTextColor: '', ctaHoverEffect: 'none', ctaFontSize: 14, ctaWidth: 140, showCounter: false, counterFree: false, counterTabIndex: -1, counterX: 85, counterY: 85, counterScale: 1, showList: false, listTabIndex: -1, listFullSize: false, listScrollDir: 'vertical' };
  }));
  const ensureTileIndex = (idx: number) => {
    setGridTiles(prev => {
      if (idx < prev.length) return prev;
      const next = [...prev];
      while (next.length <= idx) next.push({ title: '', subtitle: '', image: '', ctaText: '', ctaUrl: '', bgColor: '', textColor: '', enabled: true, reserveSpace: false, colSpan: 1, rowSpan: 1, showCounter: false, counterFree: false, counterTabIndex: -1, counterX: 85, counterY: 85, counterScale: 1, showList: false, listTabIndex: -1, listFullSize: false, listScrollDir: 'vertical' });
      return next;
    });
  };
  const openTileEditor = (idx: number) => {
    ensureTileIndex(idx);
    const src = gridTiles[idx] || { title: '', subtitle: '', image: '', images: [], imageInterval: 5, ctaText: '', ctaUrl: '', bgColor: '', textColor: '', enabled: true, reserveSpace: false, colSpan: 1, rowSpan: 1, hoverEffect: 'none', ctaFree: false, ctaX: 15, ctaY: 80, ctaScale: 1, ctaBtnBg: '', ctaBtnTextColor: '', ctaHoverEffect: 'none', ctaFontSize: 14, ctaWidth: 140, showCounter: false, counterFree: false, counterX: 85, counterY: 85, counterScale: 1, showList: false, listTabIndex: -1, listFullSize: false, listScrollDir: 'vertical' } as any;
    setTileEditorIndex(idx);
    setTeTitle(src.title || '');
    setTeSubtitle(src.subtitle || '');
    setTeCtaText(src.ctaText || '');
    setTeCtaUrl(src.ctaUrl || '');
    setTeBgColor(src.bgColor || '');
    setTeTextColor(src.textColor || '');
    setTeEnabled(src.enabled !== false);
    setTeReserveSpace(src.reserveSpace === true);
    setTeColSpan(Math.max(1, Math.min(gridCols, Number(src.colSpan)||1)));
    setTeRowSpan(Math.max(1, Math.min(gridRows, Number((src as any).rowSpan)||1)));
    setTeHoverEffect(typeof src.hoverEffect === 'string' ? src.hoverEffect : 'none');
    setTeCtaFree(src.ctaFree === true);
    setTeCtaX(Math.max(0, Math.min(100, Number(src.ctaX) || 15)));
    setTeCtaY(Math.max(0, Math.min(100, Number(src.ctaY) || 80)));
    setTeCtaScale(Math.max(0.5, Math.min(3, Number(src.ctaScale) || 1)));
    setTeCtaBtnBg(src.ctaBtnBg || '');
    setTeCtaBtnTextColor(src.ctaBtnTextColor || '');
    setTeCtaHoverEffect(typeof src.ctaHoverEffect === 'string' ? src.ctaHoverEffect : 'none');
    setTeCtaFontSize(Math.max(10, Math.min(36, Number((src as any).ctaFontSize) || 14)));
    setTeCtaWidth(Math.max(80, Math.min(400, Number((src as any).ctaWidth) || 140)));
    setTeImages(Array.isArray((src as any).images) ? (src as any).images.filter((s: any) => typeof s === 'string' && s) : ((src as any).image ? [String((src as any).image)] : []));
    setTeImageInterval(Math.max(2, Math.min(60, Number((src as any).imageInterval) || 5)));
    setTeShowCounter(src.showCounter === true);
    setTeCounterFree((src as any).counterFree === true);
    setTeCounterX(Math.max(0, Math.min(100, Number((src as any).counterX) || 85)));
    setTeCounterTabIndex(Number.isInteger((src as any).counterTabIndex) ? (src as any).counterTabIndex : -1);
    setTeCounterY(Math.max(0, Math.min(100, Number((src as any).counterY) || 85)));
    setTeCounterScale(Math.max(0.5, Math.min(3, Number((src as any).counterScale) || 1)));
    // Counter style fields
    setTeCounterBg((src as any).counterBg || 'rgba(255,255,255,0.9)');
    setTeCounterTextColor((src as any).counterTextColor || '#111827');
    setTeCounterRadius(Math.max(0, Math.min(28, Number((src as any).counterRadius) || 8)));
    setTeCounterPrefix((src as any).counterPrefix || '');
    setTeCounterSuffix((src as any).counterSuffix || '');
    setTeCounterPrefixNewline((src as any).counterPrefixNewline === true);
    setTeCounterSuffixNewline((src as any).counterSuffixNewline === true);
    setTeShowList((src as any).showList === true);
    setTeListTabIndex(Number.isInteger((src as any).listTabIndex) ? (src as any).listTabIndex : -1);
    setTeListFullSize((src as any).listFullSize === true);
    setTileEditorOpen(true);
  };
  const closeTileEditor = () => {
    if (tileEditorIndex != null) {
      const idx = tileEditorIndex;
      setGridTiles(prev => prev.map((x, i) => {
        if (i !== idx) return x;
        const cur = x || { title: '', subtitle: '', image: '', ctaText: '', ctaUrl: '', bgColor: '', textColor: '', enabled: true, reserveSpace: false, colSpan: 1, rowSpan: 1, hoverEffect: 'none', ctaFree: false, ctaX: 15, ctaY: 80, ctaScale: 1, ctaBtnBg: '', ctaBtnTextColor: '', ctaHoverEffect: 'none', ctaFontSize: 14, ctaWidth: 140, showCounter: false, counterFree: false, counterTabIndex: -1, counterX: 85, counterY: 85, counterScale: 1, showList: false, listTabIndex: -1, listFullSize: false };
        return { ...cur, title: teTitle, subtitle: teSubtitle, image: (teImages && teImages.length > 0) ? undefined : (x as any).image, images: (teImages && teImages.length > 0) ? teImages : undefined, imageInterval: (teImages && teImages.length > 1) ? Math.max(2, Math.min(60, teImageInterval)) : undefined, ctaText: teCtaText, ctaUrl: teCtaUrl, bgColor: teBgColor, textColor: teTextColor, enabled: teEnabled, reserveSpace: teReserveSpace, colSpan: Math.max(1, Math.min(gridCols, teColSpan)), rowSpan: Math.max(1, Math.min(gridRows, teRowSpan)), hoverEffect: teHoverEffect, ctaFree: teCtaFree, ctaX: Math.max(0, Math.min(100, teCtaX)), ctaY: Math.max(0, Math.min(100, teCtaY)), ctaScale: Math.max(0.5, Math.min(3, teCtaScale)), ctaBtnBg: teCtaBtnBg, ctaBtnTextColor: teCtaBtnTextColor, ctaHoverEffect: teCtaHoverEffect, ctaFontSize: Math.max(10, Math.min(36, teCtaFontSize)), ctaWidth: Math.max(80, Math.min(400, teCtaWidth)), showCounter: teShowCounter, counterFree: teCounterFree, counterTabIndex: Number.isInteger(teCounterTabIndex) ? teCounterTabIndex : -1, counterX: Math.max(0, Math.min(100, teCounterX)), counterY: Math.max(0, Math.min(100, teCounterY)), counterScale: Math.max(0.5, Math.min(3, teCounterScale)), showList: teShowList, listTabIndex: Number.isInteger(teListTabIndex) ? teListTabIndex : -1, listFullSize: !!teListFullSize };
      }));
    }
    setTileEditorOpen(false);
    setTileEditorIndex(null);
  };
  const saveTileEditor = () => {
    if (tileEditorIndex == null) return;
    const idx = tileEditorIndex;
    setGridTiles(prev => prev.map((x, i) => {
      if (i !== idx) return x;
      const cur = x || { title: '', subtitle: '', image: '', ctaText: '', ctaUrl: '', bgColor: '', textColor: '', enabled: true, reserveSpace: false, colSpan: 1, rowSpan: 1, hoverEffect: 'none', ctaFree: false, ctaX: 15, ctaY: 80, ctaScale: 1, ctaBtnBg: '', ctaBtnTextColor: '', ctaHoverEffect: 'none', ctaFontSize: 14, ctaWidth: 140, showCounter: false, counterFree: false, counterTabIndex: -1, counterX: 85, counterY: 85, counterScale: 1, showList: false, listTabIndex: -1, listFullSize: false };
      return { ...cur, title: teTitle, subtitle: teSubtitle, image: (teImages && teImages.length > 0) ? undefined : (x as any).image, images: (teImages && teImages.length > 0) ? teImages : undefined, imageInterval: (teImages && teImages.length > 1) ? Math.max(2, Math.min(60, teImageInterval)) : undefined, ctaText: teCtaText, ctaUrl: teCtaUrl, bgColor: teBgColor, textColor: teTextColor, enabled: teEnabled, reserveSpace: teReserveSpace, colSpan: Math.max(1, Math.min(gridCols, teColSpan)), rowSpan: Math.max(1, Math.min(gridRows, teRowSpan)), hoverEffect: teHoverEffect, ctaFree: teCtaFree, ctaX: Math.max(0, Math.min(100, teCtaX)), ctaY: Math.max(0, Math.min(100, teCtaY)), ctaScale: Math.max(0.5, Math.min(3, teCtaScale)), ctaBtnBg: teCtaBtnBg, ctaBtnTextColor: teCtaBtnTextColor, ctaHoverEffect: teCtaHoverEffect, ctaFontSize: Math.max(10, Math.min(36, teCtaFontSize)), ctaWidth: Math.max(80, Math.min(400, teCtaWidth)), showCounter: teShowCounter, counterFree: teCounterFree, counterTabIndex: Number.isInteger(teCounterTabIndex) ? teCounterTabIndex : -1, counterX: Math.max(0, Math.min(100, teCounterX)), counterY: Math.max(0, Math.min(100, teCounterY)), counterScale: Math.max(0.5, Math.min(3, teCounterScale)), showList: teShowList, listTabIndex: Number.isInteger(teListTabIndex) ? teListTabIndex : -1, listFullSize: !!teListFullSize };
    }));
    // include counter style on quick save
    setGridTiles(prev => prev.map((x, i) => {
      if (i !== idx) return x;
      const cur = x || { title: '', subtitle: '', image: '', ctaText: '', ctaUrl: '', bgColor: '', textColor: '', enabled: true, reserveSpace: false, colSpan: 1, rowSpan: 1, hoverEffect: 'none', ctaFree: false, ctaX: 15, ctaY: 80, ctaScale: 1, ctaBtnBg: '', ctaBtnTextColor: '', ctaHoverEffect: 'none', ctaFontSize: 14, ctaWidth: 140, showCounter: false, counterFree: false, counterTabIndex: -1, counterX: 85, counterY: 85, counterScale: 1, showList: false, listTabIndex: -1, listFullSize: false };
      return { ...cur, counterBg: teCounterBg, counterTextColor: teCounterTextColor, counterRadius: Math.max(0, Math.min(28, teCounterRadius)), counterPrefix: teCounterPrefix, counterSuffix: teCounterSuffix, counterPrefixNewline: !!teCounterPrefixNewline, counterSuffixNewline: !!teCounterSuffixNewline };
    }));
    setTileEditorOpen(false);
  };
  const saveGrid = async () => {
    const endpoint = (process.env.REACT_APP_WEBSITE_SETTINGS_ENDPOINT as string | undefined) || '/api/update-website-settings.php';
    let token = secret; if (!token) { const s = await AdminKeyService.getSecret(); setSecret(s); token = s; }
    if (!token) { toast.error('Missing admin key.'); return; }
    setSavingGrid(true);
    const loadingId = toast.loading('Grid wird gespeichert...');
    try {
      const rows = Math.max(1, Number(gridRows) || 1);
      const cols = Math.max(1, Number(gridCols) || 1);
      const total = rows * cols;
      const tilesSource = (() => {
        if (tileEditorOpen && tileEditorIndex != null) {
          const idx = tileEditorIndex as number;
          const merged = gridTiles.map((x, i) => i === idx
            ? {
                ...x,
                title: teTitle,
                subtitle: teSubtitle,
                image: (teImages && teImages.length > 0) ? undefined : (x as any).image,
                images: (teImages && teImages.length > 0) ? teImages : undefined,
                imageInterval: (teImages && teImages.length > 1) ? Math.max(2, Math.min(60, teImageInterval)) : undefined,
                ctaText: teCtaText,
                ctaUrl: teCtaUrl,
                bgColor: teBgColor,
                textColor: teTextColor,
                enabled: teEnabled,
                reserveSpace: teReserveSpace,
                colSpan: Math.max(1, Math.min(gridCols, teColSpan)),
                rowSpan: Math.max(1, Math.min(gridRows, teRowSpan)),
                hoverEffect: teHoverEffect,
                ctaFree: teCtaFree,
                ctaX: Math.max(0, Math.min(100, teCtaX)),
                ctaY: Math.max(0, Math.min(100, teCtaY)),
                ctaScale: Math.max(0.5, Math.min(3, teCtaScale)),
                ctaBtnBg: teCtaBtnBg,
                ctaBtnTextColor: teCtaBtnTextColor,
                ctaHoverEffect: teCtaHoverEffect,
                ctaFontSize: Math.max(10, Math.min(36, teCtaFontSize)),
                ctaWidth: Math.max(80, Math.min(400, teCtaWidth)),
                showCounter: teShowCounter,
                counterFree: teCounterFree,
                counterTabIndex: Number.isInteger(teCounterTabIndex) ? teCounterTabIndex : -1,
                counterX: Math.max(0, Math.min(100, teCounterX)),
                counterY: Math.max(0, Math.min(100, teCounterY)),
                counterScale: Math.max(0.5, Math.min(3, teCounterScale)),
                counterBg: teCounterBg,
                counterTextColor: teCounterTextColor,
                counterRadius: Math.max(0, Math.min(28, teCounterRadius)),
                counterPrefix: teCounterPrefix,
                counterSuffix: teCounterSuffix,
                counterPrefixNewline: !!teCounterPrefixNewline,
                counterSuffixNewline: !!teCounterSuffixNewline,
                showList: teShowList,
                listTabIndex: Number.isInteger(teListTabIndex) ? teListTabIndex : -1,
                listFullSize: !!teListFullSize,
                listScrollDir: (x as any)?.listScrollDir === 'horizontal' ? 'horizontal' : 'vertical',
              }
            : x);
          return merged;
        }
        return gridTiles;
      })();
      const padded = Array.from({ length: total }).map((_, i) => tilesSource[i] || {
        title: '',
        subtitle: '',
        image: '',
        images: [],
        imageInterval: undefined,
        ctaText: '',
        ctaUrl: '',
        bgColor: '',
        textColor: '',
        enabled: true,
        reserveSpace: false,
        colSpan: 1,
        rowSpan: 1,
        hoverEffect: 'none',
        ctaFree: false,
        ctaX: 15,
        ctaY: 80,
        ctaScale: 1,
        ctaBtnBg: '',
        ctaBtnTextColor: '',
        ctaHoverEffect: 'none',
        ctaFontSize: 14,
        ctaWidth: 140,
        showCounter: false,
        counterFree: false,
        counterTabIndex: -1,
        counterX: 85,
        counterY: 85,
        counterScale: 1,
        showList: false,
        listTabIndex: -1,
        listFullSize: false,
        listScrollDir: 'vertical',
      });
      // Merge into gridTabs
      const rowHeightPx = Math.max(80, Math.min(480, Number(gridRowHeight) || 160));
      const tabsOut = (() => {
        const base = gridTabs.length > 0 ? [...gridTabs] : [{ label: 'Tab 1', enabled: gridEnabled, title: gridTitle, rows, cols, rowHeight: rowHeightPx, tiles: padded }];
        if (gridTabs.length > 0 && base[gridActiveTab]) {
          base[gridActiveTab] = { ...base[gridActiveTab], enabled: gridEnabled, title: gridTitle, rows, cols, rowHeight: rowHeightPx, tiles: padded, colWidths: gridColWidths } as any;
        }
        return base;
      })();
      const url = pagesTabs[pageBuilderIndex]?.url || '/';
      const isPage = url !== '/';
      const isHomepage = !isPage;
      // Merge current editor state into the active section and send all sections (homepage and pages)
      const secId = activeSectionId;
      const display = { position: gridTabsPosition, style: gridTabsStyle, arrowsEnabled: gridTabsArrows, transitionType: gridTabsTransitionType } as any;
      const gridObj = gridTabs.length > 0 ? undefined : { enabled: gridEnabled, title: gridTitle, rows, cols, rowHeight: rowHeightPx, tiles: padded, colWidths: gridColWidths };
      const nextSections = builderSections.map((s: any) => s.id === secId ? { ...s, gridTabs: tabsOut, grid: gridObj, gridTabsDisplay: display } : s);
      let pageSections: any = nextSections;
      // Always update local state so UI reflects saved structure
      setBuilderSections(nextSections);
      // Build payload: for Startseite save into pages/homepage.json (page save path)
      const payload: any = {
        mode,
        pageUrl: isHomepage ? '/' : url,
        ...(isHomepage ? { pageFile: 'homepage' } : {}),
        pageSections,
        pageSectionId: secId || undefined,
        token,
      };
      const res = await fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Auth-Token': token, 'Authorization': `Bearer ${token}` }, body: JSON.stringify(payload) });
      const raw = await res.text(); let json: any = null; try { json = raw ? JSON.parse(raw) : null; } catch {}
      if (!res.ok || !json?.ok) { const detail = json?.error || raw || 'No response body'; throw new Error(`${res.status} ${res.statusText} - ${detail}`); }
      if (json?.homepage?.grid) {
        const g = json.homepage.grid;
        setGridEnabled(!!g.enabled);
        setGridTitle(g.title || '');
        setGridRows(Math.max(1, Number(g.rows)||1));
        setGridCols(Math.max(1, Number(g.cols)||1));
        if (typeof g.rowHeight !== 'undefined') {
          setGridRowHeight(Math.max(80, Math.min(480, Number(g.rowHeight) || 160)));
        }
        if (Array.isArray((g as any).colWidths) && (g as any).colWidths.length === Math.max(1, Number(g.cols)||1)) {
          setGridColWidths((g as any).colWidths.map((n: any) => Math.max(0.25, Number(n) || 1)));
        } else {
          setGridColWidths(Array.from({ length: Math.max(1, Number(g.cols)||1) }).map(() => 1));
        }
        const tiles = Array.isArray(g.tiles) ? g.tiles : [];
        setGridTiles(tiles.map((it: any) => ({
          title: it?.title || '',
          subtitle: it?.subtitle || '',
          image: typeof it?.image === 'string' ? it.image : '',
          images: Array.isArray(it?.images) ? it.images.filter((s: any) => typeof s === 'string' && s) : undefined,
          imageInterval: Number(it?.imageInterval) || undefined,
          ctaText: it?.ctaText || '',
          ctaUrl: it?.ctaUrl || '',
          bgColor: it?.bgColor || '',
          textColor: it?.textColor || '',
          enabled: it?.enabled !== false,
          reserveSpace: it?.reserveSpace === true,
          colSpan: Math.max(1, Number(it?.colSpan)||1),
          hoverEffect: typeof it?.hoverEffect === 'string' ? it.hoverEffect : 'none',
          ctaFree: it?.ctaFree === true,
          ctaX: Math.max(0, Math.min(100, Number(it?.ctaX) || 15)),
          ctaY: Math.max(0, Math.min(100, Number(it?.ctaY) || 80)),
          ctaScale: Math.max(0.5, Math.min(3, Number(it?.ctaScale) || 1)),
          ctaBtnBg: typeof it?.ctaBtnBg === 'string' ? it.ctaBtnBg : '',
          ctaBtnTextColor: typeof it?.ctaBtnTextColor === 'string' ? it.ctaBtnTextColor : '',
          ctaHoverEffect: typeof it?.ctaHoverEffect === 'string' ? it.ctaHoverEffect : 'none',
          ctaFontSize: Math.max(10, Math.min(36, Number(it?.ctaFontSize) || 14)),
          ctaWidth: Math.max(80, Math.min(400, Number(it?.ctaWidth) || 140)),
          showCounter: it?.showCounter === true,
          counterFree: it?.counterFree === true,
          counterX: Math.max(0, Math.min(100, Number(it?.counterX) || 85)),
          counterY: Math.max(0, Math.min(100, Number(it?.counterY) || 85)),
          counterScale: Math.max(0.5, Math.min(3, Number(it?.counterScale) || 1)),
          counterBg: typeof it?.counterBg === 'string' ? it.counterBg : 'rgba(255,255,255,0.9)',
          counterTextColor: typeof it?.counterTextColor === 'string' ? it.counterTextColor : '#111827',
          counterRadius: Math.max(0, Math.min(28, Number(it?.counterRadius) || 8)),
          counterPrefix: typeof it?.counterPrefix === 'string' ? it.counterPrefix : '',
          counterSuffix: typeof it?.counterSuffix === 'string' ? it.counterSuffix : '',
          counterPrefixNewline: it?.counterPrefixNewline === true,
          counterSuffixNewline: it?.counterSuffixNewline === true,
          showList: it?.showList === true,
          listTabIndex: Number.isInteger(it?.listTabIndex) ? it.listTabIndex : -1,
          listFullSize: it?.listFullSize === true,
          listScrollDir: (it?.listScrollDir === 'horizontal') ? 'horizontal' : 'vertical',
        })));
      }
      if (Array.isArray(json?.homepage?.gridTabs)) {
        const tabs = (json.homepage.gridTabs as any[]).map((tb: any, i: number) => ({
          label: typeof tb?.label === 'string' && tb.label ? tb.label : `Tab ${i+1}`,
          enabled: tb?.enabled !== false,
          title: tb?.title || '',
          rows: Math.max(1, Number(tb?.rows)||1),
          cols: Math.max(1, Number(tb?.cols)||1),
          rowHeight: Math.max(80, Math.min(480, Number(tb?.rowHeight) || 160)),
          tiles: Array.isArray(tb?.tiles) ? tb.tiles : [],
          colWidths: Array.isArray((tb as any).colWidths) ? (tb as any).colWidths : undefined,
        }));
        setGridTabs(tabs);
        const desiredIdx = Math.max(0, Math.min(gridActiveTab, tabs.length - 1));
        setGridActiveTab(desiredIdx);
        const t = tabs[desiredIdx] as any;
        setGridEnabled(t?.enabled !== false);
        setGridTitle(t?.title || '');
        setGridRows(Math.max(1, Number(t?.rows)||1));
        setGridCols(Math.max(1, Number(t?.cols)||1));
        setGridRowHeight(Math.max(80, Math.min(480, Number(t?.rowHeight) || 160)));
        if (Array.isArray(t?.colWidths) && t.colWidths.length === Math.max(1, Number(t?.cols)||1)) {
          setGridColWidths(t.colWidths.map((n: any) => Math.max(0.25, Number(n) || 1)));
        } else {
          setGridColWidths(Array.from({ length: Math.max(1, Number(t?.cols)||1) }).map(() => 1));
        }
        const tiles0 = Array.isArray(t?.tiles) ? t.tiles : [];
        setGridTiles(tiles0.map((it: any) => ({
          title: it?.title || '',
          subtitle: it?.subtitle || '',
          image: typeof it?.image === 'string' ? it.image : '',
          images: Array.isArray(it?.images) ? it.images.filter((s: any) => typeof s === 'string' && s) : undefined,
          imageInterval: Number(it?.imageInterval) || undefined,
          ctaText: it?.ctaText || '', ctaUrl: it?.ctaUrl || '',
          bgColor: it?.bgColor || '', textColor: it?.textColor || '',
          enabled: it?.enabled !== false, reserveSpace: it?.reserveSpace === true,
          colSpan: Math.max(1, Number(it?.colSpan)||1), rowSpan: Math.max(1, Number((it as any)?.rowSpan)||1),
          hoverEffect: typeof it?.hoverEffect === 'string' ? it.hoverEffect : 'none',
          ctaFree: it?.ctaFree === true, ctaX: Math.max(0, Math.min(100, Number(it?.ctaX)||15)), ctaY: Math.max(0, Math.min(100, Number(it?.ctaY)||80)), ctaScale: Math.max(0.5, Math.min(3, Number(it?.ctaScale)||1)),
          ctaBtnBg: (it as any)?.ctaBtnBg || '', ctaBtnTextColor: (it as any)?.ctaBtnTextColor || '', ctaHoverEffect: typeof (it as any)?.ctaHoverEffect === 'string' ? (it as any).ctaHoverEffect : 'none', ctaFontSize: Math.max(10, Math.min(36, Number((it as any)?.ctaFontSize)||14)), ctaWidth: Math.max(80, Math.min(400, Number((it as any)?.ctaWidth)||140)),
          showCounter: it?.showCounter === true, counterFree: (it as any)?.counterFree === true, counterX: Math.max(0, Math.min(100, Number((it as any)?.counterX)||85)), counterY: Math.max(0, Math.min(100, Number((it as any)?.counterY)||85)), counterScale: Math.max(0.5, Math.min(3, Number((it as any)?.counterScale)||1)),
          counterBg: (it as any)?.counterBg || 'rgba(255,255,255,0.9)', counterTextColor: (it as any)?.counterTextColor || '#111827', counterRadius: Math.max(0, Math.min(28, Number((it as any)?.counterRadius)||8)), counterPrefix: (it as any)?.counterPrefix || '', counterSuffix: (it as any)?.counterSuffix || '', counterPrefixNewline: (it as any)?.counterPrefixNewline === true, counterSuffixNewline: (it as any)?.counterSuffixNewline === true,
          showList: (it as any)?.showList === true, listTabIndex: Number.isInteger((it as any)?.listTabIndex) ? (it as any).listTabIndex : -1, listFullSize: (it as any)?.listFullSize === true, listScrollDir: ((it as any)?.listScrollDir === 'horizontal') ? 'horizontal' : 'vertical'
        })));
      }
      if (json?.homepage?.gridTabsDisplay) {
        const gtd = json.homepage.gridTabsDisplay as any;
        const pos = typeof gtd?.position === 'string' && ['above','below'].includes(gtd.position) ? gtd.position as any : 'above';
        const st = typeof gtd?.style === 'string' && ['labels','dots'].includes(gtd.style) ? gtd.style as any : 'labels';
        setGridTabsPosition(pos);
        setGridTabsStyle(st);
        setGridTabsArrows(!!gtd?.arrowsEnabled);
        const tt = typeof gtd?.transitionType === 'string' && ['none','slide','tiles','cube'].includes(gtd.transitionType) ? (gtd.transitionType as any) : 'none';
        setGridTabsTransitionType(tt);
      }
      toast.success('Grid gespeichert', { id: loadingId });
    } catch (e: any) {
      toast.error(`Speichern fehlgeschlagen: ${e?.message || 'Unknown error'}`, { id: loadingId });
    } finally { setSavingGrid(false); }
  };

  // Save Event + Featured section
  const saveEventFeatured = async () => {
    const endpoint = (process.env.REACT_APP_WEBSITE_SETTINGS_ENDPOINT as string | undefined) || '/api/update-website-settings.php';
    let token = secret; if (!token) { const s = await AdminKeyService.getSecret(); setSecret(s); token = s; }
    if (!token) { toast.error('Missing admin key.'); return; }
    const loadingId = toast.loading('Event + Featured wird gespeichert...');
    try {
      const ids = eventProductIdsText.split(',').map(s => s.trim()).filter(Boolean);
      const payload = { mode, homepage: { eventFeatured: { enabled: eventEnabled, title: eventTitle, subtitle: eventSubtitle, ctaText: eventCtaText, ctaUrl: eventCtaUrl, useGradient: eventUseGradient, bgColor: eventBgColor, gradientFrom: eventGradFrom, gradientTo: eventGradTo, textColor: eventTextColor || undefined, buttonBg: eventBtnBg || undefined, buttonText: eventBtnText || undefined, productIds: ids } }, token };
      const res = await fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Auth-Token': token, 'Authorization': `Bearer ${token}` }, body: JSON.stringify(payload) });
      const raw = await res.text(); let json: any = null; try { json = raw ? JSON.parse(raw) : null; } catch {}
      if (!res.ok || !json?.ok) { const detail = json?.error || raw || 'No response body'; throw new Error(`${res.status} ${res.statusText} - ${detail}`); }
      if (json?.homepage?.eventFeatured) {
        setEventEnabled(!!json.homepage.eventFeatured.enabled);
        setEventTitle(json.homepage.eventFeatured.title || '');
        setEventSubtitle(json.homepage.eventFeatured.subtitle || '');
        setEventCtaText(json.homepage.eventFeatured.ctaText || '');
        setEventCtaUrl(json.homepage.eventFeatured.ctaUrl || '');
        setEventUseGradient(!!json.homepage.eventFeatured.useGradient);
        setEventBgColor(json.homepage.eventFeatured.bgColor || '#f472b6');
        setEventGradFrom(json.homepage.eventFeatured.gradientFrom || '#ec4899');
        setEventGradTo(json.homepage.eventFeatured.gradientTo || '#f59e0b');
        setEventTextColor(json.homepage.eventFeatured.textColor || '');
        setEventBtnBg(json.homepage.eventFeatured.buttonBg || '');
        setEventBtnText(json.homepage.eventFeatured.buttonText || '');
        setEventProductIdsText(Array.isArray(json.homepage.eventFeatured.productIds) ? json.homepage.eventFeatured.productIds.join(',') : '');
      }
      toast.success('Event + Featured gespeichert', { id: loadingId });
    } catch (e: any) {
      toast.error(`Speichern fehlgeschlagen: ${e?.message || 'Unknown error'}`, { id: loadingId });
    }
  };

  // Circles helpers
  const openCirclePicker = (idx: number) => setCirclePickerOpen({ open: true, index: idx });
  const closeCirclePicker = () => setCirclePickerOpen({ open: false, index: null });
  const setCircleImage = (path: string) => {
    if (circlePickerOpen.index == null) return;
    setCirclesItems(prev => prev.map((it, i) => (i === circlePickerOpen.index ? { ...it, image: path } : it)));
  };
  const openCategoryPicker = (idx: number) => setCategoryPicker({ open: true, index: idx });
  const closeCategoryPicker = () => setCategoryPicker({ open: false, index: null });
  const applyCategoryToCircle = (category: string, subLabel: string, path: string) => {
    if (categoryPicker.index == null) return;
    // Set mode to category and assign URL
    setCircleLinkModes(prev => ({ ...prev, [categoryPicker.index as number]: 'category' }));
    setCirclesItems(prev => prev.map((it, i) => (i === categoryPicker.index ? { ...it, url: path } : it)));
    closeCategoryPicker();
  };
  const addCircle = () => setCirclesItems(prev => [...prev, { label: '', url: '', image: '' }]);
  const removeCircle = (idx: number) => setCirclesItems(prev => prev.filter((_, i) => i !== idx));
  const moveCircle = (idx: number, dir: -1 | 1) => setCirclesItems(prev => {
    const arr = [...prev];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return arr;
    const tmp = arr[idx]; arr[idx] = arr[j]; arr[j] = tmp; return arr;
  });
  const saveCircles = async () => {
    const endpoint = (process.env.REACT_APP_WEBSITE_SETTINGS_ENDPOINT as string | undefined) || '/api/update-website-settings.php';
    let token = secret; if (!token) { const s = await AdminKeyService.getSecret(); setSecret(s); token = s; }
    if (!token) { toast.error('Missing admin key.'); return; }
    setSavingCircles(true);
    const loadingId = toast.loading('Kreise speichern...');
    try {
      const payload = { mode, homepage: { circles: { enabled: circlesEnabled, title: circlesTitle, items: circlesItems } }, token };
      const res = await fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Auth-Token': token, 'Authorization': `Bearer ${token}` }, body: JSON.stringify(payload) });
      const raw = await res.text(); let json: any = null; try { json = raw ? JSON.parse(raw) : null; } catch {}
      if (!res.ok || !json?.ok) { const detail = json?.error || raw || 'No response body'; throw new Error(`${res.status} ${res.statusText} - ${detail}`); }
      if (json?.homepage?.circles) {
        setCirclesEnabled(!!json.homepage.circles.enabled);
        setCirclesTitle(json.homepage.circles.title || '');
        const items = Array.isArray(json.homepage.circles.items) ? json.homepage.circles.items : [];
        setCirclesItems(items.map((it: any) => ({ label: it?.label || '', url: it?.url || '', image: typeof it?.image === 'string' ? it.image : '' })));
      }
      toast.success('Kreise gespeichert', { id: loadingId });
    } catch (e: any) {
      toast.error(`Speichern fehlgeschlagen: ${e?.message || 'Unknown error'}`, { id: loadingId });
    } finally { setSavingCircles(false); }
  };

  // Save featured section
  const saveFeature = async () => {
    const endpoint = (process.env.REACT_APP_WEBSITE_SETTINGS_ENDPOINT as string | undefined) || '/api/update-website-settings.php';
    let token = secret; if (!token) { const s = await AdminKeyService.getSecret(); setSecret(s); token = s; }
    if (!token) { toast.error('Missing admin key.'); return; }
    setSavingFeature(true);
    const loadingId = toast.loading('Feature speichert...');
    try {
      const payload = { mode, homepage: { feature: { enabled: featureEnabled, title: featureTitle, subtitle: featureSubtitle, ctaText: featureCtaText, ctaUrl: featureCtaUrl, image: featureImagePath || undefined, useGradient: featureUseGradient, bgColor: featureBgColor, gradientFrom: featureGradFrom, gradientTo: featureGradTo, textColor: featureTextColor || undefined, buttonBg: featureBtnBg || undefined, buttonText: featureBtnText || undefined } }, token };
      const res = await fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Auth-Token': token, 'Authorization': `Bearer ${token}` }, body: JSON.stringify(payload) });
      const raw = await res.text(); let json: any = null; try { json = raw ? JSON.parse(raw) : null; } catch {}
      if (!res.ok || !json?.ok) { const detail = json?.error || raw || 'No response body'; throw new Error(`${res.status} ${res.statusText} - ${detail}`); }
      if (json?.homepage?.feature) {
        setFeatureEnabled(!!json.homepage.feature.enabled);
        setFeatureTitle(json.homepage.feature.title || '');
        setFeatureSubtitle(json.homepage.feature.subtitle || '');
        setFeatureCtaText(json.homepage.feature.ctaText || '');
        setFeatureCtaUrl(json.homepage.feature.ctaUrl || '');
        setFeatureImagePath(json.homepage.feature.image || '');
        setFeatureUseGradient(!!json.homepage.feature.useGradient);
        setFeatureBgColor(json.homepage.feature.bgColor || '#ffffff');
        setFeatureGradFrom(json.homepage.feature.gradientFrom || '#0ea5e9');
        setFeatureGradTo(json.homepage.feature.gradientTo || '#22c55e');
        setFeatureTextColor(json.homepage.feature.textColor || '');
        setFeatureBtnBg(json.homepage.feature.buttonBg || '');
        setFeatureBtnText(json.homepage.feature.buttonText || '');
      }
      toast.success('Feature gespeichert', { id: loadingId });
    } catch (e: any) {
      toast.error(`Speichern fehlgeschlagen: ${e?.message || 'Unknown error'}`, { id: loadingId });
    } finally { setSavingFeature(false); }
  };

  // Upload feature image
  const uploadFeatureImage = async () => {
    if (!featureImageFile) return;
    const endpoint = (process.env.REACT_APP_WEBSITE_SETTINGS_ENDPOINT as string | undefined) || '/api/update-website-settings.php';
    let token = secret; if (!token) { const s = await AdminKeyService.getSecret(); setSecret(s); token = s; }
    if (!token) { toast.error('Missing admin key.'); return; }
    const loadingId = toast.loading('Feature-Bild wird hochgeladen...');
    try {
      const fd = new FormData();
      fd.append('mode', mode);
      fd.append('token', token);
      fd.append('featureImage', featureImageFile);
      const res = await fetch(endpoint, { method: 'POST', headers: { 'X-Auth-Token': token, 'Authorization': `Bearer ${token}` }, body: fd });
      const raw = await res.text(); let json: any = null; try { json = raw ? JSON.parse(raw) : null; } catch {}
      if (!res.ok || !json?.ok) { const detail = json?.error || raw || 'No response body'; throw new Error(`${res.status} ${res.statusText} - ${detail}`); }
      const newPath = json?.homepage?.feature?.image;
      if (typeof newPath === 'string' && newPath) { setFeatureImagePath(newPath); setFeatureImageFile(null); toast.success('Feature-Bild gespeichert', { id: loadingId }); }
      else { throw new Error('Server returned no feature image path'); }
    } catch (e: any) {
      toast.error(`Upload fehlgeschlagen: ${e?.message || 'Unknown error'}`, { id: loadingId });
    }
  };

  // Save promo JSON (without image upload)
  const savePromo = async () => {
    const endpoint = (process.env.REACT_APP_WEBSITE_SETTINGS_ENDPOINT as string | undefined) || '/api/update-website-settings.php';
    let token = secret; if (!token) { const s = await AdminKeyService.getSecret(); setSecret(s); token = s; }
    if (!token) { toast.error('Missing admin key.'); return; }
    setSavingPromo(true);
    const loadingId = toast.loading('Promotion speichert...');
    try {
      const promoPayload: any = { enabled: promoEnabled, showOncePerSession: promoOnce, title: promoTitle, message: promoMessage, ctaText: promoCtaText, ctaUrl: promoCtaUrl, displayMode: promoDisplayMode, drawerSide: promoDrawerSide, imagesLayout: promoImagesLayout };
      if (promoImages.length > 0) {
        promoPayload.images = promoImages;
        if (promoImages.length > 1) promoPayload.imageInterval = Math.max(2, Math.min(60, Number(promoImageInterval) || 5));
        if (promoImagesLinks && Object.keys(promoImagesLinks).length > 0) promoPayload.imagesLinks = promoImagesLinks;
      } else if (promoImagePath) {
        promoPayload.image = promoImagePath;
      }
      const payload = { mode, homepage: { promo: promoPayload }, token } as any;
      const res = await fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Auth-Token': token, 'Authorization': `Bearer ${token}` }, body: JSON.stringify(payload) });
      const raw = await res.text(); let json: any = null; try { json = raw ? JSON.parse(raw) : null; } catch {}
      if (!res.ok || !json?.ok) { const detail = json?.error || raw || 'No response body'; throw new Error(`${res.status} ${res.statusText} - ${detail}`); }
      if (json?.homepage?.promo) {
        setPromoEnabled(!!json.homepage.promo.enabled);
        setPromoOnce(json.homepage.promo.showOncePerSession !== false);
        setPromoTitle(json.homepage.promo.title || '');
        setPromoMessage(json.homepage.promo.message || '');
        setPromoCtaText(json.homepage.promo.ctaText || '');
        setPromoCtaUrl(json.homepage.promo.ctaUrl || '');
        setPromoImagePath(json.homepage.promo.image || '');
        setPromoImages(Array.isArray(json.homepage.promo.images) ? json.homepage.promo.images : []);
        if (typeof json.homepage.promo.imageInterval === 'number') setPromoImageInterval(Math.max(2, Math.min(60, json.homepage.promo.imageInterval)));
        if (json.homepage.promo.imagesLinks && typeof json.homepage.promo.imagesLinks === 'object') setPromoImagesLinks(json.homepage.promo.imagesLinks);
        if (typeof json.homepage.promo.displayMode === 'string' && ['modal','drawer'].includes(json.homepage.promo.displayMode)) setPromoDisplayMode(json.homepage.promo.displayMode);
        if (typeof json.homepage.promo.drawerSide === 'string' && ['left','right','top','bottom'].includes(json.homepage.promo.drawerSide)) setPromoDrawerSide(json.homepage.promo.drawerSide);
        if (typeof json.homepage.promo.imagesLayout === 'string' && ['slideshow','list'].includes(json.homepage.promo.imagesLayout)) setPromoImagesLayout(json.homepage.promo.imagesLayout);
      }
      toast.success('Promotion gespeichert', { id: loadingId });
    } catch (e: any) {
      toast.error(`Speichern fehlgeschlagen: ${e?.message || 'Unknown error'}`, { id: loadingId });
    } finally { setSavingPromo(false); }
  };

  // Upload promo image
  const uploadPromoImage = async () => {
    if (!promoImageFile) return;
    const endpoint = (process.env.REACT_APP_WEBSITE_SETTINGS_ENDPOINT as string | undefined) || '/api/update-website-settings.php';
    let token = secret; if (!token) { const s = await AdminKeyService.getSecret(); setSecret(s); token = s; }
    if (!token) { toast.error('Missing admin key.'); return; }
    const loadingId = toast.loading('Promo-Bild wird hochgeladen...');
    try {
      const fd = new FormData();
      fd.append('mode', mode);
      fd.append('token', token);
      fd.append('promoImage', promoImageFile);
      const res = await fetch(endpoint, { method: 'POST', headers: { 'X-Auth-Token': token, 'Authorization': `Bearer ${token}` }, body: fd });
      const raw = await res.text(); let json: any = null; try { json = raw ? JSON.parse(raw) : null; } catch {}
      if (!res.ok || !json?.ok) { const detail = json?.error || raw || 'No response body'; throw new Error(`${res.status} ${res.statusText} - ${detail}`); }
      const newPath = json?.homepage?.promo?.image;
      if (typeof newPath === 'string' && newPath) { setPromoImagePath(newPath); setPromoImageFile(null); toast.success('Promo-Bild gespeichert', { id: loadingId }); }
      else { throw new Error('Server returned no promo image path'); }
    } catch (e: any) {
      toast.error(`Upload fehlgeschlagen: ${e?.message || 'Unknown error'}`, { id: loadingId });
    }
  };

  // Slideshow helpers
  const saveSlideshow = async (next: string[]) => {
    const endpoint = (process.env.REACT_APP_WEBSITE_SETTINGS_ENDPOINT as string | undefined) || '/api/update-website-settings.php';
    let token = secret;
    if (!token) { const s = await AdminKeyService.getSecret(); setSecret(s); token = s; }
    if (!token) { toast.error('Missing admin key.'); return; }
    const loadingId = toast.loading('Slideshow wird gespeichert...');
    try {
      const payload = { mode, homepage: { slideshow: next }, token };
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Auth-Token': token, 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const raw = await res.text();
      let json: any = null; try { json = raw ? JSON.parse(raw) : null; } catch {}
      if (!res.ok || !json?.ok) {
        const detail = json?.error || raw || 'No response body';
        throw new Error(`${res.status} ${res.statusText} - ${detail}`);
      }
      if (Array.isArray(json?.homepage?.slideshow)) {
        setSlideshow(json.homepage.slideshow);
        setOriginalSlideshow(json.homepage.slideshow);
      }
      toast.success('Slideshow gespeichert', { id: loadingId });
    } catch (e: any) {
      toast.error(`Speichern fehlgeschlagen: ${e?.message || 'Unknown error'}`, { id: loadingId });
    }
  };

  const openSlideshowPicker = () => {
    setSlideshowSelection(new Set());
    setSlideshowPickerOpen(true);
  };
  const closeSlideshowPicker = () => {
    setSlideshowPickerOpen(false);
    setSlideshowSelection(new Set());
  };
  const toggleSlideshowSelect = (p: string) => {
    setSlideshowSelection(prev => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p); else next.add(p);
      return next;
    });
  };
  const addSelectedToSlideshow = async () => {
    const selected = Array.from(slideshowSelection);
    if (selected.length === 0) { closeSlideshowPicker(); return; }
    const merged = Array.from(new Set([...slideshow, ...selected]));
    await saveSlideshow(merged);
    closeSlideshowPicker();
  };
  const removeFromSlideshow = async (p: string) => {
    const next = slideshow.filter(x => x !== p);
    await saveSlideshow(next);
  };
  const moveSlideshow = async (index: number, dir: -1 | 1) => {
    const j = index + dir;
    if (j < 0 || j >= slideshow.length) return;
    const next = slideshow.slice();
    const tmp = next[index]; next[index] = next[j]; next[j] = tmp;
    await saveSlideshow(next);
  };
  const uploadSlideshowImages = async () => {
    if (!slideshowFiles.length) return;
    const endpoint = (process.env.REACT_APP_WEBSITE_SETTINGS_ENDPOINT as string | undefined) || '/api/update-website-settings.php';
    let token = secret;
    if (!token) { const s = await AdminKeyService.getSecret(); setSecret(s); token = s; }
    if (!token) { toast.error('Missing admin key.'); return; }
    setUploadingSlideshow(true);
    const loadingId = toast.loading('Slideshow-Bilder werden hochgeladen...');
    try {
      const fd = new FormData();
      fd.append('mode', mode);
      fd.append('token', token);
      slideshowFiles.forEach(f => fd.append('heroGallery[]', f));
      const res = await fetch(endpoint, { method: 'POST', headers: { 'X-Auth-Token': token, 'Authorization': `Bearer ${token}` }, body: fd });
      const raw = await res.text();
      let json: any = null; try { json = raw ? JSON.parse(raw) : null; } catch {}
      if (!res.ok || !json?.ok) {
        const detail = json?.error || raw || 'No response body';
        throw new Error(`${res.status} ${res.statusText} - ${detail}`);
      }
      // Determine new gallery items (diff by path)
      const newGallery = Array.isArray(json?.homepage?.gallery) ? json.homepage.gallery : [];
      const diff = newGallery.filter((p: string) => !originalGallery.includes(p) && !gallery.includes(p));
      const merged = Array.from(new Set([...slideshow, ...diff]));
      await saveSlideshow(merged);
      setGallery(newGallery);
      setOriginalGallery(newGallery);
      setSlideshowFiles([]);
      setSlideshowObjectUrls([]);
      toast.success('Bilder hochgeladen', { id: loadingId });
    } catch (e: any) {
      toast.error(`Upload fehlgeschlagen: ${e?.message || 'Unknown error'}`, { id: loadingId });
    } finally {
      setUploadingSlideshow(false);
    }
  };

  const openGalleryPicker = () => {
    setSelectedGalleryPath(null);
    setGalleryPickerOpen(true);
  };

  const closeGalleryPicker = () => {
    setGalleryPickerOpen(false);
    setSelectedGalleryPath(null);
  };

  const saveHeroFromGallery = async () => {
    if (!selectedGalleryPath) return;
    const endpoint = (process.env.REACT_APP_WEBSITE_SETTINGS_ENDPOINT as string | undefined) || '/api/update-website-settings.php';
    let token = secret;
    if (!token) {
      const s = await AdminKeyService.getSecret();
      setSecret(s);
      token = s;
    }
    if (!token) { toast.error('Missing admin key.'); return; }
    const loadingId = toast.loading('Hintergrund wird gesetzt...');
    try {
      const payload = { mode, homepage: { mediaPath: selectedGalleryPath, mediaType: 'image' }, token };
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Auth-Token': token, 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const raw = await res.text();
      let json: any = null; try { json = raw ? JSON.parse(raw) : null; } catch {}
      if (!res.ok || !json?.ok) {
        const detail = json?.error || raw || 'No response body';
        throw new Error(`${res.status} ${res.statusText} - ${detail}`);
      }
      if (json?.homepage) {
        if (typeof json.homepage.mediaPath === 'string') {
          setMediaPath(json.homepage.mediaPath);
          setOriginalMediaPath(json.homepage.mediaPath);
        }
        if (typeof json.homepage.mediaType === 'string') {
          setMediaType(json.homepage.mediaType as any);
          setOriginalMediaType(json.homepage.mediaType as any);
        }
      }
      toast.success('Hintergrund gesetzt', { id: loadingId });
      closeGalleryPicker();
    } catch (e: any) {
      toast.error(`Setzen fehlgeschlagen: ${e?.message || 'Unknown error'}`, { id: loadingId });
    }
  };

  const deleteGalleryItem = async (path: string) => {
    if (!path) return;
    const endpoint = (process.env.REACT_APP_WEBSITE_SETTINGS_ENDPOINT as string | undefined) || '/api/update-website-settings.php';
    let token = secret;
    if (!token) {
      const s = await AdminKeyService.getSecret();
      setSecret(s);
      token = s;
    }
    if (!token) { toast.error('Missing admin key.'); return; }
    setDeletingPath(path);
    const loadingId = toast.loading('Bild wird gelöscht...');
    try {
      const fd = new FormData();
      fd.append('mode', mode);
      fd.append('token', token);
      fd.append('deleteGallery', path);
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'X-Auth-Token': token, 'Authorization': `Bearer ${token}` },
        body: fd,
      });
      const raw = await res.text();
      let json: any = null; try { json = raw ? JSON.parse(raw) : null; } catch {}
      if (!res.ok || !json?.ok) {
        const detail = json?.error || raw || 'No response body';
        throw new Error(`${res.status} ${res.statusText} - ${detail}`);
      }
      if (json?.homepage && Array.isArray(json.homepage.gallery)) {
        setGallery(json.homepage.gallery);
        setOriginalGallery(json.homepage.gallery);
      }
      toast.success('Bild gelöscht', { id: loadingId });
    } catch (e: any) {
      toast.error(`Löschen fehlgeschlagen: ${e?.message || 'Unknown error'}`, { id: loadingId });
    } finally {
      setDeletingPath(null);
    }
  };

  const discardGallerySelection = () => {
    setGalleryFiles([]);
    setGalleryObjectUrls([]);
    setGallery(originalGallery.slice());
  };

  const uploadHeroGallery = async () => {
    if (!galleryFiles.length) return;
    const endpoint = (process.env.REACT_APP_WEBSITE_SETTINGS_ENDPOINT as string | undefined) || '/api/update-website-settings.php';
    let token = secret;
    if (!token) {
      const s = await AdminKeyService.getSecret();
      setSecret(s);
      token = s;
    }
    if (!token) { toast.error('Missing admin key.'); return; }
    setUploadingGallery(true);
    const loadingId = toast.loading('Galerie wird hochgeladen...');
    try {
      const fd = new FormData();
      fd.append('mode', mode);
      fd.append('token', token);
      galleryFiles.forEach(f => fd.append('heroGallery[]', f));
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'X-Auth-Token': token, 'Authorization': `Bearer ${token}` },
        body: fd,
      });
      const raw = await res.text();
      let json: any = null; try { json = raw ? JSON.parse(raw) : null; } catch {}
      if (!res.ok || !json?.ok) {
        const detail = json?.error || raw || 'No response body';
        throw new Error(`${res.status} ${res.statusText} - ${detail}`);
      }
      if (json?.homepage && Array.isArray(json.homepage.gallery) && json.homepage.gallery.length > 0) {
        setGallery(json.homepage.gallery);
        setOriginalGallery(json.homepage.gallery);
      } else {
        throw new Error('Server reported success but no gallery paths were returned.');
      }
      setGalleryFiles([]);
      setGalleryObjectUrls([]);
      toast.success('Galerie hochgeladen', { id: loadingId });
    } catch (e: any) {
      toast.error(`Upload fehlgeschlagen: ${e?.message || 'Unknown error'}`, { id: loadingId });
    } finally {
      setUploadingGallery(false);
    }
  };

  const discardSelected = () => {
    setFile(null);
    // Revert state to original stored media
    setMediaPath(originalMediaPath || '');
    setMediaType(originalMediaType);
  };

  useEffect(() => {
    const load = async () => {
      try {
        let res = await fetch('/api/get-website-settings.php', { cache: 'no-store' });
        if (!res.ok) {
          res = await fetch('/website-settings.json', { cache: 'no-store' });
        }
        if (!res.ok) return;
        const data = await res.json();
        setHeroEnabled(typeof data?.homepage?.heroEnabled === 'boolean' ? data.homepage.heroEnabled : true);
        if (data?.homepage) {
          if (typeof data.homepage.mediaPath === 'string') setMediaPath(data.homepage.mediaPath);
          if (typeof data.homepage.mediaType === 'string') setMediaType(data.homepage.mediaType);
          if (typeof data.homepage.mediaPath === 'string') setOriginalMediaPath(data.homepage.mediaPath);
          if (typeof data.homepage.mediaType === 'string') setOriginalMediaType(data.homepage.mediaType);
          if (Array.isArray(data.homepage.gallery)) {
            setGallery(data.homepage.gallery.filter((p: any) => typeof p === 'string'));
            setOriginalGallery(data.homepage.gallery.filter((p: any) => typeof p === 'string'));
          }
          if (data.homepage?.eventFeatured) {
            const ev = data.homepage.eventFeatured;
            setEventEnabled(!!ev.enabled);
            setEventTitle(ev.title || '');
            setEventSubtitle(ev.subtitle || '');
            setEventCtaText(ev.ctaText || '');
            setEventCtaUrl(ev.ctaUrl || '');
            setEventUseGradient(!!ev.useGradient);
            setEventBgColor(ev.bgColor || '#f472b6');
            setEventGradFrom(ev.gradientFrom || '#ec4899');
            setEventGradTo(ev.gradientTo || '#f59e0b');
            setEventTextColor(ev.textColor || '');
            setEventBtnBg(ev.buttonBg || '');
            setEventBtnText(ev.buttonText || '');
            setEventProductIdsText(Array.isArray(ev.productIds) ? ev.productIds.join(',') : '');
          }
          if (data.homepage?.circles) {
            setCirclesEnabled(!!data.homepage.circles.enabled);
            setCirclesTitle(data.homepage.circles.title || '');
            const items = Array.isArray(data.homepage.circles.items) ? data.homepage.circles.items : [];
            setCirclesItems(items.map((it: any) => ({ label: it?.label || '', url: it?.url || '', image: typeof it?.image === 'string' ? it.image : '' })));
          }
          if (Array.isArray(data.homepage.slideshow)) {
            const ss = data.homepage.slideshow.filter((p: any) => typeof p === 'string');
            setSlideshow(ss);
            setOriginalSlideshow(ss);
          }
          if (data.homepage?.promo) {
            setPromoEnabled(!!data.homepage.promo.enabled);
            setPromoOnce(data.homepage.promo.showOncePerSession !== false);
            setPromoTitle(data.homepage.promo.title || '');
            setPromoMessage(data.homepage.promo.message || '');
            setPromoCtaText(data.homepage.promo.ctaText || '');
            setPromoCtaUrl(data.homepage.promo.ctaUrl || '');
            setPromoImagePath(typeof data.homepage.promo.image === 'string' ? data.homepage.promo.image : '');
            if (typeof data.homepage.promo.displayMode === 'string' && ['modal','drawer'].includes(data.homepage.promo.displayMode)) {
              setPromoDisplayMode(data.homepage.promo.displayMode);
            }
            if (typeof data.homepage.promo.drawerSide === 'string' && ['left','right','top','bottom'].includes(data.homepage.promo.drawerSide)) {
              setPromoDrawerSide(data.homepage.promo.drawerSide);
            }
            if (typeof data.homepage.promo.imagesLayout === 'string' && ['slideshow','list'].includes(data.homepage.promo.imagesLayout)) {
              setPromoImagesLayout(data.homepage.promo.imagesLayout);
            }
            if (Array.isArray(data.homepage.promo.images)) {
              setPromoImages(data.homepage.promo.images.filter((s: any) => typeof s === 'string' && s));
            } else {
              setPromoImages([]);
            }
            if (typeof data.homepage.promo.imageInterval === 'number') {
              setPromoImageInterval(Math.max(2, Math.min(60, data.homepage.promo.imageInterval)));
            }
            if (data.homepage.promo.imagesLinks && typeof data.homepage.promo.imagesLinks === 'object') {
              setPromoImagesLinks(data.homepage.promo.imagesLinks as Record<string, string>);
            } else {
              setPromoImagesLinks({});
            }
          }
          if (data.homepage?.feature) {
            setFeatureEnabled(!!data.homepage.feature.enabled);
            setFeatureTitle(data.homepage.feature.title || '');
            setFeatureSubtitle(data.homepage.feature.subtitle || '');
            setFeatureCtaText(data.homepage.feature.ctaText || '');
            setFeatureCtaUrl(data.homepage.feature.ctaUrl || '');
            setFeatureImagePath(typeof data.homepage.feature.image === 'string' ? data.homepage.feature.image : '');
            setFeatureUseGradient(!!data.homepage.feature.useGradient);
            setFeatureBgColor(data.homepage.feature.bgColor || '#ffffff');
            setFeatureGradFrom(data.homepage.feature.gradientFrom || '#0ea5e9');
            setFeatureGradTo(data.homepage.feature.gradientTo || '#22c55e');
            setFeatureTextColor(data.homepage.feature.textColor || '');
            setFeatureBtnBg(data.homepage.feature.buttonBg || '');
            setFeatureBtnText(data.homepage.feature.buttonText || '');
          }
          if (data.homepage?.grid) {
            setGridEnabled(!!data.homepage.grid.enabled);
            setGridTitle(data.homepage.grid.title || '');
            setGridRows(Math.max(1, Number(data.homepage.grid.rows)||1));
            setGridCols(Math.max(1, Number(data.homepage.grid.cols)||1));
            const tiles = Array.isArray(data.homepage.grid.tiles) ? data.homepage.grid.tiles : [];
            setGridTiles(tiles.map((it: any) => ({
              title: it?.title || '',
              subtitle: it?.subtitle || '',
              image: typeof it?.image === 'string' ? it.image : '',
              images: Array.isArray(it?.images) ? it.images.filter((s: any) => typeof s === 'string' && s) : undefined,
              imageInterval: Number(it?.imageInterval) || undefined,
              ctaText: it?.ctaText || '',
              ctaUrl: it?.ctaUrl || '',
              bgColor: it?.bgColor || '',
              textColor: it?.textColor || '',
              enabled: it?.enabled !== false,
              reserveSpace: it?.reserveSpace === true,
              colSpan: Math.max(1, Number(it?.colSpan)||1),
              rowSpan: Math.max(1, Number(it?.rowSpan)||1),
              hoverEffect: typeof it?.hoverEffect === 'string' ? it.hoverEffect : 'none',
              ctaFree: it?.ctaFree === true,
              ctaX: Math.max(0, Math.min(100, Number(it?.ctaX) || 15)),
              ctaY: Math.max(0, Math.min(100, Number(it?.ctaY) || 80)),
              ctaScale: Math.max(0.5, Math.min(3, Number(it?.ctaScale) || 1)),
              ctaBtnBg: typeof it?.ctaBtnBg === 'string' ? it.ctaBtnBg : '',
              ctaBtnTextColor: typeof it?.ctaBtnTextColor === 'string' ? it.ctaBtnTextColor : '',
              ctaHoverEffect: typeof it?.ctaHoverEffect === 'string' ? it.ctaHoverEffect : 'none',
              ctaFontSize: Math.max(10, Math.min(36, Number(it?.ctaFontSize) || 14)),
              ctaWidth: Math.max(80, Math.min(400, Number(it?.ctaWidth) || 140)),
              showCounter: it?.showCounter === true,
              counterFree: it?.counterFree === true,
              counterX: Math.max(0, Math.min(100, Number(it?.counterX) || 85)),
              counterY: Math.max(0, Math.min(100, Number(it?.counterY) || 85)),
              counterScale: Math.max(0.5, Math.min(3, Number(it?.counterScale) || 1)),
              counterBg: typeof it?.counterBg === 'string' ? it.counterBg : 'rgba(255,255,255,0.9)',
              counterTextColor: typeof it?.counterTextColor === 'string' ? it.counterTextColor : '#111827',
              counterRadius: Math.max(0, Math.min(28, Number(it?.counterRadius) || 8)),
              counterPrefix: typeof it?.counterPrefix === 'string' ? it.counterPrefix : '',
              counterSuffix: typeof it?.counterSuffix === 'string' ? it.counterSuffix : '',
            })));
          }
          if (Array.isArray(data.homepage?.gridTabs)) {
            const tabs = (data.homepage.gridTabs as any[]).map((tb: any, i: number) => ({
              label: typeof tb?.label === 'string' && tb.label ? tb.label : `Tab ${i+1}`,
              enabled: tb?.enabled !== false,
              title: tb?.title || '',
              rows: Math.max(1, Number(tb?.rows)||1),
              cols: Math.max(1, Number(tb?.cols)||1),
              rowHeight: Math.max(80, Math.min(480, Number(tb?.rowHeight) || 160)),
              tiles: Array.isArray(tb?.tiles) ? tb.tiles : [],
              colWidths: Array.isArray((tb as any)?.colWidths) ? (tb as any).colWidths : undefined,
            }));
            setGridTabs(tabs);
            const desiredIdx = Math.max(0, Math.min(gridActiveTab, tabs.length - 1));
            setGridActiveTab(desiredIdx);
            const t0 = tabs[desiredIdx];
            if (t0) {
              setGridEnabled(t0.enabled !== false);
              setGridTitle(t0.title || '');
              setGridRows(Math.max(1, Number(t0.rows)||1));
              setGridCols(Math.max(1, Number(t0.cols)||1));
              setGridRowHeight(Math.max(80, Math.min(480, Number((t0 as any).rowHeight) || 160)));
              if (Array.isArray((t0 as any).colWidths) && (t0 as any).colWidths.length === Math.max(1, Number(t0.cols)||1)) {
                setGridColWidths((t0 as any).colWidths.map((n: any) => Math.max(0.25, Number(n) || 1)));
              } else {
                setGridColWidths(Array.from({ length: Math.max(1, Number(t0.cols)||1) }).map(() => 1));
              }
              const tiles0 = Array.isArray(t0.tiles) ? t0.tiles : [];
              setGridTiles(tiles0.map((it: any) => ({
                title: it?.title || '',
                subtitle: it?.subtitle || '',
                image: typeof it?.image === 'string' ? it.image : '',
                images: Array.isArray(it?.images) ? it.images.filter((s: any)=> typeof s === 'string' && s) : undefined,
                imageInterval: Number(it?.imageInterval) || undefined,
                ctaText: it?.ctaText || '',
                ctaUrl: it?.ctaUrl || '',
                bgColor: it?.bgColor || '',
                textColor: it?.textColor || '',
                enabled: it?.enabled !== false,
                reserveSpace: it?.reserveSpace === true,
                colSpan: Math.max(1, Number(it?.colSpan)||1),
                rowSpan: Math.max(1, Number(it?.rowSpan)||1),
                hoverEffect: typeof it?.hoverEffect === 'string' ? it.hoverEffect : 'none',
                ctaFree: it?.ctaFree === true,
                ctaX: Math.max(0, Math.min(100, Number(it?.ctaX) || 15)),
                ctaY: Math.max(0, Math.min(100, Number(it?.ctaY) || 80)),
                ctaScale: Math.max(0.5, Math.min(3, Number(it?.ctaScale) || 1)),
                ctaBtnBg: typeof it?.ctaBtnBg === 'string' ? it.ctaBtnBg : '',
                ctaBtnTextColor: typeof it?.ctaBtnTextColor === 'string' ? it.ctaBtnTextColor : '',
                ctaHoverEffect: typeof it?.ctaHoverEffect === 'string' ? it.ctaHoverEffect : 'none',
                ctaFontSize: Math.max(10, Math.min(36, Number(it?.ctaFontSize) || 14)),
                ctaWidth: Math.max(80, Math.min(400, Number(it?.ctaWidth) || 140)),
                showCounter: it?.showCounter === true,
                counterFree: it?.counterFree === true,
                counterX: Math.max(0, Math.min(100, Number(it?.counterX) || 85)),
                counterY: Math.max(0, Math.min(100, Number(it?.counterY) || 85)),
                counterScale: Math.max(0.5, Math.min(3, Number(it?.counterScale) || 1)),
                counterBg: typeof it?.counterBg === 'string' ? it.counterBg : 'rgba(255,255,255,0.9)',
                counterTextColor: typeof it?.counterTextColor === 'string' ? it.counterTextColor : '#111827',
                counterRadius: Math.max(0, Math.min(28, Number(it?.counterRadius) || 8)),
                counterPrefix: typeof it?.counterPrefix === 'string' ? it.counterPrefix : '',
                counterSuffix: typeof it?.counterSuffix === 'string' ? it.counterSuffix : '',
                showList: it?.showList === true,
                listTabIndex: Number.isInteger(it?.listTabIndex) ? it.listTabIndex : -1,
                listFullSize: it?.listFullSize === true,
                listScrollDir: (it?.listScrollDir === 'horizontal') ? 'horizontal' : 'vertical',
              })));
            }
          }
          if (data.homepage?.gridTabsDisplay) {
            const gtd = data.homepage.gridTabsDisplay as any;
            if (gtd && typeof gtd === 'object') {
              if (typeof gtd.position === 'string' && ['above','below'].includes(gtd.position)) setGridTabsPosition(gtd.position);
              if (typeof gtd.style === 'string' && ['labels','dots'].includes(gtd.style)) setGridTabsStyle(gtd.style);
              if (typeof gtd.arrowsEnabled !== 'undefined') setGridTabsArrows(!!gtd.arrowsEnabled);
              if (typeof gtd.transitionType === 'string' && ['none','slide','tiles','cube'].includes(gtd.transitionType)) setGridTabsTransitionType(gtd.transitionType);
            }
          }
        }
      } catch {}
    };

    const loadSecret = async () => {
      const s = await AdminKeyService.getSecret();
      setSecret(s);
    };
    load();
    loadSecret();
  }, []);

  // Manage object URL for immediate preview when a new file is selected
  useEffect(() => {
    if (!file) {
      if (objectUrl) {
        try { URL.revokeObjectURL(objectUrl); } catch {}
      }
      setObjectUrl(null);
      return;
    }
    try {
      const url = URL.createObjectURL(file);
      setObjectUrl(url);
    } catch {}
    return () => {
      if (objectUrl) {
        try { URL.revokeObjectURL(objectUrl); } catch {}
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  // Manage object URLs for gallery selection previews
  useEffect(() => {
    // Revoke previous
    galleryObjectUrls.forEach(u => {
      try { URL.revokeObjectURL(u); } catch {}
    });
    if (!galleryFiles.length) {
      setGalleryObjectUrls([]);
      return;
    }
    const urls = galleryFiles.map(f => {
      try { return URL.createObjectURL(f); } catch { return ''; }
    }).filter(Boolean) as string[];
    setGalleryObjectUrls(urls);
    return () => {
      urls.forEach(u => { try { URL.revokeObjectURL(u); } catch {} });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [galleryFiles]);

  // Manage object URLs for slideshow selection previews
  useEffect(() => {
    slideshowObjectUrls.forEach(u => { try { URL.revokeObjectURL(u); } catch {} });
    if (!slideshowFiles.length) { setSlideshowObjectUrls([]); return; }
    const urls = slideshowFiles.map(f => { try { return URL.createObjectURL(f); } catch { return ''; } }).filter(Boolean) as string[];
    setSlideshowObjectUrls(urls);
    return () => { urls.forEach(u => { try { URL.revokeObjectURL(u); } catch {} }); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slideshowFiles]);

  // Promo object URL for preview
  useEffect(() => {
    if (!promoImageFile) {
      if (promoObjectUrl) { try { URL.revokeObjectURL(promoObjectUrl); } catch {} }
      setPromoObjectUrl(null);
      return;
    }
    try { setPromoObjectUrl(URL.createObjectURL(promoImageFile)); } catch {}
    return () => { if (promoObjectUrl) { try { URL.revokeObjectURL(promoObjectUrl); } catch {} } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promoImageFile]);

  // Manage object URL for feature image preview
  useEffect(() => {
    if (!featureImageFile) {
      if (featureObjectUrl) { try { URL.revokeObjectURL(featureObjectUrl); } catch {} }
      setFeatureObjectUrl(null);
      return;
    }
    try { setFeatureObjectUrl(URL.createObjectURL(featureImageFile)); } catch {}
    return () => { if (featureObjectUrl) { try { URL.revokeObjectURL(featureObjectUrl); } catch {} } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureImageFile]);

  const saveHomepage = async () => {
    const endpoint = (process.env.REACT_APP_WEBSITE_SETTINGS_ENDPOINT as string | undefined) || '/api/update-website-settings.php';
    let token = secret;
    if (!token) {
      const s = await AdminKeyService.getSecret();
      setSecret(s);
      token = s;
    }
    if (!token) { toast.error('Missing admin key.'); return; }
    const loadingId = toast.loading('Speichere Startseite...');
    setSaving(true);
    try {
      const rows = Math.max(1, Number(gridRows) || 1);
      const cols = Math.max(1, Number(gridCols) || 1);
      const total = rows * cols;
      const tilesSource = (() => {
        if (tileEditorOpen && tileEditorIndex != null) {
          const idx = tileEditorIndex as number;
          return gridTiles.map((x, i) => i === idx ? {
            ...x,
            title: teTitle,
            subtitle: teSubtitle,
            image: (teImages && teImages.length > 0) ? undefined : (x as any).image,
            images: (teImages && teImages.length > 0) ? teImages : undefined,
            imageInterval: (teImages && teImages.length > 1) ? Math.max(2, Math.min(60, teImageInterval)) : undefined,
            ctaText: teCtaText,
            ctaUrl: teCtaUrl,
            bgColor: teBgColor,
            textColor: teTextColor,
            enabled: teEnabled,
            reserveSpace: teReserveSpace,
            colSpan: Math.max(1, Math.min(cols, teColSpan)),
            hoverEffect: teHoverEffect,
            ctaFree: teCtaFree,
            ctaX: Math.max(0, Math.min(100, teCtaX)),
            ctaY: Math.max(0, Math.min(100, teCtaY)),
            ctaScale: Math.max(0.5, Math.min(3, teCtaScale)),
            ctaBtnBg: teCtaBtnBg,
            ctaBtnTextColor: teCtaBtnTextColor,
            ctaHoverEffect: teCtaHoverEffect,
            ctaFontSize: Math.max(10, Math.min(36, teCtaFontSize)),
            ctaWidth: Math.max(80, Math.min(400, teCtaWidth)),
          } : x);
        }
        return gridTiles;
      })();
      const paddedTiles = Array.from({ length: total }).map((_, i) => tilesSource[i] || { title: '', subtitle: '', image: '', images: [], imageInterval: undefined, ctaText: '', ctaUrl: '', bgColor: '', textColor: '', enabled: true, reserveSpace: false, colSpan: 1, hoverEffect: 'none', ctaFree: false, ctaX: 15, ctaY: 80 });
      const payload = { mode, homepage: { heroEnabled, grid: { enabled: gridEnabled, title: gridTitle, rows, cols, tiles: paddedTiles } }, token };
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Auth-Token': token, 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const raw = await res.text();
      let json: any = null; try { json = raw ? JSON.parse(raw) : null; } catch {}
      if (!res.ok || !json?.ok) {
        const detail = json?.error || raw || 'No response body';
        throw new Error(`${res.status} ${res.statusText} - ${detail}`);
      }
      if (json?.homepage) {
        if (typeof json.homepage.heroEnabled === 'boolean') setHeroEnabled(json.homepage.heroEnabled);
        if (typeof json.homepage.mediaPath === 'string') setMediaPath(json.homepage.mediaPath);
        if (typeof json.homepage.mediaType === 'string') setMediaType(json.homepage.mediaType);
        if (Array.isArray(json.homepage.gallery)) {
          setGallery(json.homepage.gallery);
          setOriginalGallery(json.homepage.gallery);
        }
        if (Array.isArray(json.homepage.slideshow)) {
          setSlideshow(json.homepage.slideshow);
          setOriginalSlideshow(json.homepage.slideshow);
        }
        if (json.homepage?.grid) {
          setGridEnabled(!!json.homepage.grid.enabled);
          setGridTitle(json.homepage.grid.title || '');
          setGridRows(Math.max(1, Number(json.homepage.grid.rows)||1));
          setGridCols(Math.max(1, Number(json.homepage.grid.cols)||1));
          const tiles = Array.isArray(json.homepage.grid.tiles) ? json.homepage.grid.tiles : [];
          setGridTiles(tiles.map((it: any) => ({
            title: it?.title || '',
            subtitle: it?.subtitle || '',
            image: typeof it?.image === 'string' ? it.image : '',
            images: Array.isArray(it?.images) ? it.images.filter((s: any)=> typeof s === 'string' && s) : undefined,
            imageInterval: Number(it?.imageInterval) || undefined,
            ctaText: it?.ctaText || '',
            ctaUrl: it?.ctaUrl || '',
            bgColor: it?.bgColor || '',
            textColor: it?.textColor || '',
            enabled: it?.enabled !== false,
            reserveSpace: it?.reserveSpace === true,
            colSpan: Math.max(1, Number(it?.colSpan)||1)
          })));
        }
      }
      toast.success('Startseite gespeichert', { id: loadingId });
    } catch (e: any) {
      toast.error(`Speichern fehlgeschlagen: ${e?.message || 'Unknown error'}`, { id: loadingId });
    } finally {
      setSaving(false);
    }
  };

  const uploadHeroMedia = async () => {
    if (!file) return;
    const endpoint = (process.env.REACT_APP_WEBSITE_SETTINGS_ENDPOINT as string | undefined) || '/api/update-website-settings.php';
    let token = secret;
    if (!token) {
      const s = await AdminKeyService.getSecret();
      setSecret(s);
      token = s;
    }
    if (!token) { toast.error('Missing admin key.'); return; }
    setUploading(true);
    const loadingId = toast.loading('Upload läuft...');
    try {
      const fd = new FormData();
      fd.append('mode', mode);
      fd.append('token', token);
      fd.append('heroMedia', file);
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'X-Auth-Token': token, 'Authorization': `Bearer ${token}` },
        body: fd,
      });
      const raw = await res.text();
      let json: any = null; try { json = raw ? JSON.parse(raw) : null; } catch {}
      if (!res.ok || !json?.ok) {
        const detail = json?.error || raw || 'No response body';
        throw new Error(`${res.status} ${res.statusText} - ${detail}`);
      }
      if (json?.homepage) {
        if (typeof json.homepage.mediaPath === 'string') {
          setMediaPath(json.homepage.mediaPath);
          setOriginalMediaPath(json.homepage.mediaPath);
        }
        if (typeof json.homepage.mediaType === 'string') {
          setMediaType(json.homepage.mediaType as any);
          setOriginalMediaType(json.homepage.mediaType as any);
        }
      }
      setFile(null);
      toast.success('Hero-Medium hochgeladen', { id: loadingId });
    } catch (e: any) {
      toast.error(`Upload fehlgeschlagen: ${e?.message || 'Unknown error'}`, { id: loadingId });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={800} gutterBottom>
            Seiten
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Verwalte Seiten und deren Inhalte.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5} alignItems="center">
          {overallDirty ? (
            <Chip
              label="Ungespeichert"
              color="warning"
              variant="outlined"
              sx={{ fontWeight: 800, borderRadius: 3 }}
            />
          ) : (
            <Chip
              label="Gespeichert"
              color="success"
              variant="outlined"
              sx={{ fontWeight: 800, borderRadius: 3 }}
            />
          )}

          <Button
            onClick={saveHomepageAll}
            disabled={overallSaving || !overallDirty}
            startIcon={overallSaving ? <CircularProgress size={18} /> : <SaveRoundedIcon />}
            variant="contained"
            sx={{
              borderRadius: 3,
              px: 2.25,
              py: 1,
              fontWeight: 900,
              textTransform: 'none',
              boxShadow: '0 14px 30px rgba(2, 132, 199, 0.22)',
            }}
          >
            {overallSaving ? 'Speichern…' : 'Speichern'}
          </Button>
        </Stack>
      </Box>

      {/* CTA Settings Dialog */}
      <Dialog open={ctaSettingsOpen} onClose={() => setCtaSettingsOpen(false)} maxWidth={ctaSettingsFull ? 'md' : 'sm'} fullWidth fullScreen={ctaSettingsFull}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          CTA-Einstellungen
          <IconButton size="small" onClick={() => setCtaSettingsFull(v => !v)}>
            {ctaSettingsFull ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gap: 2 }}>
            <TextField label="CTA Text" value={teCtaText} onChange={(e) => setTeCtaText(e.target.value)} fullWidth />
            <TextField label="CTA URL" value={teCtaUrl} onChange={(e) => setTeCtaUrl(e.target.value)} fullWidth />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField label="Button Hintergrund" value={teCtaBtnBg} onChange={(e) => setTeCtaBtnBg(e.target.value)} placeholder="#3b82f6" fullWidth />
                <input type="color" value={teCtaBtnBg || '#3b82f6'} onChange={(e) => setTeCtaBtnBg(e.target.value)} style={{ width: 44, height: 44, border: 'none', background: 'transparent', cursor: 'pointer' }} />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField label="Button Textfarbe" value={teCtaBtnTextColor} onChange={(e) => setTeCtaBtnTextColor(e.target.value)} placeholder="#ffffff" fullWidth />
                <input type="color" value={teCtaBtnTextColor || '#ffffff'} onChange={(e) => setTeCtaBtnTextColor(e.target.value)} style={{ width: 44, height: 44, border: 'none', background: 'transparent', cursor: 'pointer' }} />
              </Box>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField type="number" label="Button Schriftgröße (px)" value={teCtaFontSize} onChange={(e) => setTeCtaFontSize(Math.max(10, Math.min(36, Number(e.target.value)||14)))} inputProps={{ step: 1 }} fullWidth />
              <TextField type="number" label="Button Breite (px)" value={teCtaWidth} onChange={(e) => setTeCtaWidth(Math.max(80, Math.min(400, Number(e.target.value)||140)))} inputProps={{ step: 5 }} fullWidth />
            </Box>
            <FormControl fullWidth>
              <InputLabel id="cta-hover-effect-label">CTA Hover-Effekt</InputLabel>
              <Select labelId="cta-hover-effect-label" label="CTA Hover-Effekt" value={teCtaHoverEffect} onChange={(e) => setTeCtaHoverEffect(String(e.target.value))}>
                <MenuItem value="none">Keiner</MenuItem>
                <MenuItem value="zoom">Zoom</MenuItem>
                <MenuItem value="lift">Lift</MenuItem>
                <MenuItem value="glow">Glow</MenuItem>
                <MenuItem value="zoomGlow">Zoom + Glow</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCtaSettingsOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cardStripEditor.open} onClose={() => setCardStripEditor(prev => ({ ...prev, open: false, sectionId: null }))} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 900 }}>Cards bearbeiten</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gap: 2 }}>
            <FormControlLabel control={<Switch checked={cardStripEditor.enabled} onChange={(_, v) => setCardStripEditor(prev => ({ ...prev, enabled: v }))} />} label="Aktiv" />
            <TextField label="Überschrift" value={cardStripEditor.heading} onChange={(e) => setCardStripEditor(prev => ({ ...prev, heading: e.target.value }))} fullWidth />
            <TextField label="Text / Paragraph" value={cardStripEditor.text} onChange={(e) => setCardStripEditor(prev => ({ ...prev, text: e.target.value }))} fullWidth multiline minRows={3} />
            <TextField
              label="Produkt-IDs (Komma oder Zeilenumbruch)"
              value={cardStripEditor.productIdsText}
              onChange={(e) => setCardStripEditor(prev => ({ ...prev, productIdsText: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField label="Elemente pro Reihe" type="number" value={cardStripEditor.perRow} onChange={(e) => setCardStripEditor(prev => ({ ...prev, perRow: Math.max(1, Math.min(6, Number(e.target.value) || 3)) }))} fullWidth />
              <FormControl fullWidth>
                <InputLabel id="cs-variant">Variante</InputLabel>
                <Select labelId="cs-variant" label="Variante" value={cardStripEditor.variant} onChange={(e) => setCardStripEditor(prev => ({ ...prev, variant: (e.target.value === 'compact' ? 'compact' : 'default') }))}>
                  <MenuItem value="default">Default</MenuItem>
                  <MenuItem value="compact">Compact</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <FormControlLabel control={<Switch checked={Boolean(cardStripEditor.showSeller)} onChange={(_, v) => setCardStripEditor(prev => ({ ...prev, showSeller: v }))} />} label="Seller anzeigen" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCardStripEditor(prev => ({ ...prev, open: false, sectionId: null }))}>Abbrechen</Button>
          <Button variant="contained" onClick={() => {
            const sectionId = cardStripEditor.sectionId;
            if (!sectionId) { setCardStripEditor(prev => ({ ...prev, open: false, sectionId: null })); return; }
            setBuilderSections((prev) => updateSectionById(prev, String(sectionId), (s: any) => ({
              ...s,
              type: 'cardstrip',
              title: 'Cards',
              config: {
                enabled: cardStripEditor.enabled,
                heading: cardStripEditor.heading,
                text: cardStripEditor.text,
                productIds: cardStripEditor.productIdsText.split(/\r?\n|,/g).map((x: any) => String(x || '').trim()).filter(Boolean),
                perRow: Math.max(1, Math.min(6, Number(cardStripEditor.perRow) || 3)),
                variant: (cardStripEditor.variant === 'compact' ? 'compact' : 'default'),
                showSeller: Boolean(cardStripEditor.showSeller),
              },
            })).next);
            markDirty(sectionId);
            setCardStripEditor(prev => ({ ...prev, open: false, sectionId: null }));
          }}>Speichern</Button>
        </DialogActions>
      </Dialog>

      {/* Paragraph editor dialog */}
      <Dialog open={paragraphEditor.open} onClose={() => setParagraphEditor(prev => ({ ...prev, open: false, sectionId: null }))} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 900 }}>Paragraph bearbeiten</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gap: 2 }}>
            <FormControlLabel control={<Switch checked={paragraphEditor.enabled} onChange={(_, v) => setParagraphEditor(prev => ({ ...prev, enabled: v }))} />} label="Aktiv" />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
              <Tabs
                value={paragraphLang}
                onChange={(_, v) => setParagraphLang(String(v || 'de'))}
                variant="scrollable"
                scrollButtons
                allowScrollButtonsMobile
                sx={{ minHeight: 38, '& .MuiTab-root': { minHeight: 38, textTransform: 'none', fontWeight: 900 } }}
              >
                {(() => {
                  const keys = Array.from(new Set([
                    ...Object.keys((paragraphEditor.text && typeof paragraphEditor.text === 'object') ? (paragraphEditor.text as any) : { de: true }),
                  ].filter(Boolean)));
                  const list = keys.length > 0 ? keys : ['de'];
                  return list.map((k) => (
                    <Tab
                      key={`par-lang-${k}`}
                      value={k}
                      label={(
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Box component="span" sx={{ fontSize: 18, lineHeight: 1 }}>{heroCarouselFlag(k)}</Box>
                          <Box component="span">{String(k).toUpperCase()}</Box>
                        </Box>
                      )}
                    />
                  ));
                })()}
              </Tabs>
              <Button
                variant="outlined"
                size="small"
                onClick={(e) => setParagraphLangAnchor(e.currentTarget)}
                sx={{ textTransform: 'none', fontWeight: 900, borderRadius: 999 }}
              >
                + Sprache
              </Button>
              <Popover
                open={!!paragraphLangAnchor}
                anchorEl={paragraphLangAnchor}
                onClose={() => setParagraphLangAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <Box sx={{ p: 1, minWidth: 220 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>Sprache hinzufügen</Typography>
                  {(['de','en','tr','fr','es','it','pt','ar','hi','zh'] as string[]).map((code) => {
                    const existing = (paragraphEditor.text && typeof paragraphEditor.text === 'object')
                      ? Object.prototype.hasOwnProperty.call(paragraphEditor.text as any, code)
                      : (code === 'de');
                    return (
                      <MenuItem
                        key={`par-addlang-${code}`}
                        disabled={existing}
                        onClick={() => {
                          const baseLang = paragraphLang || 'de';
                          setParagraphEditor((prev) => ({
                            ...prev,
                            text: setLocalizedString((typeof prev.text === 'string') ? ({ [baseLang]: prev.text } as any) : prev.text, code, getLocalizedString(prev.text, code, baseLang, '')),
                          }));
                          setParagraphLang(code);
                          setParagraphLangAnchor(null);
                        }}
                        sx={{ fontWeight: 800 }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box component="span" sx={{ fontSize: 18, lineHeight: 1 }}>{heroCarouselFlag(code)}</Box>
                          <Box component="span">{code.toUpperCase()}</Box>
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Box>
              </Popover>
            </Box>

            <TextField label={`Text (${paragraphLang.toUpperCase()})`} value={getLocalizedString(paragraphEditor.text, paragraphLang, 'de', '')} onChange={(e) => setParagraphEditor(prev => ({ ...prev, text: setLocalizedString(prev.text, paragraphLang, e.target.value) }))} fullWidth multiline minRows={5} />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel id="par-align">Alignment</InputLabel>
                <Select labelId="par-align" label="Alignment" value={paragraphEditor.align} onChange={(e) => setParagraphEditor(prev => ({ ...prev, align: (e.target.value === 'left' ? 'left' : (e.target.value === 'right' ? 'right' : 'center')) }))}>
                  <MenuItem value="left">Left</MenuItem>
                  <MenuItem value="center">Center</MenuItem>
                  <MenuItem value="right">Right</MenuItem>
                </Select>
              </FormControl>
              <TextField label="Font Size (px)" type="number" value={paragraphEditor.fontSize} onChange={(e) => setParagraphEditor(prev => ({ ...prev, fontSize: Math.max(12, Math.min(32, Number(e.target.value) || 18)) }))} fullWidth />
              <TextField label="Text Color" type="color" value={paragraphEditor.textColor || '#111827'} onChange={(e) => setParagraphEditor(prev => ({ ...prev, textColor: e.target.value }))} fullWidth />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel id="par-max">Max Width</InputLabel>
                <Select labelId="par-max" label="Max Width" value={paragraphEditor.maxWidth} onChange={(e) => setParagraphEditor(prev => ({ ...prev, maxWidth: (e.target.value === 'sm' ? 'sm' : (e.target.value === 'md' ? 'md' : 'lg')) }))}>
                  <MenuItem value="sm">Small</MenuItem>
                  <MenuItem value="md">Medium</MenuItem>
                  <MenuItem value="lg">Large</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="par-variant">Style</InputLabel>
                <Select labelId="par-variant" label="Style" value={paragraphEditor.variant} onChange={(e) => setParagraphEditor(prev => ({ ...prev, variant: (e.target.value === 'card' ? 'card' : 'plain') }))}>
                  <MenuItem value="plain">Plain</MenuItem>
                  <MenuItem value="card">Card</MenuItem>
                </Select>
              </FormControl>
              <TextField label="Card BG" type="color" value={paragraphEditor.bgColor || '#ffffff'} onChange={(e) => setParagraphEditor(prev => ({ ...prev, bgColor: e.target.value }))} fullWidth disabled={paragraphEditor.variant !== 'card'} />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setParagraphEditor(prev => ({ ...prev, open: false, sectionId: null }))}>Abbrechen</Button>
          <Button variant="contained" onClick={() => {
            const sectionId = paragraphEditor.sectionId;
            if (!sectionId) { setParagraphEditor(prev => ({ ...prev, open: false, sectionId: null })); return; }
            setBuilderSections((prev) => updateSectionById(prev, String(sectionId), (s: any) => ({
              ...s,
              type: 'paragraph',
              title: 'Paragraph',
              config: {
                enabled: paragraphEditor.enabled,
                text: paragraphEditor.text,
                align: paragraphEditor.align,
                textColor: paragraphEditor.textColor,
                fontSize: paragraphEditor.fontSize,
                maxWidth: paragraphEditor.maxWidth,
                variant: paragraphEditor.variant,
                bgColor: paragraphEditor.bgColor,
              },
            })).next);
            markDirty(sectionId);
            setParagraphEditor(prev => ({ ...prev, open: false, sectionId: null }));
          }}>Speichern</Button>
        </DialogActions>
      </Dialog>

      {/* Startseite/404 display settings */}
      <Dialog open={pageDisplayDialogOpen} onClose={() => setPageDisplayDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 900 }}>Seiten-Anzeige</DialogTitle>
        <DialogContent dividers>
          {(() => {
            const idx = Math.max(0, Math.min(pageBuilderIndex, pagesTabs.length - 1));
            const curUrl = normalizePageUrl(pagesTabs[idx]?.url || '/');
            const isDefault = curUrl === '/' || curUrl === '/404';
            if (!isDefault) return null;
            const options = pagesTabs
              .map((p) => ({ label: p.label, url: normalizePageUrl(p.url) }))
              .filter((p) => p.url !== '/' && p.url !== '/404');
            return (
              <Box sx={{ display: 'grid', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Du kannst diese Standard-Seite entweder normal anzeigen oder den Inhalt einer anderen Seite übernehmen.
                </Typography>
                <FormControl fullWidth>
                  <InputLabel id="page-display-mode-label">Anzeigen</InputLabel>
                  <Select
                    labelId="page-display-mode-label"
                    label="Anzeigen"
                    value={pageDisplayMode}
                    onChange={(e) => {
                      const v = String(e.target.value) as any;
                      setPageDisplayMode(v === 'reuse' ? 'reuse' : 'own');
                      if (v !== 'reuse') setPageDisplayFrom('');
                    }}
                  >
                    <MenuItem value="own">Eigener Inhalt (Bricks)</MenuItem>
                    <MenuItem value="reuse">Inhalt von anderer Seite übernehmen</MenuItem>
                  </Select>
                </FormControl>

                {pageDisplayMode === 'reuse' && (
                  <FormControl fullWidth>
                    <InputLabel id="page-display-from-label">Seite auswählen</InputLabel>
                    <Select
                      labelId="page-display-from-label"
                      label="Seite auswählen"
                      value={pageDisplayFrom}
                      onChange={(e) => setPageDisplayFrom(normalizePageUrl(String(e.target.value || '')))}
                      MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
                    >
                      {options.map((p) => (
                        <MenuItem key={`pdf-${p.url}`} value={p.url}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 2 }}>
                            <Typography sx={{ fontWeight: 800 }}>{p.label}</Typography>
                            <Typography variant="caption" color="text.secondary">{p.url}</Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {pageDisplayMode === 'reuse' && (!pageDisplayFrom || pageDisplayFrom === '/' || pageDisplayFrom === '/404') && (
                  <Typography variant="body2" color="error.main" sx={{ fontWeight: 700 }}>
                    Bitte wähle eine gültige Seite aus.
                  </Typography>
                )}
              </Box>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPageDisplayDialogOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={async () => {
              const idx = Math.max(0, Math.min(pageBuilderIndex, pagesTabs.length - 1));
              const curUrl = normalizePageUrl(pagesTabs[idx]?.url || '/');
              if (curUrl !== '/' && curUrl !== '/404') { setPageDisplayDialogOpen(false); return; }
              const df = (pageDisplayMode === 'reuse') ? normalizePageUrl(pageDisplayFrom || '') : '';
              if (pageDisplayMode === 'reuse' && (!df || df === '/' || df === '/404')) {
                toast.error('Bitte eine gültige Seite auswählen.');
                return;
              }
              const next = pagesTabs.map((p, i) => {
                if (i !== idx) return p;
                const updated: any = { ...p };
                if (df) updated.displayFrom = df; else delete updated.displayFrom;
                return updated;
              });
              setPagesTabs(next);
              const ok = await savePages(next, []);
              if (ok) {
                setPageDisplayDialogOpen(false);
                toast.success('Einstellung gespeichert', { duration: 1200 });
              }
            }}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      {/* Slug / URL Editor Dialog */}
      <Dialog open={slugDialogOpen} onClose={() => setSlugDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 900 }}>Seiten-URL bearbeiten</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gap: 1.25 }}>
            <Typography variant="body2" color="text.secondary">
              Die URL beginnt immer mit "/". Du bearbeitest nur den Teil dahinter.
            </Typography>
            <TextField
              autoFocus
              label="Slug"
              value={slugDraft}
              onChange={(e) => {
                setSlugTouched(true);
                const next = String(e.target.value || '')
                  .toLowerCase()
                  .replace(/\s+/g, '-')
                  .replace(/[^a-z0-9\-\/]+/g, '')
                  .replace(/\/{2,}/g, '/');
                setSlugDraft(next);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography sx={{ fontWeight: 800, color: 'text.secondary' }}>/</Typography>
                  </InputAdornment>
                ),
              }}
              fullWidth
              helperText={(function () {
                const idx = Math.max(0, Math.min(pageBuilderIndex, pagesTabs.length - 1));
                const full = normalizePageUrl('/' + slugDraft);
                const err = getSlugError(full, idx);
                if (err && (slugTouched || slugDraft.length > 0)) return err;
                return `Vorschau: ${full}`;
              })()}
              error={(function () {
                const idx = Math.max(0, Math.min(pageBuilderIndex, pagesTabs.length - 1));
                const full = normalizePageUrl('/' + slugDraft);
                return Boolean(getSlugError(full, idx)) && (slugTouched || slugDraft.length > 0);
              })()}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSlugDialogOpen(false)} disabled={slugSaving}>Abbrechen</Button>
          <Button
            variant="contained"
            disabled={(function () {
              const idx = Math.max(0, Math.min(pageBuilderIndex, pagesTabs.length - 1));
              const full = normalizePageUrl('/' + slugDraft);
              return slugSaving || Boolean(getSlugError(full, idx));
            })()}
            onClick={async () => {
              const idx = Math.max(0, Math.min(pageBuilderIndex, pagesTabs.length - 1));
              const full = normalizePageUrl('/' + slugDraft);
              const err = getSlugError(full, idx);
              if (err) {
                toast.error(err);
                return;
              }
              const next = pagesTabs.map((p, i) => (i === idx ? { ...p, url: full } : p));
              setSlugSaving(true);
              try {
                setPagesTabs(next);
                await savePages(next, []);
                try {
                  if (typeof window !== 'undefined') {
                    window.localStorage.setItem('admin.pageBuilder.pageUrl', full);
                    window.localStorage.setItem('admin.pageBuilder.index', String(idx));
                    window.localStorage.setItem('admin.pages.activeTab', String(idx));
                  }
                } catch {}
                setSlugDialogOpen(false);
                toast.success('URL gespeichert', { duration: 1200 });
              } finally {
                setSlugSaving(false);
              }
            }}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      {/* Local Storage Debug Dialog */}
      <Dialog
        open={lsDialogOpen}
        onClose={() => setLsDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        keepMounted
        aria-labelledby="ls-dialog-title"
        sx={{ zIndex: (theme) => theme.zIndex.modal + 10 }}
        // Ensure this dialog always portals into body to avoid being visually hidden by other modals
        container={typeof window !== 'undefined' ? document.body : undefined}
      >
        <DialogTitle id="ls-dialog-title" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Local Storage
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button size="small" onClick={refreshLsDump} variant="outlined">Aktualisieren</Button>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <TextField value={lsDump} onChange={() => {}} fullWidth multiline minRows={14} InputProps={{ readOnly: true }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLsDialogOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      {/* Page Builder Dialog */}
      <Dialog open={pageBuilderOpen} onClose={() => { try { if (typeof window !== 'undefined') window.localStorage.setItem('admin.pageBuilder.open', '0'); } catch {} setPageBuilderOpen(false); }} fullScreen>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tabs
              value={(function(){
                let v = Math.max(0, Math.min(pageBuilderIndex, pagesTabs.length - 1));
                try {
                  if (typeof window !== 'undefined') {
                    const u = String(window.localStorage.getItem('admin.pageBuilder.pageUrl') || '').trim().toLowerCase();
                    if (u) {
                      const i = pagesTabs.findIndex(p => (String(p?.url || '').trim().toLowerCase()) === u);
                      if (i >= 0) v = i;
                    }
                  }
                } catch {}
                return v;
              })()}
              onChange={(_, i) => {
                try {
                  if (typeof window !== 'undefined') {
                    const u = (pagesTabs[i]?.url || '').trim();
                    if (u) {
                      window.localStorage.setItem('admin.pageBuilder.pageUrl', u);
                      window.localStorage.setItem('admin.pageBuilder.index', String(i));
                      window.localStorage.setItem('admin.pages.activeTab', String(i));
                      window.localStorage.setItem('admin.pageBuilder.open', '1');
                    }
                  }
                } catch {}
                setPagesActiveTab(i);
                setPageBuilderIndex(i);
                setActiveSectionId(null);
                setBuilderSections([]);
              }}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ minHeight: 40, '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, minHeight: 40 } }}
            >
              {pagesTabs.map((tb, i) => (
                <Tab
                  key={`pb-head-tab-${i}`}
                  label={(
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                      <Typography component="span" sx={{ fontWeight: 800, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}>
                        {tb.label || (i === 0 ? 'Startseite' : `Seite ${i + 1}`)}
                      </Typography>
                      {(() => {
                        const u = normalizePageUrl(tb?.url || '/');
                        const deletable = i !== 0 && u !== '/' && u !== '/404';
                        if (!deletable) return null;
                        return (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              void deletePageTab(i);
                            }}
                            title="Seite löschen"
                            sx={{
                              ml: 0.25,
                              width: 22,
                              height: 22,
                              bgcolor: 'rgba(15,23,42,0.06)',
                              '&:hover': { bgcolor: 'rgba(239,68,68,0.10)', color: 'error.main' },
                            }}
                          >
                            <CloseIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        );
                      })()}
                    </Box>
                  )}
                />
              ))}
            </Tabs>
              <IconButton
                size="small"
                title="Neue Seite anlegen"
                onClick={() => {
                  setPagesDialogName('');
                  setPagesDialogUrl('/');
                  setPagesDialogOpen(true);
                }}
                sx={{
                  flex: '0 0 auto',
                  bgcolor: 'rgba(59,130,246,0.10)',
                  border: '1px solid',
                  borderColor: 'rgba(59,130,246,0.22)',
                  '&:hover': { bgcolor: 'rgba(59,130,246,0.16)' },
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {pageBuilderDirty && (
              <IconButton
                size="small"
                title="Änderungen speichern"
                aria-label="Änderungen speichern"
                disabled={pageBuilderSaving}
                onClick={savePageBuilderAll}
                sx={{
                  bgcolor: 'rgba(59,130,246,0.14)',
                  color: 'primary.main',
                  border: '1px solid',
                  borderColor: 'rgba(59,130,246,0.35)',
                  '&:hover': { bgcolor: 'rgba(59,130,246,0.20)' },
                }}
              >
                <SaveRoundedIcon fontSize="small" />
              </IconButton>
            )}
            {(() => {
              const idx = Math.max(0, Math.min(pageBuilderIndex, pagesTabs.length - 1));
              const curUrl = normalizePageUrl((pagesTabs[idx]?.url || '/'));
              const isDefault = curUrl === '/' || curUrl === '/404';
              if (isDefault) {
                return (
                  <IconButton
                    size="small"
                    title="Seiten-Anzeige einstellen"
                    aria-label="Seiten-Anzeige einstellen"
                    onClick={openPageDisplayDialog}
                    sx={{ bgcolor: 'rgba(15,23,42,0.06)', '&:hover': { bgcolor: 'rgba(15,23,42,0.10)' } }}
                  >
                    <SettingsIcon />
                  </IconButton>
                );
              }
              return (
                <IconButton
                  size="small"
                  title="URL/Slug bearbeiten"
                  aria-label="URL/Slug bearbeiten"
                  onClick={() => {
                    const cur = normalizePageUrl((pagesTabs[idx]?.url || '/'));
                    setSlugDraft(cur.startsWith('/') ? cur.slice(1) : cur);
                    setSlugTouched(false);
                    setSlugDialogOpen(true);
                  }}
                >
                  <PublicIcon />
                </IconButton>
              );
            })()}
            <IconButton
              size="small"
              title="Seite in neuem Tab öffnen"
              aria-label="Seite in neuem Tab öffnen"
              onClick={() => {
                try {
                  const idx = Math.max(0, Math.min(pageBuilderIndex, pagesTabs.length - 1));
                  const u = (pagesTabs[idx]?.url || '/').trim() || '/';
                  let path = u;
                  if (u !== '/' && u.toLowerCase() !== '/404') {
                    if (u.toLowerCase().startsWith('/page/')) {
                      path = u;
                    } else {
                      path = `/page${u.startsWith('/') ? u : `/${u}`}`;
                    }
                  }
                  if (typeof window !== 'undefined') window.open(path, '_blank');
                } catch {}
              }}
            >
              <OpenInNewIcon />
            </IconButton>
            <IconButton
              disabled
              onClick={() => { try { toast.dismiss(); toast('Öffne Local Storage…', { id: 'ls-open', duration: 1200 }); } catch {} setLsDialogOpen(true); setTimeout(() => refreshLsDump(), 0); }}
              size="small"
              title="Local Storage anzeigen"
              aria-label="Local Storage anzeigen"
              sx={{
                color: 'text.disabled',
                opacity: 0.45,
                cursor: 'default',
              }}
            >
              <InfoOutlinedIcon />
            </IconButton>
            <IconButton onClick={() => setAddComponentOpen(true)} size="small" title="Komponente hinzufügen">
              <AddIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
            <Box sx={{ width: 260, flex: '0 0 auto', position: 'sticky', top: 0, alignSelf: 'flex-start', maxHeight: 'calc(100vh - 160px)' }}>
              <Card sx={{ borderRadius: 3, boxShadow: '0 12px 32px rgba(2,6,23,0.10)', overflow: 'hidden', maxHeight: 'calc(100vh - 160px)', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ overflowY: 'auto', maxHeight: 'calc(100vh - 160px)', pr: 1.25 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1.25, letterSpacing: 0.2 }}>Palette</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    Klicke eine Komponente um sie zur Seite hinzuzufügen.
                  </Typography>
                  <Box sx={{ display: 'grid', gap: 1 }}>
                    <Button
                      variant="outlined"
                      onClick={addHeroSection}
                      draggable
                      onDragStart={(e) => {
                        try {
                          e.dataTransfer.setData('text/plain', JSON.stringify({ kind: 'palette', paletteType: 'hero' }));
                          e.dataTransfer.effectAllowed = 'copy';
                        } catch {}
                        setDndState({ kind: 'palette', paletteType: 'hero', overIndex: null, overSide: null });
                      }}
                      onDragEnd={() => setDndState({ kind: null, overIndex: null, overSide: null })}
                      sx={{ justifyContent: 'flex-start', textTransform: 'none', fontWeight: 800, borderRadius: 2.5, py: 1.25, cursor: 'grab' }}
                    >
                      <AnimationIcon sx={{ mr: 1 }} /> Hero Section
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={addHeroCarouselSection}
                      draggable
                      onDragStart={(e) => {
                        try {
                          e.dataTransfer.setData('text/plain', JSON.stringify({ kind: 'palette', paletteType: 'herocarousel' }));
                          e.dataTransfer.effectAllowed = 'copy';
                        } catch {}
                        setDndState({ kind: 'palette', paletteType: 'herocarousel', overIndex: null, overSide: null });
                      }}
                      onDragEnd={() => setDndState({ kind: null, overIndex: null, overSide: null })}
                      sx={{ justifyContent: 'flex-start', textTransform: 'none', fontWeight: 800, borderRadius: 2.5, py: 1.25, cursor: 'grab' }}
                    >
                      <ViewCarouselIcon sx={{ mr: 1 }} /> Hero Carousel
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={addProductsSection}
                      draggable
                      onDragStart={(e) => {
                        try {
                          e.dataTransfer.setData('text/plain', JSON.stringify({ kind: 'palette', paletteType: 'products' }));
                          e.dataTransfer.effectAllowed = 'copy';
                        } catch {}
                        setDndState({ kind: 'palette', paletteType: 'products', overIndex: null, overSide: null });
                      }}
                      onDragEnd={() => setDndState({ kind: null, overIndex: null, overSide: null })}
                      sx={{ justifyContent: 'flex-start', textTransform: 'none', fontWeight: 800, borderRadius: 2.5, py: 1.25, cursor: 'grab' }}
                    >
                      <LocalOfferIcon sx={{ mr: 1 }} /> Produkte
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        const created: any = createSectionByType('cardstrip');
                        setBuilderSections((prev) => [...prev, created]);
                        markDirty(String(created?.id));
                        toast.success('Cards hinzugefügt', { duration: 1200 });
                      }}
                      draggable
                      onDragStart={(e) => {
                        try {
                          e.dataTransfer.setData('text/plain', JSON.stringify({ kind: 'palette', paletteType: 'cardstrip' }));
                          e.dataTransfer.effectAllowed = 'copy';
                        } catch {}
                        setDndState({ kind: 'palette', paletteType: 'cardstrip', overIndex: null, overSide: null });
                      }}
                      onDragEnd={() => setDndState({ kind: null, overIndex: null, overSide: null })}
                      sx={{ justifyContent: 'flex-start', textTransform: 'none', fontWeight: 800, borderRadius: 2.5, py: 1.25, cursor: 'grab' }}
                    >
                      <LocalOfferIcon sx={{ mr: 1 }} /> Cards
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={addCtaSection}
                      draggable
                      onDragStart={(e) => {
                        try {
                          e.dataTransfer.setData('text/plain', JSON.stringify({ kind: 'palette', paletteType: 'cta' }));
                          e.dataTransfer.effectAllowed = 'copy';
                        } catch {}
                        setDndState({ kind: 'palette', paletteType: 'cta', overIndex: null, overSide: null });
                      }}
                      onDragEnd={() => setDndState({ kind: null, overIndex: null, overSide: null })}
                      sx={{ justifyContent: 'flex-start', textTransform: 'none', fontWeight: 800, borderRadius: 2.5, py: 1.25, cursor: 'grab' }}
                    >
                      <AddIcon sx={{ mr: 1 }} /> CTA
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={addReviewsSection}
                      draggable
                      onDragStart={(e) => {
                        try {
                          e.dataTransfer.setData('text/plain', JSON.stringify({ kind: 'palette', paletteType: 'reviews' }));
                          e.dataTransfer.effectAllowed = 'copy';
                        } catch {}
                        setDndState({ kind: 'palette', paletteType: 'reviews', overIndex: null, overSide: null });
                      }}
                      onDragEnd={() => setDndState({ kind: null, overIndex: null, overSide: null })}
                      sx={{ justifyContent: 'flex-start', textTransform: 'none', fontWeight: 800, borderRadius: 2.5, py: 1.25, cursor: 'grab' }}
                    >
                      <AddIcon sx={{ mr: 1 }} /> Reviews
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={addCounterSection}
                      draggable
                      onDragStart={(e) => {
                        try {
                          e.dataTransfer.setData('text/plain', JSON.stringify({ kind: 'palette', paletteType: 'counter' }));
                          e.dataTransfer.effectAllowed = 'copy';
                        } catch {}
                        setDndState({ kind: 'palette', paletteType: 'counter', overIndex: null, overSide: null });
                      }}
                      onDragEnd={() => setDndState({ kind: null, overIndex: null, overSide: null })}
                      sx={{ justifyContent: 'flex-start', textTransform: 'none', fontWeight: 800, borderRadius: 2.5, py: 1.25, cursor: 'grab' }}
                    >
                      <AddIcon sx={{ mr: 1 }} /> Counter
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={addParagraphSection}
                      draggable
                      onDragStart={(e) => {
                        try {
                          e.dataTransfer.setData('text/plain', JSON.stringify({ kind: 'palette', paletteType: 'paragraph' }));
                          e.dataTransfer.effectAllowed = 'copy';
                        } catch {}
                        setDndState({ kind: 'palette', paletteType: 'paragraph', overIndex: null, overSide: null });
                      }}
                      onDragEnd={() => setDndState({ kind: null, overIndex: null, overSide: null })}
                      sx={{ justifyContent: 'flex-start', textTransform: 'none', fontWeight: 800, borderRadius: 2.5, py: 1.25, cursor: 'grab' }}
                    >
                      <AddIcon sx={{ mr: 1 }} /> Text
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={addMediaTextSection}
                      draggable
                      onDragStart={(e) => {
                        try {
                          e.dataTransfer.setData('text/plain', JSON.stringify({ kind: 'palette', paletteType: 'mediatext' }));
                          e.dataTransfer.effectAllowed = 'copy';
                        } catch {}
                        setDndState({ kind: 'palette', paletteType: 'mediatext', overIndex: null, overSide: null });
                      }}
                      onDragEnd={() => setDndState({ kind: null, overIndex: null, overSide: null })}
                      sx={{ justifyContent: 'flex-start', textTransform: 'none', fontWeight: 800, borderRadius: 2.5, py: 1.25, cursor: 'grab' }}
                    >
                      <AddPhotoAlternateIcon sx={{ mr: 1 }} /> Media + Text
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={addScrollListSection}
                      draggable
                      onDragStart={(e) => {
                        try {
                          e.dataTransfer.setData('text/plain', JSON.stringify({ kind: 'palette', paletteType: 'scrolllist' }));
                          e.dataTransfer.effectAllowed = 'copy';
                        } catch {}
                        setDndState({ kind: 'palette', paletteType: 'scrolllist', overIndex: null, overSide: null });
                      }}
                      onDragEnd={() => setDndState({ kind: null, overIndex: null, overSide: null })}
                      sx={{ justifyContent: 'flex-start', textTransform: 'none', fontWeight: 800, borderRadius: 2.5, py: 1.25, cursor: 'grab' }}
                    >
                      <ViewCarouselIcon sx={{ mr: 1 }} /> Scroll Liste
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={addGallerySection}
                      draggable
                      onDragStart={(e) => {
                        try {
                          e.dataTransfer.setData('text/plain', JSON.stringify({ kind: 'palette', paletteType: 'gallery' }));
                          e.dataTransfer.effectAllowed = 'copy';
                        } catch {}
                        setDndState({ kind: 'palette', paletteType: 'gallery', overIndex: null, overSide: null });
                      }}
                      onDragEnd={() => setDndState({ kind: null, overIndex: null, overSide: null })}
                      sx={{ justifyContent: 'flex-start', textTransform: 'none', fontWeight: 800, borderRadius: 2.5, py: 1.25, cursor: 'grab' }}
                    >
                      <AddPhotoAlternateIcon sx={{ mr: 1 }} /> Gallery
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={addGridSection}
                      draggable
                      onDragStart={(e) => {
                        try {
                          e.dataTransfer.setData('text/plain', JSON.stringify({ kind: 'palette', paletteType: 'grid' }));
                          e.dataTransfer.effectAllowed = 'copy';
                        } catch {}
                        setDndState({ kind: 'palette', paletteType: 'grid', overIndex: null, overSide: null });
                      }}
                      onDragEnd={() => setDndState({ kind: null, overIndex: null, overSide: null })}
                      sx={{ justifyContent: 'flex-start', textTransform: 'none', fontWeight: 800, borderRadius: 2.5, py: 1.25, cursor: 'grab' }}
                    >
                      <TableChartIcon sx={{ mr: 1 }} /> Grid
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={addSideNavSection}
                      draggable
                      onDragStart={(e) => {
                        try {
                          e.dataTransfer.setData('text/plain', JSON.stringify({ kind: 'palette', paletteType: 'sidenav' }));
                          e.dataTransfer.effectAllowed = 'copy';
                        } catch {}
                        setDndState({ kind: 'palette', paletteType: 'sidenav', overIndex: null, overSide: null });
                      }}
                      onDragEnd={() => setDndState({ kind: null, overIndex: null, overSide: null })}
                      sx={{ justifyContent: 'flex-start', textTransform: 'none', fontWeight: 800, borderRadius: 2.5, py: 1.25, cursor: 'grab' }}
                    >
                      <MenuRoundedIcon sx={{ mr: 1 }} /> Side navigation
                    </Button>
                  </Box>
                  <Divider sx={{ my: 2 }} />
                  <Button variant="contained" onClick={() => setAddComponentOpen(true)} sx={{ textTransform: 'none', fontWeight: 800, borderRadius: 2.5 }}>
                    <AddIcon sx={{ mr: 1 }} /> Mehr…
                  </Button>
                </CardContent>
              </Card>
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              {builderSections.length === 0 && (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 2 }}>
                    Baue die Seite mit Komponenten.
                  </Typography>
                  <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={addHeroSection}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 800,
                        borderRadius: 999,
                        px: 3.5,
                        py: 1.5,
                        boxShadow: '0 14px 36px rgba(59,130,246,0.25)',
                        background: 'linear-gradient(90deg, #60a5fa, #3b82f6)',
                        '&:hover': { background: 'linear-gradient(90deg, #3b82f6, #2563eb)', boxShadow: '0 18px 40px rgba(37,99,235,0.28)' },
                      }}
                    >
                      Hero Section hinzufügen
                    </Button>
                  </Box>
                </>
              )}

              {builderSections.length > 0 && (
                <React.Fragment>
                  {renderDropZone(0)}

                  {builderSections.map((section, sectionIndex) => {
                    const isDraggingSelf = dndState.kind === 'section' && String(dndState.sectionId || '') === String(section?.id || '');
                    const dragStart = (e: any) => {
                      try {
                        e.dataTransfer.setData('text/plain', JSON.stringify({ kind: 'section', sectionId: String(section.id) }));
                        e.dataTransfer.effectAllowed = 'move';
                      } catch {}
                      setDndState({ kind: 'section', sectionId: String(section.id), overIndex: null, overSide: null });
                    };
                    const dragEnd = () => setDndState({ kind: null, overIndex: null, overSide: null });

                    if (section?.type === 'row') {
                      const colsRaw = Array.isArray((section as any).columns) ? (section as any).columns : [];
                      const cols = [colsRaw[0] || null, colsRaw[1] || null];
                      const isDirty = dirtySectionIds.has(String(section.id));
                      return (
                        <React.Fragment key={`pb-sec-${section.id}`}>
                          <Box
                            draggable
                            onDragStart={dragStart}
                            onDragEnd={dragEnd}
                            sx={{ mb: 3, borderRadius: 3, boxShadow: isDraggingSelf ? '0 18px 44px rgba(59,130,246,0.18)' : '0 12px 32px rgba(2,6,23,0.10)', overflow: 'hidden', position: 'relative', bgcolor: 'background.paper', cursor: 'grab', opacity: isDraggingSelf ? 0.7 : 1 }}
                          >
                            {renderSideDropZone(String(section.id), 'left')}
                            {renderSideDropZone(String(section.id), 'right')}

                            <Box sx={{ p: 2.25, pb: 2.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Chip size="small" label="Row" />
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>2 Spalten</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <IconButton size="small" color="error" title="Row entfernen" onClick={() => {
                                    setBuilderSections((prev) => prev.filter((s: any) => s.id !== section.id));
                                    markDirty();
                                  }}>
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </Box>

                              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.25 }}>
                                {[0, 1].map((colIdx) => {
                                  const child = cols[colIdx];
                                  const childId = child?.id ? String(child.id) : `row-${section.id}-${colIdx}`;
                                  return (
                                    <Box key={`row-col-${section.id}-${colIdx}`} sx={{ position: 'relative', borderRadius: 2.5, border: '1px dashed', borderColor: child ? 'transparent' : 'divider', bgcolor: child ? 'transparent' : 'rgba(2,6,23,0.02)', minHeight: 120, overflow: 'hidden' }}>
                                      {renderSideDropZone(String(section.id), colIdx === 0 ? 'left' : 'right')}
                                      {child ? (
                                        <Box sx={{ position: 'relative' }}>
                                          {/* Allow dropping another brick beside this brick */}
                                          {renderSideDropZone(String(childId), 'left')}
                                          {renderSideDropZone(String(childId), 'right')}
                                          <Box sx={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: 0.75, zIndex: 6 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, bgcolor: 'rgba(255,255,255,0.75)', backdropFilter: 'blur(8px)', border: '1px solid', borderColor: 'divider', borderRadius: 999, px: 1, py: 0.5, boxShadow: 1 }}>
                                              <Chip size="small" label={String(child?.title || child?.type || 'Sektion')} />
                                              <Chip size="small" color={(child as any)?.config?.enabled === false ? 'default' : 'success'} label={(child as any)?.config?.enabled === false ? 'Disabled' : 'Enabled'} />
                                              <IconButton
                                                size="small"
                                                title="Bearbeiten"
                                                onClick={() => openEditorForSection(child)}
                                                sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}
                                              >
                                                <EditIcon fontSize="small" />
                                              </IconButton>
                                              <IconButton
                                                size="small"
                                                color="error"
                                                title="Entfernen"
                                                onClick={() => {
                                                  setBuilderSections((prev) => removeSectionById(prev, String(child.id)).next);
                                                  markDirty();
                                                  toast.success('Komponente entfernt', { duration: 1200 });
                                                }}
                                                sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}
                                              >
                                                <DeleteOutlineIcon fontSize="small" />
                                              </IconButton>
                                            </Box>
                                          </Box>
                                          <Box draggable onDragStart={(e: any) => {
                                            try {
                                              e.dataTransfer.setData('text/plain', JSON.stringify({ kind: 'section', sectionId: String(child.id) }));
                                              e.dataTransfer.effectAllowed = 'move';
                                            } catch {}
                                            setDndState({ kind: 'section', sectionId: String(child.id), overIndex: null, overSide: null });
                                          }} onDragEnd={() => setDndState({ kind: null, overIndex: null, overSide: null })} sx={{ cursor: 'grab', borderRadius: 2.5, overflow: 'hidden', boxShadow: '0 10px 26px rgba(2,6,23,0.08)' }}>
                                            {(() => {
                                              const tp = String(child?.type || '').toLowerCase();
                                              if (tp === 'hero') return <PageHeroSection config={(child as any).config} resolvePublicUrl={(p) => toPublicUrl(p)} />;
                                              if (tp === 'cta') return <PageCtaSection config={(child as any).config} resolvePublicUrl={(p) => toPublicUrl(p)} />;
                                              if (tp === 'reviews') return <PageReviewsSection config={(child as any).config} resolvePublicUrl={(p) => toPublicUrl(p)} />;
                                              if (tp === 'counter') return <PageCounterSection config={(child as any).config} />;
                                              if (tp === 'paragraph') return <PageParagraphSection config={(child as any).config} />;
                                              if (tp === 'mediatext') return <PageMediaTextSection config={(child as any).config} resolvePublicUrl={(p) => toPublicUrl(p)} />;
                                              if (tp === 'scrolllist') return <PageScrollListSection config={(child as any).config} resolvePublicUrl={(p) => toPublicUrl(p)} />;
                                              if (tp === 'herocarousel') return <PageHeroCarouselSection config={(child as any).config} resolvePublicUrl={(p) => toPublicUrl(p)} />;
                                              if (tp === 'products') return <PageProductsStripSection config={(child as any).config} />;
                                              if (tp === 'cardstrip') return <PageCardStripSection config={(child as any).config} />;
                                              if (tp === 'gallery') return <PageGallerySection config={(child as any).config} resolvePublicUrl={(p) => toPublicUrl(p)} />;
                                              return (
                                                <Box sx={{ p: 2 }}>
                                                  <Typography sx={{ fontWeight: 900 }}>{String(child?.title || child?.type || 'Sektion')}</Typography>
                                                </Box>
                                              );
                                            })()}
                                          </Box>
                                        </Box>
                                      ) : (
                                        <Box sx={{ p: 2.25, display: 'grid', placeItems: 'center', height: '100%' }}>
                                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 800 }}>Hier ablegen</Typography>
                                        </Box>
                                      )}
                                    </Box>
                                  );
                                })}
                              </Box>
                            </Box>

                            {isDirty && (
                              <Box sx={{ position: 'absolute', right: 14, bottom: 14, zIndex: 6 }}>
                                <IconButton
                                  onClick={() => savePageBuilderSection(String(section.id))}
                                  disabled={pageBuilderSaving}
                                  title="Nur diese Komponente speichern"
                                  sx={{
                                    bgcolor: 'rgba(59,130,246,0.95)',
                                    color: 'white',
                                    boxShadow: '0 12px 30px rgba(37,99,235,0.35)',
                                    '&:hover': { bgcolor: 'rgba(37,99,235,0.98)' },
                                  }}
                                >
                                  <SaveRoundedIcon />
                                </IconButton>
                              </Box>
                            )}
                          </Box>
                          {renderDropZone(sectionIndex + 1)}
                        </React.Fragment>
                      );
                    }

                    if (section?.type === 'hero') {
                    const cfg: any = (section && typeof section.config === 'object') ? section.config : {};
                    const isDirty = dirtySectionIds.has(String(section.id));
                    return (
                      <React.Fragment key={`pb-sec-${section.id}`}>
                        <Box draggable onDragStart={dragStart} onDragEnd={dragEnd} sx={{ mb: 3, borderRadius: 3, boxShadow: isDraggingSelf ? '0 18px 44px rgba(59,130,246,0.18)' : '0 12px 32px rgba(2,6,23,0.10)', overflow: 'hidden', position: 'relative', cursor: 'grab', opacity: isDraggingSelf ? 0.7 : 1 }}>
                          {renderSideDropZone(String(section.id), 'left')}
                          {renderSideDropZone(String(section.id), 'right')}
                          <PageHeroSection config={cfg} resolvePublicUrl={(p) => toPublicUrl(p)} />

                        <Box sx={{ position: 'absolute', top: 10, left: 10, display: 'flex', gap: 0.5, zIndex: 6 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, bgcolor: 'rgba(255,255,255,0.78)', backdropFilter: 'blur(8px)', border: '1px solid', borderColor: 'divider', borderRadius: 999, px: 0.5, py: 0.25, boxShadow: 1 }}>
                            <IconButton size="small" disabled={sectionIndex === 0} onClick={() => moveBuilderSection(String(section.id), -1)} title="Nach oben" sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}>
                              <ArrowUpwardIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" disabled={sectionIndex === builderSections.length - 1} onClick={() => moveBuilderSection(String(section.id), 1)} title="Nach unten" sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}>
                              <ArrowDownwardIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>

                        <Box sx={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: 0.75, zIndex: 5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, bgcolor: 'rgba(255,255,255,0.75)', backdropFilter: 'blur(8px)', border: '1px solid', borderColor: 'divider', borderRadius: 999, px: 1, py: 0.5, boxShadow: 1 }}>
                            <Chip size="small" label="Hero" />
                            <Chip size="small" color={cfg?.enabled === false ? 'default' : 'success'} label={cfg?.enabled === false ? 'Disabled' : 'Enabled'} />
                            <IconButton
                              size="small"
                              title="Hero bearbeiten"
                              onClick={() => {
                                const titleVal: LocalizedString = (typeof cfg?.title === 'string')
                                  ? ({ de: String(cfg?.title || section.title || 'Hero') } as any)
                                  : ((cfg?.title && typeof cfg?.title === 'object') ? cfg.title : ({ de: String(section.title || 'Hero') } as any));
                                const subtitleVal: LocalizedString = (typeof cfg?.subtitle === 'string')
                                  ? ({ de: String(cfg?.subtitle || '') } as any)
                                  : ((cfg?.subtitle && typeof cfg?.subtitle === 'object') ? cfg.subtitle : ({ de: '' } as any));
                                const ctaTextVal: LocalizedString = (typeof cfg?.ctaText === 'string')
                                  ? ({ de: String(cfg?.ctaText || '') } as any)
                                  : ((cfg?.ctaText && typeof cfg?.ctaText === 'object') ? cfg.ctaText : ({ de: '' } as any));
                                try {
                                  const keys = (titleVal && typeof titleVal === 'object') ? Object.keys(titleVal as any).filter(Boolean) : [];
                                  const nextLang = keys.includes('de') ? 'de' : (keys[0] || 'de');
                                  setHeroLang(nextLang);
                                } catch { setHeroLang('de'); }
                                setHeroEditor({
                                  open: true,
                                  sectionId: section.id,
                                  enabled: cfg?.enabled !== false,
                                  title: titleVal,
                                  subtitle: subtitleVal,
                                  ctaText: ctaTextVal,
                                  ctaUrl: String(cfg?.ctaUrl || ''),
                                  backgroundImage: String(cfg?.backgroundImage || ''),
                                  align: (cfg?.align === 'left' ? 'left' : 'center'),
                                  minHeight: Math.max(360, Math.min(900, Number(cfg?.minHeight) || 520)),
                                });
                              }}
                              sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              title="Hero entfernen"
                              onClick={() => {
                                setBuilderSections((prev) => {
                                  const next = prev.filter((s: any) => s.id !== section.id);
                                  return next;
                                });
                                markDirty();
                                toast.success('Hero entfernt', { duration: 1200 });
                              }}
                              sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>

                        {isDirty && (
                          <Box sx={{ position: 'absolute', right: 14, bottom: 14, zIndex: 6 }}>
                            <IconButton
                              onClick={() => savePageBuilderSection(String(section.id))}
                              disabled={pageBuilderSaving}
                              title="Nur diese Komponente speichern"
                              sx={{
                                bgcolor: 'rgba(59,130,246,0.95)',
                                color: 'white',
                                boxShadow: '0 12px 30px rgba(37,99,235,0.35)',
                                '&:hover': { bgcolor: 'rgba(37,99,235,0.98)' },
                              }}
                            >
                              <SaveRoundedIcon />
                            </IconButton>
                          </Box>
                        )}
                        </Box>
                        {renderDropZone(sectionIndex + 1)}
                      </React.Fragment>
                    );
                  }

                  if (section?.type === 'sidenav') {
                    const cfg: any = (section && typeof section.config === 'object') ? section.config : {};
                    const isDirty = dirtySectionIds.has(String(section.id));
                    const tabs = Array.isArray(cfg?.tabs) ? cfg.tabs : [];
                    const enabled = cfg?.enabled !== false;
                    return (
                      <React.Fragment key={`pb-sec-${section.id}`}>
                        <Box
                          draggable
                          onDragStart={dragStart}
                          onDragEnd={dragEnd}
                          sx={{
                            position: 'relative',
                            borderRadius: 3,
                            border: '1px solid',
                            borderColor: isDirty ? 'primary.main' : 'divider',
                            boxShadow: isDirty ? '0 14px 34px rgba(59,130,246,0.18)' : '0 10px 26px rgba(2,6,23,0.08)',
                            overflow: 'hidden',
                            opacity: isDraggingSelf ? 0.55 : 1,
                            transform: isDraggingSelf ? 'scale(0.99)' : 'scale(1)',
                            transition: 'all .15s',
                            background: 'linear-gradient(180deg, rgba(255,255,255,0.95), rgba(255,255,255,0.85))',
                          }}
                        >
                          {renderSideDropZone(String(section.id), 'left')}
                          {renderSideDropZone(String(section.id), 'right')}
                          <Box sx={{ p: 2.25 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                <Box sx={{ width: 40, height: 40, borderRadius: 999, display: 'grid', placeItems: 'center', color: 'white', background: enabled ? 'linear-gradient(135deg, rgba(59,130,246,0.95), rgba(168,85,247,0.92))' : 'rgba(148,163,184,0.9)' }}>
                                  <MenuRoundedIcon />
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography sx={{ fontWeight: 900, fontSize: 14, lineHeight: 1.15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    Side navigation
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    Fixiert rechts am Bildschirmrand • Tabs: {tabs.length}
                                  </Typography>
                                </Box>
                              </Box>

                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Chip
                                  size="small"
                                  label={enabled ? 'Aktiv' : 'Aus'}
                                  color={enabled ? 'success' : 'default'}
                                  sx={{ fontWeight: 900 }}
                                />
                                <IconButton
                                  size="small"
                                  title="Bearbeiten"
                                  onClick={() => openEditorForSection(section)}
                                  sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  title="Entfernen"
                                  color="error"
                                  onClick={() => {
                                    setBuilderSections((prev) => removeSectionById(prev, String(section.id)).next);
                                    markDirty();
                                    toast.success('Komponente entfernt', { duration: 1200 });
                                  }}
                                  sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                        {renderDropZone(sectionIndex + 1)}
                      </React.Fragment>
                    );
                  }

                  if (section?.type === 'products') {
                    const cfg: any = (section && typeof section.config === 'object') ? section.config : {};
                    const isDirty = dirtySectionIds.has(String(section.id));
                    return (
                      <React.Fragment key={`pb-sec-${section.id}`}>
                        <Box draggable onDragStart={dragStart} onDragEnd={dragEnd} sx={{ mb: 3, borderRadius: 3, boxShadow: isDraggingSelf ? '0 18px 44px rgba(59,130,246,0.18)' : '0 12px 32px rgba(2,6,23,0.10)', overflow: 'hidden', position: 'relative', bgcolor: 'background.paper', cursor: 'grab', opacity: isDraggingSelf ? 0.7 : 1 }}>
                          {renderSideDropZone(String(section.id), 'left')}
                          {renderSideDropZone(String(section.id), 'right')}
                          <PageProductsStripSection config={cfg} />

                        <Box sx={{ position: 'absolute', top: 10, left: 10, display: 'flex', gap: 0.5, zIndex: 6 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, bgcolor: 'rgba(255,255,255,0.78)', backdropFilter: 'blur(8px)', border: '1px solid', borderColor: 'divider', borderRadius: 999, px: 0.5, py: 0.25, boxShadow: 1 }}>
                            <IconButton size="small" disabled={sectionIndex === 0} onClick={() => moveBuilderSection(String(section.id), -1)} title="Nach oben" sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}>
                              <ArrowUpwardIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" disabled={sectionIndex === builderSections.length - 1} onClick={() => moveBuilderSection(String(section.id), 1)} title="Nach unten" sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}>
                              <ArrowDownwardIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>

                        <Box sx={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: 0.75, zIndex: 5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, bgcolor: 'rgba(255,255,255,0.75)', backdropFilter: 'blur(8px)', border: '1px solid', borderColor: 'divider', borderRadius: 999, px: 1, py: 0.5, boxShadow: 1 }}>
                            <Chip size="small" label="Produkte" />
                            <Chip size="small" color={cfg?.enabled === false ? 'default' : 'success'} label={cfg?.enabled === false ? 'Disabled' : 'Enabled'} />
                            <IconButton
                              size="small"
                              title="Produkte bearbeiten"
                              onClick={() => {
                                setProductsEditor({
                                  open: true,
                                  sectionId: section.id,
                                  enabled: cfg?.enabled !== false,
                                  heading: String(cfg?.heading || 'Deals des Tages'),
                                  subtitle: String(cfg?.subtitle || ''),
                                  badgeText: String(cfg?.badgeText || ''),
                                  radius: Math.max(0, Math.min(32, Number(cfg?.radius) || 0)),
                                  limit: Math.max(2, Math.min(30, Number(cfg?.limit) || 12)),
                                  sortBy: (cfg?.sortBy === 'favorite_count' || cfg?.sortBy === 'view_count' || cfg?.sortBy === 'price') ? cfg.sortBy : 'created_at',
                                  sortOrder: (cfg?.sortOrder === 'asc' ? 'asc' : 'desc'),
                                  productIdsText: Array.isArray(cfg?.productIds) ? cfg.productIds.join(', ') : '',
                                  tagsText: Array.isArray(cfg?.tags) ? cfg.tags.join(', ') : '',
                                  keywordsText: Array.isArray(cfg?.nameKeywords) ? cfg.nameKeywords.join(', ') : '',
                                });
                              }}
                              sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              title="Produkte entfernen"
                              onClick={() => {
                                setBuilderSections((prev) => {
                                  const next = prev.filter((s: any) => s.id !== section.id);
                                  return next;
                                });
                                markDirty();
                                toast.success('Produkte entfernt', { duration: 1200 });
                              }}
                              sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>

                        {isDirty && (
                          <Box sx={{ position: 'absolute', right: 14, bottom: 14, zIndex: 6 }}>
                            <IconButton
                              onClick={() => savePageBuilderSection(String(section.id))}
                              disabled={pageBuilderSaving}
                              title="Nur diese Komponente speichern"
                              sx={{
                                bgcolor: 'rgba(59,130,246,0.95)',
                                color: 'white',
                                boxShadow: '0 12px 30px rgba(37,99,235,0.35)',
                                '&:hover': { bgcolor: 'rgba(37,99,235,0.98)' },
                              }}
                            >
                              <SaveRoundedIcon />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                      {renderDropZone(sectionIndex + 1)}
                    </React.Fragment>
                    );
                  }

                  if (section?.type === 'cardstrip') {
                    const cfg: any = (section && typeof section.config === 'object') ? section.config : {};
                    const isDirty = dirtySectionIds.has(String(section.id));
                    return (
                      <React.Fragment key={`pb-sec-${section.id}`}>
                        <Box draggable onDragStart={dragStart} onDragEnd={dragEnd} sx={{ mb: 3, borderRadius: 3, boxShadow: isDraggingSelf ? '0 18px 44px rgba(59,130,246,0.18)' : '0 12px 32px rgba(2,6,23,0.10)', overflow: 'hidden', position: 'relative', bgcolor: 'background.paper', cursor: 'grab', opacity: isDraggingSelf ? 0.7 : 1 }}>
                          {renderSideDropZone(String(section.id), 'left')}
                          {renderSideDropZone(String(section.id), 'right')}
                          <PageCardStripSection config={cfg} />

                          <Box sx={{ position: 'absolute', top: 10, left: 10, display: 'flex', gap: 0.5, zIndex: 6 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, bgcolor: 'rgba(255,255,255,0.78)', backdropFilter: 'blur(8px)', border: '1px solid', borderColor: 'divider', borderRadius: 999, px: 0.5, py: 0.25, boxShadow: 1 }}>
                              <IconButton size="small" disabled={sectionIndex === 0} onClick={() => moveBuilderSection(String(section.id), -1)} title="Nach oben" sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}>
                                <ArrowUpwardIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" disabled={sectionIndex === builderSections.length - 1} onClick={() => moveBuilderSection(String(section.id), 1)} title="Nach unten" sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}>
                                <ArrowDownwardIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>

                          <Box sx={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: 0.75, zIndex: 5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, bgcolor: 'rgba(255,255,255,0.75)', backdropFilter: 'blur(8px)', border: '1px solid', borderColor: 'divider', borderRadius: 999, px: 1, py: 0.5, boxShadow: 1 }}>
                              <Chip size="small" label="Cards" />
                              <Chip size="small" color={cfg?.enabled === false ? 'default' : 'success'} label={cfg?.enabled === false ? 'Disabled' : 'Enabled'} />
                              <IconButton size="small" title="Cards bearbeiten" onClick={() => openEditorForSection(section)} sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" color="error" title="Cards entfernen" onClick={() => {
                                setBuilderSections((prev) => {
                                  const next = prev.filter((s: any) => s.id !== section.id);
                                  return next;
                                });
                                markDirty();
                                toast.success('Cards entfernt', { duration: 1200 });
                              }} sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}>
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>

                          {isDirty && (
                            <Box sx={{ position: 'absolute', right: 14, bottom: 14, zIndex: 6 }}>
                              <IconButton
                                onClick={() => savePageBuilderSection(String(section.id))}
                                disabled={pageBuilderSaving}
                                title="Nur diese Komponente speichern"
                                sx={{
                                  bgcolor: 'rgba(59,130,246,0.95)',
                                  color: 'white',
                                  boxShadow: '0 12px 30px rgba(37,99,235,0.35)',
                                  '&:hover': { bgcolor: 'rgba(37,99,235,0.98)' },
                                }}
                              >
                                <SaveRoundedIcon />
                              </IconButton>
                            </Box>
                          )}
                        </Box>
                        {renderDropZone(sectionIndex + 1)}
                      </React.Fragment>
                    );
                  }

                  if (section?.type === 'herocarousel') {
                    const cfg: any = (section && typeof section.config === 'object') ? section.config : {};
                    const isDirty = dirtySectionIds.has(String(section.id));
                    return (
                      <React.Fragment key={`pb-sec-${section.id}`}>
                        <Box draggable onDragStart={dragStart} onDragEnd={dragEnd} sx={{ mb: 3, borderRadius: 3, boxShadow: isDraggingSelf ? '0 18px 44px rgba(59,130,246,0.18)' : '0 12px 32px rgba(2,6,23,0.10)', overflow: 'hidden', position: 'relative', cursor: 'grab', opacity: isDraggingSelf ? 0.7 : 1 }}>
                          {renderSideDropZone(String(section.id), 'left')}
                          {renderSideDropZone(String(section.id), 'right')}
                          <PageHeroCarouselSection config={cfg} resolvePublicUrl={(p) => toPublicUrl(p)} />

                        <Box sx={{ position: 'absolute', top: 10, left: 10, display: 'flex', gap: 0.5, zIndex: 6 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, bgcolor: 'rgba(255,255,255,0.78)', backdropFilter: 'blur(8px)', border: '1px solid', borderColor: 'divider', borderRadius: 999, px: 0.5, py: 0.25, boxShadow: 1 }}>
                            <IconButton size="small" disabled={sectionIndex === 0} onClick={() => moveBuilderSection(String(section.id), -1)} title="Nach oben" sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}>
                              <ArrowUpwardIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" disabled={sectionIndex === builderSections.length - 1} onClick={() => moveBuilderSection(String(section.id), 1)} title="Nach unten" sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}>
                              <ArrowDownwardIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>

                        <Box sx={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: 0.75, zIndex: 5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, bgcolor: 'rgba(255,255,255,0.75)', backdropFilter: 'blur(8px)', border: '1px solid', borderColor: 'divider', borderRadius: 999, px: 1, py: 0.5, boxShadow: 1 }}>
                            <Chip size="small" label="Hero Carousel" />
                            <Chip size="small" color={cfg?.enabled === false ? 'default' : 'success'} label={cfg?.enabled === false ? 'Disabled' : 'Enabled'} />
                            <IconButton
                              size="small"
                              title="Hero Carousel bearbeiten"
                              onClick={() => {
                                const imgs = Array.isArray(cfg?.images) ? cfg.images : [];
                                const titleVal: LocalizedString = (typeof cfg?.title === 'string')
                                  ? ({ de: String(cfg?.title || 'Echt luxuriöse Uhren') } as any)
                                  : ((cfg?.title && typeof cfg?.title === 'object') ? cfg.title : ({ de: 'Echt luxuriöse Uhren' } as any));
                                const subtitleVal: LocalizedString = (typeof cfg?.subtitle === 'string')
                                  ? ({ de: String(cfg?.subtitle || '') } as any)
                                  : ((cfg?.subtitle && typeof cfg?.subtitle === 'object') ? cfg.subtitle : ({ de: '' } as any));
                                const ctaTextVal: LocalizedString = (typeof cfg?.ctaText === 'string')
                                  ? ({ de: String(cfg?.ctaText || '') } as any)
                                  : ((cfg?.ctaText && typeof cfg?.ctaText === 'object') ? cfg.ctaText : ({ de: '' } as any));
                                try {
                                  const keys = (titleVal && typeof titleVal === 'object') ? Object.keys(titleVal as any).filter(Boolean) : [];
                                  const nextLang = keys.includes('de') ? 'de' : (keys[0] || 'de');
                                  setHeroCarouselLang(nextLang);
                                } catch { setHeroCarouselLang('de'); }
                                setHeroCarouselEditor({
                                  open: true,
                                  sectionId: section.id,
                                  enabled: cfg?.enabled !== false,
                                  title: titleVal,
                                  subtitle: subtitleVal,
                                  ctaText: ctaTextVal,
                                  ctaUrl: String(cfg?.ctaUrl || ''),
                                  align: (cfg?.align === 'right' ? 'right' : 'left'),
                                  minHeight: Math.max(360, Math.min(900, Number(cfg?.minHeight) || 520)),
                                  autoPlay: cfg?.autoPlay !== false,
                                  autoPlayInterval: Math.max(1500, Math.min(20000, Number(cfg?.autoPlayInterval) || 4500)),
                                  showArrows: cfg?.showArrows !== false,
                                  showDots: cfg?.showDots !== false,
                                  images: imgs.filter((s: any) => typeof s === 'string' && s),
                                });
                              }}
                              sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              title="Hero Carousel entfernen"
                              onClick={() => {
                                setBuilderSections((prev) => {
                                  const next = prev.filter((s: any) => s.id !== section.id);
                                  return next;
                                });
                                markDirty();
                                toast.success('Hero Carousel entfernt', { duration: 1200 });
                              }}
                              sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>

                        {isDirty && (
                          <Box sx={{ position: 'absolute', right: 14, bottom: 14, zIndex: 6 }}>
                            <IconButton
                              onClick={() => savePageBuilderSection(String(section.id))}
                              disabled={pageBuilderSaving}
                              title="Nur diese Komponente speichern"
                              sx={{
                                bgcolor: 'rgba(59,130,246,0.95)',
                                color: 'white',
                                boxShadow: '0 12px 30px rgba(37,99,235,0.35)',
                                '&:hover': { bgcolor: 'rgba(37,99,235,0.98)' },
                              }}
                            >
                              <SaveRoundedIcon />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                      {renderDropZone(sectionIndex + 1)}
                    </React.Fragment>
                    );
                  }

                  if (section?.type === 'scrolllist') {
                    const cfg: any = (section && typeof section.config === 'object') ? section.config : {};
                    const isDirty = dirtySectionIds.has(String(section.id));
                    return (
                      <React.Fragment key={`pb-sec-${section.id}`}>
                        <Box draggable onDragStart={dragStart} onDragEnd={dragEnd} sx={{ mb: 3, borderRadius: 3, boxShadow: isDraggingSelf ? '0 18px 44px rgba(59,130,246,0.18)' : '0 12px 32px rgba(2,6,23,0.10)', overflow: 'hidden', position: 'relative', bgcolor: 'background.paper', cursor: 'grab', opacity: isDraggingSelf ? 0.7 : 1 }}>
                          {renderSideDropZone(String(section.id), 'left')}
                          {renderSideDropZone(String(section.id), 'right')}
                          <PageScrollListSection config={cfg} resolvePublicUrl={(p) => toPublicUrl(p)} />

                        <Box sx={{ position: 'absolute', top: 10, left: 10, display: 'flex', gap: 0.5, zIndex: 6 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, bgcolor: 'rgba(255,255,255,0.78)', backdropFilter: 'blur(8px)', border: '1px solid', borderColor: 'divider', borderRadius: 999, px: 0.5, py: 0.25, boxShadow: 1 }}>
                            <IconButton size="small" disabled={sectionIndex === 0} onClick={() => moveBuilderSection(String(section.id), -1)} title="Nach oben" sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}>
                              <ArrowUpwardIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" disabled={sectionIndex === builderSections.length - 1} onClick={() => moveBuilderSection(String(section.id), 1)} title="Nach unten" sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}>
                              <ArrowDownwardIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>

                        <Box sx={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: 0.75, zIndex: 5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, bgcolor: 'rgba(255,255,255,0.75)', backdropFilter: 'blur(8px)', border: '1px solid', borderColor: 'divider', borderRadius: 999, px: 1, py: 0.5, boxShadow: 1 }}>
                            <Chip size="small" label="Scroll" />
                            <Chip size="small" color={cfg?.enabled === false ? 'default' : 'success'} label={cfg?.enabled === false ? 'Disabled' : 'Enabled'} />
                            <IconButton
                              size="small"
                              title="Scroll Liste bearbeiten"
                              onClick={() => {
                                const items = Array.isArray(cfg?.items) ? cfg.items : [];
                                const headingVal: LocalizedString = (typeof cfg?.heading === 'string')
                                  ? ({ de: String(cfg?.heading || 'Neues Jahr, neue Lieblinge') } as any)
                                  : ((cfg?.heading && typeof cfg?.heading === 'object') ? cfg.heading : ({ de: 'Neues Jahr, neue Lieblinge' } as any));
                                const itemVals = items.map((it: any) => ({
                                  image: String(it?.image || ''),
                                  title: (typeof it?.title === 'string')
                                    ? ({ de: String(it?.title || '') } as any)
                                    : ((it?.title && typeof it?.title === 'object') ? it.title : ({ de: '' } as any)),
                                  linkUrl: (typeof it?.linkUrl === 'string' ? it.linkUrl : ''),
                                })).filter((it: any) => it.image && getLocalizedString(it.title, 'de', 'de', ''));
                                try {
                                  const keys = (headingVal && typeof headingVal === 'object') ? Object.keys(headingVal as any).filter(Boolean) : [];
                                  const nextLang = keys.includes('de') ? 'de' : (keys[0] || 'de');
                                  setScrollListLang(nextLang);
                                } catch { setScrollListLang('de'); }
                                setScrollListEditor({
                                  open: true,
                                  sectionId: section.id,
                                  enabled: cfg?.enabled !== false,
                                  heading: headingVal,
                                  roundness: Math.max(0, Math.min(50, Number(cfg?.roundness) || 0)),
                                  itemSize: Math.max(72, Math.min(180, Number(cfg?.itemSize) || 112)),
                                  gap: Math.max(6, Math.min(28, Number(cfg?.gap) || 18)),
                                  items: itemVals,
                                });
                              }}
                              sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              title="Scroll Liste entfernen"
                              onClick={() => {
                                setBuilderSections((prev) => {
                                  const next = prev.filter((s: any) => s.id !== section.id);
                                  return next;
                                });
                                markDirty();
                                toast.success('Scroll Liste entfernt', { duration: 1200 });
                              }}
                              sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>

                        {isDirty && (
                          <Box sx={{ position: 'absolute', right: 14, bottom: 14, zIndex: 6 }}>
                            <IconButton
                              onClick={() => savePageBuilderSection(String(section.id))}
                              disabled={pageBuilderSaving}
                              title="Nur diese Komponente speichern"
                              sx={{
                                bgcolor: 'rgba(59,130,246,0.95)',
                                color: 'white',
                                boxShadow: '0 12px 30px rgba(37,99,235,0.35)',
                                '&:hover': { bgcolor: 'rgba(37,99,235,0.98)' },
                              }}
                            >
                              <SaveRoundedIcon />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                      {renderDropZone(sectionIndex + 1)}
                    </React.Fragment>
                    );
                  }

                  if (section?.type === 'mediatext') {
                    const cfg: any = (section && typeof section.config === 'object') ? section.config : {};
                    const isDirty = dirtySectionIds.has(String(section.id));
                    return (
                      <React.Fragment key={`pb-sec-${section.id}`}>
                        <Box draggable onDragStart={dragStart} onDragEnd={dragEnd} sx={{ mb: 3, borderRadius: 3, boxShadow: isDraggingSelf ? '0 18px 44px rgba(59,130,246,0.18)' : '0 12px 32px rgba(2,6,23,0.10)', overflow: 'hidden', position: 'relative', bgcolor: 'background.paper', cursor: 'grab', opacity: isDraggingSelf ? 0.7 : 1 }}>
                          {renderSideDropZone(String(section.id), 'left')}
                          {renderSideDropZone(String(section.id), 'right')}
                          <PageMediaTextSection config={cfg} resolvePublicUrl={(p) => toPublicUrl(p)} />

                        <Box sx={{ position: 'absolute', top: 10, left: 10, display: 'flex', gap: 0.5, zIndex: 6 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, bgcolor: 'rgba(255,255,255,0.78)', backdropFilter: 'blur(8px)', border: '1px solid', borderColor: 'divider', borderRadius: 999, px: 0.5, py: 0.25, boxShadow: 1 }}>
                            <IconButton size="small" disabled={sectionIndex === 0} onClick={() => moveBuilderSection(String(section.id), -1)} title="Nach oben" sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}>
                              <ArrowUpwardIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" disabled={sectionIndex === builderSections.length - 1} onClick={() => moveBuilderSection(String(section.id), 1)} title="Nach unten" sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}>
                              <ArrowDownwardIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>

                        <Box sx={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: 0.75, zIndex: 5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, bgcolor: 'rgba(255,255,255,0.75)', backdropFilter: 'blur(8px)', border: '1px solid', borderColor: 'divider', borderRadius: 999, px: 1, py: 0.5, boxShadow: 1 }}>
                            <Chip size="small" label="Media+Text" />
                            <Chip size="small" color={cfg?.enabled === false ? 'default' : 'success'} label={cfg?.enabled === false ? 'Disabled' : 'Enabled'} />
                            <IconButton
                              size="small"
                              title="Media+Text bearbeiten"
                              onClick={() => {
                                const imgs = Array.isArray(cfg?.images) ? cfg.images : [];
                                const eyebrowVal: LocalizedString = (typeof cfg?.eyebrow === 'string')
                                  ? ({ de: String(cfg?.eyebrow || '') } as any)
                                  : ((cfg?.eyebrow && typeof cfg?.eyebrow === 'object') ? cfg.eyebrow : ({ de: '' } as any));
                                const titleVal: LocalizedString = (typeof cfg?.title === 'string')
                                  ? ({ de: String(cfg?.title || 'Unsere Story') } as any)
                                  : ((cfg?.title && typeof cfg?.title === 'object') ? cfg.title : ({ de: 'Unsere Story' } as any));
                                const textVal: LocalizedString = (typeof cfg?.text === 'string')
                                  ? ({ de: String(cfg?.text || '') } as any)
                                  : ((cfg?.text && typeof cfg?.text === 'object') ? cfg.text : ({ de: '' } as any));
                                const ctaTextVal: LocalizedString = (typeof cfg?.ctaText === 'string')
                                  ? ({ de: String(cfg?.ctaText || '') } as any)
                                  : ((cfg?.ctaText && typeof cfg?.ctaText === 'object') ? cfg.ctaText : ({ de: '' } as any));
                                try {
                                  const keys = Array.from(new Set([
                                    ...Object.keys((titleVal && typeof titleVal === 'object') ? (titleVal as any) : {}),
                                    ...Object.keys((textVal && typeof textVal === 'object') ? (textVal as any) : {}),
                                    ...Object.keys((eyebrowVal && typeof eyebrowVal === 'object') ? (eyebrowVal as any) : {}),
                                    ...Object.keys((ctaTextVal && typeof ctaTextVal === 'object') ? (ctaTextVal as any) : {}),
                                  ].filter(Boolean)));
                                  const nextLang = keys.includes('de') ? 'de' : (keys[0] || 'de');
                                  setMediaTextLang(nextLang);
                                } catch { setMediaTextLang('de'); }
                                setMediaTextEditor({
                                  open: true,
                                  sectionId: section.id,
                                  enabled: cfg?.enabled !== false,
                                  eyebrow: eyebrowVal,
                                  title: titleVal,
                                  text: textVal,
                                  ctaText: ctaTextVal,
                                  ctaUrl: String(cfg?.ctaUrl || ''),
                                  mediaSide: (cfg?.mediaSide === 'right' ? 'right' : 'left'),
                                  imageHeight: Math.max(220, Math.min(640, Number(cfg?.imageHeight) || 420)),
                                  autoplay: cfg?.autoPlay === true,
                                  autoPlayInterval: Math.max(1500, Math.min(20000, Number(cfg?.autoPlayInterval) || 4500)),
                                  showArrows: cfg?.showArrows !== false,
                                  showDots: cfg?.showDots !== false,
                                  images: imgs.filter((s: any) => typeof s === 'string' && s),
                                });
                              }}
                              sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              title="Media+Text entfernen"
                              onClick={() => {
                                setBuilderSections((prev) => {
                                  const next = prev.filter((s: any) => s.id !== section.id);
                                  return next;
                                });
                                markDirty();
                                toast.success('Media+Text entfernt', { duration: 1200 });
                              }}
                              sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>

                        {isDirty && (
                          <Box sx={{ position: 'absolute', right: 14, bottom: 14, zIndex: 6 }}>
                            <IconButton
                              onClick={() => savePageBuilderSection(String(section.id))}
                              disabled={pageBuilderSaving}
                              title="Nur diese Komponente speichern"
                              sx={{
                                bgcolor: 'rgba(59,130,246,0.95)',
                                color: 'white',
                                boxShadow: '0 12px 30px rgba(37,99,235,0.35)',
                                '&:hover': { bgcolor: 'rgba(37,99,235,0.98)' },
                              }}
                            >
                              <SaveRoundedIcon />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                      {renderDropZone(sectionIndex + 1)}
                    </React.Fragment>
                    );
                  }

                  if (section?.type === 'reviews') {
                    const cfg: any = (section && typeof section.config === 'object') ? section.config : {};
                    const isDirty = dirtySectionIds.has(String(section.id));
                    return (
                      <React.Fragment key={`pb-sec-${section.id}`}>
                        <Box draggable onDragStart={dragStart} onDragEnd={dragEnd} sx={{ mb: 3, borderRadius: 3, boxShadow: isDraggingSelf ? '0 18px 44px rgba(59,130,246,0.18)' : '0 12px 32px rgba(2,6,23,0.10)', overflow: 'hidden', position: 'relative', cursor: 'grab', opacity: isDraggingSelf ? 0.7 : 1 }}>
                          {renderSideDropZone(String(section.id), 'left')}
                          {renderSideDropZone(String(section.id), 'right')}
                          <PageReviewsSection config={cfg} resolvePublicUrl={(p) => toPublicUrl(p)} />

                        <Box sx={{ position: 'absolute', top: 10, left: 10, display: 'flex', gap: 0.5, zIndex: 6 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, bgcolor: 'rgba(255,255,255,0.78)', backdropFilter: 'blur(8px)', border: '1px solid', borderColor: 'divider', borderRadius: 999, px: 0.5, py: 0.25, boxShadow: 1 }}>
                            <IconButton size="small" disabled={sectionIndex === 0} onClick={() => moveBuilderSection(String(section.id), -1)} title="Nach oben" sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}>
                              <ArrowUpwardIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" disabled={sectionIndex === builderSections.length - 1} onClick={() => moveBuilderSection(String(section.id), 1)} title="Nach unten" sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}>
                              <ArrowDownwardIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>

                        <Box sx={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: 0.75, zIndex: 5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, bgcolor: 'rgba(255,255,255,0.75)', backdropFilter: 'blur(8px)', border: '1px solid', borderColor: 'divider', borderRadius: 999, px: 1, py: 0.5, boxShadow: 1 }}>
                            <Chip size="small" label="Reviews" />
                            <Chip size="small" color={cfg?.enabled === false ? 'default' : 'success'} label={cfg?.enabled === false ? 'Disabled' : 'Enabled'} />
                            <IconButton
                              size="small"
                              title="Reviews bearbeiten"
                              onClick={() => {
                                const items = Array.isArray(cfg?.items) ? cfg.items : [];
                                const titleVal: LocalizedString = (typeof cfg?.title === 'string')
                                  ? ({ de: String(cfg?.title || 'Was unsere Kunden sagen') } as any)
                                  : ((cfg?.title && typeof cfg?.title === 'object') ? cfg.title : ({ de: 'Was unsere Kunden sagen' } as any));
                                const subtitleVal: LocalizedString = (typeof cfg?.subtitle === 'string')
                                  ? ({ de: String(cfg?.subtitle || '') } as any)
                                  : ((cfg?.subtitle && typeof cfg?.subtitle === 'object') ? cfg.subtitle : ({ de: '' } as any));
                                const normalizeItem = (it: any) => {
                                  const nm: LocalizedString = (typeof it?.name === 'string') ? ({ de: String(it?.name || 'Kunde') } as any) : ((it?.name && typeof it?.name === 'object') ? it.name : ({ de: 'Kunde' } as any));
                                  const rl: LocalizedString = (typeof it?.role === 'string') ? ({ de: String(it?.role || '') } as any) : ((it?.role && typeof it?.role === 'object') ? it.role : ({ de: '' } as any));
                                  const tx: LocalizedString = (typeof it?.text === 'string') ? ({ de: String(it?.text || '') } as any) : ((it?.text && typeof it?.text === 'object') ? it.text : ({ de: '' } as any));
                                  return {
                                    name: nm,
                                    role: rl,
                                    text: tx,
                                    rating: Math.max(0, Math.min(5, Number(it?.rating) || 5)),
                                    avatar: (typeof it?.avatar === 'string' ? it.avatar : ''),
                                  };
                                };
                                try {
                                  const keys = Array.from(new Set([
                                    ...Object.keys((titleVal && typeof titleVal === 'object') ? (titleVal as any) : {}),
                                    ...Object.keys((subtitleVal && typeof subtitleVal === 'object') ? (subtitleVal as any) : {}),
                                  ].filter(Boolean)));
                                  const nextLang = keys.includes('de') ? 'de' : (keys[0] || 'de');
                                  setReviewsLang(nextLang);
                                } catch { setReviewsLang('de'); }
                                setReviewsEditor({
                                  open: true,
                                  sectionId: section.id,
                                  enabled: cfg?.enabled !== false,
                                  title: titleVal,
                                  subtitle: subtitleVal,
                                  variant: (cfg?.variant === 'soft' ? 'soft' : 'glass'),
                                  autoplay: cfg?.autoplay !== false,
                                  intervalMs: Math.max(1500, Math.min(20000, Number(cfg?.intervalMs) || 5500)),
                                  showArrows: cfg?.showArrows !== false,
                                  showDots: cfg?.showDots !== false,
                                  items: items.map(normalizeItem).filter((it: any) => getLocalizedString(it?.text, 'de', 'de', '').trim() !== ''),
                                });
                              }}
                              sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              title="Reviews entfernen"
                              onClick={() => {
                                setBuilderSections((prev) => {
                                  const next = prev.filter((s: any) => s.id !== section.id);
                                  return next;
                                });
                                markDirty();
                                toast.success('Reviews entfernt', { duration: 1200 });
                              }}
                              sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>

                        {isDirty && (
                          <Box sx={{ position: 'absolute', right: 14, bottom: 14, zIndex: 6 }}>
                            <IconButton
                              onClick={() => savePageBuilderSection(String(section.id))}
                              disabled={pageBuilderSaving}
                              title="Nur diese Komponente speichern"
                              sx={{
                                bgcolor: 'rgba(59,130,246,0.95)',
                                color: 'white',
                                boxShadow: '0 12px 30px rgba(37,99,235,0.35)',
                                '&:hover': { bgcolor: 'rgba(37,99,235,0.98)' },
                              }}
                            >
                              <SaveRoundedIcon />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                      {renderDropZone(sectionIndex + 1)}
                    </React.Fragment>
                    );
                  }

                  if (section?.type === 'counter') {
                    const cfg: any = (section && typeof section.config === 'object') ? section.config : {};
                    const isDirty = dirtySectionIds.has(String(section.id));
                    return (
                      <React.Fragment key={`pb-sec-${section.id}`}>
                        <Box draggable onDragStart={dragStart} onDragEnd={dragEnd} sx={{ mb: 3, borderRadius: 3, boxShadow: isDraggingSelf ? '0 18px 44px rgba(59,130,246,0.18)' : '0 12px 32px rgba(2,6,23,0.10)', overflow: 'hidden', position: 'relative', cursor: 'grab', opacity: isDraggingSelf ? 0.7 : 1 }}>
                          {renderSideDropZone(String(section.id), 'left')}
                          {renderSideDropZone(String(section.id), 'right')}
                          <PageCounterSection config={cfg} />

                          <Box sx={{ position: 'absolute', top: 10, left: 10, display: 'flex', gap: 0.5, zIndex: 6 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, bgcolor: 'rgba(255,255,255,0.78)', backdropFilter: 'blur(8px)', border: '1px solid', borderColor: 'divider', borderRadius: 999, px: 0.5, py: 0.25, boxShadow: 1 }}>
                              <IconButton size="small" disabled={sectionIndex === 0} onClick={() => moveBuilderSection(String(section.id), -1)} title="Nach oben" sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}>
                                <ArrowUpwardIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" disabled={sectionIndex === builderSections.length - 1} onClick={() => moveBuilderSection(String(section.id), 1)} title="Nach unten" sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}>
                                <ArrowDownwardIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>

                          <Box sx={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: 0.75, zIndex: 5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, bgcolor: 'rgba(255,255,255,0.75)', backdropFilter: 'blur(8px)', border: '1px solid', borderColor: 'divider', borderRadius: 999, px: 1, py: 0.5, boxShadow: 1 }}>
                              <Chip size="small" label="Counter" />
                              <Chip size="small" color={cfg?.enabled === false ? 'default' : 'success'} label={cfg?.enabled === false ? 'Disabled' : 'Enabled'} />
                              <IconButton
                                size="small"
                                title="Counter bearbeiten"
                                onClick={() => {
                                  const topVal: LocalizedString = (typeof cfg?.topText === 'string')
                                    ? ({ de: String(cfg?.topText || '') } as any)
                                    : ((cfg?.topText && typeof cfg?.topText === 'object') ? cfg.topText : ({ de: '' } as any));
                                  const bottomVal: LocalizedString = (typeof cfg?.bottomText === 'string')
                                    ? ({ de: String(cfg?.bottomText || '') } as any)
                                    : ((cfg?.bottomText && typeof cfg?.bottomText === 'object') ? cfg.bottomText : ({ de: '' } as any));
                                  const blocksIn = Array.isArray(cfg?.blocks) ? cfg.blocks : null;
                                  const itemsIn = Array.isArray(cfg?.items) ? cfg.items : [];
                                  const normalizeItem = (it: any) => {
                                    const pre: LocalizedString = (typeof it?.prefix === 'string') ? ({ de: String(it?.prefix || '') } as any) : ((it?.prefix && typeof it?.prefix === 'object') ? it.prefix : ({ de: '' } as any));
                                    const suf: LocalizedString = (typeof it?.suffix === 'string') ? ({ de: String(it?.suffix || '') } as any) : ((it?.suffix && typeof it?.suffix === 'object') ? it.suffix : ({ de: '' } as any));
                                    const lt: LocalizedString = (typeof it?.labelTop === 'string') ? ({ de: String(it?.labelTop || '') } as any) : ((it?.labelTop && typeof it?.labelTop === 'object') ? it.labelTop : ({ de: '' } as any));
                                    const lb: LocalizedString = (typeof it?.labelBottom === 'string') ? ({ de: String(it?.labelBottom || '') } as any) : ((it?.labelBottom && typeof it?.labelBottom === 'object') ? it.labelBottom : ({ de: '' } as any));
                                    return {
                                      kind: 'counter',
                                      value: Number(it?.value) || 0,
                                      max: it?.max == null ? undefined : (Number(it?.max) || 0),
                                      decimals: it?.decimals == null ? undefined : Math.max(0, Math.min(6, Number(it?.decimals) || 0)),
                                      prefix: pre,
                                      suffix: suf,
                                      labelTop: lt,
                                      labelBottom: lb,
                                    };
                                  };
                                  const normalizeTextBlock = (b: any) => {
                                    const txt: LocalizedString = (typeof b?.text === 'string')
                                      ? ({ de: String(b?.text || '') } as any)
                                      : ((b?.text && typeof b?.text === 'object') ? b.text : ({ de: '' } as any));
                                    return {
                                      kind: 'text',
                                      text: txt,
                                      align: (b?.align === 'left' ? 'left' : (b?.align === 'right' ? 'right' : 'center')),
                                      textColor: String(b?.textColor || ''),
                                      fontSize: Math.max(12, Math.min(32, Number(b?.fontSize) || 18)),
                                      fullWidth: b?.fullWidth !== false,
                                    };
                                  };
                                  const blocks = Array.isArray(blocksIn)
                                    ? blocksIn.map((b: any) => {
                                      const k = String(b?.kind || '').toLowerCase();
                                      if (k === 'text') return normalizeTextBlock(b);
                                      return normalizeItem(b);
                                    })
                                    : itemsIn.map(normalizeItem);
                                  try {
                                    const keys = Array.from(new Set([
                                      ...Object.keys((topVal && typeof topVal === 'object') ? (topVal as any) : {}),
                                      ...Object.keys((bottomVal && typeof bottomVal === 'object') ? (bottomVal as any) : {}),
                                    ].filter(Boolean)));
                                    const nextLang = keys.includes('de') ? 'de' : (keys[0] || 'de');
                                    setCounterLang(nextLang);
                                  } catch { setCounterLang('de'); }
                                  setCounterEditor({
                                    open: true,
                                    sectionId: section.id,
                                    enabled: cfg?.enabled !== false,
                                    topText: topVal,
                                    bottomText: bottomVal,
                                    type: (cfg?.type === 'classic' ? 'classic' : 'radial'),
                                    align: (cfg?.align === 'left' ? 'left' : (cfg?.align === 'right' ? 'right' : 'center')),
                                    perRow: Math.max(1, Math.min(6, Number(cfg?.perRow ?? cfg?.columns) || 3)),
                                    size: Math.max(0.65, Math.min(1.6, Number(cfg?.size) || 1)),
                                    textScale: Math.max(0.75, Math.min(1.5, Number(cfg?.textScale) || 1)),
                                    showPercent: cfg?.showPercent === true,
                                    bgMode: (cfg?.bgMode === 'custom' || cfg?.bgMode === 'transparent') ? cfg.bgMode : 'theme',
                                    bgColor: String(cfg?.bgColor || '#ffffff'),
                                    textColor: String(cfg?.textColor || ''),
                                    ringBgColor: String(cfg?.ringBgColor || ''),
                                    gradientFrom: String(cfg?.gradientFrom || ''),
                                    gradientTo: String(cfg?.gradientTo || ''),
                                    animate: cfg?.animate !== false,
                                    animationDurationMs: Math.max(250, Math.min(8000, Number(cfg?.animationDurationMs) || 1400)),
                                    blocks,
                                  });
                                }}
                                sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                title="Counter entfernen"
                                onClick={() => {
                                  setBuilderSections((prev) => {
                                    const next = prev.filter((s: any) => s.id !== section.id);
                                    return next;
                                  });
                                  markDirty();
                                  toast.success('Counter entfernt', { duration: 1200 });
                                }}
                                sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>

                          {isDirty && (
                            <Box sx={{ position: 'absolute', right: 14, bottom: 14, zIndex: 6 }}>
                              <IconButton
                                onClick={() => savePageBuilderSection(String(section.id))}
                                disabled={pageBuilderSaving}
                                title="Nur diese Komponente speichern"
                                sx={{
                                  bgcolor: 'rgba(59,130,246,0.95)',
                                  color: 'white',
                                  boxShadow: '0 12px 30px rgba(37,99,235,0.35)',
                                  '&:hover': { bgcolor: 'rgba(37,99,235,0.98)' },
                                }}
                              >
                                <SaveRoundedIcon />
                              </IconButton>
                            </Box>
                          )}
                        </Box>
                        {renderDropZone(sectionIndex + 1)}
                      </React.Fragment>
                    );
                  }

                  if (section?.type === 'paragraph') {
                    const cfg: any = (section && typeof section.config === 'object') ? section.config : {};
                    const isDirty = dirtySectionIds.has(String(section.id));
                    return (
                      <React.Fragment key={`pb-sec-${section.id}`}>
                        <Box draggable onDragStart={dragStart} onDragEnd={dragEnd} sx={{ mb: 3, borderRadius: 3, boxShadow: isDraggingSelf ? '0 18px 44px rgba(59,130,246,0.18)' : '0 12px 32px rgba(2,6,23,0.10)', overflow: 'hidden', position: 'relative', cursor: 'grab', opacity: isDraggingSelf ? 0.7 : 1 }}>
                          {renderSideDropZone(String(section.id), 'left')}
                          {renderSideDropZone(String(section.id), 'right')}
                          <PageParagraphSection config={cfg} />

                          <Box sx={{ position: 'absolute', top: 10, left: 10, display: 'flex', gap: 0.5, zIndex: 6 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, bgcolor: 'rgba(255,255,255,0.78)', backdropFilter: 'blur(8px)', border: '1px solid', borderColor: 'divider', borderRadius: 999, px: 0.5, py: 0.25, boxShadow: 1 }}>
                              <IconButton size="small" disabled={sectionIndex === 0} onClick={() => moveBuilderSection(String(section.id), -1)} title="Nach oben" sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}>
                                <ArrowUpwardIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" disabled={sectionIndex === builderSections.length - 1} onClick={() => moveBuilderSection(String(section.id), 1)} title="Nach unten" sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}>
                                <ArrowDownwardIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>

                          <Box sx={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: 0.75, zIndex: 5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, bgcolor: 'rgba(255,255,255,0.75)', backdropFilter: 'blur(8px)', border: '1px solid', borderColor: 'divider', borderRadius: 999, px: 1, py: 0.5, boxShadow: 1 }}>
                              <Chip size="small" label="Paragraph" />
                              <Chip size="small" color={cfg?.enabled === false ? 'default' : 'success'} label={cfg?.enabled === false ? 'Disabled' : 'Enabled'} />
                              <IconButton
                                size="small"
                                title="Paragraph bearbeiten"
                                onClick={() => {
                                  const txt: LocalizedString = (typeof cfg?.text === 'string')
                                    ? ({ de: String(cfg?.text || '') } as any)
                                    : ((cfg?.text && typeof cfg?.text === 'object') ? cfg.text : ({ de: '' } as any));
                                  try {
                                    const keys = (txt && typeof txt === 'object') ? Object.keys(txt as any).filter(Boolean) : [];
                                    const nextLang = keys.includes('de') ? 'de' : (keys[0] || 'de');
                                    setParagraphLang(nextLang);
                                  } catch { setParagraphLang('de'); }
                                  setParagraphEditor({
                                    open: true,
                                    sectionId: section.id,
                                    enabled: cfg?.enabled !== false,
                                    text: txt,
                                    align: (cfg?.align === 'left' ? 'left' : (cfg?.align === 'right' ? 'right' : 'center')),
                                    textColor: String(cfg?.textColor || ''),
                                    fontSize: Math.max(12, Math.min(32, Number(cfg?.fontSize) || 18)),
                                    maxWidth: (cfg?.maxWidth === 'sm' ? 'sm' : (cfg?.maxWidth === 'md' ? 'md' : 'lg')),
                                    variant: (cfg?.variant === 'card' ? 'card' : 'plain'),
                                    bgColor: String(cfg?.bgColor || ''),
                                  });
                                }}
                                sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                title="Paragraph entfernen"
                                onClick={() => {
                                  setBuilderSections((prev) => {
                                    const next = prev.filter((s: any) => s.id !== section.id);
                                    return next;
                                  });
                                  markDirty();
                                  toast.success('Paragraph entfernt', { duration: 1200 });
                                }}
                                sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>

                          {isDirty && (
                            <Box sx={{ position: 'absolute', right: 14, bottom: 14, zIndex: 6 }}>
                              <IconButton
                                onClick={() => savePageBuilderSection(String(section.id))}
                                disabled={pageBuilderSaving}
                                title="Nur diese Komponente speichern"
                                sx={{
                                  bgcolor: 'rgba(59,130,246,0.95)',
                                  color: 'white',
                                  boxShadow: '0 12px 30px rgba(37,99,235,0.35)',
                                  '&:hover': { bgcolor: 'rgba(37,99,235,0.98)' },
                                }}
                              >
                                <SaveRoundedIcon />
                              </IconButton>
                            </Box>
                          )}
                        </Box>
                        {renderDropZone(sectionIndex + 1)}
                      </React.Fragment>
                    );
                  }

                  if (section?.type === 'cta') {
                    const cfg: any = (section && typeof section.config === 'object') ? section.config : {};
                    const isDirty = dirtySectionIds.has(String(section.id));
                    return (
                      <React.Fragment key={`pb-sec-${section.id}`}>
                        <Box draggable onDragStart={dragStart} onDragEnd={dragEnd} sx={{ mb: 3, borderRadius: 3, boxShadow: isDraggingSelf ? '0 18px 44px rgba(59,130,246,0.18)' : '0 12px 32px rgba(2,6,23,0.10)', overflow: 'hidden', position: 'relative', cursor: 'grab', opacity: isDraggingSelf ? 0.7 : 1 }}>
                          {renderSideDropZone(String(section.id), 'left')}
                          {renderSideDropZone(String(section.id), 'right')}
                          <PageCtaSection config={cfg} resolvePublicUrl={(p) => toPublicUrl(p)} />

                        <Box sx={{ position: 'absolute', top: 10, left: 10, display: 'flex', gap: 0.5, zIndex: 6 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, bgcolor: 'rgba(255,255,255,0.78)', backdropFilter: 'blur(8px)', border: '1px solid', borderColor: 'divider', borderRadius: 999, px: 0.5, py: 0.25, boxShadow: 1 }}>
                            <IconButton size="small" disabled={sectionIndex === 0} onClick={() => moveBuilderSection(String(section.id), -1)} title="Nach oben" sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}>
                              <ArrowUpwardIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" disabled={sectionIndex === builderSections.length - 1} onClick={() => moveBuilderSection(String(section.id), 1)} title="Nach unten" sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}>
                              <ArrowDownwardIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>

                        <Box sx={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: 0.75, zIndex: 5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, bgcolor: 'rgba(255,255,255,0.75)', backdropFilter: 'blur(8px)', border: '1px solid', borderColor: 'divider', borderRadius: 999, px: 1, py: 0.5, boxShadow: 1 }}>
                            <Chip size="small" label="CTA" />
                            <Chip size="small" color={cfg?.enabled === false ? 'default' : 'success'} label={cfg?.enabled === false ? 'Disabled' : 'Enabled'} />
                            <IconButton
                              size="small"
                              title="CTA bearbeiten"
                              onClick={() => {
                                const titleVal: LocalizedString = (typeof cfg?.title === 'string')
                                  ? ({ de: String(cfg?.title || 'Bereit loszulegen?') } as any)
                                  : ((cfg?.title && typeof cfg?.title === 'object') ? cfg.title : ({ de: 'Bereit loszulegen?' } as any));
                                const textVal: LocalizedString = (typeof cfg?.text === 'string')
                                  ? ({ de: String(cfg?.text || '') } as any)
                                  : ((cfg?.text && typeof cfg?.text === 'object') ? cfg.text : ({ de: '' } as any));
                                const btnTextVal: LocalizedString = (typeof cfg?.buttonText === 'string')
                                  ? ({ de: String(cfg?.buttonText || '') } as any)
                                  : ((cfg?.buttonText && typeof cfg?.buttonText === 'object') ? cfg.buttonText : ({ de: '' } as any));
                                try {
                                  const keys = (titleVal && typeof titleVal === 'object') ? Object.keys(titleVal as any).filter(Boolean) : [];
                                  const nextLang = keys.includes('de') ? 'de' : (keys[0] || 'de');
                                  setCtaLang(nextLang);
                                } catch { setCtaLang('de'); }
                                setCtaEditor({
                                  open: true,
                                  sectionId: section.id,
                                  enabled: cfg?.enabled !== false,
                                  title: titleVal,
                                  text: textVal,
                                  buttonText: btnTextVal,
                                  buttonUrl: String(cfg?.buttonUrl || ''),
                                  backgroundImage: String(cfg?.backgroundImage || ''),
                                  align: (cfg?.align === 'left' ? 'left' : 'center'),
                                  variant: (cfg?.variant === 'soft' ? 'soft' : 'gradient'),
                                });
                              }}
                              sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              title="CTA entfernen"
                              onClick={() => {
                                setBuilderSections((prev) => {
                                  const next = prev.filter((s: any) => s.id !== section.id);
                                  return next;
                                });
                                markDirty();
                                toast.success('CTA entfernt', { duration: 1200 });
                              }}
                              sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>

                        {isDirty && (
                          <Box sx={{ position: 'absolute', right: 14, bottom: 14, zIndex: 6 }}>
                            <IconButton
                              onClick={() => savePageBuilderSection(String(section.id))}
                              disabled={pageBuilderSaving}
                              title="Nur diese Komponente speichern"
                              sx={{
                                bgcolor: 'rgba(59,130,246,0.95)',
                                color: 'white',
                                boxShadow: '0 12px 30px rgba(37,99,235,0.35)',
                                '&:hover': { bgcolor: 'rgba(37,99,235,0.98)' },
                              }}
                            >
                              <SaveRoundedIcon />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                      {renderDropZone(sectionIndex + 1)}
                    </React.Fragment>
                    );
                  }

                  if (section?.type === 'gallery') {
                    const cfg: any = (section && typeof section.config === 'object') ? section.config : {};
                    const isDirty = dirtySectionIds.has(String(section.id));
                    return (
                      <React.Fragment key={`pb-sec-${section.id}`}>
                        <Box draggable onDragStart={dragStart} onDragEnd={dragEnd} sx={{ mb: 3, borderRadius: 3, boxShadow: isDraggingSelf ? '0 18px 44px rgba(59,130,246,0.18)' : '0 12px 32px rgba(2,6,23,0.10)', overflow: 'hidden', position: 'relative', cursor: 'grab', opacity: isDraggingSelf ? 0.7 : 1 }}>
                          {renderSideDropZone(String(section.id), 'left')}
                          {renderSideDropZone(String(section.id), 'right')}
                          <PageGallerySection config={cfg} resolvePublicUrl={(p) => toPublicUrl(p)} />

                        <Box sx={{ position: 'absolute', top: 10, left: 10, display: 'flex', gap: 0.5, zIndex: 6 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, bgcolor: 'rgba(255,255,255,0.78)', backdropFilter: 'blur(8px)', border: '1px solid', borderColor: 'divider', borderRadius: 999, px: 0.5, py: 0.25, boxShadow: 1 }}>
                            <IconButton size="small" disabled={sectionIndex === 0} onClick={() => moveBuilderSection(String(section.id), -1)} title="Nach oben" sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}>
                              <ArrowUpwardIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" disabled={sectionIndex === builderSections.length - 1} onClick={() => moveBuilderSection(String(section.id), 1)} title="Nach unten" sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}>
                              <ArrowDownwardIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>

                        <Box sx={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: 0.75, zIndex: 5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, bgcolor: 'rgba(255,255,255,0.75)', backdropFilter: 'blur(8px)', border: '1px solid', borderColor: 'divider', borderRadius: 999, px: 1, py: 0.5, boxShadow: 1 }}>
                            <Chip size="small" label="Gallery" />
                            <Chip size="small" color={cfg?.enabled === false ? 'default' : 'success'} label={cfg?.enabled === false ? 'Disabled' : 'Enabled'} />
                            <IconButton
                              size="small"
                              title="Gallery bearbeiten"
                              onClick={() => {
                                const items = Array.isArray(cfg?.items) ? cfg.items : [];
                                const cols = (cfg && typeof cfg.columns === 'object') ? cfg.columns : {};
                                setGalleryEditor({
                                  open: true,
                                  sectionId: section.id,
                                  enabled: cfg?.enabled !== false,
                                  title: String(cfg?.title || 'Galerie'),
                                  subtitle: String(cfg?.subtitle || ''),
                                  layout: (cfg?.layout === 'masonry' ? 'masonry' : 'grid'),
                                  aspectRatio: String(cfg?.aspectRatio || '1 / 1'),
                                  xsCols: Math.max(1, Math.min(4, Number(cols?.xs) || 2)),
                                  smCols: Math.max(1, Math.min(6, Number(cols?.sm) || 3)),
                                  mdCols: Math.max(1, Math.min(8, Number(cols?.md) || 4)),
                                  gap: Math.max(0, Math.min(48, Number(cfg?.gap) || 12)),
                                  radius: Math.max(0, Math.min(28, Number(cfg?.radius) || 16)),
                                  shadow: (cfg?.shadow === 'none' ? 'none' : (cfg?.shadow === 'medium' ? 'medium' : 'soft')),
                                  lightboxEnabled: cfg?.lightboxEnabled !== false,
                                  items: items.map((it: any) => ({ image: String(it?.image || ''), caption: (typeof it?.caption === 'string' ? it.caption : ''), linkUrl: (typeof it?.linkUrl === 'string' ? it.linkUrl : '') })).filter((it: any) => it.image),
                                });
                              }}
                              sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              title="Gallery entfernen"
                              onClick={() => {
                                setBuilderSections((prev) => {
                                  const next = prev.filter((s: any) => s.id !== section.id);
                                  return next;
                                });
                                markDirty();
                                toast.success('Galerie entfernt', { duration: 1200 });
                              }}
                              sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>

                        {isDirty && (
                          <Box sx={{ position: 'absolute', right: 14, bottom: 14, zIndex: 6 }}>
                            <IconButton
                              onClick={() => savePageBuilderSection(String(section.id))}
                              disabled={pageBuilderSaving}
                              title="Nur diese Komponente speichern"
                              sx={{
                                bgcolor: 'rgba(59,130,246,0.95)',
                                color: 'white',
                                boxShadow: '0 12px 30px rgba(37,99,235,0.35)',
                                '&:hover': { bgcolor: 'rgba(37,99,235,0.98)' },
                              }}
                            >
                              <SaveRoundedIcon />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                      {renderDropZone(sectionIndex + 1)}
                    </React.Fragment>
                    );
                  }

                  return (
                    <GridEditor
                      key={section.id}
                      render={() => (
                        <React.Fragment>
                          <Card draggable onDragStart={dragStart} onDragEnd={dragEnd} sx={{ mb: 3, borderRadius: 3, boxShadow: isDraggingSelf ? '0 18px 44px rgba(59,130,246,0.18)' : '0 12px 32px rgba(2,6,23,0.10)', position: 'relative', cursor: 'grab', opacity: isDraggingSelf ? 0.7 : 1 }}>
                            <CardContent>
                            <Box sx={{ position: 'absolute', top: 10, left: 10, display: 'flex', gap: 0.5, zIndex: 6 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, bgcolor: 'rgba(255,255,255,0.78)', backdropFilter: 'blur(8px)', border: '1px solid', borderColor: 'divider', borderRadius: 999, px: 0.5, py: 0.25, boxShadow: 1 }}>
                                <IconButton size="small" disabled={sectionIndex === 0} onClick={() => moveBuilderSection(String(section.id), -1)} title="Nach oben" sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}>
                                  <ArrowUpwardIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" disabled={sectionIndex === builderSections.length - 1} onClick={() => moveBuilderSection(String(section.id), 1)} title="Nach unten" sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}>
                                  <ArrowDownwardIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="h6" fontWeight={700}>{section.title || 'Grid'}</Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <IconButton onClick={async () => {
                                  const clone = { ...section, id: `${Date.now()}-${Math.random().toString(36).slice(2,7)}`, open: false };
                                  setBuilderSections(prev => {
                                    const next = [...prev, clone];
                                    return next;
                                  });
                                  markDirty(clone.id);
                                  toast.success('Sektion dupliziert', { duration: 1200 });
                                }} size="small" title="Duplizieren">
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                                <IconButton color="error" onClick={() => {
                                  setBuilderSections(prev => {
                                    const next = prev.filter(s => s.id !== section.id);
                                    return next;
                                  });
                                  markDirty();
                                  toast.success('Grid entfernt', { duration: 1200 });
                                }} size="small" title="Grid entfernen">
                                  <DeleteOutlineIcon />
                                </IconButton>
                                <IconButton onClick={() => {
                                  const nextOpen = !section.open;
                                  setBuilderSections(prev => prev.map(s => s.id === section.id ? { ...s, open: nextOpen } : s));
                                  if (nextOpen) {
                                    setActiveSectionId(section.id);
                                    const tabs = Array.isArray((section as any).gridTabs) ? (section as any).gridTabs : [];
                                    const normTabs = tabs.map((tb: any, i: number) => ({
                                      label: typeof tb?.label === 'string' && tb.label ? tb.label : `Tab ${i+1}`,
                                      enabled: tb?.enabled !== false,
                                      title: tb?.title || '',
                                      rows: Math.max(1, Number(tb?.rows)||1),
                                      cols: Math.max(1, Number(tb?.cols)||1),
                                      rowHeight: Math.max(80, Math.min(480, Number(tb?.rowHeight) || 160)),
                                      tiles: Array.isArray(tb?.tiles) ? tb.tiles : [],
                                      colWidths: Array.isArray((tb as any).colWidths) ? (tb as any).colWidths : undefined,
                                    }));
                                    if (normTabs.length > 0) {
                                      setGridTabs(normTabs);
                                      let desiredIdx = 0;
                                      try {
                                        const url = (pagesTabs && pagesTabs[pageBuilderIndex]) ? (pagesTabs[pageBuilderIndex].url || '/') : '/';
                                        const raw = typeof window !== 'undefined' ? window.localStorage.getItem(`admin.pageBuilder.gridActiveTab::${url}::${section.id}`) : null;
                                        const n = Number(raw);
                                        if (Number.isInteger(n) && n >= 0 && n < normTabs.length) desiredIdx = n;
                                      } catch {}
                                      setGridActiveTab(desiredIdx);
                                      const t: any = normTabs[desiredIdx];
                                      setGridEnabled(t?.enabled !== false);
                                      setGridTitle(t?.title || '');
                                      setGridRows(Math.max(1, Number(t?.rows)||1));
                                      setGridCols(Math.max(1, Number(t?.cols)||1));
                                      setGridRowHeight(Math.max(80, Math.min(480, Number(t?.rowHeight) || 160)));
                                      if (Array.isArray(t?.colWidths) && t.colWidths.length === Math.max(1, Number(t?.cols)||1)) {
                                        setGridColWidths(t.colWidths.map((n: any) => Math.max(0.25, Number(n) || 1)));
                                      } else {
                                        setGridColWidths(Array.from({ length: Math.max(1, Number(t?.cols)||1) }).map(() => 1));
                                      }
                                      const tiles0 = Array.isArray(t?.tiles) ? t.tiles : [];
                                      setGridTiles(tiles0.map((it: any) => ({
                                        title: it?.title || '', subtitle: it?.subtitle || '', image: typeof it?.image === 'string' ? it.image : '',
                                        images: Array.isArray(it?.images) ? it.images.filter((s: any)=> typeof s === 'string' && s) : undefined,
                                        imageInterval: Number(it?.imageInterval) || undefined, ctaText: it?.ctaText || '', ctaUrl: it?.ctaUrl || '',
                                        bgColor: it?.bgColor || '', textColor: it?.textColor || '', enabled: it?.enabled !== false, reserveSpace: it?.reserveSpace === true,
                                        colSpan: Math.max(1, Number(it?.colSpan)||1), rowSpan: Math.max(1, Number((it as any)?.rowSpan)||1),
                                        hoverEffect: typeof it?.hoverEffect === 'string' ? it.hoverEffect : 'none', ctaFree: it?.ctaFree === true,
                                        ctaX: Math.max(0, Math.min(100, Number(it?.ctaX)||15)), ctaY: Math.max(0, Math.min(100, Number(it?.ctaY)||80)), ctaScale: Math.max(0.5, Math.min(3, Number(it?.ctaScale)||1)),
                                        ctaBtnBg: (it as any)?.ctaBtnBg || '', ctaBtnTextColor: (it as any)?.ctaBtnTextColor || '', ctaHoverEffect: typeof (it as any)?.ctaHoverEffect === 'string' ? (it as any).ctaHoverEffect : 'none',
                                        ctaFontSize: Math.max(10, Math.min(36, Number((it as any)?.ctaFontSize)||14)), ctaWidth: Math.max(80, Math.min(400, Number((it as any)?.ctaWidth)||140)),
                                        showCounter: it?.showCounter === true, counterFree: (it as any)?.counterFree === true, counterX: Math.max(0, Math.min(100, Number((it as any)?.counterX)||85)), counterY: Math.max(0, Math.min(100, Number((it as any)?.counterY)||85)), counterScale: Math.max(0.5, Math.min(3, Number((it as any)?.counterScale)||1)),
                                        counterBg: (it as any)?.counterBg || 'rgba(255,255,255,0.9)', counterTextColor: (it as any)?.counterTextColor || '#111827', counterRadius: Math.max(0, Math.min(28, Number((it as any)?.counterRadius)||8)),
                                        counterPrefix: (it as any)?.counterPrefix || '', counterSuffix: (it as any)?.counterSuffix || '', counterPrefixNewline: (it as any)?.counterPrefixNewline === true, counterSuffixNewline: (it as any)?.counterSuffixNewline === true,
                                        showList: (it as any)?.showList === true, listTabIndex: Number.isInteger((it as any)?.listTabIndex) ? (it as any).listTabIndex : -1, listFullSize: (it as any)?.listFullSize === true, listScrollDir: ((it as any)?.listScrollDir === 'horizontal') ? 'horizontal' : 'vertical'
                                      })));
                                    }
                                    const gtd = (section as any).gridTabsDisplay;
                                    if (gtd) {
                                      const pos = typeof gtd?.position === 'string' && ['above','below'].includes(gtd.position) ? gtd.position as any : 'above';
                                      const st = typeof gtd?.style === 'string' && ['labels','dots'].includes(gtd.style) ? gtd.style as any : 'labels';
                                      setGridTabsPosition(pos);
                                      setGridTabsStyle(st);
                                      setGridTabsArrows(!!gtd?.arrowsEnabled);
                                      const tt = typeof gtd?.transitionType === 'string' && ['none','slide','tiles','cube'].includes(gtd.transitionType) ? (gtd.transitionType as any) : 'none';
                                      setGridTabsTransitionType(tt);
                                    }
                                  }
                                }} size="small" title={section.open ? 'Schließen' : 'Öffnen'}>
                                  <ExpandMoreIcon sx={{ transform: section.open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform .2s ease' }} />
                                </IconButton>
                              </Box>
                            </Box>
                            <Collapse in={section.open} timeout={200}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Erstelle attraktive Kacheln in einem Raster. Klicke auf eine Zelle, um sie zu bearbeiten.</Typography>
                      {/* Grid Tabs Bar */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                        {gridTabs.map((tb, i) => (
                          <Box key={`gtab-${i}`} sx={{ display: 'flex', alignItems: 'center' }}>
                            <Button
                              variant={i === gridActiveTab ? 'contained' : 'outlined'}
                              size="small"
                              onClick={() => { persistEditingIntoActiveTab(); setGridActiveTab(i); loadFromTab(i); }}
                              sx={{ textTransform: 'none', borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                            >
                              {tb.label || `Tab ${i+1}`}
                            </Button>
                            <IconButton size="small" color="error" onClick={() => removeGridTab(i)} sx={{ border: '1px solid', borderColor: 'divider', borderLeft: 'none', borderTopRightRadius: 999, borderBottomRightRadius: 999 }}>
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        ))}
                        <Button variant="text" size="small" onClick={addGridTab} sx={{ textTransform: 'none', fontWeight: 700 }}>+ Neuer Tab</Button>
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                        <Box sx={{ minWidth: 220 }}>
                          <Typography variant="caption" color="text.secondary">Tabs Position</Typography>
                          <Select size="small" fullWidth value={gridTabsPosition} onChange={(e) => setGridTabsPosition(e.target.value as ('above'|'below'))}>
                            <MenuItem value="above">Über dem Grid</MenuItem>
                            <MenuItem value="below">Unter dem Grid</MenuItem>
                          </Select>
                        </Box>
                        <Box sx={{ minWidth: 220 }}>
                          <Typography variant="caption" color="text.secondary">Tabs Stil</Typography>
                          <Select size="small" fullWidth value={gridTabsStyle} onChange={(e) => setGridTabsStyle(e.target.value as ('labels'|'dots'))}>
                            <MenuItem value="labels">Beschriftete Tabs</MenuItem>
                            <MenuItem value="dots">Nur Punkte</MenuItem>
                          </Select>
                        </Box>
                        <FormControlLabel
                          control={<Switch checked={gridTabsArrows} onChange={(e) => setGridTabsArrows(e.target.checked)} />}
                          label="Seitliche Handler anzeigen"
                        />
                        <Box sx={{ minWidth: 220 }}>
                          <Typography variant="caption" color="text.secondary">Tabs Animation</Typography>
                          <Select size="small" fullWidth value={gridTabsTransitionType} onChange={(e) => setGridTabsTransitionType(e.target.value as ('none'|'slide'|'tiles'|'cube'))}>
                            <MenuItem value="none">Keine</MenuItem>
                            <MenuItem value="slide">Slide (ganzer Grid)</MenuItem>
                            <MenuItem value="tiles">Tiles Stagger</MenuItem>
                            <MenuItem value="cube">3D Cube</MenuItem>
                          </Select>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 2 }}>
                        <FormControlLabel control={<Switch checked={gridEnabled} onChange={(e) => setGridEnabled(e.target.checked)} />} label={gridEnabled ? 'Aktiviert' : 'Deaktiviert'} />
                        <TextField label="Titel" value={gridTitle} onChange={(e) => setGridTitle(e.target.value)} sx={{ minWidth: 320 }} />
                        <TextField type="number" label="Reihen" value={gridRows} onChange={(e) => setGridRows(Math.max(1, Number(e.target.value)||1))} sx={{ width: 140 }} />
                        <TextField type="number" label="Spalten" value={gridCols} onChange={(e) => setGridCols(Math.max(1, Number(e.target.value)||1))} sx={{ width: 140 }} />
                        <Box sx={{ display: 'grid', gap: 0.5, minWidth: 260 }}>
                          <Typography variant="caption" color="text.secondary">Zeilenhöhe (px)</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Slider value={gridRowHeight} onChange={(_, v) => setGridRowHeight(Math.max(80, Math.min(480, Number(v) || 160)))} min={80} max={480} step={5} sx={{ width: 180 }} />
                            <TextField type="number" value={gridRowHeight} onChange={(e)=> setGridRowHeight(Math.max(80, Math.min(480, Number(e.target.value)||160)))} sx={{ width: 90 }} />
                          </Box>
                        </Box>
                      </Box>
                      <Divider sx={{ my: 1.5 }} />
                      {/* Live Grid Preview */}
                      <Box sx={{
                        border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2,
                        background: 'linear-gradient(135deg, rgba(59,130,246,0.04), rgba(236,72,153,0.04))',
                        position: 'relative',
                      }}>
                        <Box ref={gridPreviewRef} sx={{
                          display: 'grid',
                          gridTemplateColumns: {
                            xs: 'repeat(2, 1fr)',
                            sm: `repeat(${Math.max(2, Math.min(3, gridCols))}, 1fr)`,
                            md: (gridColWidths.length === Math.max(1, Number(gridCols) || 1)) ? gridColWidths.map(v => `${Math.max(0.25, v)}fr`).join(' ') : `repeat(${gridCols}, 1fr)`
                          },
                          gridAutoRows: `${Math.max(80, Math.min(480, Number(gridRowHeight)||160))}px`,
                          gap: 1.5,
                        }}>
                          {Array.from({ length: Math.max(1, gridRows * gridCols) }).map((_, idx) => {
                            const it = gridTiles[idx];
                            const imgsArr = Array.isArray((it as any)?.images) ? (it as any).images.filter((s: any)=> typeof s === 'string' && s) : [];
                            const singleImg = typeof it?.image === 'string' && it.image ? it.image : '';
                            const gallery = imgsArr.length > 0 ? imgsArr : (singleImg ? [singleImg] : []);
                            const intervalSec = Math.max(2, Math.min(60, Number((it as any)?.imageInterval) || 5));
                            const curImg = gallery.length > 0 ? gallery[(Math.floor(Date.now() / (intervalSec * 1000))) % gallery.length] : '';
                            const imgUrl = curImg ? toPublicUrl(curImg) : '';
                            const bg = it?.bgColor || (imgUrl ? 'transparent' : '#f8fafc');
                            const color = it?.textColor || (imgUrl ? '#ffffff' : 'rgba(0,0,0,0.85)');
                            return (
                              <Box key={idx} onClick={() => openTileEditor(idx)} sx={{ position: 'relative', cursor: 'pointer', borderRadius: 2, overflow: 'hidden', boxShadow: it ? 2 : 1, border: '1px solid', borderColor: it?.enabled === false && it?.reserveSpace ? 'warning.light' : 'divider', minHeight: `${Math.max(80, Math.min(480, Number(gridRowHeight)||160))}px`, transition: 'all .2s', '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' }, '&:hover .tile-controls': { opacity: 1, pointerEvents: 'auto', transform: 'translateY(0px)' }, background: bg, opacity: it?.enabled === false ? 0.45 : 1, filter: it?.enabled === false ? 'grayscale(0.3)' : 'none', gridColumn: `span ${Math.max(1, it?.colSpan || 1)}`, gridRow: `span ${Math.max(1, (it as any)?.rowSpan || 1)}` }}>
                                {imgUrl && (
                                  <Box component="img" src={imgUrl} alt={it?.title || `Tile ${idx+1}`} sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: 'brightness(0.96)' }} />
                                )}
                                <Box sx={{ position: 'absolute', inset: 0, background: imgUrl ? 'linear-gradient(to top, rgba(0,0,0,0.45), rgba(0,0,0,0.10))' : 'transparent' }} />
                                {it?.showCounter && !it?.counterFree && (
                                  <Box sx={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', zIndex: 3 }}>
                                    {(() => { const idxSel = Number.isInteger((it as any)?.counterTabIndex) ? (it as any).counterTabIndex : -1; const src = (Array.isArray(counterTabs) && idxSel >= 0 && idxSel < counterTabs.length) ? counterTabs[idxSel] : null; return (
                                      <Counter
                                        value={1234}
                                        prefix={typeof src?.prefix === 'string' ? src.prefix : ''}
                                        suffix={typeof src?.suffix === 'string' ? src.suffix : ''}
                                        prefixNewline={!!src?.prefixNewline}
                                        suffixNewline={!!src?.suffixNewline}
                                        variant={(typeof src?.variant === 'string' && ['classic','radial','glass'].includes(src.variant)) ? (src.variant as any) : 'classic'}
                                        bg={typeof src?.bg === 'string' ? src.bg : 'rgba(255,255,255,0.9)'}
                                        textColor={typeof src?.textColor === 'string' ? src.textColor : '#111827'}
                                        radius={Math.max(0, Math.min(28, Number(src?.radius) || 8))}
                                        scale={Math.max(0.5, Math.min(3, Number(src?.scale) || 1))}
                                        fontFamily={typeof src?.fontFamily === 'string' ? src.fontFamily : undefined}
                                        valueFontSize={Math.max(10, Math.min(96, Number(src?.valueFontSize) || 28))}
                                        prefixFontSize={Math.max(8, Math.min(48, Number(src?.prefixFontSize) || 12))}
                                        suffixFontSize={Math.max(8, Math.min(48, Number(src?.suffixFontSize) || 12))}
                                        gradientFrom={typeof src?.gradientFrom === 'string' ? src.gradientFrom : '#60a5fa'}
                                        gradientTo={typeof src?.gradientTo === 'string' ? src.gradientTo : '#22c55e'}
                                        ringWidth={Math.max(2, Math.min(24, Number(src?.ringWidth) || 10))}
                                        shadow={(typeof src?.shadow === 'string' && ['soft','medium','strong','glow','none'].includes(src.shadow)) ? (src.shadow as any) : 'soft'}
                                        numberLocale={counterNumberLocale}
                                      /> ); })()}
                                  </Box>
                                )}
                                {(it as any)?.showList === true && Number.isInteger((it as any)?.listTabIndex) && (it as any).listTabIndex >= 0 && (it as any).listTabIndex < listTabs.length && (
                                  <Box sx={{ position: 'absolute', zIndex: 2, ...( ((it as any)?.listFullSize === true) ? { inset: 8 } : { left: '50%', top: '50%', transform: 'translate(-50%, -50%)', width: '88%' }) }}>
                                    {(() => {
                                      const tab = listTabs[(it as any).listTabIndex] as any;
                                      const title = typeof tab?.title === 'string' ? tab.title : '';
                                      const items = Array.isArray(tab?.items) ? tab.items : [];
                                      const size = Math.max(20, Math.min(160, Number(tab?.itemSize) || 40));
                                      const iconColor = typeof tab?.iconColor === 'string' ? tab.iconColor : undefined;
                                      const iconBg = typeof tab?.iconBg === 'string' ? tab.iconBg : undefined;
                                      return (
                                        <ListBuilder
                                          title={title}
                                          items={items}
                                          itemSize={size}
                                          iconColor={iconColor}
                                          iconBg={iconBg}
                                          showSettingsIcon
                                          fullSize={(it as any)?.listFullSize === true}
                                          scrollDir={(it as any)?.listScrollDir === 'horizontal' ? 'horizontal' : 'vertical'}
                                          onOpenSettings={() => setListSettingsDialog({ open: true, index: idx })}
                                        />
                                      );
                                    })()}
                                  </Box>
                                )}
                                <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 6, display: 'flex', gap: .5 }}>
                                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleTileEnabled(idx); }} sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}>
                                    {it?.enabled === false ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                  </IconButton>
                                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); setGridTiles(prev => prev.map((x, i) => i===idx ? { ...x, reserveSpace: x.reserveSpace === true ? false : true } : x)); }} sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }} title="Platz freihalten">
                                    {it?.reserveSpace ? <CropSquareIcon fontSize="small" /> : <CropSquareOutlinedIcon fontSize="small" />}
                                  </IconButton>
                                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); openTileEditor(idx); }} sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }} title="Bearbeiten">
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                                <Box sx={{ position: 'absolute', bottom: 8, right: 8, zIndex: 6 }}>
                                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); clearGridTile(idx); }} sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }} title="Kachel löschen">
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                                {/* Span controls on left/right edges */}
                                <Box sx={{ position: 'absolute', top: '50%', left: 6, transform: 'translateY(-50%)', zIndex: 6 }}>
                                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); expandTile(idx, -1); }} sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' }, p: 0.25 }} title="schmaler">
                                    <KeyboardArrowLeftIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                                <Box sx={{ position: 'absolute', top: '50%', right: 6, transform: 'translateY(-50%)', zIndex: 6 }}>
                                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); expandTile(idx, 1); }} sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' }, p: 0.25 }} title="breiter">
                                    <KeyboardArrowRightIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                                {/* Row span controls on top/bottom edges */}
                                <Box sx={{ position: 'absolute', top: 6, left: '50%', transform: 'translateX(-50%)', zIndex: 6 }}>
                                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); expandTileVertical(idx, -1); }} sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' }, p: 0.25 }} title="niedriger">
                                    <KeyboardArrowUpIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                                <Box sx={{ position: 'absolute', bottom: 6, left: '50%', transform: 'translateX(-50%)', zIndex: 6 }}>
                                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); expandTileVertical(idx, 1); }} sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' }, p: 0.25 }} title="höher">
                                    <KeyboardArrowDownIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                                {it?.enabled === false && it?.reserveSpace && (
                                  <Box sx={{ position: 'absolute', inset: 0, border: '2px dashed', borderColor: 'warning.light', borderRadius: 2 }} />
                                )}
                                {it?.hoverEffect && it.hoverEffect !== 'none' && (
                                  <Box sx={{ position: 'absolute', left: 8, bottom: 8, zIndex: 2, display: 'flex', alignItems: 'center', gap: .5, bgcolor: 'rgba(255,255,255,0.9)', color: 'text.primary', px: 1, py: .25, borderRadius: 999, boxShadow: 1 }}>
                                    <AnimationIcon sx={{ fontSize: 16 }} />
                                    <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'capitalize' }}>
                                      {it.hoverEffect === 'zoomGlow' ? 'Zoom + Glow' : it.hoverEffect}
                                    </Typography>
                                  </Box>
                                )}
                                {!!it?.ctaText && it?.ctaFree !== true && (
                                  <Box sx={{ position: 'absolute', left: 16, bottom: 16, zIndex: 2 }}>
                                    <Button variant="contained" size="small" href={it?.ctaUrl || '#'} sx={{
                                      bgcolor: (it as any)?.ctaBtnBg ? (it as any).ctaBtnBg : (imgUrl ? 'rgba(59,130,246,0.95)' : undefined),
                                      color: (it as any)?.ctaBtnTextColor || undefined,
                                      borderRadius: 9999,
                                      px: 1.5,
                                      minWidth: `${Math.max(80, Math.min(400, Number((it as any)?.ctaWidth) || 140))}px`,
                                      fontSize: `${Math.max(10, Math.min(36, Number((it as any)?.ctaFontSize) || 14))}px`,
                                      transition: 'transform .2s ease, box-shadow .2s ease, background-color .2s ease',
                                      ...(((it as any)?.ctaHoverEffect) === 'lift' && { '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 } }),
                                      ...(((it as any)?.ctaHoverEffect) === 'glow' && { '&:hover': { boxShadow: '0 10px 28px rgba(0,0,0,0.25)' } }),
                                      ...(((it as any)?.ctaHoverEffect) === 'zoom' && { '&:hover': { transform: 'scale(1.05)' } }),
                                      ...(((it as any)?.ctaHoverEffect) === 'zoomGlow' && { '&:hover': { transform: 'scale(1.05)', boxShadow: '0 12px 32px rgba(0,0,0,0.28)' } }),
                                    }}>
                                      {it.ctaText}
                                    </Button>
                                  </Box>
                                )}
                                {/* Content type selector */}
                                <Box className="tile-controls" sx={{ position: 'absolute', left: 8, bottom: (Math.max(1, it?.colSpan || 1) === 1) ? 56 : 16, zIndex: 4, opacity: 0, pointerEvents: 'none', transform: 'translateY(8px)', transition: 'opacity .15s ease, transform .15s ease' }} onClick={(e)=> e.stopPropagation()}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexDirection: (Math.max(1, it?.colSpan || 1) === 1) ? 'column' : 'row', bgcolor: 'rgba(255,255,255,0.92)', borderRadius: 1, px: 0.5, py: 0.25, boxShadow: 1, maxWidth: '68%', pointerEvents: 'auto' }}>
                                    <Select
                                      size="small"
                                      value={(it as any)?.showList ? 'list' : (it?.showCounter ? 'counter' : 'default')}
                                      onChange={(e) => {
                                        const v = String(e.target.value);
                                        setGridTiles(prev => prev.map((x, i) => {
                                          if (i !== idx) return x;
                                          if (v === 'list') { return { ...x, showList: true, listTabIndex: Number.isInteger((x as any)?.listTabIndex) ? (x as any).listTabIndex : (listTabs.length > 0 ? 0 : -1), showCounter: false }; }
                                          if (v === 'counter') { return { ...x, showCounter: true, showList: false }; }
                                          return { ...x, showCounter: false, showList: false };
                                        }));
                                      }}
                                      displayEmpty
                                      sx={{ minWidth: 120, '& .MuiSelect-select': { py: 0.5 } }}
                                    >
                                      <MenuItem value="default">Default</MenuItem>
                                      <MenuItem value="counter">Counter</MenuItem>
                                      <MenuItem value="list">List</MenuItem>
                                    </Select>
                                    {(it as any)?.showCounter && (
                                      <Select
                                        size="small"
                                        value={Number.isInteger((it as any)?.counterTabIndex) ? (it as any).counterTabIndex : -1}
                                        onChange={(e) => {
                                          const v = Number(e.target.value);
                                          setGridTiles(prev => prev.map((x, i) => i === idx ? { ...x, counterTabIndex: v } : x));
                                        }}
                                        sx={{ minWidth: 140, '& .MuiSelect-select': { py: 0.5 }, mt: (Math.max(1, it?.colSpan || 1) === 1) ? 0.5 : 0 }}
                                      >
                                        <MenuItem value={-1}>Globaler Counter</MenuItem>
                                        {counterTabs.map((tb, i2) => (
                                          <MenuItem key={`ct-opt-${i2}`} value={i2}>{tb.label || `Counter ${i2+1}`}</MenuItem>
                                        ))}
                                      </Select>
                                    )}
                                    {(it as any)?.showList && (
                                      <Select
                                        size="small"
                                        value={Number.isInteger((it as any)?.listTabIndex) ? (it as any).listTabIndex : -1}
                                        onChange={(e) => {
                                          const v = Number(e.target.value);
                                          setGridTiles(prev => prev.map((x, i) => i === idx ? { ...x, listTabIndex: v } : x));
                                        }}
                                        sx={{ minWidth: 140, '& .MuiSelect-select': { py: 0.5 }, mt: (Math.max(1, it?.colSpan || 1) === 1) ? 0.5 : 0 }}
                                      >
                                        <MenuItem value={-1} disabled>Liste wählen</MenuItem>
                                        {listTabs.map((tb, i2) => (
                                          <MenuItem key={`lt-opt-${i2}`} value={i2}>{tb.label || `Liste ${i2+1}`}</MenuItem>
                                        ))}
                                      </Select>
                                    )}
                                  </Box>
                                </Box>
                                {/* CTA free placement */}
                                {!!it?.ctaText && it?.ctaFree === true && (
                                  <Box sx={{ position: 'absolute', left: `${Math.max(0, Math.min(100, Number(it?.ctaX) || 15))}%`, top: `${Math.max(0, Math.min(100, Number(it?.ctaY) || 80))}%`, transform: 'translate(-50%, -50%)', zIndex: 2 }}>
                                    <Button variant="contained" size="small" href={it?.ctaUrl || '#'} sx={{
                                      bgcolor: (it as any)?.ctaBtnBg ? (it as any).ctaBtnBg : (imgUrl ? 'rgba(59,130,246,0.95)' : undefined),
                                      color: (it as any)?.ctaBtnTextColor || undefined,
                                      borderRadius: 9999,
                                      px: 1.5,
                                      minWidth: `${Math.max(80, Math.min(400, Number((it as any)?.ctaWidth) || 140))}px`,
                                      fontSize: `${Math.max(10, Math.min(36, Number((it as any)?.ctaFontSize) || 14))}px`,
                                      transform: `scale(${Math.max(0.5, Math.min(3, Number((it as any)?.ctaScale) || 1))})`,
                                      transformOrigin: 'center',
                                      transition: 'transform .2s ease, box-shadow .2s ease, background-color .2s ease',
                                      ...(((it as any)?.ctaHoverEffect) === 'lift' && { '&:hover': { transform: `translateY(-2px) scale(${Math.max(0.5, Math.min(3, Number((it as any)?.ctaScale) || 1))})`, boxShadow: 6 } }),
                                      ...(((it as any)?.ctaHoverEffect) === 'glow' && { '&:hover': { boxShadow: '0 10px 28px rgba(0,0,0,0.25)' } }),
                                      ...(((it as any)?.ctaHoverEffect) === 'zoom' && { '&:hover': { transform: `scale(${Math.max(0.5, Math.min(3, Number((it as any)?.ctaScale) || 1)) * 1.05})` } }),
                                      ...(((it as any)?.ctaHoverEffect) === 'zoomGlow' && { '&:hover': { transform: `scale(${Math.max(0.5, Math.min(3, Number((it as any)?.ctaScale) || 1)) * 1.05})`, boxShadow: '0 12px 32px rgba(0,0,0,0.28)' } }),
                                    }}>
                                      {it.ctaText}
                                    </Button>
                                  </Box>
                                )}
                                <Box sx={{ position: 'relative', zIndex: 1, color: imgUrl ? 'white' : color, p: 2, display: 'grid', gap: .5 }}>
                                  <Typography variant="subtitle1" fontWeight={800} sx={{ textShadow: imgUrl ? '0 1px 3px rgba(0,0,0,.5)' : 'none' }}>{it?.title || `Kachel ${idx+1}`}</Typography>
                                  {it?.subtitle && (
                                    <Typography variant="body2" sx={{ opacity: .9, textShadow: imgUrl ? '0 1px 2px rgba(0,0,0,.45)' : 'none' }}>{it.subtitle}</Typography>
                                  )}
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                        {/* Column helper guide lines (responsive, overlayed above tiles) */}
                        {/* xs: 2 columns */}
                        <Box sx={{ position: 'absolute', inset: 16, pointerEvents: 'none', zIndex: 60, display: { xs: 'block', sm: 'none' } }}>
                          <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: `${(1 / 2) * 100}%`, transform: 'translateX(-6px)', width: 12, pointerEvents: 'auto', cursor: 'col-resize', display: 'flex', alignItems: 'stretch', justifyContent: 'center' }}>
                            <Box sx={{ width: 2, bgcolor: '#3b82f6', opacity: 0.6, transition: 'all .12s ease', '&:hover': { opacity: 0.98, bgcolor: '#2563eb', boxShadow: '0 0 0 2px rgba(37,99,235,0.25)' } }} />
                          </Box>
                        </Box>
                        {/* sm: up to 3 columns */}
                        <Box sx={{ position: 'absolute', inset: 16, pointerEvents: 'none', zIndex: 60, display: { xs: 'none', sm: 'block', md: 'none' } }}>
                          {(() => { const cols = Math.max(2, Math.min(3, gridCols)); return Array.from({ length: Math.max(0, cols - 1) }).map((_, i) => (
                            <Box key={`guide-sm2-${i}`} sx={{ position: 'absolute', top: 0, bottom: 0, left: `${((i + 1) / cols) * 100}%`, transform: 'translateX(-6px)', width: 12, pointerEvents: 'auto', cursor: 'col-resize', display: 'flex', alignItems: 'stretch', justifyContent: 'center' }}>
                              <Box sx={{ width: 2, bgcolor: '#3b82f6', opacity: 0.6, transition: 'all .12s ease', '&:hover': { opacity: 0.98, bgcolor: '#2563eb', boxShadow: '0 0 0 2px rgba(37,99,235,0.25)' } }} />
                            </Box>
                          )); })()}
                        </Box>
                        {/* md+: gridCols columns with segmentation and draggable handles */}
                        {gridCols > 1 && (
                          <Box sx={{ position: 'absolute', inset: 16, pointerEvents: 'none', zIndex: 60, display: { xs: 'none', md: 'block' } }}>
                            {(() => {
                              const rows = Math.max(1, Number(gridRows) || 1);
                              const cols = Math.max(1, Number(gridCols) || 1);
                              const rowH = Math.max(80, Math.min(480, Number(gridRowHeight) || 160));
                              const gapPx = 12;
                              const total = rows * cols;
                              const tiles = Array.from({ length: total }).map((_, i) => gridTiles[i] || { colSpan: 1, rowSpan: 1 });
                              const widths = (gridColWidths.length === cols && gridColWidths.every(n => typeof n === 'number' && n > 0)) ? gridColWidths : Array.from({ length: cols }).map(() => 1);
                              const totalFr = widths.reduce((a, v) => a + v, 0) || cols;
                              const cumPerc: number[] = [];
                              let accFr = 0; for (let c = 0; c < cols; c++) { accFr += widths[c]; if (c < cols - 1) cumPerc[c + 1] = (accFr / totalFr) * 100; }
                              const mat: number[][] = Array.from({ length: rows }, () => Array(cols).fill(-1));
                              const fits = (r: number, c: number, w: number, h: number) => {
                                if (c + w > cols || r + h > rows) return false;
                                for (let rr = r; rr < r + h; rr++) { for (let cc = c; cc < c + w; cc++) { if (mat[rr][cc] !== -1) return false; } }
                                return true;
                              };
                              outer: for (let ti = 0; ti < tiles.length; ti++) {
                                const w = Math.max(1, Math.min(cols, Number((tiles[ti] as any)?.colSpan) || 1));
                                const h = Math.max(1, Math.min(rows, Number((tiles[ti] as any)?.rowSpan) || 1));
                                for (let r = 0; r < rows; r++) { for (let c = 0; c < cols; c++) { if (fits(r, c, w, h)) { for (let rr = r; rr < r + h; rr++) { for (let cc = c; cc < c + w; cc++) mat[rr][cc] = ti; } continue outer; } } }
                              }
                              const nodes: React.ReactElement[] = [];
                              for (let b = 1; b < cols; b++) {
                                let active = false; let segStart = 0;
                                for (let r = 0; r < rows; r++) {
                                  const blocked = mat[r][b - 1] === mat[r][b];
                                  if (blocked) {
                                    if (active) {
                                      const top = segStart * (rowH + gapPx);
                                      const height = r * (rowH + gapPx) - top - gapPx;
                                      if (height > 0) nodes.push(
                                        <Box key={`guide-md2-${b}-${segStart}`}
                                          onMouseDown={(e) => {
                                            e.preventDefault(); e.stopPropagation();
                                            const root = gridPreviewRef.current; if (!root) return;
                                            const rect = root.getBoundingClientRect();
                                            const start = [...gridColWidths];
                                            const minFr = 0.25;
                                            const pairSumFr = (start[b - 1] + start[b]) || 2;
                                            const totalFrAll = start.reduce((a, v) => a + v, 0) || cols;
                                            const sumBefore = start.slice(0, b - 1).reduce((a, v) => a + v, 0);
                                            const onMove = (ev: MouseEvent) => {
                                              let ratio = (ev.clientX - rect.left) / rect.width; if (!isFinite(ratio)) ratio = 0; ratio = Math.max(0, Math.min(1, ratio));
                                              const targetCumFr = ratio * totalFrAll;
                                              let leftNew = targetCumFr - sumBefore;
                                              const minLeft = minFr, maxLeft = Math.max(minFr, pairSumFr - minFr);
                                              if (leftNew < minLeft) leftNew = minLeft; if (leftNew > maxLeft) leftNew = maxLeft;
                                              const rightNew = Math.max(minFr, pairSumFr - leftNew);
                                              setGridColWidths(prev => { const cur = (prev.length === cols) ? [...prev] : [...start]; cur[b - 1] = leftNew; cur[b] = rightNew; return cur; });
                                            };
                                            const onUp = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
                                            window.addEventListener('mousemove', onMove);
                                            window.addEventListener('mouseup', onUp);
                                          }}
                                          sx={{ position: 'absolute', left: `${cumPerc[b]}%`, top: `${top}px`, height: `${height}px`, width: 12, transform: 'translateX(-6px)', cursor: 'col-resize', display: 'flex', alignItems: 'stretch', justifyContent: 'center', pointerEvents: 'auto' }}
                                        >
                                          <Box sx={{ width: 2, bgcolor: '#3b82f6', opacity: 0.6, transition: 'all .12s ease', '&:hover': { opacity: 0.98, bgcolor: '#2563eb', boxShadow: '0 0 0 2px rgba(37,99,235,0.25)' }, flex: '0 0 auto' }} />
                                        </Box>
                                      );
                                      active = false;
                                    }
                                  } else if (!active) { segStart = r; active = true; }
                                }
                                if (active) {
                                  const top = segStart * (rowH + gapPx);
                                  const height = rows * (rowH + gapPx) - top;
                                  nodes.push(
                                    <Box key={`guide-md2-${b}-${segStart}-end`}
                                      onMouseDown={(e) => {
                                        e.preventDefault(); e.stopPropagation();
                                        const root = gridPreviewRef.current; if (!root) return;
                                        const rect = root.getBoundingClientRect();
                                        const start = [...gridColWidths];
                                        const minFr = 0.25;
                                        const pairSumFr = (start[b - 1] + start[b]) || 2;
                                        const totalFrAll = start.reduce((a, v) => a + v, 0) || cols;
                                        const sumBefore = start.slice(0, b - 1).reduce((a, v) => a + v, 0);
                                        const onMove = (ev: MouseEvent) => {
                                          let ratio = (ev.clientX - rect.left) / rect.width; if (!isFinite(ratio)) ratio = 0; ratio = Math.max(0, Math.min(1, ratio));
                                          const targetCumFr = ratio * totalFrAll;
                                          let leftNew = targetCumFr - sumBefore;
                                          const minLeft = minFr, maxLeft = Math.max(minFr, pairSumFr - minFr);
                                          if (leftNew < minLeft) leftNew = minLeft; if (leftNew > maxLeft) leftNew = maxLeft;
                                          const rightNew = Math.max(minFr, pairSumFr - leftNew);
                                          setGridColWidths(prev => { const cur = (prev.length === cols) ? [...prev] : [...start]; cur[b - 1] = leftNew; cur[b] = rightNew; return cur; });
                                        };
                                        const onUp = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
                                        window.addEventListener('mousemove', onMove);
                                        window.addEventListener('mouseup', onUp);
                                      }}
                                      sx={{ position: 'absolute', left: `${cumPerc[b]}%`, top: `${top}px`, height: `${height}px`, width: 12, transform: 'translateX(-6px)', cursor: 'col-resize', display: 'flex', alignItems: 'stretch', justifyContent: 'center', pointerEvents: 'auto' }}
                                    >
                                      <Box sx={{ width: 2, bgcolor: '#3b82f6', opacity: 0.6, transition: 'all .12s ease', '&:hover': { opacity: 0.98, bgcolor: '#2563eb', boxShadow: '0 0 0 2px rgba(37,99,235,0.25)' }, flex: '0 0 auto' }} />
                                    </Box>
                                  );
                                }
                              }
                              return nodes;
                            })()}
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ mt: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                        <Button variant="contained" onClick={saveGrid} disabled={savingGrid || deletingGrid} sx={{ textTransform: 'none', fontWeight: 700 }}>{savingGrid ? 'Speichern…' : 'Einstellungen speichern'}</Button>
                        <Button variant="outlined" color="error" onClick={deleteGridSection} disabled={deletingGrid || savingGrid} sx={{ textTransform: 'none', fontWeight: 700 }}>
                          {deletingGrid ? 'Lösche…' : 'Grid löschen'}
                        </Button>
                      </Box>
                            </Collapse>
                            </CardContent>
                          </Card>
                          {renderDropZone(sectionIndex + 1)}
                        </React.Fragment>
                      )}
                    />
                  );
                })}
                </React.Fragment>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPageBuilderOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      {/* Hero editor dialog */}
      <Dialog open={heroEditor.open} onClose={() => setHeroEditor(prev => ({ ...prev, open: false, sectionId: null }))} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 900 }}>Hero Section bearbeiten</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gap: 2 }}>
            <FormControlLabel
              control={<Switch checked={heroEditor.enabled} onChange={(_, v) => setHeroEditor(prev => ({ ...prev, enabled: v }))} />}
              label="Aktiv"
            />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
              <Tabs
                value={heroLang}
                onChange={(_, v) => setHeroLang(String(v || 'de'))}
                variant="scrollable"
                scrollButtons
                allowScrollButtonsMobile
                sx={{ minHeight: 38, '& .MuiTab-root': { minHeight: 38, textTransform: 'none', fontWeight: 900 } }}
              >
                {Object.keys((heroEditor.title && typeof heroEditor.title === 'object') ? (heroEditor.title as any) : { de: true })
                  .filter((k) => typeof k === 'string' && k)
                  .map((k) => (
                    <Tab
                      key={`hero-lang-${k}`}
                      value={k}
                      label={(
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Box component="span" sx={{ fontSize: 18, lineHeight: 1 }}>{heroCarouselFlag(k)}</Box>
                          <Box component="span">{String(k).toUpperCase()}</Box>
                        </Box>
                      )}
                    />
                  ))}
              </Tabs>
              <Button
                variant="outlined"
                size="small"
                onClick={(e) => setHeroLangAnchor(e.currentTarget)}
                sx={{ textTransform: 'none', fontWeight: 900, borderRadius: 999 }}
              >
                + Sprache
              </Button>
              <Popover
                open={!!heroLangAnchor}
                anchorEl={heroLangAnchor}
                onClose={() => setHeroLangAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <Box sx={{ p: 1, minWidth: 220 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>Sprache hinzufügen</Typography>
                  {(['de','en','tr','fr','es','it','pt','ar','hi','zh'] as string[]).map((code) => {
                    const existing = (heroEditor.title && typeof heroEditor.title === 'object')
                      ? Object.prototype.hasOwnProperty.call(heroEditor.title as any, code)
                      : (code === 'de');
                    return (
                      <MenuItem
                        key={`hero-addlang-${code}`}
                        disabled={existing}
                        onClick={() => {
                          const baseLang = heroLang || 'de';
                          setHeroEditor((prev) => ({
                            ...prev,
                            title: setLocalizedString(
                              (typeof prev.title === 'string') ? ({ [baseLang]: prev.title } as any) : prev.title,
                              code,
                              getLocalizedString(prev.title, code, baseLang, '')
                            ),
                            subtitle: setLocalizedString(
                              (typeof prev.subtitle === 'string') ? ({ [baseLang]: prev.subtitle } as any) : prev.subtitle,
                              code,
                              getLocalizedString(prev.subtitle, code, baseLang, '')
                            ),
                            ctaText: setLocalizedString(
                              (typeof prev.ctaText === 'string') ? ({ [baseLang]: prev.ctaText } as any) : prev.ctaText,
                              code,
                              getLocalizedString(prev.ctaText, code, baseLang, '')
                            ),
                          }));
                          setHeroLang(code);
                          setHeroLangAnchor(null);
                        }}
                        sx={{ fontWeight: 800 }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box component="span" sx={{ fontSize: 18, lineHeight: 1 }}>{heroCarouselFlag(code)}</Box>
                          <Box component="span">{code.toUpperCase()}</Box>
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Box>
              </Popover>
            </Box>

            <TextField
              label={`Titel (${heroLang.toUpperCase()})`}
              value={getLocalizedString(heroEditor.title, heroLang, 'de', '')}
              onChange={(e) => setHeroEditor(prev => ({ ...prev, title: setLocalizedString(prev.title, heroLang, e.target.value) }))}
              fullWidth
            />
            <TextField
              label={`Untertitel (${heroLang.toUpperCase()})`}
              value={getLocalizedString(heroEditor.subtitle, heroLang, 'de', '')}
              onChange={(e) => setHeroEditor(prev => ({ ...prev, subtitle: setLocalizedString(prev.subtitle, heroLang, e.target.value) }))}
              fullWidth
              multiline
              minRows={2}
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField
                label={`CTA Text (${heroLang.toUpperCase()})`}
                value={getLocalizedString(heroEditor.ctaText, heroLang, 'de', '')}
                onChange={(e) => setHeroEditor(prev => ({ ...prev, ctaText: setLocalizedString(prev.ctaText, heroLang, e.target.value) }))}
                fullWidth
              />
              <TextField label="CTA URL" value={heroEditor.ctaUrl} onChange={(e) => setHeroEditor(prev => ({ ...prev, ctaUrl: e.target.value }))} fullWidth placeholder="/shop oder https://…" />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel id="hero-align-label">Ausrichtung</InputLabel>
                <Select
                  labelId="hero-align-label"
                  label="Ausrichtung"
                  value={heroEditor.align}
                  onChange={(e) => setHeroEditor(prev => ({ ...prev, align: (e.target.value === 'left' ? 'left' : 'center') }))}
                >
                  <MenuItem value="center">Zentriert</MenuItem>
                  <MenuItem value="left">Links</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Min Height"
                type="number"
                value={heroEditor.minHeight}
                onChange={(e) => setHeroEditor(prev => ({ ...prev, minHeight: Math.max(360, Math.min(900, Number(e.target.value) || 520)) }))}
                fullWidth
                inputProps={{ min: 360, max: 900 }}
              />
            </Box>
            <Divider />
            <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Hintergrundbild</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button variant="outlined" onClick={() => setHeroBgPickerOpen(true)} sx={{ textTransform: 'none', fontWeight: 800 }}>
                Bild auswählen
              </Button>
              <Button
                variant="text"
                color="error"
                onClick={() => setHeroEditor(prev => ({ ...prev, backgroundImage: '' }))}
                disabled={!heroEditor.backgroundImage}
                sx={{ textTransform: 'none', fontWeight: 800 }}
              >
                Entfernen
              </Button>
            </Box>
            {heroEditor.backgroundImage ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box component="img" src={toPublicUrl(heroEditor.backgroundImage)} alt="Hero" sx={{ width: 110, height: 80, borderRadius: 2, objectFit: 'cover', border: '1px solid', borderColor: 'divider' }} />
                <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>{heroEditor.backgroundImage}</Typography>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">Kein Bild ausgewählt.</Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHeroEditor(prev => ({ ...prev, open: false, sectionId: null }))}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={() => {
              const sectionId = heroEditor.sectionId;
              if (!sectionId) {
                setHeroEditor(prev => ({ ...prev, open: false, sectionId: null }));
                return;
              }
              setBuilderSections((prev) => updateSectionById(prev, String(sectionId), (s: any) => ({
                ...s,
                type: 'hero',
                title: 'Hero',
                config: {
                  enabled: heroEditor.enabled,
                  title: heroEditor.title,
                  subtitle: heroEditor.subtitle,
                  ctaText: heroEditor.ctaText,
                  ctaUrl: heroEditor.ctaUrl,
                  backgroundImage: heroEditor.backgroundImage,
                  align: heroEditor.align,
                  minHeight: heroEditor.minHeight,
                },
              })).next);
              markDirty(sectionId);
              setHeroEditor(prev => ({ ...prev, open: false, sectionId: null }));
            }}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      <MediaPickerModal
        open={heroBgPickerOpen}
        onClose={() => setHeroBgPickerOpen(false)}
        onSelect={(it: MediaItem) => {
          setHeroEditor(prev => ({ ...prev, backgroundImage: normalizePickedUrl(it.url) }));
          setHeroBgPickerOpen(false);
        }}
      />

      {/* Hero Carousel editor dialog */}
      <Dialog open={heroCarouselEditor.open} onClose={() => setHeroCarouselEditor(prev => ({ ...prev, open: false, sectionId: null }))} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 900 }}>Hero Carousel bearbeiten</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gap: 2 }}>
            <FormControlLabel control={<Switch checked={heroCarouselEditor.enabled} onChange={(_, v) => setHeroCarouselEditor(prev => ({ ...prev, enabled: v }))} />} label="Aktiv" />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
              <Tabs
                value={heroCarouselLang}
                onChange={(_, v) => setHeroCarouselLang(String(v || 'de'))}
                variant="scrollable"
                scrollButtons
                allowScrollButtonsMobile
                sx={{ minHeight: 38, '& .MuiTab-root': { minHeight: 38, textTransform: 'none', fontWeight: 900 } }}
              >
                {Object.keys((heroCarouselEditor.title && typeof heroCarouselEditor.title === 'object') ? (heroCarouselEditor.title as any) : { de: true })
                  .filter((k) => typeof k === 'string' && k)
                  .map((k) => (
                    <Tab
                      key={`hc-lang-${k}`}
                      value={k}
                      label={(
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Box component="span" sx={{ fontSize: 18, lineHeight: 1 }}>{heroCarouselFlag(k)}</Box>
                          <Box component="span">{String(k).toUpperCase()}</Box>
                        </Box>
                      )}
                    />
                  ))}
              </Tabs>
              <Button
                variant="outlined"
                size="small"
                onClick={(e) => setHeroCarouselLangAnchor(e.currentTarget)}
                sx={{ textTransform: 'none', fontWeight: 900, borderRadius: 999 }}
              >
                + Sprache
              </Button>
              <Popover
                open={!!heroCarouselLangAnchor}
                anchorEl={heroCarouselLangAnchor}
                onClose={() => setHeroCarouselLangAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <Box sx={{ p: 1, minWidth: 220 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>Sprache hinzufügen</Typography>
                  {(['de','en','tr','fr','es','it','pt','ar','hi','zh'] as string[]).map((code) => {
                    const existing = (heroCarouselEditor.title && typeof heroCarouselEditor.title === 'object') ? Object.prototype.hasOwnProperty.call(heroCarouselEditor.title as any, code) : (code === 'de');
                    return (
                      <MenuItem
                        key={`hc-addlang-${code}`}
                        disabled={existing}
                        onClick={() => {
                          const baseLang = heroCarouselLang || 'de';
                          setHeroCarouselEditor((prev) => ({
                            ...prev,
                            title: setLocalizedString(
                              (typeof prev.title === 'string') ? ({ [baseLang]: prev.title } as any) : prev.title,
                              code,
                              getLocalizedString(prev.title, code, baseLang, '')
                            ),
                            subtitle: setLocalizedString(
                              (typeof prev.subtitle === 'string') ? ({ [baseLang]: prev.subtitle } as any) : prev.subtitle,
                              code,
                              getLocalizedString(prev.subtitle, code, baseLang, '')
                            ),
                            ctaText: setLocalizedString(
                              (typeof prev.ctaText === 'string') ? ({ [baseLang]: prev.ctaText } as any) : prev.ctaText,
                              code,
                              getLocalizedString(prev.ctaText, code, baseLang, '')
                            ),
                          }));
                          setHeroCarouselLang(code);
                          setHeroCarouselLangAnchor(null);
                        }}
                        sx={{ fontWeight: 800 }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box component="span" sx={{ fontSize: 18, lineHeight: 1 }}>{heroCarouselFlag(code)}</Box>
                          <Box component="span">{code.toUpperCase()}</Box>
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Box>
              </Popover>
            </Box>

            <TextField
              label={`Titel (${heroCarouselLang.toUpperCase()})`}
              value={getLocalizedString(heroCarouselEditor.title, heroCarouselLang, 'de', '')}
              onChange={(e) => setHeroCarouselEditor(prev => ({ ...prev, title: setLocalizedString(prev.title, heroCarouselLang, e.target.value) }))}
              fullWidth
            />
            <TextField
              label={`Untertitel (${heroCarouselLang.toUpperCase()})`}
              value={getLocalizedString(heroCarouselEditor.subtitle, heroCarouselLang, 'de', '')}
              onChange={(e) => setHeroCarouselEditor(prev => ({ ...prev, subtitle: setLocalizedString(prev.subtitle, heroCarouselLang, e.target.value) }))}
              fullWidth
              multiline
              minRows={2}
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField
                label={`CTA Text (${heroCarouselLang.toUpperCase()})`}
                value={getLocalizedString(heroCarouselEditor.ctaText, heroCarouselLang, 'de', '')}
                onChange={(e) => setHeroCarouselEditor(prev => ({ ...prev, ctaText: setLocalizedString(prev.ctaText, heroCarouselLang, e.target.value) }))}
                fullWidth
              />
              <TextField label="CTA URL" value={heroCarouselEditor.ctaUrl} onChange={(e) => setHeroCarouselEditor(prev => ({ ...prev, ctaUrl: e.target.value }))} fullWidth placeholder="/shop oder https://…" />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel id="hero-carousel-align">Text Position</InputLabel>
                <Select labelId="hero-carousel-align" label="Text Position" value={heroCarouselEditor.align} onChange={(e) => setHeroCarouselEditor(prev => ({ ...prev, align: (e.target.value === 'right' ? 'right' : 'left') }))}>
                  <MenuItem value="left">Links</MenuItem>
                  <MenuItem value="right">Rechts</MenuItem>
                </Select>
              </FormControl>
              <TextField label="Min Height" type="number" value={heroCarouselEditor.minHeight} onChange={(e) => setHeroCarouselEditor(prev => ({ ...prev, minHeight: Math.max(360, Math.min(900, Number(e.target.value) || 520)) }))} fullWidth inputProps={{ min: 360, max: 900 }} />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr 1fr' }, gap: 2 }}>
              <FormControlLabel control={<Switch checked={heroCarouselEditor.autoPlay} onChange={(_, v) => setHeroCarouselEditor(prev => ({ ...prev, autoPlay: v }))} />} label="Autoplay" />
              <FormControlLabel control={<Switch checked={heroCarouselEditor.showArrows} onChange={(_, v) => setHeroCarouselEditor(prev => ({ ...prev, showArrows: v }))} />} label="Arrows" />
              <FormControlLabel control={<Switch checked={heroCarouselEditor.showDots} onChange={(_, v) => setHeroCarouselEditor(prev => ({ ...prev, showDots: v }))} />} label="Dots" />
              <TextField label="Autoplay (ms)" type="number" value={heroCarouselEditor.autoPlayInterval} onChange={(e) => setHeroCarouselEditor(prev => ({ ...prev, autoPlayInterval: Math.max(1500, Math.min(20000, Number(e.target.value) || 4500)) }))} fullWidth />
            </Box>

            <Divider />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Bilder</Typography>
              <Button variant="outlined" onClick={() => setHeroCarouselPickerOpen(true)} sx={{ textTransform: 'none', fontWeight: 800 }}>
                Bild hinzufügen
              </Button>
            </Box>

            {heroCarouselEditor.images.length === 0 ? (
              <Typography variant="body2" color="text.secondary">Noch keine Bilder.</Typography>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)', md: 'repeat(6, 1fr)' }, gap: 1.5 }}>
                {heroCarouselEditor.images.map((p, idx) => (
                  <Box key={`${p}-${idx}`} sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider', aspectRatio: '1 / 1', bgcolor: 'grey.100' }}>
                    <Box component="img" src={toPublicUrl(p)} alt={`hc-${idx+1}`} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    <IconButton
                      size="small"
                      onClick={() => setHeroCarouselEditor(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== idx) }))}
                      sx={{ position: 'absolute', top: 6, right: 6, bgcolor: 'rgba(0,0,0,0.55)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' } }}
                      title="Entfernen"
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHeroCarouselEditor(prev => ({ ...prev, open: false, sectionId: null }))}>Abbrechen</Button>
          <Button variant="contained" onClick={() => {
            const sectionId = heroCarouselEditor.sectionId;
            if (!sectionId) { setHeroCarouselEditor(prev => ({ ...prev, open: false, sectionId: null })); return; }
            setBuilderSections((prev) => updateSectionById(prev, String(sectionId), (s: any) => ({
              ...s,
              type: 'herocarousel',
              title: 'Hero Carousel',
              config: {
                enabled: heroCarouselEditor.enabled,
                title: heroCarouselEditor.title,
                subtitle: heroCarouselEditor.subtitle,
                ctaText: heroCarouselEditor.ctaText,
                ctaUrl: heroCarouselEditor.ctaUrl,
                align: heroCarouselEditor.align,
                minHeight: heroCarouselEditor.minHeight,
                autoPlay: heroCarouselEditor.autoPlay,
                autoPlayInterval: heroCarouselEditor.autoPlayInterval,
                showArrows: heroCarouselEditor.showArrows,
                showDots: heroCarouselEditor.showDots,
                images: heroCarouselEditor.images,
              },
            })).next);
            markDirty(sectionId);
            setHeroCarouselEditor(prev => ({ ...prev, open: false, sectionId: null }));
          }}>Speichern</Button>
        </DialogActions>
      </Dialog>

      <MediaPickerModal
        open={heroCarouselPickerOpen}
        onClose={() => setHeroCarouselPickerOpen(false)}
        onSelect={(it: MediaItem) => {
          const path = normalizePickedUrl(it.url);
          setHeroCarouselEditor(prev => ({ ...prev, images: [...prev.images, path] }));
          setHeroCarouselPickerOpen(false);
        }}
      />

      <Dialog open={productsEditor.open} onClose={() => setProductsEditor(prev => ({ ...prev, open: false, sectionId: null }))} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 900 }}>Produkte bearbeiten</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gap: 2 }}>
            <FormControlLabel control={<Switch checked={productsEditor.enabled} onChange={(_, v) => setProductsEditor(prev => ({ ...prev, enabled: v }))} />} label="Aktiv" />
            <TextField label="Überschrift" value={productsEditor.heading} onChange={(e) => setProductsEditor(prev => ({ ...prev, heading: e.target.value }))} fullWidth />
            <TextField label="Untertitel" value={productsEditor.subtitle} onChange={(e) => setProductsEditor(prev => ({ ...prev, subtitle: e.target.value }))} fullWidth />
            <TextField label="Label auf Bild (optional)" value={productsEditor.badgeText} onChange={(e) => setProductsEditor(prev => ({ ...prev, badgeText: e.target.value }))} fullWidth />

            <Box sx={{ display: 'grid', gap: 0.75 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Filter</Typography>
              <Typography variant="caption" color="text.secondary">
                Wenn Produkt-IDs gesetzt sind, werden exakt diese Produkte (in dieser Reihenfolge) angezeigt.
                Andernfalls greifen Tags/Keywords.
              </Typography>
              <TextField
                label="Produkt-IDs (optional, Komma oder Zeilenumbruch)"
                value={productsEditor.productIdsText}
                onChange={(e) => setProductsEditor(prev => ({ ...prev, productIdsText: e.target.value }))}
                fullWidth
                multiline
                minRows={2}
              />
              <TextField
                label="Tags (optional, Komma getrennt)"
                value={productsEditor.tagsText}
                onChange={(e) => setProductsEditor(prev => ({ ...prev, tagsText: e.target.value }))}
                fullWidth
              />
              <TextField
                label="Name Keywords (optional, Komma getrennt)"
                value={productsEditor.keywordsText}
                onChange={(e) => setProductsEditor(prev => ({ ...prev, keywordsText: e.target.value }))}
                fullWidth
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField label="Anzahl" type="number" value={productsEditor.limit} onChange={(e) => setProductsEditor(prev => ({ ...prev, limit: Math.max(2, Math.min(30, Number(e.target.value) || 12)) }))} fullWidth inputProps={{ min: 2, max: 30 }} />
              <TextField label="Ecken-Radius" type="number" value={productsEditor.radius} onChange={(e) => setProductsEditor(prev => ({ ...prev, radius: Math.max(0, Math.min(32, Number(e.target.value) || 0)) }))} fullWidth inputProps={{ min: 0, max: 32 }} />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel id="products-sort-by">Sortierung</InputLabel>
                <Select labelId="products-sort-by" label="Sortierung" value={productsEditor.sortBy} onChange={(e) => setProductsEditor(prev => ({ ...prev, sortBy: (e.target.value as any) }))}>
                  <MenuItem value="created_at">Neueste</MenuItem>
                  <MenuItem value="favorite_count">Favoriten</MenuItem>
                  <MenuItem value="view_count">Views</MenuItem>
                  <MenuItem value="price">Preis</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="products-sort-order">Reihenfolge</InputLabel>
                <Select labelId="products-sort-order" label="Reihenfolge" value={productsEditor.sortOrder} onChange={(e) => setProductsEditor(prev => ({ ...prev, sortOrder: (e.target.value === 'asc' ? 'asc' : 'desc') }))}>
                  <MenuItem value="desc">Absteigend</MenuItem>
                  <MenuItem value="asc">Aufsteigend</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'grid', gap: 0.5 }}>
              <Typography variant="caption" color="text.secondary">Radius Slider</Typography>
              <Slider value={productsEditor.radius} onChange={(_, v) => setProductsEditor(prev => ({ ...prev, radius: Math.max(0, Math.min(32, Number(v) || 0)) }))} min={0} max={32} step={1} />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProductsEditor(prev => ({ ...prev, open: false, sectionId: null }))}>Abbrechen</Button>
          <Button variant="contained" onClick={() => {
            const sectionId = productsEditor.sectionId;
            if (!sectionId) { setProductsEditor(prev => ({ ...prev, open: false, sectionId: null })); return; }
            setBuilderSections((prev) => updateSectionById(prev, String(sectionId), (s: any) => ({
              ...s,
              type: 'products',
              title: 'Products',
              config: {
                enabled: productsEditor.enabled,
                heading: productsEditor.heading,
                subtitle: productsEditor.subtitle,
                badgeText: productsEditor.badgeText,
                radius: productsEditor.radius,
                limit: productsEditor.limit,
                sortBy: productsEditor.sortBy,
                sortOrder: productsEditor.sortOrder,
                productIds: productsEditor.productIdsText.split(/\r?\n|,/g).map((x: any) => String(x || '').trim()).filter(Boolean),
                tags: productsEditor.tagsText.split(/\r?\n|,/g).map((x: any) => String(x || '').trim()).filter(Boolean),
                nameKeywords: productsEditor.keywordsText.split(/\r?\n|,/g).map((x: any) => String(x || '').trim()).filter(Boolean),
              },
            })).next);
            markDirty(sectionId);
            setProductsEditor(prev => ({ ...prev, open: false, sectionId: null }));
          }}>Speichern</Button>
        </DialogActions>
      </Dialog>

      {/* Gallery editor dialog */}
      <Dialog open={galleryEditor.open} onClose={() => setGalleryEditor(prev => ({ ...prev, open: false, sectionId: null }))} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 900 }}>Gallery bearbeiten</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gap: 2 }}>
            <FormControlLabel
              control={<Switch checked={galleryEditor.enabled} onChange={(_, v) => setGalleryEditor(prev => ({ ...prev, enabled: v }))} />}
              label="Aktiv"
            />
            <TextField label="Titel" value={galleryEditor.title} onChange={(e) => setGalleryEditor(prev => ({ ...prev, title: e.target.value }))} fullWidth />
            <TextField label="Untertitel" value={galleryEditor.subtitle} onChange={(e) => setGalleryEditor(prev => ({ ...prev, subtitle: e.target.value }))} fullWidth />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel id="gal-layout">Layout</InputLabel>
                <Select labelId="gal-layout" label="Layout" value={galleryEditor.layout} onChange={(e) => setGalleryEditor(prev => ({ ...prev, layout: (e.target.value === 'masonry' ? 'masonry' : 'grid') }))}>
                  <MenuItem value="grid">Grid</MenuItem>
                  <MenuItem value="masonry">Masonry</MenuItem>
                </Select>
              </FormControl>
              <TextField label="Aspect Ratio" value={galleryEditor.aspectRatio} onChange={(e) => setGalleryEditor(prev => ({ ...prev, aspectRatio: e.target.value }))} placeholder="1 / 1, 4 / 3, 16 / 9" fullWidth />
              <FormControl fullWidth>
                <InputLabel id="gal-shadow">Shadow</InputLabel>
                <Select labelId="gal-shadow" label="Shadow" value={galleryEditor.shadow} onChange={(e) => setGalleryEditor(prev => ({ ...prev, shadow: (e.target.value === 'none' ? 'none' : (e.target.value === 'medium' ? 'medium' : 'soft')) }))}>
                  <MenuItem value="soft">Soft</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="none">None</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
              <TextField label="Columns XS" type="number" value={galleryEditor.xsCols} onChange={(e) => setGalleryEditor(prev => ({ ...prev, xsCols: Math.max(1, Math.min(4, Number(e.target.value) || 2)) }))} fullWidth />
              <TextField label="Columns SM" type="number" value={galleryEditor.smCols} onChange={(e) => setGalleryEditor(prev => ({ ...prev, smCols: Math.max(1, Math.min(6, Number(e.target.value) || 3)) }))} fullWidth />
              <TextField label="Columns MD" type="number" value={galleryEditor.mdCols} onChange={(e) => setGalleryEditor(prev => ({ ...prev, mdCols: Math.max(1, Math.min(8, Number(e.target.value) || 4)) }))} fullWidth />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
              <TextField label="Gap" type="number" value={galleryEditor.gap} onChange={(e) => setGalleryEditor(prev => ({ ...prev, gap: Math.max(0, Math.min(48, Number(e.target.value) || 12)) }))} fullWidth />
              <TextField label="Radius" type="number" value={galleryEditor.radius} onChange={(e) => setGalleryEditor(prev => ({ ...prev, radius: Math.max(0, Math.min(28, Number(e.target.value) || 16)) }))} fullWidth />
              <FormControlLabel control={<Switch checked={galleryEditor.lightboxEnabled} onChange={(_, v) => setGalleryEditor(prev => ({ ...prev, lightboxEnabled: v }))} />} label="Lightbox" />
            </Box>

            <Divider />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Bilder</Typography>
              <Button variant="outlined" onClick={() => setGalleryItemPickerOpen(true)} sx={{ textTransform: 'none', fontWeight: 800 }}>
                Bild hinzufügen
              </Button>
            </Box>

            {galleryEditor.items.length === 0 ? (
              <Typography variant="body2" color="text.secondary">Noch keine Bilder.</Typography>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)', md: 'repeat(6, 1fr)' }, gap: 1.5 }}>
                {galleryEditor.items.map((it, idx) => (
                  <Box key={`${it.image}-${idx}`} sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider', aspectRatio: '1 / 1', bgcolor: 'grey.100' }}>
                    <Box component="img" src={toPublicUrl(it.image)} alt={it.caption || `img-${idx+1}`} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    <IconButton
                      size="small"
                      onClick={() => setGalleryEditor(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }))}
                      sx={{ position: 'absolute', top: 6, right: 6, bgcolor: 'rgba(0,0,0,0.55)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' } }}
                      title="Entfernen"
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGalleryEditor(prev => ({ ...prev, open: false, sectionId: null }))}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={() => {
              const sectionId = galleryEditor.sectionId;
              if (!sectionId) {
                setGalleryEditor(prev => ({ ...prev, open: false, sectionId: null }));
                return;
              }
              setBuilderSections((prev) => updateSectionById(prev, String(sectionId), (s: any) => ({
                ...s,
                type: 'gallery',
                title: 'Gallery',
                config: {
                  enabled: galleryEditor.enabled,
                  title: galleryEditor.title,
                  subtitle: galleryEditor.subtitle,
                  layout: galleryEditor.layout,
                  aspectRatio: galleryEditor.aspectRatio,
                  gap: galleryEditor.gap,
                  radius: galleryEditor.radius,
                  shadow: galleryEditor.shadow,
                  lightboxEnabled: galleryEditor.lightboxEnabled,
                  columns: { xs: galleryEditor.xsCols, sm: galleryEditor.smCols, md: galleryEditor.mdCols },
                  items: galleryEditor.items.map((it: any) => ({ image: String(it?.image || ''), caption: String(it?.caption || ''), linkUrl: String(it?.linkUrl || '') })).filter((it: any) => it.image),
                },
              })).next);
              markDirty(sectionId);
              setGalleryEditor(prev => ({ ...prev, open: false, sectionId: null }));
            }}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      <MediaPickerModal
        open={galleryItemPickerOpen}
        onClose={() => setGalleryItemPickerOpen(false)}
        onSelect={(it: MediaItem) => {
          const path = normalizePickedUrl(it.url);
          setGalleryEditor(prev => ({ ...prev, items: [...prev.items, { image: path }] }));
          setGalleryItemPickerOpen(false);
        }}
      />

      {/* CTA editor dialog */}
      <Dialog open={ctaEditor.open} onClose={() => setCtaEditor(prev => ({ ...prev, open: false, sectionId: null }))} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 900 }}>CTA bearbeiten</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gap: 2 }}>
            <FormControlLabel
              control={<Switch checked={ctaEditor.enabled} onChange={(_, v) => setCtaEditor(prev => ({ ...prev, enabled: v }))} />}
              label="Aktiv"
            />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
              <Tabs
                value={ctaLang}
                onChange={(_, v) => setCtaLang(String(v || 'de'))}
                variant="scrollable"
                scrollButtons
                allowScrollButtonsMobile
                sx={{ minHeight: 38, '& .MuiTab-root': { minHeight: 38, textTransform: 'none', fontWeight: 900 } }}
              >
                {Object.keys((ctaEditor.title && typeof ctaEditor.title === 'object') ? (ctaEditor.title as any) : { de: true })
                  .filter((k) => typeof k === 'string' && k)
                  .map((k) => (
                    <Tab
                      key={`cta-lang-${k}`}
                      value={k}
                      label={(
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Box component="span" sx={{ fontSize: 18, lineHeight: 1 }}>{heroCarouselFlag(k)}</Box>
                          <Box component="span">{String(k).toUpperCase()}</Box>
                        </Box>
                      )}
                    />
                  ))}
              </Tabs>
              <Button
                variant="outlined"
                size="small"
                onClick={(e) => setCtaLangAnchor(e.currentTarget)}
                sx={{ textTransform: 'none', fontWeight: 900, borderRadius: 999 }}
              >
                + Sprache
              </Button>
              <Popover
                open={!!ctaLangAnchor}
                anchorEl={ctaLangAnchor}
                onClose={() => setCtaLangAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <Box sx={{ p: 1, minWidth: 220 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>Sprache hinzufügen</Typography>
                  {(['de','en','tr','fr','es','it','pt','ar','hi','zh'] as string[]).map((code) => {
                    const existing = (ctaEditor.title && typeof ctaEditor.title === 'object')
                      ? Object.prototype.hasOwnProperty.call(ctaEditor.title as any, code)
                      : (code === 'de');
                    return (
                      <MenuItem
                        key={`cta-addlang-${code}`}
                        disabled={existing}
                        onClick={() => {
                          const baseLang = ctaLang || 'de';
                          setCtaEditor((prev) => ({
                            ...prev,
                            title: setLocalizedString(
                              (typeof prev.title === 'string') ? ({ [baseLang]: prev.title } as any) : prev.title,
                              code,
                              getLocalizedString(prev.title, code, baseLang, '')
                            ),
                            text: setLocalizedString(
                              (typeof prev.text === 'string') ? ({ [baseLang]: prev.text } as any) : prev.text,
                              code,
                              getLocalizedString(prev.text, code, baseLang, '')
                            ),
                            buttonText: setLocalizedString(
                              (typeof prev.buttonText === 'string') ? ({ [baseLang]: prev.buttonText } as any) : prev.buttonText,
                              code,
                              getLocalizedString(prev.buttonText, code, baseLang, '')
                            ),
                          }));
                          setCtaLang(code);
                          setCtaLangAnchor(null);
                        }}
                        sx={{ fontWeight: 800 }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box component="span" sx={{ fontSize: 18, lineHeight: 1 }}>{heroCarouselFlag(code)}</Box>
                          <Box component="span">{code.toUpperCase()}</Box>
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Box>
              </Popover>
            </Box>

            <TextField
              label={`Titel (${ctaLang.toUpperCase()})`}
              value={getLocalizedString(ctaEditor.title, ctaLang, 'de', '')}
              onChange={(e) => setCtaEditor(prev => ({ ...prev, title: setLocalizedString(prev.title, ctaLang, e.target.value) }))}
              fullWidth
            />
            <TextField
              label={`Text (${ctaLang.toUpperCase()})`}
              value={getLocalizedString(ctaEditor.text, ctaLang, 'de', '')}
              onChange={(e) => setCtaEditor(prev => ({ ...prev, text: setLocalizedString(prev.text, ctaLang, e.target.value) }))}
              fullWidth
              multiline
              minRows={3}
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField
                label={`Button Text (${ctaLang.toUpperCase()})`}
                value={getLocalizedString(ctaEditor.buttonText, ctaLang, 'de', '')}
                onChange={(e) => setCtaEditor(prev => ({ ...prev, buttonText: setLocalizedString(prev.buttonText, ctaLang, e.target.value) }))}
                fullWidth
              />
              <TextField label="Button URL" value={ctaEditor.buttonUrl} onChange={(e) => setCtaEditor(prev => ({ ...prev, buttonUrl: e.target.value }))} fullWidth placeholder="/ oder https://…" />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel id="cta-align">Align</InputLabel>
                <Select labelId="cta-align" label="Align" value={ctaEditor.align} onChange={(e) => setCtaEditor(prev => ({ ...prev, align: (e.target.value === 'left' ? 'left' : 'center') }))}>
                  <MenuItem value="center">Center</MenuItem>
                  <MenuItem value="left">Left</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="cta-variant">Style</InputLabel>
                <Select labelId="cta-variant" label="Style" value={ctaEditor.variant} onChange={(e) => setCtaEditor(prev => ({ ...prev, variant: (e.target.value === 'soft' ? 'soft' : 'gradient') }))}>
                  <MenuItem value="gradient">Gradient</MenuItem>
                  <MenuItem value="soft">Soft</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Divider />
            <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Hintergrundbild (optional)</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Button variant="outlined" onClick={() => setCtaBgPickerOpen(true)} sx={{ textTransform: 'none', fontWeight: 800 }}>
                Bild auswählen
              </Button>
              <Button
                variant="text"
                color="error"
                onClick={() => setCtaEditor(prev => ({ ...prev, backgroundImage: '' }))}
                disabled={!ctaEditor.backgroundImage}
                sx={{ textTransform: 'none', fontWeight: 800 }}
              >
                Entfernen
              </Button>
            </Box>
            {ctaEditor.backgroundImage ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box component="img" src={toPublicUrl(ctaEditor.backgroundImage)} alt="CTA" sx={{ width: 110, height: 80, borderRadius: 2, objectFit: 'cover', border: '1px solid', borderColor: 'divider' }} />
                <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>{ctaEditor.backgroundImage}</Typography>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">Kein Bild ausgewählt.</Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCtaEditor(prev => ({ ...prev, open: false, sectionId: null }))}>Abbrechen</Button>
          <Button variant="contained" onClick={() => {
            const sectionId = ctaEditor.sectionId;
            if (!sectionId) { setCtaEditor(prev => ({ ...prev, open: false, sectionId: null })); return; }
            setBuilderSections((prev) => updateSectionById(prev, String(sectionId), (s: any) => ({
              ...s,
              type: 'cta',
              title: 'CTA',
              config: {
                enabled: ctaEditor.enabled,
                title: ctaEditor.title,
                text: ctaEditor.text,
                buttonText: ctaEditor.buttonText,
                buttonUrl: ctaEditor.buttonUrl,
                backgroundImage: ctaEditor.backgroundImage,
                align: ctaEditor.align,
                variant: ctaEditor.variant,
              },
            })).next);
            markDirty(sectionId);
            setCtaEditor(prev => ({ ...prev, open: false, sectionId: null }));
          }}>Speichern</Button>
        </DialogActions>
      </Dialog>

      <MediaPickerModal
        open={ctaBgPickerOpen}
        onClose={() => setCtaBgPickerOpen(false)}
        onSelect={(it: MediaItem) => {
          setCtaEditor(prev => ({ ...prev, backgroundImage: normalizePickedUrl(it.url) }));
          setCtaBgPickerOpen(false);
        }}
      />

      {/* Reviews editor dialog */}
      <Dialog open={reviewsEditor.open} onClose={() => setReviewsEditor(prev => ({ ...prev, open: false, sectionId: null }))} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 900 }}>Reviews bearbeiten</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gap: 2 }}>
            <FormControlLabel control={<Switch checked={reviewsEditor.enabled} onChange={(_, v) => setReviewsEditor(prev => ({ ...prev, enabled: v }))} />} label="Aktiv" />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
              <Tabs
                value={reviewsLang}
                onChange={(_, v) => setReviewsLang(String(v || 'de'))}
                variant="scrollable"
                scrollButtons
                allowScrollButtonsMobile
                sx={{ minHeight: 38, '& .MuiTab-root': { minHeight: 38, textTransform: 'none', fontWeight: 900 } }}
              >
                {(() => {
                  const keys = Array.from(new Set([
                    ...Object.keys((reviewsEditor.title && typeof reviewsEditor.title === 'object') ? (reviewsEditor.title as any) : { de: true }),
                    ...Object.keys((reviewsEditor.subtitle && typeof reviewsEditor.subtitle === 'object') ? (reviewsEditor.subtitle as any) : { de: true }),
                    ...(reviewsEditor.items || []).flatMap((it) => Object.keys((it?.text && typeof it.text === 'object') ? (it.text as any) : {})),
                  ].filter(Boolean)));
                  const list = keys.length > 0 ? keys : ['de'];
                  return list.map((k) => (
                    <Tab
                      key={`rev-lang-${k}`}
                      value={k}
                      label={(
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Box component="span" sx={{ fontSize: 18, lineHeight: 1 }}>{heroCarouselFlag(k)}</Box>
                          <Box component="span">{String(k).toUpperCase()}</Box>
                        </Box>
                      )}
                    />
                  ));
                })()}
              </Tabs>
              <Button
                variant="outlined"
                size="small"
                onClick={(e) => setReviewsLangAnchor(e.currentTarget)}
                sx={{ textTransform: 'none', fontWeight: 900, borderRadius: 999 }}
              >
                + Sprache
              </Button>
              <Popover
                open={!!reviewsLangAnchor}
                anchorEl={reviewsLangAnchor}
                onClose={() => setReviewsLangAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <Box sx={{ p: 1, minWidth: 220 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>Sprache hinzufügen</Typography>
                  {(['de','en','tr','fr','es','it','pt','ar','hi','zh'] as string[]).map((code) => {
                    const existing = (reviewsEditor.title && typeof reviewsEditor.title === 'object')
                      ? Object.prototype.hasOwnProperty.call(reviewsEditor.title as any, code)
                      : (code === 'de');
                    return (
                      <MenuItem
                        key={`rev-addlang-${code}`}
                        disabled={existing}
                        onClick={() => {
                          const baseLang = reviewsLang || 'de';
                          setReviewsEditor((prev) => ({
                            ...prev,
                            title: setLocalizedString(
                              (typeof prev.title === 'string') ? ({ [baseLang]: prev.title } as any) : prev.title,
                              code,
                              getLocalizedString(prev.title, code, baseLang, '')
                            ),
                            subtitle: setLocalizedString(
                              (typeof prev.subtitle === 'string') ? ({ [baseLang]: prev.subtitle } as any) : prev.subtitle,
                              code,
                              getLocalizedString(prev.subtitle, code, baseLang, '')
                            ),
                            items: (prev.items || []).map((it) => ({
                              ...it,
                              name: setLocalizedString(
                                (typeof it.name === 'string') ? ({ [baseLang]: it.name } as any) : it.name,
                                code,
                                getLocalizedString(it.name, code, baseLang, '')
                              ),
                              role: setLocalizedString(
                                (typeof it.role === 'string') ? ({ [baseLang]: it.role } as any) : it.role,
                                code,
                                getLocalizedString(it.role, code, baseLang, '')
                              ),
                              text: setLocalizedString(
                                (typeof it.text === 'string') ? ({ [baseLang]: it.text } as any) : it.text,
                                code,
                                getLocalizedString(it.text, code, baseLang, '')
                              ),
                            })),
                          }));
                          setReviewsLang(code);
                          setReviewsLangAnchor(null);
                        }}
                        sx={{ fontWeight: 800 }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box component="span" sx={{ fontSize: 18, lineHeight: 1 }}>{heroCarouselFlag(code)}</Box>
                          <Box component="span">{code.toUpperCase()}</Box>
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Box>
              </Popover>
            </Box>

            <TextField label={`Titel (${reviewsLang.toUpperCase()})`} value={getLocalizedString(reviewsEditor.title, reviewsLang, 'de', '')} onChange={(e) => setReviewsEditor(prev => ({ ...prev, title: setLocalizedString(prev.title, reviewsLang, e.target.value) }))} fullWidth />
            <TextField label={`Untertitel (${reviewsLang.toUpperCase()})`} value={getLocalizedString(reviewsEditor.subtitle, reviewsLang, 'de', '')} onChange={(e) => setReviewsEditor(prev => ({ ...prev, subtitle: setLocalizedString(prev.subtitle, reviewsLang, e.target.value) }))} fullWidth />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel id="rev-variant">Style</InputLabel>
                <Select labelId="rev-variant" label="Style" value={reviewsEditor.variant} onChange={(e) => setReviewsEditor(prev => ({ ...prev, variant: (e.target.value === 'soft' ? 'soft' : 'glass') }))}>
                  <MenuItem value="glass">Glass</MenuItem>
                  <MenuItem value="soft">Soft</MenuItem>
                </Select>
              </FormControl>
              <TextField label="Autoplay (ms)" type="number" value={reviewsEditor.intervalMs} onChange={(e) => setReviewsEditor(prev => ({ ...prev, intervalMs: Math.max(1500, Math.min(20000, Number(e.target.value) || 5500)) }))} fullWidth />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr 1fr' }, gap: 2 }}>
              <FormControlLabel control={<Switch checked={reviewsEditor.autoplay} onChange={(_, v) => setReviewsEditor(prev => ({ ...prev, autoplay: v }))} />} label="Autoplay" />
              <FormControlLabel control={<Switch checked={reviewsEditor.showArrows} onChange={(_, v) => setReviewsEditor(prev => ({ ...prev, showArrows: v }))} />} label="Arrows" />
              <FormControlLabel control={<Switch checked={reviewsEditor.showDots} onChange={(_, v) => setReviewsEditor(prev => ({ ...prev, showDots: v }))} />} label="Dots" />
            </Box>

            <Divider />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Reviews</Typography>
              <Button variant="outlined" onClick={() => setReviewsEditor(prev => ({ ...prev, items: [...prev.items, { name: { de: 'Kunde' }, role: { de: '' }, text: { de: 'Top Service und schnelle Lieferung!' }, rating: 5, avatar: '' }] }))} sx={{ textTransform: 'none', fontWeight: 800 }}>
                Review hinzufügen
              </Button>
            </Box>

            <Box sx={{ display: 'grid', gap: 2 }}>
              {reviewsEditor.items.map((it, idx) => (
                <Card key={`rev-${idx}`} sx={{ borderRadius: 3, boxShadow: '0 10px 26px rgba(2,6,23,0.08)' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
                      <Typography sx={{ fontWeight: 900 }}>Review #{idx + 1}</Typography>
                      <IconButton color="error" onClick={() => setReviewsEditor(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }))} size="small" title="Entfernen">
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                      <TextField label={`Name (${reviewsLang.toUpperCase()})`} value={getLocalizedString(it.name, reviewsLang, 'de', '')} onChange={(e) => setReviewsEditor(prev => ({ ...prev, items: prev.items.map((x, i) => i === idx ? { ...x, name: setLocalizedString(x.name, reviewsLang, e.target.value) } : x) }))} fullWidth />
                      <TextField label={`Rolle (${reviewsLang.toUpperCase()})`} value={getLocalizedString(it.role, reviewsLang, 'de', '')} onChange={(e) => setReviewsEditor(prev => ({ ...prev, items: prev.items.map((x, i) => i === idx ? { ...x, role: setLocalizedString(x.role, reviewsLang, e.target.value) } : x) }))} fullWidth />
                    </Box>

                    <TextField label={`Text (${reviewsLang.toUpperCase()})`} value={getLocalizedString(it.text, reviewsLang, 'de', '')} onChange={(e) => setReviewsEditor(prev => ({ ...prev, items: prev.items.map((x, i) => i === idx ? { ...x, text: setLocalizedString(x.text, reviewsLang, e.target.value) } : x) }))} fullWidth multiline minRows={3} sx={{ mt: 2 }} />

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                      <TextField label="Rating" type="number" value={Number(it.rating ?? 5)} onChange={(e) => setReviewsEditor(prev => ({ ...prev, items: prev.items.map((x, i) => i === idx ? { ...x, rating: Math.max(0, Math.min(5, Number(e.target.value) || 5)) } : x) }))} inputProps={{ min: 0, max: 5, step: 0.5 }} sx={{ width: 140 }} />
                      <Button variant="outlined" onClick={() => setReviewAvatarPicker({ open: true, index: idx })} sx={{ textTransform: 'none', fontWeight: 800 }}>
                        Avatar wählen
                      </Button>
                      {it.avatar ? (
                        <Button variant="text" color="error" onClick={() => setReviewsEditor(prev => ({ ...prev, items: prev.items.map((x, i) => i === idx ? { ...x, avatar: '' } : x) }))} sx={{ textTransform: 'none', fontWeight: 800 }}>
                          Entfernen
                        </Button>
                      ) : null}
                      {it.avatar ? (
                        <Box component="img" src={toPublicUrl(it.avatar)} alt="avatar" sx={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', border: '1px solid', borderColor: 'divider' }} />
                      ) : null}
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewsEditor(prev => ({ ...prev, open: false, sectionId: null }))}>Abbrechen</Button>
          <Button variant="contained" onClick={() => {
            const sectionId = reviewsEditor.sectionId;
            if (!sectionId) { setReviewsEditor(prev => ({ ...prev, open: false, sectionId: null })); return; }
            setBuilderSections((prev) => updateSectionById(prev, String(sectionId), (s: any) => ({
              ...s,
              type: 'reviews',
              title: 'Reviews',
              config: {
                enabled: reviewsEditor.enabled,
                title: reviewsEditor.title,
                subtitle: reviewsEditor.subtitle,
                variant: reviewsEditor.variant,
                autoplay: reviewsEditor.autoplay,
                intervalMs: reviewsEditor.intervalMs,
                showArrows: reviewsEditor.showArrows,
                showDots: reviewsEditor.showDots,
                items: reviewsEditor.items
                  .map((it: any) => ({
                    name: it?.name,
                    role: it?.role,
                    text: it?.text,
                    rating: Math.max(0, Math.min(5, Number(it?.rating) || 5)),
                    avatar: (typeof it?.avatar === 'string' ? it.avatar : ''),
                  }))
                  .filter((it: any) => getLocalizedString(it?.text, 'de', 'de', '').trim() !== ''),
              },
            })).next);
            markDirty(sectionId);
            setReviewsEditor(prev => ({ ...prev, open: false, sectionId: null }));
          }}>Speichern</Button>
        </DialogActions>
      </Dialog>

      {/* Counter editor dialog */}
      <Dialog open={counterEditor.open} onClose={() => setCounterEditor(prev => ({ ...prev, open: false, sectionId: null }))} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 900 }}>Counter bearbeiten</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gap: 2 }}>
            <FormControlLabel control={<Switch checked={counterEditor.enabled} onChange={(_, v) => setCounterEditor(prev => ({ ...prev, enabled: v }))} />} label="Aktiv" />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
              <Tabs
                value={counterLang}
                onChange={(_, v) => setCounterLang(String(v || 'de'))}
                variant="scrollable"
                scrollButtons
                allowScrollButtonsMobile
                sx={{ minHeight: 38, '& .MuiTab-root': { minHeight: 38, textTransform: 'none', fontWeight: 900 } }}
              >
                {(() => {
                  const keys = Array.from(new Set([
                    ...Object.keys((counterEditor.topText && typeof counterEditor.topText === 'object') ? (counterEditor.topText as any) : { de: true }),
                    ...Object.keys((counterEditor.bottomText && typeof counterEditor.bottomText === 'object') ? (counterEditor.bottomText as any) : { de: true }),
                    ...(counterEditor.blocks || []).flatMap((b: any) => {
                      if (String(b?.kind || '').toLowerCase() === 'text') return Object.keys((b?.text && typeof b.text === 'object') ? (b.text as any) : {});
                      return [
                        ...Object.keys((b?.labelTop && typeof b.labelTop === 'object') ? (b.labelTop as any) : {}),
                        ...Object.keys((b?.labelBottom && typeof b.labelBottom === 'object') ? (b.labelBottom as any) : {}),
                        ...Object.keys((b?.prefix && typeof b.prefix === 'object') ? (b.prefix as any) : {}),
                        ...Object.keys((b?.suffix && typeof b.suffix === 'object') ? (b.suffix as any) : {}),
                      ];
                    }),
                  ].filter(Boolean)));
                  const list = keys.length > 0 ? keys : ['de'];
                  return list.map((k) => (
                    <Tab
                      key={`cnt-lang-${k}`}
                      value={k}
                      label={(
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Box component="span" sx={{ fontSize: 18, lineHeight: 1 }}>{heroCarouselFlag(k)}</Box>
                          <Box component="span">{String(k).toUpperCase()}</Box>
                        </Box>
                      )}
                    />
                  ));
                })()}
              </Tabs>
              <Button
                variant="outlined"
                size="small"
                onClick={(e) => setCounterLangAnchor(e.currentTarget)}
                sx={{ textTransform: 'none', fontWeight: 900, borderRadius: 999 }}
              >
                + Sprache
              </Button>
              <Popover
                open={!!counterLangAnchor}
                anchorEl={counterLangAnchor}
                onClose={() => setCounterLangAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <Box sx={{ p: 1, minWidth: 220 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>Sprache hinzufügen</Typography>
                  {(['de','en','tr','fr','es','it','pt','ar','hi','zh'] as string[]).map((code) => {
                    const existing = (counterEditor.topText && typeof counterEditor.topText === 'object')
                      ? Object.prototype.hasOwnProperty.call(counterEditor.topText as any, code)
                      : (code === 'de');
                    return (
                      <MenuItem
                        key={`cnt-addlang-${code}`}
                        disabled={existing}
                        onClick={() => {
                          const baseLang = counterLang || 'de';
                          setCounterEditor((prev) => ({
                            ...prev,
                            topText: setLocalizedString((typeof prev.topText === 'string') ? ({ [baseLang]: prev.topText } as any) : prev.topText, code, getLocalizedString(prev.topText, code, baseLang, '')),
                            bottomText: setLocalizedString((typeof prev.bottomText === 'string') ? ({ [baseLang]: prev.bottomText } as any) : prev.bottomText, code, getLocalizedString(prev.bottomText, code, baseLang, '')),
                            blocks: (prev.blocks || []).map((b: any) => {
                              const knd = String(b?.kind || '').toLowerCase();
                              if (knd === 'text') {
                                return {
                                  ...b,
                                  text: setLocalizedString((typeof b.text === 'string') ? ({ [baseLang]: b.text } as any) : b.text, code, getLocalizedString(b.text, code, baseLang, '')),
                                };
                              }
                              return {
                                ...b,
                                prefix: setLocalizedString((typeof b.prefix === 'string') ? ({ [baseLang]: b.prefix } as any) : b.prefix, code, getLocalizedString(b.prefix, code, baseLang, '')),
                                suffix: setLocalizedString((typeof b.suffix === 'string') ? ({ [baseLang]: b.suffix } as any) : b.suffix, code, getLocalizedString(b.suffix, code, baseLang, '')),
                                labelTop: setLocalizedString((typeof b.labelTop === 'string') ? ({ [baseLang]: b.labelTop } as any) : b.labelTop, code, getLocalizedString(b.labelTop, code, baseLang, '')),
                                labelBottom: setLocalizedString((typeof b.labelBottom === 'string') ? ({ [baseLang]: b.labelBottom } as any) : b.labelBottom, code, getLocalizedString(b.labelBottom, code, baseLang, '')),
                              };
                            }),
                          }));
                          setCounterLang(code);
                          setCounterLangAnchor(null);
                        }}
                        sx={{ fontWeight: 800 }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box component="span" sx={{ fontSize: 18, lineHeight: 1 }}>{heroCarouselFlag(code)}</Box>
                          <Box component="span">{code.toUpperCase()}</Box>
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Box>
              </Popover>
            </Box>

            <TextField label={`Text oben (${counterLang.toUpperCase()})`} value={getLocalizedString(counterEditor.topText, counterLang, 'de', '')} onChange={(e) => setCounterEditor(prev => ({ ...prev, topText: setLocalizedString(prev.topText, counterLang, e.target.value) }))} fullWidth />
            <TextField label={`Text unten (${counterLang.toUpperCase()})`} value={getLocalizedString(counterEditor.bottomText, counterLang, 'de', '')} onChange={(e) => setCounterEditor(prev => ({ ...prev, bottomText: setLocalizedString(prev.bottomText, counterLang, e.target.value) }))} fullWidth />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel id="cnt-type">Counter Type</InputLabel>
                <Select labelId="cnt-type" label="Counter Type" value={counterEditor.type} onChange={(e) => setCounterEditor(prev => ({ ...prev, type: (e.target.value === 'classic' ? 'classic' : 'radial') }))}>
                  <MenuItem value="radial">Radial</MenuItem>
                  <MenuItem value="classic">Classic</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="cnt-bgmode">Colors</InputLabel>
                <Select labelId="cnt-bgmode" label="Colors" value={counterEditor.bgMode} onChange={(e) => setCounterEditor(prev => ({ ...prev, bgMode: (e.target.value === 'custom' || e.target.value === 'transparent') ? (e.target.value as any) : 'theme' }))}>
                  <MenuItem value="theme">Theme</MenuItem>
                  <MenuItem value="custom">Custom</MenuItem>
                  <MenuItem value="transparent">Transparent</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel id="cnt-align">Alignment</InputLabel>
                <Select labelId="cnt-align" label="Alignment" value={counterEditor.align} onChange={(e) => setCounterEditor(prev => ({ ...prev, align: (e.target.value === 'left' ? 'left' : (e.target.value === 'right' ? 'right' : 'center')) }))}>
                  <MenuItem value="left">Left</MenuItem>
                  <MenuItem value="center">Center</MenuItem>
                  <MenuItem value="right">Right</MenuItem>
                </Select>
              </FormControl>
              <TextField label="Elemente pro Reihe" type="number" value={counterEditor.perRow} onChange={(e) => setCounterEditor(prev => ({ ...prev, perRow: Math.max(1, Math.min(6, Number(e.target.value) || 3)) }))} fullWidth helperText="1 = jedes Element eigene Zeile" />
              <TextField label="Text Size (Scale)" type="number" value={counterEditor.textScale} onChange={(e) => setCounterEditor(prev => ({ ...prev, textScale: Math.max(0.75, Math.min(1.5, Number(e.target.value) || 1)) }))} fullWidth inputProps={{ step: 0.05 }} />
            </Box>

            <Box sx={{ mt: 0.5, px: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2, mb: 0.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Counter Größe</Typography>
                <Chip size="small" label={`${(Number(counterEditor.size) || 1).toFixed(2)}x`} sx={{ fontWeight: 900 }} />
              </Box>
              <Slider
                value={Number(counterEditor.size) || 1}
                min={0.65}
                max={1.6}
                step={0.05}
                marks={[
                  { value: 0.75, label: 'S' },
                  { value: 1.0, label: 'M' },
                  { value: 1.25, label: 'L' },
                  { value: 1.5, label: 'XL' },
                ]}
                valueLabelDisplay="auto"
                onChange={(_, v) => {
                  const n = Array.isArray(v) ? v[0] : v;
                  setCounterEditor(prev => ({ ...prev, size: Math.max(0.65, Math.min(1.6, Number(n) || 1)) }));
                }}
                sx={{
                  '& .MuiSlider-rail': { opacity: 0.25 },
                  '& .MuiSlider-track': { border: 'none' },
                  '& .MuiSlider-thumb': { width: 18, height: 18, boxShadow: '0 8px 20px rgba(2,6,23,0.18)' },
                }}
              />
              <Typography variant="caption" color="text.secondary">
                Skaliert den Counter (inkl. Zahl/Text im Counter).
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <FormControlLabel
                control={<Switch checked={Boolean(counterEditor.showPercent)} onChange={(_, v) => setCounterEditor(prev => ({ ...prev, showPercent: v }))} />}
                label="Prozent anzeigen"
              />
              <Typography variant="caption" color="text.secondary">
                Zeigt die % Anzeige unter dem Counter (nur wenn Max gesetzt ist).
              </Typography>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
              <TextField label="Text Color" type="color" value={counterEditor.textColor || '#111827'} onChange={(e) => setCounterEditor(prev => ({ ...prev, textColor: e.target.value }))} fullWidth />
              <TextField label="Ring BG" type="color" value={counterEditor.ringBgColor || '#e5e7eb'} onChange={(e) => setCounterEditor(prev => ({ ...prev, ringBgColor: e.target.value }))} fullWidth />
              <TextField label="BG Color" type="color" value={counterEditor.bgColor || '#ffffff'} onChange={(e) => setCounterEditor(prev => ({ ...prev, bgColor: e.target.value }))} fullWidth disabled={counterEditor.bgMode !== 'custom'} />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField label="Gradient From" value={counterEditor.gradientFrom} onChange={(e) => setCounterEditor(prev => ({ ...prev, gradientFrom: e.target.value }))} fullWidth placeholder={counterEditor.bgMode === 'custom' ? '#60a5fa' : '(Theme primary)'} />
              <TextField label="Gradient To" value={counterEditor.gradientTo} onChange={(e) => setCounterEditor(prev => ({ ...prev, gradientTo: e.target.value }))} fullWidth placeholder={counterEditor.bgMode === 'custom' ? '#22c55e' : '(Theme secondary)'} />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <FormControlLabel control={<Switch checked={counterEditor.animate} onChange={(_, v) => setCounterEditor(prev => ({ ...prev, animate: v }))} />} label="Count-up animation" />
              <TextField label="Duration (ms)" type="number" value={counterEditor.animationDurationMs} onChange={(e) => setCounterEditor(prev => ({ ...prev, animationDurationMs: Math.max(250, Math.min(8000, Number(e.target.value) || 1400)) }))} fullWidth />
            </Box>

            <Divider />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Blocks</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  variant="outlined"
                  onClick={() => setCounterEditor(prev => ({ ...prev, blocks: [...(prev.blocks || []), { kind: 'text', text: { de: 'Text...' }, align: 'center', textColor: '', fontSize: 18, fullWidth: true }] }))}
                  sx={{ textTransform: 'none', fontWeight: 800 }}
                >
                  Text-Block hinzufügen
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setCounterEditor(prev => ({ ...prev, blocks: [...(prev.blocks || []), { kind: 'counter', value: 100, max: 100, decimals: 0, prefix: { de: '' }, suffix: { de: '%' }, labelTop: { de: 'Neu' }, labelBottom: { de: 'Beschreibung' } }] }))}
                  sx={{ textTransform: 'none', fontWeight: 800 }}
                >
                  Counter hinzufügen
                </Button>
              </Box>
            </Box>

            <Box sx={{ display: 'grid', gap: 2 }}>
              {(counterEditor.blocks || []).map((it: any, idx: number) => (
                <Card key={`cntb-${idx}`} sx={{ borderRadius: 3, boxShadow: '0 10px 26px rgba(2,6,23,0.08)' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
                      <Typography sx={{ fontWeight: 900 }}>
                        {String(it?.kind || '').toLowerCase() === 'text' ? `Text #${idx + 1}` : `Counter #${idx + 1}`}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          disabled={idx === 0}
                          title="Nach oben"
                          onClick={() => {
                            setCounterEditor((prev) => {
                              const blocks = Array.isArray(prev.blocks) ? [...prev.blocks] : [];
                              if (idx <= 0 || idx >= blocks.length) return prev;
                              const tmp = blocks[idx - 1];
                              blocks[idx - 1] = blocks[idx];
                              blocks[idx] = tmp;
                              return { ...prev, blocks };
                            });
                          }}
                        >
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          disabled={idx === (counterEditor.blocks || []).length - 1}
                          title="Nach unten"
                          onClick={() => {
                            setCounterEditor((prev) => {
                              const blocks = Array.isArray(prev.blocks) ? [...prev.blocks] : [];
                              if (idx < 0 || idx >= blocks.length - 1) return prev;
                              const tmp = blocks[idx + 1];
                              blocks[idx + 1] = blocks[idx];
                              blocks[idx] = tmp;
                              return { ...prev, blocks };
                            });
                          }}
                        >
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                        <IconButton color="error" onClick={() => setCounterEditor(prev => ({ ...prev, blocks: (prev.blocks || []).filter((_: any, i: number) => i !== idx) }))} size="small" title="Entfernen">
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>

                    {String(it?.kind || '').toLowerCase() === 'text' ? (
                      <>
                        <TextField
                          label={`Text (${counterLang.toUpperCase()})`}
                          value={getLocalizedString(it.text, counterLang, 'de', '')}
                          onChange={(e) => setCounterEditor(prev => ({ ...prev, blocks: (prev.blocks || []).map((x: any, i: number) => i === idx ? { ...x, text: setLocalizedString(x.text, counterLang, e.target.value) } : x) }))}
                          fullWidth
                          multiline
                          minRows={3}
                        />
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2, mt: 2 }}>
                          <FormControlLabel
                            control={<Switch checked={it.fullWidth !== false} onChange={(_, v) => setCounterEditor(prev => ({ ...prev, blocks: (prev.blocks || []).map((x: any, i: number) => i === idx ? { ...x, fullWidth: v } : x) }))} />}
                            label="Volle Breite"
                          />
                          <FormControl fullWidth>
                            <InputLabel id={`cntb-talign-${idx}`}>Alignment</InputLabel>
                            <Select labelId={`cntb-talign-${idx}`} label="Alignment" value={(it.align === 'left' ? 'left' : (it.align === 'right' ? 'right' : 'center'))} onChange={(e) => setCounterEditor(prev => ({ ...prev, blocks: (prev.blocks || []).map((x: any, i: number) => i === idx ? { ...x, align: (e.target.value === 'left' ? 'left' : (e.target.value === 'right' ? 'right' : 'center')) } : x) }))}>
                              <MenuItem value="left">Left</MenuItem>
                              <MenuItem value="center">Center</MenuItem>
                              <MenuItem value="right">Right</MenuItem>
                            </Select>
                          </FormControl>
                          <TextField label="Font size" type="number" value={Number(it.fontSize || 18)} onChange={(e) => setCounterEditor(prev => ({ ...prev, blocks: (prev.blocks || []).map((x: any, i: number) => i === idx ? { ...x, fontSize: Math.max(12, Math.min(32, Number(e.target.value) || 18)) } : x) }))} />
                          <TextField label="Text color" type="color" value={String(it.textColor || '#111827')} onChange={(e) => setCounterEditor(prev => ({ ...prev, blocks: (prev.blocks || []).map((x: any, i: number) => i === idx ? { ...x, textColor: e.target.value } : x) }))} />
                        </Box>
                      </>
                    ) : (
                      <>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
                          <TextField label="Value" type="number" value={Number(it.value || 0)} onChange={(e) => setCounterEditor(prev => ({ ...prev, blocks: (prev.blocks || []).map((x: any, i: number) => i === idx ? { ...x, value: Number(e.target.value) || 0 } : x) }))} fullWidth />
                          <TextField label="Max" type="number" value={it.max ?? ''} onChange={(e) => setCounterEditor(prev => ({ ...prev, blocks: (prev.blocks || []).map((x: any, i: number) => i === idx ? { ...x, max: (e.target.value === '' ? undefined : (Number(e.target.value) || 0)) } : x) }))} fullWidth />
                          <TextField label="Decimals" type="number" value={it.decimals ?? 0} onChange={(e) => setCounterEditor(prev => ({ ...prev, blocks: (prev.blocks || []).map((x: any, i: number) => i === idx ? { ...x, decimals: Math.max(0, Math.min(6, Number(e.target.value) || 0)) } : x) }))} fullWidth />
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 2 }}>
                          <TextField label={`Label oben (${counterLang.toUpperCase()})`} value={getLocalizedString(it.labelTop, counterLang, 'de', '')} onChange={(e) => setCounterEditor(prev => ({ ...prev, blocks: (prev.blocks || []).map((x: any, i: number) => i === idx ? { ...x, labelTop: setLocalizedString(x.labelTop, counterLang, e.target.value) } : x) }))} fullWidth />
                          <TextField label={`Label unten (${counterLang.toUpperCase()})`} value={getLocalizedString(it.labelBottom, counterLang, 'de', '')} onChange={(e) => setCounterEditor(prev => ({ ...prev, blocks: (prev.blocks || []).map((x: any, i: number) => i === idx ? { ...x, labelBottom: setLocalizedString(x.labelBottom, counterLang, e.target.value) } : x) }))} fullWidth />
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 2 }}>
                          <TextField label={`Prefix (${counterLang.toUpperCase()})`} value={getLocalizedString(it.prefix, counterLang, 'de', '')} onChange={(e) => setCounterEditor(prev => ({ ...prev, blocks: (prev.blocks || []).map((x: any, i: number) => i === idx ? { ...x, prefix: setLocalizedString(x.prefix, counterLang, e.target.value) } : x) }))} fullWidth />
                          <TextField label={`Suffix (${counterLang.toUpperCase()})`} value={getLocalizedString(it.suffix, counterLang, 'de', '')} onChange={(e) => setCounterEditor(prev => ({ ...prev, blocks: (prev.blocks || []).map((x: any, i: number) => i === idx ? { ...x, suffix: setLocalizedString(x.suffix, counterLang, e.target.value) } : x) }))} fullWidth />
                        </Box>
                      </>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCounterEditor(prev => ({ ...prev, open: false, sectionId: null }))}>Abbrechen</Button>
          <Button variant="contained" onClick={() => {
            const sectionId = counterEditor.sectionId;
            if (!sectionId) { setCounterEditor(prev => ({ ...prev, open: false, sectionId: null })); return; }
            setBuilderSections((prev) => updateSectionById(prev, String(sectionId), (s: any) => ({
              ...s,
              type: 'counter',
              title: 'Counter',
              config: {
                enabled: counterEditor.enabled,
                topText: counterEditor.topText,
                bottomText: counterEditor.bottomText,
                type: counterEditor.type,
                align: counterEditor.align,
                perRow: counterEditor.perRow,
                size: Math.max(0.65, Math.min(1.6, Number(counterEditor.size) || 1)),
                textScale: counterEditor.textScale,
                showPercent: Boolean(counterEditor.showPercent),
                bgMode: counterEditor.bgMode,
                bgColor: counterEditor.bgColor,
                textColor: counterEditor.textColor,
                ringBgColor: counterEditor.ringBgColor,
                gradientFrom: counterEditor.gradientFrom,
                gradientTo: counterEditor.gradientTo,
                animate: counterEditor.animate,
                animationDurationMs: counterEditor.animationDurationMs,
                blocks: (counterEditor.blocks || []).map((b: any) => {
                  const kind = String(b?.kind || '').toLowerCase();
                  if (kind === 'text') {
                    return {
                      kind: 'text',
                      text: b?.text,
                      align: (b?.align === 'left' ? 'left' : (b?.align === 'right' ? 'right' : 'center')),
                      textColor: (typeof b?.textColor === 'string' ? b.textColor : ''),
                      fontSize: Math.max(12, Math.min(32, Number(b?.fontSize) || 18)),
                      fullWidth: b?.fullWidth !== false,
                    };
                  }
                  return {
                    kind: 'counter',
                    value: Number(b?.value) || 0,
                    max: b?.max == null ? undefined : (Number(b?.max) || 0),
                    decimals: b?.decimals == null ? undefined : Math.max(0, Math.min(6, Number(b?.decimals) || 0)),
                    prefix: b?.prefix,
                    suffix: b?.suffix,
                    labelTop: b?.labelTop,
                    labelBottom: b?.labelBottom,
                  };
                }),
              },
            })).next);
            markDirty(sectionId);
            setCounterEditor(prev => ({ ...prev, open: false, sectionId: null }));
          }}>Speichern</Button>
        </DialogActions>
      </Dialog>

      <MediaPickerModal
        open={reviewAvatarPicker.open}
        onClose={() => setReviewAvatarPicker({ open: false, index: null })}
        onSelect={(it: MediaItem) => {
          const idx = reviewAvatarPicker.index;
          const path = normalizePickedUrl(it.url);
          if (idx != null) {
            setReviewsEditor(prev => ({ ...prev, items: prev.items.map((x, i) => i === idx ? { ...x, avatar: path } : x) }));
          }
          setReviewAvatarPicker({ open: false, index: null });
        }}
      />

      {/* Media+Text editor dialog */}
      <Dialog open={mediaTextEditor.open} onClose={() => setMediaTextEditor(prev => ({ ...prev, open: false, sectionId: null }))} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 900 }}>Media + Text bearbeiten</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gap: 2 }}>
            <FormControlLabel control={<Switch checked={mediaTextEditor.enabled} onChange={(_, v) => setMediaTextEditor(prev => ({ ...prev, enabled: v }))} />} label="Aktiv" />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
              <Tabs
                value={mediaTextLang}
                onChange={(_, v) => setMediaTextLang(String(v || 'de'))}
                variant="scrollable"
                scrollButtons
                allowScrollButtonsMobile
                sx={{ minHeight: 38, '& .MuiTab-root': { minHeight: 38, textTransform: 'none', fontWeight: 900 } }}
              >
                {(() => {
                  const keys = Array.from(new Set([
                    ...Object.keys((mediaTextEditor.title && typeof mediaTextEditor.title === 'object') ? (mediaTextEditor.title as any) : { de: true }),
                    ...Object.keys((mediaTextEditor.text && typeof mediaTextEditor.text === 'object') ? (mediaTextEditor.text as any) : { de: true }),
                    ...Object.keys((mediaTextEditor.eyebrow && typeof mediaTextEditor.eyebrow === 'object') ? (mediaTextEditor.eyebrow as any) : {}),
                    ...Object.keys((mediaTextEditor.ctaText && typeof mediaTextEditor.ctaText === 'object') ? (mediaTextEditor.ctaText as any) : {}),
                  ].filter(Boolean)));
                  const list = keys.length > 0 ? keys : ['de'];
                  return list.map((k) => (
                    <Tab
                      key={`mt-lang-${k}`}
                      value={k}
                      label={(
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Box component="span" sx={{ fontSize: 18, lineHeight: 1 }}>{heroCarouselFlag(k)}</Box>
                          <Box component="span">{String(k).toUpperCase()}</Box>
                        </Box>
                      )}
                    />
                  ));
                })()}
              </Tabs>
              <Button
                variant="outlined"
                size="small"
                onClick={(e) => setMediaTextLangAnchor(e.currentTarget)}
                sx={{ textTransform: 'none', fontWeight: 900, borderRadius: 999 }}
              >
                + Sprache
              </Button>
              <Popover
                open={!!mediaTextLangAnchor}
                anchorEl={mediaTextLangAnchor}
                onClose={() => setMediaTextLangAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <Box sx={{ p: 1, minWidth: 220 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>Sprache hinzufügen</Typography>
                  {(['de','en','tr','fr','es','it','pt','ar','hi','zh'] as string[]).map((code) => {
                    const existing = (mediaTextEditor.title && typeof mediaTextEditor.title === 'object')
                      ? Object.prototype.hasOwnProperty.call(mediaTextEditor.title as any, code)
                      : (code === 'de');
                    return (
                      <MenuItem
                        key={`mt-addlang-${code}`}
                        disabled={existing}
                        onClick={() => {
                          const baseLang = mediaTextLang || 'de';
                          setMediaTextEditor((prev) => ({
                            ...prev,
                            eyebrow: setLocalizedString(
                              (typeof prev.eyebrow === 'string') ? ({ [baseLang]: prev.eyebrow } as any) : prev.eyebrow,
                              code,
                              getLocalizedString(prev.eyebrow, code, baseLang, '')
                            ),
                            title: setLocalizedString(
                              (typeof prev.title === 'string') ? ({ [baseLang]: prev.title } as any) : prev.title,
                              code,
                              getLocalizedString(prev.title, code, baseLang, '')
                            ),
                            text: setLocalizedString(
                              (typeof prev.text === 'string') ? ({ [baseLang]: prev.text } as any) : prev.text,
                              code,
                              getLocalizedString(prev.text, code, baseLang, '')
                            ),
                            ctaText: setLocalizedString(
                              (typeof prev.ctaText === 'string') ? ({ [baseLang]: prev.ctaText } as any) : prev.ctaText,
                              code,
                              getLocalizedString(prev.ctaText, code, baseLang, '')
                            ),
                          }));
                          setMediaTextLang(code);
                          setMediaTextLangAnchor(null);
                        }}
                        sx={{ fontWeight: 800 }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box component="span" sx={{ fontSize: 18, lineHeight: 1 }}>{heroCarouselFlag(code)}</Box>
                          <Box component="span">{code.toUpperCase()}</Box>
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Box>
              </Popover>
            </Box>

            <TextField label={`Eyebrow (optional) (${mediaTextLang.toUpperCase()})`} value={getLocalizedString(mediaTextEditor.eyebrow, mediaTextLang, 'de', '')} onChange={(e) => setMediaTextEditor(prev => ({ ...prev, eyebrow: setLocalizedString(prev.eyebrow, mediaTextLang, e.target.value) }))} fullWidth />
            <TextField label={`Titel (${mediaTextLang.toUpperCase()})`} value={getLocalizedString(mediaTextEditor.title, mediaTextLang, 'de', '')} onChange={(e) => setMediaTextEditor(prev => ({ ...prev, title: setLocalizedString(prev.title, mediaTextLang, e.target.value) }))} fullWidth />
            <TextField label={`Text (${mediaTextLang.toUpperCase()})`} value={getLocalizedString(mediaTextEditor.text, mediaTextLang, 'de', '')} onChange={(e) => setMediaTextEditor(prev => ({ ...prev, text: setLocalizedString(prev.text, mediaTextLang, e.target.value) }))} fullWidth multiline minRows={4} />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField label={`CTA Text (optional) (${mediaTextLang.toUpperCase()})`} value={getLocalizedString(mediaTextEditor.ctaText, mediaTextLang, 'de', '')} onChange={(e) => setMediaTextEditor(prev => ({ ...prev, ctaText: setLocalizedString(prev.ctaText, mediaTextLang, e.target.value) }))} fullWidth />
              <TextField label="CTA URL (optional)" value={mediaTextEditor.ctaUrl} onChange={(e) => setMediaTextEditor(prev => ({ ...prev, ctaUrl: e.target.value }))} placeholder="/ oder https://..." fullWidth />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel id="mt-side">Seite</InputLabel>
                <Select labelId="mt-side" label="Seite" value={mediaTextEditor.mediaSide} onChange={(e) => setMediaTextEditor(prev => ({ ...prev, mediaSide: (e.target.value === 'right' ? 'right' : 'left') }))}>
                  <MenuItem value="left">Bilder links / Text rechts</MenuItem>
                  <MenuItem value="right">Text links / Bilder rechts</MenuItem>
                </Select>
              </FormControl>
              <TextField label="Bildhöhe" type="number" value={mediaTextEditor.imageHeight} onChange={(e) => setMediaTextEditor(prev => ({ ...prev, imageHeight: Math.max(220, Math.min(640, Number(e.target.value) || 420)) }))} fullWidth />
              <TextField label="Autoplay (ms)" type="number" value={mediaTextEditor.autoPlayInterval} onChange={(e) => setMediaTextEditor(prev => ({ ...prev, autoPlayInterval: Math.max(1500, Math.min(20000, Number(e.target.value) || 4500)) }))} fullWidth />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
              <FormControlLabel control={<Switch checked={mediaTextEditor.autoplay} onChange={(_, v) => setMediaTextEditor(prev => ({ ...prev, autoplay: v }))} />} label="Autoplay" />
              <FormControlLabel control={<Switch checked={mediaTextEditor.showArrows} onChange={(_, v) => setMediaTextEditor(prev => ({ ...prev, showArrows: v }))} />} label="Arrows" />
              <FormControlLabel control={<Switch checked={mediaTextEditor.showDots} onChange={(_, v) => setMediaTextEditor(prev => ({ ...prev, showDots: v }))} />} label="Dots" />
            </Box>

            <Divider />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Bilder</Typography>
              <Button variant="outlined" onClick={() => setMediaTextPickerOpen(true)} sx={{ textTransform: 'none', fontWeight: 800 }}>
                Bild hinzufügen
              </Button>
            </Box>

            {mediaTextEditor.images.length === 0 ? (
              <Typography variant="body2" color="text.secondary">Noch keine Bilder.</Typography>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)', md: 'repeat(6, 1fr)' }, gap: 1.5 }}>
                {mediaTextEditor.images.map((p, idx) => (
                  <Box key={`${p}-${idx}`} sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider', aspectRatio: '1 / 1', bgcolor: 'grey.100' }}>
                    <Box component="img" src={toPublicUrl(p)} alt={`mt-${idx+1}`} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    <IconButton
                      size="small"
                      onClick={() => setMediaTextEditor(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== idx) }))}
                      sx={{ position: 'absolute', top: 6, right: 6, bgcolor: 'rgba(0,0,0,0.55)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' } }}
                      title="Entfernen"
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMediaTextEditor(prev => ({ ...prev, open: false, sectionId: null }))}>Abbrechen</Button>
          <Button variant="contained" onClick={() => {
            const sectionId = mediaTextEditor.sectionId;
            if (!sectionId) { setMediaTextEditor(prev => ({ ...prev, open: false, sectionId: null })); return; }
            setBuilderSections((prev) => updateSectionById(prev, String(sectionId), (s: any) => ({
              ...s,
              type: 'mediatext',
              title: 'Media + Text',
              config: {
                enabled: mediaTextEditor.enabled,
                eyebrow: mediaTextEditor.eyebrow,
                title: mediaTextEditor.title,
                text: mediaTextEditor.text,
                images: mediaTextEditor.images,
                mediaSide: mediaTextEditor.mediaSide,
                imageHeight: mediaTextEditor.imageHeight,
                autoPlay: mediaTextEditor.autoplay,
                autoPlayInterval: mediaTextEditor.autoPlayInterval,
                showArrows: mediaTextEditor.showArrows,
                showDots: mediaTextEditor.showDots,
                ctaText: mediaTextEditor.ctaText,
                ctaUrl: mediaTextEditor.ctaUrl,
              },
            })).next);
            markDirty(sectionId);
            setMediaTextEditor(prev => ({ ...prev, open: false, sectionId: null }));
          }}>Speichern</Button>
        </DialogActions>
      </Dialog>

      <MediaPickerModal
        open={mediaTextPickerOpen}
        onClose={() => setMediaTextPickerOpen(false)}
        onSelect={(it: MediaItem) => {
          const path = normalizePickedUrl(it.url);
          setMediaTextEditor(prev => ({ ...prev, images: [...prev.images, path] }));
          setMediaTextPickerOpen(false);
        }}
      />

      {/* Scroll list editor dialog */}
      <Dialog open={scrollListEditor.open} onClose={() => setScrollListEditor(prev => ({ ...prev, open: false, sectionId: null }))} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 900 }}>Scroll Liste bearbeiten</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gap: 2 }}>
            <FormControlLabel control={<Switch checked={scrollListEditor.enabled} onChange={(_, v) => setScrollListEditor(prev => ({ ...prev, enabled: v }))} />} label="Aktiv" />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
              <Tabs
                value={scrollListLang}
                onChange={(_, v) => setScrollListLang(String(v || 'de'))}
                variant="scrollable"
                scrollButtons
                allowScrollButtonsMobile
                sx={{ minHeight: 38, '& .MuiTab-root': { minHeight: 38, textTransform: 'none', fontWeight: 900 } }}
              >
                {Object.keys((scrollListEditor.heading && typeof scrollListEditor.heading === 'object') ? (scrollListEditor.heading as any) : { de: true })
                  .filter((k) => typeof k === 'string' && k)
                  .map((k) => (
                    <Tab
                      key={`sl-lang-${k}`}
                      value={k}
                      label={(
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Box component="span" sx={{ fontSize: 18, lineHeight: 1 }}>{heroCarouselFlag(k)}</Box>
                          <Box component="span">{String(k).toUpperCase()}</Box>
                        </Box>
                      )}
                    />
                  ))}
              </Tabs>
              <Button
                variant="outlined"
                size="small"
                onClick={(e) => setScrollListLangAnchor(e.currentTarget)}
                sx={{ textTransform: 'none', fontWeight: 900, borderRadius: 999 }}
              >
                + Sprache
              </Button>
              <Popover
                open={!!scrollListLangAnchor}
                anchorEl={scrollListLangAnchor}
                onClose={() => setScrollListLangAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <Box sx={{ p: 1, minWidth: 220 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>Sprache hinzufügen</Typography>
                  {(['de','en','tr','fr','es','it','pt','ar','hi','zh'] as string[]).map((code) => {
                    const existing = (scrollListEditor.heading && typeof scrollListEditor.heading === 'object')
                      ? Object.prototype.hasOwnProperty.call(scrollListEditor.heading as any, code)
                      : (code === 'de');
                    return (
                      <MenuItem
                        key={`sl-addlang-${code}`}
                        disabled={existing}
                        onClick={() => {
                          const baseLang = scrollListLang || 'de';
                          setScrollListEditor((prev) => ({
                            ...prev,
                            heading: setLocalizedString(
                              (typeof prev.heading === 'string') ? ({ [baseLang]: prev.heading } as any) : prev.heading,
                              code,
                              getLocalizedString(prev.heading, code, baseLang, '')
                            ),
                            items: prev.items.map((it) => ({
                              ...it,
                              title: setLocalizedString(
                                (typeof it.title === 'string') ? ({ [baseLang]: it.title } as any) : it.title,
                                code,
                                getLocalizedString(it.title, code, baseLang, '')
                              ),
                            })),
                          }));
                          setScrollListLang(code);
                          setScrollListLangAnchor(null);
                        }}
                        sx={{ fontWeight: 800 }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box component="span" sx={{ fontSize: 18, lineHeight: 1 }}>{heroCarouselFlag(code)}</Box>
                          <Box component="span">{code.toUpperCase()}</Box>
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Box>
              </Popover>
            </Box>

            <TextField
              label={`Überschrift (${scrollListLang.toUpperCase()})`}
              value={getLocalizedString(scrollListEditor.heading, scrollListLang, 'de', '')}
              onChange={(e) => setScrollListEditor(prev => ({ ...prev, heading: setLocalizedString(prev.heading, scrollListLang, e.target.value) }))}
              fullWidth
            />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2, alignItems: 'center' }}>
              <Box sx={{ display: 'grid', gap: 0.5 }}>
                <Typography variant="caption" color="text.secondary">Rundung (0 = eckig, 50 = Kreis)</Typography>
                <Slider value={scrollListEditor.roundness} onChange={(_, v) => setScrollListEditor(prev => ({ ...prev, roundness: Math.max(0, Math.min(50, Number(v) || 0)) }))} min={0} max={50} step={1} />
              </Box>
              <Box sx={{ display: 'grid', gap: 0.5 }}>
                <Typography variant="caption" color="text.secondary">Größe</Typography>
                <Slider value={scrollListEditor.itemSize} onChange={(_, v) => setScrollListEditor(prev => ({ ...prev, itemSize: Math.max(72, Math.min(180, Number(v) || 112)) }))} min={72} max={180} step={1} />
              </Box>
              <Box sx={{ display: 'grid', gap: 0.5 }}>
                <Typography variant="caption" color="text.secondary">Abstand</Typography>
                <Slider value={scrollListEditor.gap} onChange={(_, v) => setScrollListEditor(prev => ({ ...prev, gap: Math.max(6, Math.min(28, Number(v) || 18)) }))} min={6} max={28} step={1} />
              </Box>
            </Box>

            <Divider />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Items</Typography>
              <Button variant="outlined" onClick={() => setScrollListEditor(prev => ({ ...prev, items: [...prev.items, { image: '', title: { [scrollListLang || 'de']: 'Neues Item' }, linkUrl: '' }] }))} sx={{ textTransform: 'none', fontWeight: 800 }}>
                Item hinzufügen
              </Button>
            </Box>

            <Box sx={{ display: 'grid', gap: 2 }}>
              {scrollListEditor.items.length === 0 ? (
                <Typography variant="body2" color="text.secondary">Noch keine Items.</Typography>
              ) : (
                scrollListEditor.items.map((it, idx) => (
                  <Card key={`scroll-item-${idx}`} sx={{ borderRadius: 3, boxShadow: '0 10px 26px rgba(2,6,23,0.08)' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
                        <Typography sx={{ fontWeight: 900 }}>Item #{idx + 1}</Typography>
                        <IconButton color="error" onClick={() => setScrollListEditor(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }))} size="small" title="Entfernen">
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, alignItems: 'center' }}>
                        <TextField
                          label={`Titel (${scrollListLang.toUpperCase()})`}
                          value={getLocalizedString(it.title, scrollListLang, 'de', '')}
                          onChange={(e) => setScrollListEditor(prev => ({ ...prev, items: prev.items.map((x, i) => i === idx ? { ...x, title: setLocalizedString(x.title, scrollListLang, e.target.value) } : x) }))}
                          fullWidth
                        />
                        <TextField label="Link (optional)" value={it.linkUrl || ''} onChange={(e) => setScrollListEditor(prev => ({ ...prev, items: prev.items.map((x, i) => i === idx ? { ...x, linkUrl: e.target.value } : x) }))} placeholder="/ oder https://..." fullWidth />
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                        <Button variant="outlined" onClick={() => setScrollListItemPicker({ open: true, index: idx })} sx={{ textTransform: 'none', fontWeight: 800 }}>
                          Bild wählen
                        </Button>
                        {it.image ? (
                          <Button variant="text" color="error" onClick={() => setScrollListEditor(prev => ({ ...prev, items: prev.items.map((x, i) => i === idx ? { ...x, image: '' } : x) }))} sx={{ textTransform: 'none', fontWeight: 800 }}>
                            Entfernen
                          </Button>
                        ) : null}
                        {it.image ? (
                          <Box component="img" src={toPublicUrl(it.image)} alt="item" sx={{ width: 56, height: 56, borderRadius: 2, objectFit: 'cover', border: '1px solid', borderColor: 'divider' }} />
                        ) : null}
                      </Box>
                    </CardContent>
                  </Card>
                ))
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScrollListEditor(prev => ({ ...prev, open: false, sectionId: null }))}>Abbrechen</Button>
          <Button variant="contained" onClick={() => {
            const sectionId = scrollListEditor.sectionId;
            if (!sectionId) { setScrollListEditor(prev => ({ ...prev, open: false, sectionId: null })); return; }
            setBuilderSections((prev) => updateSectionById(prev, String(sectionId), (s: any) => ({
              ...s,
              type: 'scrolllist',
              title: 'Scroll List',
              config: {
                enabled: scrollListEditor.enabled,
                heading: scrollListEditor.heading,
                roundness: scrollListEditor.roundness,
                itemSize: scrollListEditor.itemSize,
                gap: scrollListEditor.gap,
                items: scrollListEditor.items
                  .map((it: any) => ({ image: String(it?.image || ''), title: (typeof it?.title === 'string' || (it?.title && typeof it?.title === 'object')) ? it.title : { de: '' }, linkUrl: String(it?.linkUrl || '') }))
                  .filter((it: any) => it.image && getLocalizedString(it.title, 'de', 'de', '')),
              },
            })).next);
            markDirty(sectionId);
            setScrollListEditor(prev => ({ ...prev, open: false, sectionId: null }));
          }}>Speichern</Button>
        </DialogActions>
      </Dialog>

      <MediaPickerModal
        open={scrollListItemPicker.open}
        onClose={() => setScrollListItemPicker({ open: false, index: null })}
        onSelect={(it: MediaItem) => {
          const idx = scrollListItemPicker.index;
          const path = normalizePickedUrl(it.url);
          if (idx != null) {
            setScrollListEditor(prev => ({ ...prev, items: prev.items.map((x, i) => i === idx ? { ...x, image: path } : x) }));
          }
          setScrollListItemPicker({ open: false, index: null });
        }}
      />

      {/* Add Component Dialog */}
      <Dialog open={addComponentOpen} onClose={() => setAddComponentOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 800 }}>Komponente hinzufügen</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1 }}>
            <Button variant="outlined" onClick={() => {
              setAddComponentOpen(false);
              addHeroSection();
            }} sx={{ justifyContent: 'flex-start', textTransform: 'none' }}>
              <AnimationIcon sx={{ mr: 1 }} /> Hero Section
            </Button>
            <Button variant="outlined" onClick={() => {
              setAddComponentOpen(false);
              addHeroCarouselSection();
            }} sx={{ justifyContent: 'flex-start', textTransform: 'none' }}>
              <ViewCarouselIcon sx={{ mr: 1 }} /> Hero Carousel
            </Button>
            <Button variant="outlined" onClick={() => {
              setAddComponentOpen(false);
              addProductsSection();
            }} sx={{ justifyContent: 'flex-start', textTransform: 'none' }}>
              <LocalOfferIcon sx={{ mr: 1 }} /> Produkte
            </Button>
            <Button variant="outlined" onClick={() => {
              setAddComponentOpen(false);
              addCtaSection();
            }} sx={{ justifyContent: 'flex-start', textTransform: 'none' }}>
              <AddIcon sx={{ mr: 1 }} /> CTA
            </Button>
            <Button variant="outlined" onClick={() => {
              setAddComponentOpen(false);
              addReviewsSection();
            }} sx={{ justifyContent: 'flex-start', textTransform: 'none' }}>
              <AddIcon sx={{ mr: 1 }} /> Reviews
            </Button>
            <Button variant="outlined" onClick={() => {
              setAddComponentOpen(false);
              addMediaTextSection();
            }} sx={{ justifyContent: 'flex-start', textTransform: 'none' }}>
              <AddPhotoAlternateIcon sx={{ mr: 1 }} /> Media + Text
            </Button>
            <Button variant="outlined" onClick={() => {
              setAddComponentOpen(false);
              addScrollListSection();
            }} sx={{ justifyContent: 'flex-start', textTransform: 'none' }}>
              <ViewCarouselIcon sx={{ mr: 1 }} /> Scroll Liste
            </Button>
            <Button variant="outlined" onClick={() => {
              setAddComponentOpen(false);
              addGallerySection();
            }} sx={{ justifyContent: 'flex-start', textTransform: 'none' }}>
              <AddPhotoAlternateIcon sx={{ mr: 1 }} /> Gallery
            </Button>
            <Button variant="outlined" onClick={() => {
              setAddComponentOpen(false);
              addGridSection();
            }} sx={{ justifyContent: 'flex-start', textTransform: 'none' }}>
              <TableChartIcon sx={{ mr: 1 }} /> Grid
            </Button>
            <Button variant="outlined" onClick={() => {
              setAddComponentOpen(false);
              addSideNavSection();
            }} sx={{ justifyContent: 'flex-start', textTransform: 'none' }}>
              <MenuRoundedIcon sx={{ mr: 1 }} /> Side navigation
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddComponentOpen(false)}>Abbrechen</Button>
        </DialogActions>
      </Dialog>

      {/* Side Navigation Editor */}
      <Dialog open={sidenavEditor.open} onClose={() => setSidenavEditor(prev => ({ ...prev, open: false, sectionId: null }))} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 900 }}>Side navigation</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gap: 2 }}>
            <FormControlLabel control={<Switch checked={sidenavEditor.enabled} onChange={(e) => setSidenavEditor(prev => ({ ...prev, enabled: e.target.checked }))} />} label="Aktiv" />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
              <Tabs
                value={sidenavLang}
                onChange={(_, v) => setSidenavLang(String(v || 'de'))}
                variant="scrollable"
                scrollButtons
                allowScrollButtonsMobile
                sx={{ minHeight: 38, '& .MuiTab-root': { minHeight: 38, textTransform: 'none', fontWeight: 900 } }}
              >
                {(() => {
                  const keys = Array.from(new Set([
                    ...Object.keys((sidenavEditor.buttonTooltip && typeof sidenavEditor.buttonTooltip === 'object') ? (sidenavEditor.buttonTooltip as any) : { de: true }),
                    ...Object.keys((sidenavEditor.popoverTitle && typeof sidenavEditor.popoverTitle === 'object') ? (sidenavEditor.popoverTitle as any) : { de: true }),
                    ...Object.keys((sidenavEditor.popoverSubtitle && typeof sidenavEditor.popoverSubtitle === 'object') ? (sidenavEditor.popoverSubtitle as any) : { de: true }),
                    ...Object.keys((sidenavEditor.popoverHint && typeof sidenavEditor.popoverHint === 'object') ? (sidenavEditor.popoverHint as any) : { de: true }),
                    ...(sidenavEditor.tabs || []).flatMap(t => Object.keys(((t as any)?.label && typeof (t as any)?.label === 'object') ? ((t as any).label as any) : { de: true })),
                  ].filter(Boolean)));
                  const list = keys.length > 0 ? keys : ['de'];
                  return list.map((k) => (
                    <Tab
                      key={`sidenav-lang-${k}`}
                      value={k}
                      label={(
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Box component="span" sx={{ fontSize: 18, lineHeight: 1 }}>{heroCarouselFlag(k)}</Box>
                          <Box component="span">{String(k).toUpperCase()}</Box>
                        </Box>
                      )}
                    />
                  ));
                })()}
              </Tabs>

              <Button
                variant="outlined"
                size="small"
                onClick={(e) => setSidenavLangAnchor(e.currentTarget)}
                sx={{ textTransform: 'none', fontWeight: 900, borderRadius: 999 }}
              >
                + Sprache
              </Button>
              <Popover
                open={!!sidenavLangAnchor}
                anchorEl={sidenavLangAnchor}
                onClose={() => setSidenavLangAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <Box sx={{ p: 1, minWidth: 220 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>Sprache hinzufügen</Typography>
                  {(['de','en','tr','fr','es','it','pt','ar','hi','zh'] as string[]).map((code) => {
                    const existing = (() => {
                      const has = (obj: any) => (obj && typeof obj === 'object') ? Object.prototype.hasOwnProperty.call(obj as any, code) : (code === 'de');
                      if (has(sidenavEditor.buttonTooltip)) return true;
                      if (has(sidenavEditor.popoverTitle)) return true;
                      if (has(sidenavEditor.popoverSubtitle)) return true;
                      if (has(sidenavEditor.popoverHint)) return true;
                      for (const t of (sidenavEditor.tabs || [])) {
                        if (has((t as any)?.label)) return true;
                      }
                      return false;
                    })();
                    return (
                      <MenuItem
                        key={`sidenav-addlang-${code}`}
                        disabled={existing}
                        onClick={() => {
                          const baseLang = sidenavLang || 'de';
                          setSidenavEditor((prev) => ({
                            ...prev,
                            buttonTooltip: setLocalizedString(
                              (typeof prev.buttonTooltip === 'string') ? ({ [baseLang]: prev.buttonTooltip } as any) : prev.buttonTooltip,
                              code,
                              getLocalizedString(prev.buttonTooltip, code, baseLang, '')
                            ),
                            popoverTitle: setLocalizedString(
                              (typeof prev.popoverTitle === 'string') ? ({ [baseLang]: prev.popoverTitle } as any) : prev.popoverTitle,
                              code,
                              getLocalizedString(prev.popoverTitle, code, baseLang, '')
                            ),
                            popoverSubtitle: setLocalizedString(
                              (typeof prev.popoverSubtitle === 'string') ? ({ [baseLang]: prev.popoverSubtitle } as any) : prev.popoverSubtitle,
                              code,
                              getLocalizedString(prev.popoverSubtitle, code, baseLang, '')
                            ),
                            popoverHint: setLocalizedString(
                              (typeof prev.popoverHint === 'string') ? ({ [baseLang]: prev.popoverHint } as any) : prev.popoverHint,
                              code,
                              getLocalizedString(prev.popoverHint, code, baseLang, '')
                            ),
                            tabs: (prev.tabs || []).map((t) => ({
                              ...t,
                              label: setLocalizedString(
                                (typeof (t as any)?.label === 'string') ? ({ [baseLang]: (t as any).label } as any) : (t as any)?.label,
                                code,
                                getLocalizedString((t as any)?.label, code, baseLang, '')
                              ),
                            })),
                          }));
                          setSidenavLang(code);
                          setSidenavLangAnchor(null);
                        }}
                        sx={{ fontWeight: 800 }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box component="span" sx={{ fontSize: 18, lineHeight: 1 }}>{heroCarouselFlag(code)}</Box>
                          <Box component="span">{code.toUpperCase()}</Box>
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Box>
              </Popover>
            </Box>

            <TextField
              label="Tooltip (Button)"
              value={getLocalizedString(sidenavEditor.buttonTooltip, sidenavLang, 'de', '')}
              onChange={(e) => setSidenavEditor(prev => ({ ...prev, buttonTooltip: setLocalizedString(prev.buttonTooltip, sidenavLang, e.target.value) }))}
              fullWidth
            />

            <TextField
              label="Popover title"
              value={getLocalizedString(sidenavEditor.popoverTitle, sidenavLang, 'de', '')}
              onChange={(e) => setSidenavEditor(prev => ({ ...prev, popoverTitle: setLocalizedString(prev.popoverTitle, sidenavLang, e.target.value) }))}
              fullWidth
            />

            <TextField
              label="Popover subtitle"
              value={getLocalizedString(sidenavEditor.popoverSubtitle, sidenavLang, 'de', '')}
              onChange={(e) => setSidenavEditor(prev => ({ ...prev, popoverSubtitle: setLocalizedString(prev.popoverSubtitle, sidenavLang, e.target.value) }))}
              fullWidth
            />

            <TextField
              label="Popover hint (footer)"
              value={getLocalizedString(sidenavEditor.popoverHint, sidenavLang, 'de', '')}
              onChange={(e) => setSidenavEditor(prev => ({ ...prev, popoverHint: setLocalizedString(prev.popoverHint, sidenavLang, e.target.value) }))}
              fullWidth
            />

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                Corner radius (Trigger): {Math.round(sidenavEditor.triggerRadius)}
              </Typography>
              <Slider
                value={sidenavEditor.triggerRadius}
                onChange={(_, v) => setSidenavEditor(prev => ({ ...prev, triggerRadius: Array.isArray(v) ? Number(v[0]) : Number(v) }))}
                min={0}
                max={999}
                step={1}
              />
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                Corner radius (Popup): {Math.round(sidenavEditor.popupRadius)}
              </Typography>
              <Slider
                value={sidenavEditor.popupRadius}
                onChange={(_, v) => setSidenavEditor(prev => ({ ...prev, popupRadius: Array.isArray(v) ? Number(v[0]) : Number(v) }))}
                min={0}
                max={64}
                step={1}
              />
            </Box>

            <Divider />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
              <Typography sx={{ fontWeight: 900 }}>Tabs</Typography>
              <Button
                variant="contained"
                size="small"
                onClick={() => setSidenavEditor(prev => ({ ...prev, tabs: [...prev.tabs, { label: { [sidenavLang]: '' } as any, url: '' }] }))}
                sx={{ textTransform: 'none', fontWeight: 900, borderRadius: 999 }}
              >
                + Tab
              </Button>
            </Box>

            <Box sx={{ display: 'grid', gap: 1.25 }}>
              {sidenavEditor.tabs.map((t, i) => (
                <Card key={`sidenav-tab-edit-${i}`} sx={{ borderRadius: 3, boxShadow: '0 12px 28px rgba(2,6,23,0.08)' }}>
                  <CardContent sx={{ display: 'grid', gap: 1.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                      <Typography sx={{ fontWeight: 900 }}>Tab {i + 1}</Typography>
                      <IconButton size="small" color="error" title="Entfernen" onClick={() => setSidenavEditor(prev => ({ ...prev, tabs: prev.tabs.filter((_, idx) => idx !== i) }))}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <TextField
                      label={`Label (${sidenavLang.toUpperCase()})`}
                      value={getLocalizedString(t.label, sidenavLang, 'de', '')}
                      onChange={(e) => setSidenavEditor(prev => ({ ...prev, tabs: prev.tabs.map((x, idx) => idx === i ? { ...x, label: setLocalizedString(x.label, sidenavLang, e.target.value) } : x) }))}
                      fullWidth
                    />
                    <TextField
                      label="Link / URL"
                      value={t.url}
                      onChange={(e) => setSidenavEditor(prev => ({ ...prev, tabs: prev.tabs.map((x, idx) => idx === i ? { ...x, url: e.target.value } : x) }))}
                      placeholder="/angebote oder https://..."
                      fullWidth
                    />
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSidenavEditor(prev => ({ ...prev, open: false, sectionId: null }))}>Abbrechen</Button>
          <Button variant="contained" onClick={() => {
            const sectionId = sidenavEditor.sectionId;
            if (!sectionId) { setSidenavEditor(prev => ({ ...prev, open: false, sectionId: null })); return; }
            setBuilderSections((prev) => updateSectionById(prev, String(sectionId), (s: any) => ({
              ...s,
              type: 'sidenav',
              title: 'Side navigation',
              config: {
                enabled: sidenavEditor.enabled,
                buttonTooltip: sidenavEditor.buttonTooltip,
                triggerRadius: Math.max(0, Math.min(999, Number(sidenavEditor.triggerRadius) || 0)),
                popupRadius: Math.max(0, Math.min(64, Number(sidenavEditor.popupRadius) || 0)),
                popoverTitle: sidenavEditor.popoverTitle,
                popoverSubtitle: sidenavEditor.popoverSubtitle,
                popoverHint: sidenavEditor.popoverHint,
                tabs: (sidenavEditor.tabs || [])
                  .map((t) => ({ label: (typeof t?.label === 'string' ? ({ de: String(t?.label || '') } as any) : (t?.label && typeof t?.label === 'object' ? t.label : ({ de: '' } as any))), url: String(t?.url || '') }))
                  .filter((t) => String(t.url || '').trim() || getLocalizedString(t.label, 'de', 'de', '').trim()),
              },
            })).next);
            markDirty(sectionId);
            setSidenavEditor(prev => ({ ...prev, open: false, sectionId: null }));
          }}>Speichern</Button>
        </DialogActions>
      </Dialog>

      {/* Promo image editor dialog */}
      <Dialog open={promoImgEditor.open} onClose={() => setPromoImgEditor({ open: false, path: '', url: '' })} maxWidth="sm" fullWidth>
        <DialogTitle>Bild-Einstellungen</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {promoImgEditor.path && (
                <Box component="img" src={toPublicUrl(promoImgEditor.path)} alt="Promo" sx={{ width: 80, height: 80, borderRadius: 2, objectFit: 'cover', border: '1px solid', borderColor: 'divider' }} />
              )}
              <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>{promoImgEditor.path}</Typography>
            </Box>
            <TextField label="Ziel-URL (optional)" value={promoImgEditor.url} onChange={(e) => setPromoImgEditor(prev => ({ ...prev, url: e.target.value }))} placeholder="https://example.com" fullWidth />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPromoImgEditor({ open: false, path: '', url: '' })}>Abbrechen</Button>
          <Button variant="contained" onClick={() => { setPromoImagesLinks(prev => ({ ...prev, ...(promoImgEditor.path ? { [promoImgEditor.path]: promoImgEditor.url.trim() } : {}) })); setPromoImgEditor({ open: false, path: '', url: '' }); }}>Speichern</Button>
        </DialogActions>
      </Dialog>

      {/* Seiten (Pages) Section - directly above Grid */}
      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 12px 32px rgba(2,6,23,0.10)' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="h6" fontWeight={700}>Seiten</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton onClick={() => { try { toast.dismiss(); toast('Öffne Local Storage…', { id: 'ls-open', duration: 1200 }); } catch {} setLsDialogOpen(true); setTimeout(() => refreshLsDump(), 0); }} size="small" title="Local Storage anzeigen" aria-label="Local Storage anzeigen">
                <InfoOutlinedIcon />
              </IconButton>
              <IconButton onClick={() => setPagesOpen(v => !v)} size="small" sx={{ transform: pagesOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform .2s ease' }}>
                <ExpandMoreIcon />
              </IconButton>
            </Box>
          </Box>
          <Collapse in={pagesOpen} timeout={200}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Verwalte Seiten-Tabs. "Startseite" ist immer vorhanden und kann nicht gelöscht werden.
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            {pagesTabs.map((tb, i) => (
              <Box key={`ptab-${i}`} sx={{ display: 'flex', alignItems: 'center' }}>
                <Button
                  variant={i === pagesActiveTab ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => { setPagesActiveTab(i); setPageBuilderIndex(i); try { if (typeof window !== 'undefined') { const u = (pagesTabs[i]?.url || '').trim(); window.localStorage.setItem('admin.pageBuilder.pageUrl', u); window.localStorage.setItem('admin.pageBuilder.index', String(i)); window.localStorage.setItem('admin.pages.activeTab', String(i)); window.localStorage.setItem('admin.pageBuilder.open', '1'); } } catch {} setPageBuilderOpen(true); }}
                  sx={{ textTransform: 'none', borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                >
                  {tb.label || (i === 0 ? 'Startseite' : `Seite ${i+1}`)}
                </Button>
                {i > 0 && (tb?.url || '').toLowerCase() !== '/404' && (
                  <IconButton size="small" color="error" onClick={() => {
                    setPagesTabs(prev => {
                      const next = prev.filter((_, idx) => idx !== i);
                      const fixed = next.length > 0 ? next : [{ label: 'Startseite', url: '/' }, { label: '404 Seite', url: '/404' }];
                      const nextActive = fixed.length === 0 ? 0 : (i === pagesActiveTab ? 0 : (pagesActiveTab > i ? pagesActiveTab - 1 : pagesActiveTab));
                      try { if (typeof window !== 'undefined') window.localStorage.setItem('admin.pages.activeTab', String(nextActive)); } catch {}
                      setPagesActiveTab(nextActive);
                      void savePages(fixed, []);
                      return fixed;
                    });
                  }} sx={{ border: '1px solid', borderColor: 'divider', borderLeft: 'none', borderTopRightRadius: 999, borderBottomRightRadius: 999 }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ))}
            <Button variant="text" size="small" onClick={() => {
              setPagesDialogName('');
              setPagesDialogUrl('/');
              setPagesDialogOpen(true);
            }} sx={{ textTransform: 'none', fontWeight: 700 }}>+ Neue Seite</Button>
          </Box>
          </Collapse>
        </CardContent>
      </Card>

      {/* Pages Create Dialog */}
      <Dialog open={pagesDialogOpen} onClose={() => setPagesDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 800 }}>Neue Seite anlegen</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Vergib einen Namen und eine URL. Die URL sollte mit einem Schrägstrich beginnen, z. B. "/angebote".
          </Typography>
          {(() => {
            const current = normalizePageUrl(pagesDialogUrl || '/');
            const existsUrl = pagesTabs.some(p => normalizePageUrl(p?.url || '/') === current);
            if (!pagesDialogName.trim() || !existsUrl) return null;
            const suggested = suggestUniquePageUrl(current);
            return (
              <Box sx={{ mb: 2, p: 1.25, borderRadius: 2, border: '1px solid', borderColor: 'warning.light', bgcolor: 'rgba(245, 158, 11, 0.08)' }}>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  Diese URL ist bereits vergeben.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Vorschlag: <Box component="span" sx={{ fontWeight: 900, color: 'text.primary' }}>{suggested}</Box>
                </Typography>
                <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                  <Button size="small" variant="outlined" onClick={() => setPagesDialogUrl(suggested)} sx={{ textTransform: 'none', fontWeight: 800 }}>
                    Vorschlag übernehmen
                  </Button>
                </Box>
              </Box>
            );
          })()}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
            <TextField
              label="Name"
              value={pagesDialogName}
              onChange={(e) => {
                const val = e.target.value;
                setPagesDialogName(val);
                // Derive URL from title (lowercase, spaces -> '-', strip invalid, ensure leading '/')
                const slug = (val || '')
                  .toLowerCase()
                  .replace(/\s+/g, '-')
                  .replace(/[^a-z0-9\-\/]+/g, '')
                  .replace(/\/{2,}/g, '/');
                const withSlash = slug.startsWith('/') ? slug : `/${slug}`;
                setPagesDialogUrl(withSlash === '/' ? '/' : withSlash.replace(/\/$/, ''));
              }}
              autoFocus
              fullWidth
            />
            <TextField
              label="URL (automatisch)"
              value={pagesDialogUrl}
              InputProps={{ readOnly: true }}
              fullWidth
              helperText="Wird aus dem Titel erzeugt: klein geschrieben, Leerzeichen → Bindestrich (-)."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPagesDialogOpen(false)} disabled={pagesSaving}>Abbrechen</Button>
          <Button variant="contained" onClick={async () => {
            if (!pagesDialogName.trim()) { toast.error('Bitte einen Namen eingeben.'); return; }
            // Derive URL from title reliably on submit
            let url = (pagesDialogName || '')
              .toLowerCase()
              .replace(/\s+/g, '-')
              .replace(/[^a-z0-9\-\/]+/g, '')
              .replace(/\/{2,}/g, '/');
            if (!url.startsWith('/')) url = '/' + url;
            if (url === '') url = '/';
            url = normalizePageUrl(url);
            // basic whitelist
            if (!/^\/[a-z0-9\-\/]*$/.test(url)) { toast.error('Ungültige URL. Erlaubt sind Kleinbuchstaben, Ziffern, Bindestriche und /.'); return; }
            // frontend duplicate checks
            const nameTrim = pagesDialogName.trim().toLowerCase();
            const existsName = pagesTabs.some(p => (p.label || '').trim().toLowerCase() === nameTrim);
            const existsUrl = pagesTabs.some(p => (p.url || '').trim().toLowerCase() === url);
            if (existsName || existsUrl) {
              const suggested = suggestUniquePageUrl(url);
              setPagesDialogUrl(suggested);
              toast.error('Diese Seite/URL existiert bereits. Ich habe dir einen freien Vorschlag vorbereitet.');
              return;
            }
            const newTab = { label: pagesDialogName.trim(), url };
            const next = [...pagesTabs, newTab];
            setPagesSaving(true);
            try {
              const ok = await savePages(next, [newTab]);
              if (ok) {
                setPagesTabs(next);
                setPagesActiveTab(next.length - 1);
                setPagesDialogOpen(false);
              }
            } finally { setPagesSaving(false); }
          }} disabled={pagesSaving}>Anlegen</Button>
        </DialogActions>
      </Dialog>

      <GridEditor
        render={() => (
          <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 12px 32px rgba(2,6,23,0.10)', opacity: 0.6, filter: 'grayscale(0.3)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="h6" fontWeight={700}>Grid</Typography>
                <IconButton onClick={() => {}} disabled size="small" sx={{ opacity: 0.6 }}>
                  <ExpandMoreIcon />
                </IconButton>
              </Box>
              <Collapse in={false} timeout={200}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Erstelle attraktive Kacheln in einem Raster. Klicke auf eine Zelle, um sie zu bearbeiten.</Typography>
          {/* Grid Tabs Bar */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            {gridTabs.map((tb, i) => (
              <Box key={`gtab-${i}`} sx={{ display: 'flex', alignItems: 'center' }}>
                <Button
                  variant={i === gridActiveTab ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => { persistEditingIntoActiveTab(); setGridActiveTab(i); loadFromTab(i); }}
                  sx={{ textTransform: 'none', borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                >
                  {tb.label || `Tab ${i+1}`}
                </Button>
                <IconButton size="small" color="error" onClick={() => removeGridTab(i)} sx={{ border: '1px solid', borderColor: 'divider', borderLeft: 'none', borderTopRightRadius: 999, borderBottomRightRadius: 999 }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button variant="text" size="small" onClick={addGridTab} sx={{ textTransform: 'none', fontWeight: 700 }}>+ Neuer Tab</Button>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <Box sx={{ minWidth: 220 }}>
              <Typography variant="caption" color="text.secondary">Tabs Position</Typography>
              <Select size="small" fullWidth value={gridTabsPosition} onChange={(e) => setGridTabsPosition(e.target.value as ('above'|'below'))}>
                <MenuItem value="above">Über dem Grid</MenuItem>
                <MenuItem value="below">Unter dem Grid</MenuItem>
              </Select>
            </Box>
            <Box sx={{ minWidth: 220 }}>
              <Typography variant="caption" color="text.secondary">Tabs Stil</Typography>
              <Select size="small" fullWidth value={gridTabsStyle} onChange={(e) => setGridTabsStyle(e.target.value as ('labels'|'dots'))}>
                <MenuItem value="labels">Beschriftete Tabs</MenuItem>
                <MenuItem value="dots">Nur Punkte</MenuItem>
              </Select>
            </Box>
            <FormControlLabel
              control={<Switch checked={gridTabsArrows} onChange={(e) => setGridTabsArrows(e.target.checked)} />}
              label="Seitliche Handler anzeigen"
            />
            <Box sx={{ minWidth: 220 }}>
              <Typography variant="caption" color="text.secondary">Tabs Animation</Typography>
              <Select size="small" fullWidth value={gridTabsTransitionType} onChange={(e) => setGridTabsTransitionType(e.target.value as ('none'|'slide'|'tiles'|'cube'))}>
                <MenuItem value="none">Keine</MenuItem>
                <MenuItem value="slide">Slide (ganzer Grid)</MenuItem>
                <MenuItem value="tiles">Tiles Stagger</MenuItem>
                <MenuItem value="cube">3D Cube</MenuItem>
              </Select>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 2 }}>
            <FormControlLabel control={<Switch checked={gridEnabled} onChange={(e) => setGridEnabled(e.target.checked)} />} label={gridEnabled ? 'Aktiviert' : 'Deaktiviert'} />
            <TextField label="Titel" value={gridTitle} onChange={(e) => setGridTitle(e.target.value)} sx={{ minWidth: 320 }} />
            <TextField type="number" label="Reihen" value={gridRows} onChange={(e) => setGridRows(Math.max(1, Number(e.target.value)||1))} sx={{ width: 140 }} />
            <TextField type="number" label="Spalten" value={gridCols} onChange={(e) => setGridCols(Math.max(1, Number(e.target.value)||1))} sx={{ width: 140 }} />
            <Box sx={{ display: 'grid', gap: 0.5, minWidth: 260 }}>
              <Typography variant="caption" color="text.secondary">Zeilenhöhe (px)</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Slider value={gridRowHeight} onChange={(_, v) => setGridRowHeight(Math.max(80, Math.min(480, Number(v) || 160)))} min={80} max={480} step={5} sx={{ width: 180 }} />
                <TextField type="number" value={gridRowHeight} onChange={(e)=> setGridRowHeight(Math.max(80, Math.min(480, Number(e.target.value)||160)))} sx={{ width: 90 }} />
              </Box>
            </Box>
          </Box>
          <Divider sx={{ my: 1.5 }} />
          {/* Live Grid Preview */}
          <Box sx={{
            border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2,
            background: 'linear-gradient(135deg, rgba(59,130,246,0.04), rgba(236,72,153,0.04))',
            position: 'relative',
          }}>
            <Box ref={gridPreviewRef} sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, 1fr)',
                sm: `repeat(${Math.max(2, Math.min(3, gridCols))}, 1fr)`,
                md: (gridColWidths.length === Math.max(1, Number(gridCols) || 1)) ? gridColWidths.map(v => `${Math.max(0.25, v)}fr`).join(' ') : `repeat(${gridCols}, 1fr)`
              },
              gridAutoRows: `${Math.max(80, Math.min(480, Number(gridRowHeight)||160))}px`,
              gap: 1.5,
            }}>
              {Array.from({ length: Math.max(1, gridRows * gridCols) }).map((_, idx) => {
                const it = gridTiles[idx];
                const imgsArr = Array.isArray((it as any)?.images) ? (it as any).images.filter((s: any)=> typeof s === 'string' && s) : [];
                const singleImg = typeof it?.image === 'string' && it.image ? it.image : '';
                const gallery = imgsArr.length > 0 ? imgsArr : (singleImg ? [singleImg] : []);
                const intervalSec = Math.max(2, Math.min(60, Number((it as any)?.imageInterval) || 5));
                const curImg = gallery.length > 0 ? gallery[(Math.floor(Date.now() / (intervalSec * 1000))) % gallery.length] : '';
                const imgUrl = curImg ? toPublicUrl(curImg) : '';
                const bg = it?.bgColor || (imgUrl ? 'transparent' : '#f8fafc');
                const color = it?.textColor || (imgUrl ? '#ffffff' : 'rgba(0,0,0,0.85)');
                return (
                  <Box key={idx} onClick={() => openTileEditor(idx)} sx={{ position: 'relative', cursor: 'pointer', borderRadius: 2, overflow: 'hidden', boxShadow: it ? 2 : 1, border: '1px solid', borderColor: it?.enabled === false && it?.reserveSpace ? 'warning.light' : 'divider', minHeight: `${Math.max(80, Math.min(480, Number(gridRowHeight)||160))}px`, transition: 'all .2s', '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' }, '&:hover .tile-controls': { opacity: 1, pointerEvents: 'auto', transform: 'translateY(0px)' }, background: bg, opacity: it?.enabled === false ? 0.45 : 1, filter: it?.enabled === false ? 'grayscale(0.3)' : 'none', gridColumn: `span ${Math.max(1, it?.colSpan || 1)}`, gridRow: `span ${Math.max(1, (it as any)?.rowSpan || 1)}` }}>
                    {imgUrl && (
                      <Box component="img" src={imgUrl} alt={it?.title || `Tile ${idx+1}`} sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: 'brightness(0.96)' }} />
                    )}
                    <Box sx={{ position: 'absolute', inset: 0, background: imgUrl ? 'linear-gradient(to top, rgba(0,0,0,0.45), rgba(0,0,0,0.10))' : 'transparent' }} />
                    {it?.showCounter && !it?.counterFree && (
                      <Box sx={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', zIndex: 2 }}>
                        <Counter
                          value={1234}
                          prefix={''}
                          suffix={''}
                          prefixNewline={false}
                          suffixNewline={false}
                          variant={'classic'}
                          bg={'rgba(255,255,255,0.9)'}
                          textColor={'#111827'}
                          radius={8}
                          scale={1}
                          valueFontSize={28}
                          prefixFontSize={12}
                          suffixFontSize={12}
                          gradientFrom={'#60a5fa'}
                          gradientTo={'#22c55e'}
                          ringWidth={10}
                          shadow={'soft'}
                          numberLocale={counterNumberLocale}
                        />
                      </Box>
                    )}
                    {/* List embedding in live preview */}
                    {(it as any)?.showList === true && Number.isInteger((it as any)?.listTabIndex) && (it as any).listTabIndex >= 0 && (it as any).listTabIndex < listTabs.length && (
                      <Box sx={{ position: 'absolute', zIndex: 2, ...( ((it as any)?.listFullSize === true) ? { inset: 8 } : { left: '50%', top: '50%', transform: 'translate(-50%, -50%)', width: '88%' }) }}>
                        {(() => {
                          const tab = listTabs[(it as any).listTabIndex] as any;
                          const title = typeof tab?.title === 'string' ? tab.title : '';
                          const items = Array.isArray(tab?.items) ? tab.items : [];
                          const size = Math.max(20, Math.min(160, Number(tab?.itemSize) || 40));
                          const iconColor = typeof tab?.iconColor === 'string' ? tab.iconColor : undefined;
                          const iconBg = typeof tab?.iconBg === 'string' ? tab.iconBg : undefined;
                          return (
                            <ListBuilder
                              title={title}
                              items={items}
                              itemSize={size}
                              iconColor={iconColor}
                              iconBg={iconBg}
                              showSettingsIcon
                              fullSize={(it as any)?.listFullSize === true}
                              scrollDir={(it as any)?.listScrollDir === 'horizontal' ? 'horizontal' : 'vertical'}
                              onOpenSettings={() => setListSettingsDialog({ open: true, index: idx })}
                            />
                          );
                        })()}
                      </Box>
                    )}
                    <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 6, display: 'flex', gap: .5 }}>
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleTileEnabled(idx); }} sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }}>
                        {it?.enabled === false ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); setGridTiles(prev => prev.map((x, i) => i===idx ? { ...x, reserveSpace: x.reserveSpace === true ? false : true } : x)); }} sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }} title="Platz freihalten">
                        {it?.reserveSpace ? <CropSquareIcon fontSize="small" /> : <CropSquareOutlinedIcon fontSize="small" />}
                      </IconButton>
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); openTileEditor(idx); }} sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }} title="Bearbeiten">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Box sx={{ position: 'absolute', bottom: 8, right: 8, zIndex: 6 }}>
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); clearGridTile(idx); }} sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' } }} title="Kachel löschen">
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    {/* Span controls on left/right edges */}
                    <Box sx={{ position: 'absolute', top: '50%', left: 6, transform: 'translateY(-50%)', zIndex: 6 }}>
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); expandTile(idx, -1); }} sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' }, p: 0.25 }} title="schmaler">
                        <KeyboardArrowLeftIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Box sx={{ position: 'absolute', top: '50%', right: 6, transform: 'translateY(-50%)', zIndex: 6 }}>
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); expandTile(idx, 1); }} sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' }, p: 0.25 }} title="breiter">
                        <KeyboardArrowRightIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    {/* Row span controls on top/bottom edges */}
                    <Box sx={{ position: 'absolute', top: 6, left: '50%', transform: 'translateX(-50%)', zIndex: 6 }}>
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); expandTileVertical(idx, -1); }} sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' }, p: 0.25 }} title="niedriger">
                        <KeyboardArrowUpIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Box sx={{ position: 'absolute', bottom: 6, left: '50%', transform: 'translateX(-50%)', zIndex: 6 }}>
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); expandTileVertical(idx, 1); }} sx={{ bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,1)' }, p: 0.25 }} title="höher">
                        <KeyboardArrowDownIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    {it?.enabled === false && it?.reserveSpace && (
                      <Box sx={{ position: 'absolute', inset: 0, border: '2px dashed', borderColor: 'warning.light', borderRadius: 2 }} />
                    )}
                    {it?.hoverEffect && it.hoverEffect !== 'none' && (
                      <Box sx={{ position: 'absolute', left: 8, bottom: 8, zIndex: 2, display: 'flex', alignItems: 'center', gap: .5, bgcolor: 'rgba(255,255,255,0.9)', color: 'text.primary', px: 1, py: .25, borderRadius: 999, boxShadow: 1 }}>
                        <AnimationIcon sx={{ fontSize: 16 }} />
                        <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'capitalize' }}>
                          {it.hoverEffect === 'zoomGlow' ? 'Zoom + Glow' : it.hoverEffect}
                        </Typography>
                      </Box>
                    )}
                    {/* CTA fixed position */}
                    {!!it?.ctaText && it?.ctaFree !== true && (
                      <Box sx={{ position: 'absolute', left: 16, bottom: 16, zIndex: 2 }}>
                        <Button variant="contained" size="small" href={it?.ctaUrl || '#'} sx={{
                          bgcolor: (it as any)?.ctaBtnBg ? (it as any).ctaBtnBg : (imgUrl ? 'rgba(59,130,246,0.95)' : undefined),
                          color: (it as any)?.ctaBtnTextColor || undefined,
                          borderRadius: 9999,
                          px: 1.5,
                          minWidth: `${Math.max(80, Math.min(400, Number((it as any)?.ctaWidth) || 140))}px`,
                          fontSize: `${Math.max(10, Math.min(36, Number((it as any)?.ctaFontSize) || 14))}px`,
                          transition: 'transform .2s ease, box-shadow .2s ease, background-color .2s ease',
                          ...(((it as any)?.ctaHoverEffect) === 'lift' && { '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 } }),
                          ...(((it as any)?.ctaHoverEffect) === 'glow' && { '&:hover': { boxShadow: '0 10px 28px rgba(0,0,0,0.25)' } }),
                          ...(((it as any)?.ctaHoverEffect) === 'zoom' && { '&:hover': { transform: 'scale(1.05)' } }),
                          ...(((it as any)?.ctaHoverEffect) === 'zoomGlow' && { '&:hover': { transform: 'scale(1.05)', boxShadow: '0 12px 32px rgba(0,0,0,0.28)' } }),
                        }}>
                          {it.ctaText}
                        </Button>
                      </Box>
                    )}
                    {/* Content type selector (default vs counter vs list) */}
                    <Box className="tile-controls" sx={{ position: 'absolute', left: 8, bottom: (Math.max(1, it?.colSpan || 1) === 1) ? 56 : 16, zIndex: 4, opacity: 0, pointerEvents: 'none', transform: 'translateY(8px)', transition: 'opacity .15s ease, transform .15s ease' }} onClick={(e)=> e.stopPropagation()}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexDirection: (Math.max(1, it?.colSpan || 1) === 1) ? 'column' : 'row', bgcolor: 'rgba(255,255,255,0.92)', borderRadius: 1, px: 0.5, py: 0.25, boxShadow: 1, maxWidth: '68%', pointerEvents: 'auto' }}>
                        <Select
                          size="small"
                          value={(it as any)?.showList ? 'list' : (it?.showCounter ? 'counter' : 'default')}
                          onChange={(e) => {
                            const v = String(e.target.value);
                            setGridTiles(prev => prev.map((x, i) => {
                              if (i !== idx) return x;
                              if (v === 'list') { return { ...x, showList: true, listTabIndex: Number.isInteger((x as any)?.listTabIndex) ? (x as any).listTabIndex : (listTabs.length > 0 ? 0 : -1), showCounter: false }; }
                              if (v === 'counter') { return { ...x, showCounter: true, showList: false }; }
                              return { ...x, showCounter: false, showList: false };
                            }));
                          }}
                          displayEmpty
                          sx={{ minWidth: 120, '& .MuiSelect-select': { py: 0.5 } }}
                        >
                          <MenuItem value="default">Default</MenuItem>
                          <MenuItem value="counter">Counter</MenuItem>
                          <MenuItem value="list">List</MenuItem>
                        </Select>
                        {(it as any)?.showList && (
                          <Select
                            size="small"
                            value={Number.isInteger((it as any)?.listTabIndex) ? (it as any).listTabIndex : -1}
                            onChange={(e) => {
                              const v = Number(e.target.value);
                              setGridTiles(prev => prev.map((x, i) => i === idx ? { ...x, listTabIndex: v } : x));
                            }}
                            sx={{ minWidth: 140, '& .MuiSelect-select': { py: 0.5 }, mt: (Math.max(1, it?.colSpan || 1) === 1) ? 0.5 : 0 }}
                          >
                            <MenuItem value={-1} disabled>Liste wählen</MenuItem>
                            {listTabs.map((tb, i2) => (
                              <MenuItem key={`lt-opt-${i2}`} value={i2}>{tb.label || `Liste ${i2+1}`}</MenuItem>
                            ))}
                          </Select>
                        )}
                      </Box>
                    </Box>
                    {/* CTA free placement */}
                    {!!it?.ctaText && it?.ctaFree === true && (
                      <Box sx={{ position: 'absolute', left: `${Math.max(0, Math.min(100, Number(it?.ctaX) || 15))}%`, top: `${Math.max(0, Math.min(100, Number(it?.ctaY) || 80))}%`, transform: 'translate(-50%, -50%)', zIndex: 2 }}>
                        <Button variant="contained" size="small" href={it?.ctaUrl || '#'} sx={{
                          bgcolor: (it as any)?.ctaBtnBg ? (it as any).ctaBtnBg : (imgUrl ? 'rgba(59,130,246,0.95)' : undefined),
                          color: (it as any)?.ctaBtnTextColor || undefined,
                          borderRadius: 9999,
                          px: 1.5,
                          minWidth: `${Math.max(80, Math.min(400, Number((it as any)?.ctaWidth) || 140))}px`,
                          fontSize: `${Math.max(10, Math.min(36, Number((it as any)?.ctaFontSize) || 14))}px`,
                          transform: `scale(${Math.max(0.5, Math.min(3, Number((it as any)?.ctaScale) || 1))})`,
                          transformOrigin: 'center',
                          transition: 'transform .2s ease, box-shadow .2s ease, background-color .2s ease',
                          ...(((it as any)?.ctaHoverEffect) === 'lift' && { '&:hover': { transform: `translateY(-2px) scale(${Math.max(0.5, Math.min(3, Number((it as any)?.ctaScale) || 1))})`, boxShadow: 6 } }),
                          ...(((it as any)?.ctaHoverEffect) === 'glow' && { '&:hover': { boxShadow: '0 10px 28px rgba(0,0,0,0.25)' } }),
                          ...(((it as any)?.ctaHoverEffect) === 'zoom' && { '&:hover': { transform: `scale(${Math.max(0.5, Math.min(3, Number((it as any)?.ctaScale) || 1)) * 1.05}` } }),
                          ...(((it as any)?.ctaHoverEffect) === 'zoomGlow' && { '&:hover': { transform: `scale(${Math.max(0.5, Math.min(3, Number((it as any)?.ctaScale) || 1)) * 1.05}`, boxShadow: '0 12px 32px rgba(0,0,0,0.28)' } }),
                        }}>
                          {it.ctaText}
                        </Button>
                      </Box>
                    )}
                    <Box sx={{ position: 'relative', zIndex: 1, color: imgUrl ? 'white' : color, p: 2, display: 'grid', gap: .5 }}>
                      <Typography variant="subtitle1" fontWeight={800} sx={{ textShadow: imgUrl ? '0 1px 3px rgba(0,0,0,.5)' : 'none' }}>{it?.title || `Kachel ${idx+1}`}</Typography>
                      {it?.subtitle && (
                        <Typography variant="body2" sx={{ opacity: .9, textShadow: imgUrl ? '0 1px 2px rgba(0,0,0,.45)' : 'none' }}>{it.subtitle}</Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
            {/* Column helper guide lines (responsive, overlayed above tiles) */}
            {/* xs: 2 columns */}
            <Box sx={{ position: 'absolute', inset: 16, pointerEvents: 'none', zIndex: 60, display: { xs: 'block', sm: 'none' } }}>
              <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: `${(1 / 2) * 100}%`, transform: 'translateX(-6px)', width: 12, pointerEvents: 'auto', cursor: 'col-resize', display: 'flex', alignItems: 'stretch', justifyContent: 'center' }}>
                <Box sx={{ width: 2, bgcolor: '#3b82f6', opacity: 0.6, transition: 'all .12s ease', '&:hover': { opacity: 0.98, bgcolor: '#2563eb', boxShadow: '0 0 0 2px rgba(37,99,235,0.25)' } }} />
              </Box>
            </Box>
            {/* sm: up to 3 columns */}
            <Box sx={{ position: 'absolute', inset: 16, pointerEvents: 'none', zIndex: 60, display: { xs: 'none', sm: 'block', md: 'none' } }}>
              {(() => { const cols = Math.max(2, Math.min(3, gridCols)); return Array.from({ length: Math.max(0, cols - 1) }).map((_, i) => (
                <Box key={`guide-sm2-${i}`} sx={{ position: 'absolute', top: 0, bottom: 0, left: `${((i + 1) / cols) * 100}%`, transform: 'translateX(-6px)', width: 12, pointerEvents: 'auto', cursor: 'col-resize', display: 'flex', alignItems: 'stretch', justifyContent: 'center' }}>
                  <Box sx={{ width: 2, bgcolor: '#3b82f6', opacity: 0.6, transition: 'all .12s ease', '&:hover': { opacity: 0.98, bgcolor: '#2563eb', boxShadow: '0 0 0 2px rgba(37,99,235,0.25)' } }} />
                </Box>
              )); })()}
            </Box>
            {/* md+: gridCols columns with segmentation (avoid spanning tiles) and draggable handles */}
            {gridCols > 1 && (
              <Box sx={{ position: 'absolute', inset: 16, pointerEvents: 'none', zIndex: 60, display: { xs: 'none', md: 'block' } }}>
                {(() => {
                  const rows = Math.max(1, Number(gridRows) || 1);
                  const cols = Math.max(1, Number(gridCols) || 1);
                  const rowH = Math.max(80, Math.min(480, Number(gridRowHeight) || 160));
                  const gapPx = 12; // matches gap: 1.5 (8px * 1.5)
                  const total = rows * cols;
                  const tiles = Array.from({ length: total }).map((_, i) => gridTiles[i] || { colSpan: 1, rowSpan: 1 });
                  // build cumulative percentage positions for each boundary based on current fr widths
                  const widths = (gridColWidths.length === cols && gridColWidths.every(n => typeof n === 'number' && n > 0))
                    ? gridColWidths
                    : Array.from({ length: cols }).map(() => 1);
                  const totalFr = widths.reduce((a, v) => a + v, 0) || cols;
                  const cumPerc: number[] = []; // boundary b at cumPerc[b]
                  let accFr = 0;
                  for (let c = 0; c < cols; c++) {
                    accFr += widths[c];
                    if (c < cols - 1) cumPerc[c + 1] = (accFr / totalFr) * 100;
                  }
                  const mat: number[][] = Array.from({ length: rows }, () => Array(cols).fill(-1));
                  const fits = (r: number, c: number, w: number, h: number) => {
                    if (c + w > cols || r + h > rows) return false;
                    for (let rr = r; rr < r + h; rr++) {
                      for (let cc = c; cc < c + w; cc++) {
                        if (mat[rr][cc] !== -1) return false;
                      }
                    }
                    return true;
                  };
                  outer: for (let ti = 0; ti < tiles.length; ti++) {
                    const w = Math.max(1, Math.min(cols, Number((tiles[ti] as any)?.colSpan) || 1));
                    const h = Math.max(1, Math.min(rows, Number((tiles[ti] as any)?.rowSpan) || 1));
                    for (let r = 0; r < rows; r++) {
                      for (let c = 0; c < cols; c++) {
                        if (fits(r, c, w, h)) {
                          for (let rr = r; rr < r + h; rr++) {
                            for (let cc = c; cc < c + w; cc++) mat[rr][cc] = ti;
                          }
                          continue outer;
                        }
                      }
                    }
                  }
                  const nodes: React.ReactElement[] = [];
                  for (let b = 1; b < cols; b++) {
                    let active = false;
                    let segStart = 0;
                    for (let r = 0; r < rows; r++) {
                      const blocked = mat[r][b - 1] === mat[r][b];
                      if (blocked) {
                        if (active) {
                          const top = segStart * (rowH + gapPx);
                          const height = r * (rowH + gapPx) - top - gapPx; // stop before gap to the blocked row
                          if (height > 0) nodes.push(
                            <Box key={`guide-md2-${b}-${segStart}`}
                              onMouseDown={(e) => {
                                e.preventDefault(); e.stopPropagation();
                                const root = gridPreviewRef.current; if (!root) return;
                                const rect = root.getBoundingClientRect();
                                const start = [...gridColWidths];
                                const minFr = 0.25;
                                const pairSumFr = (start[b - 1] + start[b]) || 2;
                                const totalFrAll = start.reduce((a, v) => a + v, 0) || cols;
                                const sumBefore = start.slice(0, b - 1).reduce((a, v) => a + v, 0);
                                const onMove = (ev: MouseEvent) => {
                                  let ratio = (ev.clientX - rect.left) / rect.width; if (!isFinite(ratio)) ratio = 0; ratio = Math.max(0, Math.min(1, ratio));
                                  const targetCumFr = ratio * totalFrAll;
                                  let leftNew = targetCumFr - sumBefore;
                                  const minLeft = minFr, maxLeft = Math.max(minFr, pairSumFr - minFr);
                                  if (leftNew < minLeft) leftNew = minLeft; if (leftNew > maxLeft) leftNew = maxLeft;
                                  const rightNew = Math.max(minFr, pairSumFr - leftNew);
                                  setGridColWidths(prev => { const cur = (prev.length === cols) ? [...prev] : [...start]; cur[b - 1] = leftNew; cur[b] = rightNew; return cur; });
                                };
                                const onUp = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
                                window.addEventListener('mousemove', onMove);
                                window.addEventListener('mouseup', onUp);
                              }}
                              sx={{ position: 'absolute', left: `${cumPerc[b]}%`, top: `${top}px`, height: `${height}px`, width: 12, transform: 'translateX(-6px)', cursor: 'col-resize', display: 'flex', alignItems: 'stretch', justifyContent: 'center', pointerEvents: 'auto' }}
                            >
                              <Box sx={{ width: 2, bgcolor: '#3b82f6', opacity: 0.6, transition: 'all .12s ease', '&:hover': { opacity: 0.98, bgcolor: '#2563eb', boxShadow: '0 0 0 2px rgba(37,99,235,0.25)' }, flex: '0 0 auto' }} />
                            </Box>
                          );
                          active = false;
                        }
                      } else if (!active) {
                        segStart = r;
                        active = true;
                      }
                    }
                    if (active) {
                      const top = segStart * (rowH + gapPx);
                      const height = rows * (rowH + gapPx) - top;
                      nodes.push(
                        <Box key={`guide-md2-${b}-${segStart}-end`}
                          onMouseDown={(e) => {
                            e.preventDefault(); e.stopPropagation();
                            const root = gridPreviewRef.current; if (!root) return;
                            const rect = root.getBoundingClientRect();
                            const start = [...gridColWidths];
                            const minFr = 0.25;
                            const pairSumFr = (start[b - 1] + start[b]) || 2;
                            const totalFrAll = start.reduce((a, v) => a + v, 0) || cols;
                            const sumBefore = start.slice(0, b - 1).reduce((a, v) => a + v, 0);
                            const onMove = (ev: MouseEvent) => {
                              let ratio = (ev.clientX - rect.left) / rect.width; if (!isFinite(ratio)) ratio = 0; ratio = Math.max(0, Math.min(1, ratio));
                              const targetCumFr = ratio * totalFrAll;
                              let leftNew = targetCumFr - sumBefore;
                              const minLeft = minFr, maxLeft = Math.max(minFr, pairSumFr - minFr);
                              if (leftNew < minLeft) leftNew = minLeft; if (leftNew > maxLeft) leftNew = maxLeft;
                              const rightNew = Math.max(minFr, pairSumFr - leftNew);
                              setGridColWidths(prev => { const cur = (prev.length === cols) ? [...prev] : [...start]; cur[b - 1] = leftNew; cur[b] = rightNew; return cur; });
                            };
                            const onUp = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
                            window.addEventListener('mousemove', onMove);
                            window.addEventListener('mouseup', onUp);
                          }}
                          sx={{ position: 'absolute', left: `${cumPerc[b]}%`, top: `${top}px`, height: `${height}px`, width: 12, transform: 'translateX(-6px)', cursor: 'col-resize', display: 'flex', alignItems: 'stretch', justifyContent: 'center', pointerEvents: 'auto' }}
                        >
                          <Box sx={{ width: 2, bgcolor: '#3b82f6', opacity: 0.6, transition: 'all .12s ease', '&:hover': { opacity: 0.98, bgcolor: '#2563eb', boxShadow: '0 0 0 2px rgba(37,99,235,0.25)' }, flex: '0 0 auto' }} />
                        </Box>
                      );
                    }
                    // (drag handle replaced by the line wrapper above)
                  }
                  return nodes;
                })()}
              </Box>
            )}
          </Box>
          <Box sx={{ mt: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Button variant="contained" onClick={saveGrid} disabled={savingGrid || deletingGrid} sx={{ textTransform: 'none', fontWeight: 700 }}>{savingGrid ? 'Speichern…' : 'Einstellungen speichern'}</Button>
            <Button variant="outlined" color="error" onClick={deleteGridSection} disabled={deletingGrid || savingGrid} sx={{ textTransform: 'none', fontWeight: 700 }}>
              {deletingGrid ? 'Lösche…' : 'Grid löschen'}
            </Button>
          </Box>
          </Collapse>
        </CardContent>
      </Card>
      )}
      />

      {/* Gallery Picker Dialog */}
      <Dialog open={galleryPickerOpen} onClose={closeGalleryPicker} maxWidth="md" fullWidth>
        <DialogTitle>Aus Galerie wählen</DialogTitle>
        <DialogContent dividers>
          {gallery.length === 0 ? (
            <Typography color="text.secondary">Keine Galerie-Bilder vorhanden.</Typography>
          ) : (
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' },
              gap: 1.5,
            }}>
              {gallery.map((p) => {
                const selected = selectedGalleryPath === p;
                return (
                  <Box
                    key={p}
                    onClick={() => setSelectedGalleryPath(p)}
                    sx={{
                      position: 'relative',
                      borderRadius: 2,
                      overflow: 'hidden',
                      border: '2px solid',
                      borderColor: selected ? 'primary.main' : 'divider',
                      cursor: 'pointer',
                      aspectRatio: '1 / 1',
                    }}
                  >
                    <Box component="img" src={toPublicUrl(p)} alt={p} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    {selected && (
                      <Box sx={{ position: 'absolute', inset: 0, boxShadow: 'inset 0 0 0 3px rgba(59,130,246,0.8)' }} />
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeGalleryPicker}>Abbrechen</Button>
          <Button variant="contained" onClick={saveHeroFromGallery} disabled={!selectedGalleryPath}>Als Hintergrund setzen</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={gridPickerOpen.open} onClose={closeGridPicker} maxWidth="md" fullWidth>
        <DialogTitle>Grid-Bilder aus Media Gallery wählen</DialogTitle>
        <DialogContent dividers>
          {gallery.length === 0 ? (
            <Typography color="text.secondary">Keine Galerie-Bilder vorhanden.</Typography>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
              {gallery.map((p) => (
                <Box
                  key={p}
                  onClick={() => {
                    setGridPickerSelection(prev => { const next = new Set(prev); if (next.has(p)) next.delete(p); else next.add(p); return next; });
                  }}
                  sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', border: '2px solid', borderColor: gridPickerSelection.has(p) ? 'primary.main' : 'divider', cursor: 'pointer', aspectRatio: '1 / 1' }}
                >
                  <Box component="img" src={toPublicUrl(p)} alt={p} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  {gridPickerSelection.has(p) && <Box sx={{ position: 'absolute', inset: 0, boxShadow: 'inset 0 0 0 3px rgba(59,130,246,0.9)' }} />}
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeGridPicker}>Abbrechen</Button>
          {tileEditorOpen && tileEditorIndex === gridPickerOpen.index ? (
            <Button
              variant="contained"
              disabled={gridPickerSelection.size === 0}
              onClick={() => {
                const toAdd = Array.from(gridPickerSelection);
                setTeImages(prev => Array.from(new Set([...(prev || []), ...toAdd])));
                closeGridPicker();
              }}
            >
              Zur Galerie hinzufügen
            </Button>
          ) : (
            <Button
              variant="contained"
              disabled={gridPickerSelection.size !== 1}
              onClick={() => {
                const [path] = Array.from(gridPickerSelection);
                if (path) setGridTileImage(path);
                closeGridPicker();
              }}
            >
              Als Hintergrund setzen
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Grid Tile Editor Dialog */}
      <Dialog open={tileEditorOpen} onClose={closeTileEditor} maxWidth="sm" fullWidth fullScreen={tileEditorFullScreen}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          Kachel bearbeiten
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton size="small" onClick={() => setTileEditorFullScreen(v => !v)}>
              {tileEditorFullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gap: 2 }}>
            {/* Image preview & picker */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Box sx={{ width: 96, height: 96, borderRadius: 2, overflow: 'hidden', bgcolor: 'grey.100', border: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
                {teImages.length > 0 ? (
                  <Box component="img" src={toPublicUrl(teImages[0])} alt="Tile" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (tileEditorIndex != null && gridTiles[tileEditorIndex]?.image ? (
                  <Box component="img" src={toPublicUrl(gridTiles[tileEditorIndex].image)} alt="Tile" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <Box sx={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center', color: 'text.disabled' }}>
                    <AddPhotoAlternateIcon />
                  </Box>
                ))}
              </Box>
              <Button variant="outlined" startIcon={<AddPhotoAlternateIcon />} onClick={() => { if (tileEditorIndex != null) { setGridPickerOpen({ open: true, index: tileEditorIndex }); } }} sx={{ textTransform: 'none', fontWeight: 600 }}>
                Aus Media Gallery wählen
              </Button>
            </Box>

            {/* Gallery thumbnails and interval (inside dialog) */}
            <Box sx={{ display: 'grid', gap: 1.5 }}>
              <Typography variant="subtitle2" color="text.secondary">Hintergrund-Galerie</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                {teImages.map((p, idx) => (
                  <React.Fragment key={p+idx}>
                    {(teImgDropIndex === idx && teImgDropSide === 'left') && (
                      <Box sx={{ width: 8, height: 72, borderRadius: 1, bgcolor: 'rgba(59,130,246,0.35)', boxShadow: 'inset 0 0 0 2px rgba(59,130,246,0.6)', transition: 'all .15s ease' }} />
                    )}
                    <Box
                      draggable
                      onDragStart={() => { setTeImgDragIdx(idx); setTeImgDragging(true); }}
                      onDragOver={(e) => {
                        e.preventDefault();
                        const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
                        const midX = rect.left + rect.width / 2;
                        const side = e.clientX < midX ? 'left' : 'right';
                        setTeImgDropIndex(idx);
                        setTeImgDropSide(side);
                      }}
                      onDragEnd={() => { setTeImgDragging(false); setTeImgDragIdx(null); setTeImgDropIndex(null); setTeImgDropSide(null); }}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (teImgDragIdx == null) return;
                        const insertAt = (() => {
                          if (teImgDropIndex == null) return idx;
                          const base = teImgDropIndex + (teImgDropSide === 'right' ? 1 : 0);
                          return base;
                        })();
                        setTeImages(prev => {
                          const arr = [...prev];
                          const [moved] = arr.splice(teImgDragIdx, 1);
                          let target = insertAt;
                          if (teImgDragIdx < insertAt) target = insertAt - 1;
                          target = Math.max(0, Math.min(arr.length, target));
                          arr.splice(target, 0, moved);
                          return arr;
                        });
                        setTeImgDragging(false);
                        setTeImgDragIdx(null);
                        setTeImgDropIndex(null);
                        setTeImgDropSide(null);
                      }}
                      sx={{
                        position: 'relative',
                        width: 72,
                        height: 72,
                        borderRadius: 1,
                        overflow: 'hidden',
                        boxShadow: 1,
                        outline: teImgDragIdx === idx ? '2px dashed rgba(59,130,246,0.8)' : 'none',
                        transition: 'transform .18s ease, box-shadow .18s ease, opacity .18s ease',
                        transform: teImgDragIdx === idx && teImgDragging ? 'scale(0.95)' : 'scale(1)',
                        opacity: teImgDragIdx === idx && teImgDragging ? 0.7 : 1,
                        cursor: teImgDragging && teImgDragIdx === idx ? 'grabbing' : 'grab',
                        '&:hover': { transform: teImgDragIdx === idx ? undefined : 'scale(1.03)', boxShadow: 2 },
                      }}
                    >
                      <Box component="img" src={toPublicUrl(p)} alt="thumb" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <IconButton size="small" onClick={() => setTeImages(prev => prev.filter((_, i) => i !== idx))} sx={{ position: 'absolute', top: 0, right: 0, bgcolor: 'rgba(0,0,0,0.45)', color: '#fff' }}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    {(teImgDropIndex === idx && teImgDropSide === 'right') && (
                      <Box sx={{ width: 8, height: 72, borderRadius: 1, bgcolor: 'rgba(59,130,246,0.35)', boxShadow: 'inset 0 0 0 2px rgba(59,130,246,0.6)', transition: 'all .15s ease' }} />
                    )}
                  </React.Fragment>
                ))}
                <IconButton size="large" onClick={() => { if (tileEditorIndex != null) openGridPicker(tileEditorIndex); }} sx={{ width: 72, height: 72, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                  <AddPhotoAlternateIcon />
                </IconButton>
              </Box>
              {teImages.length > 1 && (
                <TextField type="number" label="Wechselintervall (Sekunden)" value={teImageInterval} onChange={(e)=> setTeImageInterval(Math.max(2, Math.min(60, Number(e.target.value)||5)))} inputProps={{ step: 1 }} sx={{ maxWidth: 280 }} />
              )}
            </Box>

            <TextField label="Titel" value={teTitle} onChange={(e) => setTeTitle(e.target.value)} fullWidth />
            <TextField label="Untertitel" value={teSubtitle} onChange={(e) => setTeSubtitle(e.target.value)} fullWidth />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField label="CTA Text" value={teCtaText} onChange={(e) => setTeCtaText(e.target.value)} />
              <TextField label="CTA URL" value={teCtaUrl} onChange={(e) => setTeCtaUrl(e.target.value)} />
            </Box>

            {/* Color pickers */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField label="Hintergrundfarbe" value={teBgColor} onChange={(e) => setTeBgColor(e.target.value)} placeholder="#ffffff" fullWidth />
                <input type="color" value={teBgColor || '#ffffff'} onChange={(e) => setTeBgColor(e.target.value)} style={{ width: 44, height: 44, border: 'none', background: 'transparent', cursor: 'pointer' }} />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField label="Textfarbe" value={teTextColor} onChange={(e) => setTeTextColor(e.target.value)} placeholder="#111827" fullWidth />
                <input type="color" value={teTextColor || '#111827'} onChange={(e) => setTeTextColor(e.target.value)} style={{ width: 44, height: 44, border: 'none', background: 'transparent', cursor: 'pointer' }} />
              </Box>
            </Box>

            {/* CTA Positioning */}
            <FormControlLabel control={<Switch checked={teCtaFree} onChange={(e) => setTeCtaFree(e.target.checked)} />} label="CTA frei platzieren" />
            {teCtaFree && (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
                <TextField type="number" label="CTA X (%)" value={teCtaX} onChange={(e)=> setTeCtaX(Math.max(0, Math.min(100, Number(e.target.value)||0)))} />
                <TextField type="number" label="CTA Y (%)" value={teCtaY} onChange={(e)=> setTeCtaY(Math.max(0, Math.min(100, Number(e.target.value)||0)))} />
                <TextField type="number" label="CTA Größe (x)" value={teCtaScale} onChange={(e)=> setTeCtaScale(Math.max(0.5, Math.min(3, Number(e.target.value)||1)))} inputProps={{ step: 0.1 }} />
              </Box>
            )}

            {/* Hover Effect */}
            <FormControl fullWidth>
              <InputLabel id="tile-hover-effect-label">Hover-Effekt</InputLabel>
              <Select labelId="tile-hover-effect-label" label="Hover-Effekt" value={teHoverEffect} onChange={(e) => setTeHoverEffect(String(e.target.value))}>
                <MenuItem value="none">Keiner</MenuItem>
                <MenuItem value="zoom">Zoom</MenuItem>
                <MenuItem value="lift">Lift</MenuItem>
                <MenuItem value="glow">Glow</MenuItem>
                <MenuItem value="zoomGlow">Zoom + Glow</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={<Switch checked={teShowCounter} onChange={(e) => setTeShowCounter(e.target.checked)} />}
              label="Counter anzeigen"
            />
            {teShowCounter && (
              <>
                <FormControlLabel
                  control={<Switch checked={teCounterFree} onChange={(e) => setTeCounterFree(e.target.checked)} />}
                  label="Counter frei platzieren"
                />
                {/* Counter Tab selection */}
                <FormControl fullWidth sx={{ mt: 1 }}>
                  <InputLabel id="tile-counter-tab-label">Counter-Tab</InputLabel>
                  <Select labelId="tile-counter-tab-label" label="Counter-Tab" value={Number.isInteger(teCounterTabIndex) ? teCounterTabIndex : -1} onChange={(e) => setTeCounterTabIndex(Number(e.target.value))}>
                    <MenuItem value={-1}>Globaler Counter</MenuItem>
                    {(Array.isArray(counterTabs) ? counterTabs : []).map((tb, i) => (
                      <MenuItem key={`ctsel-${i}`} value={i}>{tb?.label || `Counter ${i+1}`}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {teCounterFree && (
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
                    <TextField type="number" label="Counter X (%)" value={teCounterX} onChange={(e)=> setTeCounterX(Math.max(0, Math.min(100, Number(e.target.value)||0)))} />
                    <TextField type="number" label="Counter Y (%)" value={teCounterY} onChange={(e)=> setTeCounterY(Math.max(0, Math.min(100, Number(e.target.value)||0)))} />
                    <TextField type="number" label="Counter Größe (x)" value={teCounterScale} onChange={(e)=> setTeCounterScale(Math.max(0.5, Math.min(3, Number(e.target.value)||1)))} inputProps={{ step: 0.1 }} />
                  </Box>
                )}
              </>
            )}

            {/* Live Preview */}
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, opacity: 0.8 }}>Live-Vorschau</Typography>
            {(() => {
              const previewGallery = teImages && teImages.length > 0 ? teImages : (tileEditorIndex != null && gridTiles[tileEditorIndex]?.image ? [gridTiles[tileEditorIndex].image] : []);
              const img = previewGallery.length > 0 ? toPublicUrl(previewGallery[0]) : '';
              const bg = teBgColor || (img ? 'transparent' : 'linear-gradient(135deg, #f8fafc, #eef2ff)');
              const color = teTextColor || (img ? '#ffffff' : 'text.primary');
              const he = teHoverEffect || 'none';
              const span = Math.max(1, Math.min(gridCols, teColSpan || 1));
              return (
                <Box sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: `repeat(${Math.max(2, Math.min(3, gridCols))}, 1fr)`, md: `repeat(${gridCols}, 1fr)` },
                  gap: 1.5,
                  p: 1,
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: 2,
                  bgcolor: 'background.default'
                }}>
                  <Card sx={{
                    gridColumn: `span ${span}`,
                    position: 'relative',
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: 2,
                    minHeight: 200,
                    bgcolor: bg,
                    color,
                    transition: 'transform .3s ease, box-shadow .3s ease, filter .3s ease',
                    ...(he === 'zoom' && { '&:hover': { transform: 'scale(1.02)' } }),
                    ...(he === 'lift' && { '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 } }),
                    ...(he === 'glow' && { '&:hover': { boxShadow: '0 12px 32px rgba(0,0,0,0.25)' } }),
                    ...(he === 'zoomGlow' && { '&:hover': { transform: 'scale(1.02)', boxShadow: '0 14px 36px rgba(0,0,0,0.28)' } }),
                  }}>
                    {img && (
                      <Box component="img" src={img} alt={teTitle || 'Tile'} sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: 'brightness(.9)' }} />
                    )}
                    <Box sx={{ position: 'absolute', inset: 0, background: img ? 'linear-gradient(180deg, rgba(0,0,0,0.10) 0%, rgba(0,0,0,0.35) 55%, rgba(0,0,0,0.55) 100%)' : 'transparent' }} />
                    {teShowCounter && !teCounterFree && (
                      <Box sx={{ position: 'absolute', right: 14, bottom: 14, zIndex: 2, bgcolor: teCounterBg, color: teCounterTextColor, backdropFilter: 'blur(6px)', borderRadius: `${teCounterRadius}px`, px: 1.5, py: 0.75, boxShadow: '0 10px 24px rgba(0,0,0,0.18)' }}>
                        {!!teCounterPrefix && teCounterPrefixNewline && (
                          <Typography component="div" sx={{ fontWeight: 800, fontSize: 12, opacity: .85, mb: .25 }}>{teCounterPrefix}</Typography>
                        )}
                        <Typography component="div" sx={{ fontWeight: 900, fontSize: 28, lineHeight: 1 }}>
                          {(!teCounterPrefixNewline ? (teCounterPrefix || '') : '')}{typeof counterTabs[teCounterTabIndex]?.count === 'number' ? new Intl.NumberFormat('de-DE').format(counterTabs[teCounterTabIndex]?.count) : new Intl.NumberFormat('de-DE').format(1234)}{(!teCounterSuffixNewline ? (teCounterSuffix || '') : '')}
                        </Typography>
                        {!!teCounterSuffix && teCounterSuffixNewline && (
                          <Typography component="div" sx={{ fontWeight: 800, fontSize: 12, opacity: .85, mt: .25 }}>{teCounterSuffix}</Typography>
                        )}
                        <Typography variant="caption" sx={{ opacity: 0.7, fontWeight: 700 }}>Placeholder</Typography>
                      </Box>
                    )}
                    {teShowCounter && teCounterFree && (
                      <Box sx={{ position: 'absolute', left: `${teCounterX}%`, top: `${teCounterY}%`, transform: 'translate(-50%, -50%)', zIndex: 3 }}>
                        {/* Snap helper guides */}
                        {typeof teCounterSnapX === 'number' && (
                          <Box sx={{ position: 'absolute', left: `${teCounterSnapX}%`, top: '-9999px', bottom: '-9999px', width: 0, borderLeft: '1.5px dashed rgba(59,130,246,0.8)' }} />
                        )}
                        {typeof teCounterSnapY === 'number' && (
                          <Box sx={{ position: 'absolute', top: `${teCounterSnapY}%`, left: '-9999px', right: '-9999px', height: 0, borderTop: '1.5px dashed rgba(59,130,246,0.8)' }} />
                        )}
                        <Box sx={{ position: 'relative' }}
                          onMouseDown={(e) => {
                            const card = (e.currentTarget.parentElement?.parentElement as HTMLElement) || null;
                            if (!card) return;
                            const startX = e.clientX; const startY = e.clientY;
                            const rect = card.getBoundingClientRect();
                            setTeCounterDragging(true);
                            const prevUserSelect = document.body.style.userSelect;
                            document.body.style.userSelect = 'none';
                            const onMove = (ev: MouseEvent) => {
                              const dx = ev.clientX - startX; const dy = ev.clientY - startY;
                              let nx = Math.max(0, Math.min(100, teCounterX + (dx / rect.width) * 100));
                              let ny = Math.max(0, Math.min(100, teCounterY + (dy / rect.height) * 100));
                              const points = [0, 25, 50, 75, 100];
                              const thresh = 3; // percent
                              let snapX: number | null = null; let snapY: number | null = null;
                              for (const p of points) { if (Math.abs(nx - p) <= thresh) { nx = p; snapX = p; break; } }
                              for (const p of points) { if (Math.abs(ny - p) <= thresh) { ny = p; snapY = p; break; } }
                              setTeCounterX(nx); setTeCounterY(ny);
                              setTeCounterSnapX(snapX); setTeCounterSnapY(snapY);
                            };
                            const onUp = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); setTeCounterDragging(false); setTeCounterSnapX(null); setTeCounterSnapY(null); document.body.style.userSelect = prevUserSelect; };
                            window.addEventListener('mousemove', onMove);
                            window.addEventListener('mouseup', onUp);
                          }}
                        >
                          {/* Unscaled wrapper: gear + handles stay crisp */}
                          <Box sx={{ cursor: 'move', position: 'relative', outline: teCounterDragging || teCounterResizing ? '2px dashed rgba(59,130,246,0.8)' : 'none', outlineOffset: 4 }}>
                            {/* Scaled inner content only */}
                            <Box sx={{ transform: `scale(${teCounterScale})`, transformOrigin: 'center' }}>
                              <Box sx={{ bgcolor: teCounterBg, color: teCounterTextColor, backdropFilter: 'blur(6px)', borderRadius: `${teCounterRadius}px`, px: 1.5, py: 0.75, boxShadow: '0 10px 24px rgba(0,0,0,0.18)' }}>
                                {!!teCounterPrefix && teCounterPrefixNewline && (
                                  <Typography component="div" sx={{ fontWeight: 800, fontSize: 12, opacity: .85, mb: .25 }}>{teCounterPrefix}</Typography>
                                )}
                                <Typography component="div" sx={{ fontWeight: 900, fontSize: 28, lineHeight: 1 }}>
                                  {(!teCounterPrefixNewline ? (teCounterPrefix || '') : '')}{typeof teCounterCount === 'number' ? new Intl.NumberFormat('de-DE').format(teCounterCount) : new Intl.NumberFormat('de-DE').format(1234)}{(!teCounterSuffixNewline ? (teCounterSuffix || '') : '')}
                                </Typography>
                                {!!teCounterSuffix && teCounterSuffixNewline && (
                                  <Typography component="div" sx={{ fontWeight: 800, fontSize: 12, opacity: .85, mt: .25 }}>{teCounterSuffix}</Typography>
                                )}
                                <Typography variant="caption" sx={{ opacity: 0.7, fontWeight: 700 }}>Placeholder</Typography>
                              </Box>
                            </Box>
                            {/* Settings gear - unscaled */}
                            <IconButton size="small" onClick={(e)=> { e.stopPropagation(); setTeCounterSettingsAnchor(e.currentTarget); }} sx={{ position: 'absolute', top: -28, left: -28, bgcolor: 'rgba(255,255,255,0.9)', '&:hover': { bgcolor: '#fff' }, boxShadow: 1 }}>
                              <SettingsOutlinedIcon fontSize="small" />
                            </IconButton>
                            {/* Resize handle (scale) - unscaled */}
                            <Box
                              onMouseDown={(e) => {
                                e.stopPropagation();
                                const startX = e.clientX; const startScale = teCounterScale;
                                setTeCounterResizing(true);
                                const prevUserSelect = document.body.style.userSelect;
                                document.body.style.userSelect = 'none';
                                const onMove = (ev: MouseEvent) => {
                                  const dx = ev.clientX - startX;
                                  const next = Math.max(0.5, Math.min(3, Number((startScale + dx / 120).toFixed(2))));
                                  setTeCounterScale(next);
                                };
                                const onUp = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); setTeCounterResizing(false); document.body.style.userSelect = prevUserSelect; };
                                window.addEventListener('mousemove', onMove);
                                window.addEventListener('mouseup', onUp);
                              }}
                              sx={{ position: 'absolute', right: -10, top: '50%', transform: 'translateY(-50%)', width: 10, height: 10, borderRadius: '50%', bgcolor: '#3b82f6', boxShadow: '0 0 0 2px #fff' }}
                            />
                            <Box sx={{ position: 'absolute', right: -24, top: '50%', transform: 'translateY(-50%)', width: 10, height: 10, borderRadius: '50%', bgcolor: '#3b82f6', opacity: 0.7 }} />
                          </Box>
                        </Box>
                      </Box>
                    )}
                    {/* List embedding */}
                    {teShowList === true && Number.isInteger(teListTabIndex) && teListTabIndex >= 0 && teListTabIndex < listTabs.length && (
                      <Box sx={{ position: 'absolute', zIndex: 2, ...( teListFullSize ? { inset: 8 } : { left: '50%', top: '50%', transform: 'translate(-50%, -50%)', width: '88%' }) }}>
                        {(() => {
                          const tab = listTabs[teListTabIndex] as any;
                          const title = typeof tab?.title === 'string' ? tab.title : '';
                          const items = Array.isArray(tab?.items) ? tab.items : [];
                          const size = Math.max(20, Math.min(160, Number(tab?.itemSize) || 40));
                          const iconColor = typeof tab?.iconColor === 'string' ? tab.iconColor : undefined;
                          const iconBg = typeof tab?.iconBg === 'string' ? tab.iconBg : undefined;
                          return (
                            <ListBuilder
                              title={title}
                              items={items}
                              itemSize={size}
                              iconColor={iconColor}
                              iconBg={iconBg}
                              showSettingsIcon
                              fullSize={teListFullSize}
                              scrollDir={(tileEditorIndex != null && tileEditorIndex >= 0 && tileEditorIndex < gridTiles.length && (gridTiles[tileEditorIndex] as any)?.listScrollDir === 'horizontal') ? 'horizontal' : 'vertical'}
                              onOpenSettings={() => setListSettingsDialog({ open: true, index: tileEditorIndex })}
                            />
                          );
                        })()}
                      </Box>
                    )}
                    {/* Counter settings popover */}
                    <Popover
                      open={Boolean(teCounterSettingsAnchor)}
                      onClose={() => setTeCounterSettingsAnchor(null)}
                      anchorEl={teCounterSettingsAnchor}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    >
                      <Box sx={{ p: 2, display: 'grid', gap: 1.25, minWidth: 360 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <SettingsOutlinedIcon fontSize="small" /> Counter Einstellungen
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
                          <TextField size="small" type="number" label="X (%)" value={teCounterX} onChange={(e)=> setTeCounterX(Math.max(0, Math.min(100, Number(e.target.value)||0)))} />
                          <TextField size="small" type="number" label="Y (%)" value={teCounterY} onChange={(e)=> setTeCounterY(Math.max(0, Math.min(100, Number(e.target.value)||0)))} />
                          <TextField size="small" type="number" label="Größe (x)" value={teCounterScale} onChange={(e)=> setTeCounterScale(Math.max(0.5, Math.min(3, Number(e.target.value)||1)))} inputProps={{ step: 0.1 }} />
                        </Box>
                        <Divider sx={{ my: 0.5 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TableChartIcon fontSize="small" /> Supabase Datenquelle (Vorschau)
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                          <FormControl size="small">
                            <InputLabel>Schema</InputLabel>
                            <Select label="Schema" value={teCounterSchema} onChange={(e)=> setTeCounterSchema(String(e.target.value))}>
                              <MenuItem value="public">public</MenuItem>
                            </Select>
                          </FormControl>
                          <FormControl size="small">
                            <InputLabel>Tabelle</InputLabel>
                            <Select label="Tabelle" value={teCounterTable} onChange={(e)=> setTeCounterTable(String(e.target.value))} disabled={teCounterTablesLoading || !!teCounterTablesError}>
                              {teCounterTables.filter(t=> !teCounterSearch || t.toLowerCase().includes(teCounterSearch.toLowerCase())).map(t => (
                                <MenuItem key={t} value={t}>{t}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>
                        {teCounterTablesLoading && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <CircularProgress size={18} /> <Typography variant="body2">Lade Tabellen…</Typography>
                          </Box>
                        )}
                        {teCounterTablesError && (
                          <Typography variant="body2" color="error">{teCounterTablesError}</Typography>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 24 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>Zeilen:</Typography>
                          {teCounterCountLoading ? (<CircularProgress size={16} />) : (
                            teCounterCountError ? (<Typography variant="body2" color="error">{teCounterCountError}</Typography>) : (
                              <Typography variant="body2">{typeof teCounterCount === 'number' ? new Intl.NumberFormat('de-DE').format(teCounterCount) : '-'}</Typography>
                            )
                          )}
                        </Box>
                        <Divider sx={{ my: 0.5 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Stil</Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TextField size="small" label="Hintergrund" value={teCounterBg} onChange={(e)=> setTeCounterBg(e.target.value)} placeholder="rgba(255,255,255,0.9)" fullWidth />
                            <input type="color" value={/^#/.test(teCounterBg) ? teCounterBg : '#ffffff'} onChange={(e)=> setTeCounterBg(e.target.value)} style={{ width: 36, height: 36, border: 'none', background: 'transparent', cursor: 'pointer' }} />
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TextField size="small" label="Textfarbe" value={teCounterTextColor} onChange={(e)=> setTeCounterTextColor(e.target.value)} placeholder="#111827" fullWidth />
                            <input type="color" value={/^#/.test(teCounterTextColor) ? teCounterTextColor : '#111827'} onChange={(e)=> setTeCounterTextColor(e.target.value)} style={{ width: 36, height: 36, border: 'none', background: 'transparent', cursor: 'pointer' }} />
                          </Box>
                        </Box>
                        <TextField size="small" type="number" label="Eckenradius (px)" value={teCounterRadius} onChange={(e)=> setTeCounterRadius(Math.max(0, Math.min(28, Number(e.target.value)||0)))} />
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                          <TextField size="small" label="Präfix" value={teCounterPrefix} onChange={(e)=> setTeCounterPrefix(e.target.value)} placeholder="z.B. +" />
                          <TextField size="small" label="Suffix" value={teCounterSuffix} onChange={(e)=> setTeCounterSuffix(e.target.value)} placeholder="z.B. €" />
                        </Box>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                          <FormControlLabel control={<Switch size="small" checked={teCounterPrefixNewline} onChange={(e)=> setTeCounterPrefixNewline(e.target.checked)} />} label="Präfix in eigener Zeile" />
                          <FormControlLabel control={<Switch size="small" checked={teCounterSuffixNewline} onChange={(e)=> setTeCounterSuffixNewline(e.target.checked)} />} label="Suffix in eigener Zeile" />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, pt: 0.5 }}>
                          <Button size="small" variant="outlined" onClick={()=> { setTeCounterX(85); setTeCounterY(85); setTeCounterScale(1); }}>Zurücksetzen Position</Button>
                          <Button size="small" variant="contained" onClick={()=> setTeCounterSettingsAnchor(null)}>Fertig</Button>
                        </Box>
                      </Box>
                    </Popover>
                    <Box sx={{ position: 'relative', zIndex: 1, display: 'grid', gap: 1, p: 2.0, color: img ? '#fff' : color }}>
                      <Typography variant="subtitle1" fontWeight={800} sx={{ textShadow: img ? '0 1px 3px rgba(0,0,0,.55)' : 'none' }}>
                        {teTitle || 'Kachel Vorschau'}
                      </Typography>
                      {!!teSubtitle && (
                        <Typography variant="body2" sx={{ opacity: 0.9, textShadow: img ? '0 1px 2px rgba(0,0,0,0.45)' : 'none' }}>
                          {teSubtitle}
                        </Typography>
                      )}
                      {!!teCtaText && !teCtaFree && (
                        <Box sx={{ position: 'relative', display: 'inline-block' }}>
                          <Button variant="contained" size="small" href={teCtaUrl || '#'} sx={{
                            mt: 0.5,
                            bgcolor: teCtaBtnBg ? teCtaBtnBg : (img ? 'rgba(59,130,246,0.95)' : undefined),
                            color: teCtaBtnTextColor || undefined,
                            borderRadius: 9999,
                            px: 1.5,
                            width: `${Math.max(80, Math.min(400, teCtaWidth))}px`,
                            fontSize: `${Math.max(10, Math.min(36, teCtaFontSize))}px`,
                            transition: 'transform .2s ease, box-shadow .2s ease, background-color .2s ease',
                            ...(teCtaHoverEffect === 'lift' && { '&:hover': { transform: 'translateY(-2px)', boxShadow: 6 } }),
                            ...(teCtaHoverEffect === 'glow' && { '&:hover': { boxShadow: '0 10px 28px rgba(0,0,0,0.25)' } }),
                            ...(teCtaHoverEffect === 'zoom' && { '&:hover': { transform: 'scale(1.05)' } }),
                            ...(teCtaHoverEffect === 'zoomGlow' && { '&:hover': { transform: 'scale(1.05)', boxShadow: '0 12px 32px rgba(0,0,0,0.28)' } }),
                          }}>
                            {teCtaText}
                          </Button>
                          <Box sx={{ position: 'absolute', right: -36, top: -8, opacity: 0, transition: 'opacity .2s', pointerEvents: 'none' }} className="cta-settings-hover">
                            <IconButton size="small" onClick={() => setCtaSettingsOpen(true)} sx={{ pointerEvents: 'auto', bgcolor: 'background.paper', boxShadow: 2 }}>
                              <SettingsIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      )}
                    </Box>
                    {teCtaFree && !!teCtaText && (
                      <Box sx={{ position: 'absolute', left: `${teCtaX}%`, top: `${teCtaY}%`, transform: 'translate(-50%, -50%)', zIndex: 2 }}>
                        <Box sx={{ position: 'relative' }}
                          onMouseDown={(e) => {
                            const card = (e.currentTarget.parentElement?.parentElement as HTMLElement) || null;
                            if (!card) return;
                            const startX = e.clientX; const startY = e.clientY;
                            const rect = card.getBoundingClientRect();
                            const onMove = (ev: MouseEvent) => {
                              const dx = ev.clientX - startX; const dy = ev.clientY - startY;
                              let nx = Math.max(0, Math.min(100, teCtaX + (dx / rect.width) * 100));
                              let ny = Math.max(0, Math.min(100, teCtaY + (dy / rect.height) * 100));
                              const points = [0, 25, 50, 75, 100];
                              const thresh = 3; // percent
                              let gx: number | null = null; let gy: number | null = null;
                              for (const p of points) { if (Math.abs(nx - p) <= thresh) { nx = p; gx = p; break; } }
                              for (const p of points) { if (Math.abs(ny - p) <= thresh) { ny = p; gy = p; break; } }
                              setTeGuideX(gx);
                              setTeGuideY(gy);
                              setTeCtaX(nx); setTeCtaY(ny);
                            };
                            const onUp = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); setTeGuideX(null); setTeGuideY(null); };
                            window.addEventListener('mousemove', onMove);
                            window.addEventListener('mouseup', onUp);
                          }}
                        >
                          <Button variant="contained" size="small" sx={{
                            bgcolor: teCtaBtnBg ? teCtaBtnBg : (img ? 'rgba(59,130,246,0.95)' : undefined),
                            color: teCtaBtnTextColor || undefined,
                            borderRadius: 9999,
                            px: 1.5,
                            minWidth: `${Math.max(80, Math.min(400, teCtaWidth))}px`,
                            cursor: 'move',
                            transform: `scale(${teCtaScale})`,
                            transformOrigin: 'center',
                            fontSize: `${Math.max(10, Math.min(36, teCtaFontSize))}px`
                          }}>
                            {teCtaText}
                          </Button>
                          <Box sx={{ position: 'absolute', inset: -8, border: '1px dashed rgba(255,255,255,0.8)', borderRadius: 2, pointerEvents: 'none' }} />
                          {/* Left resize handle */}
                          <Box
                            sx={{ position: 'absolute', left: -10, top: '50%', transform: 'translateY(-50%)', width: 14, height: 14, borderRadius: '50%', bgcolor: 'primary.main', boxShadow: 2, cursor: 'ew-resize' }}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              const startX = e.clientX; const startW = teCtaWidth; const scale = teCtaScale || 1;
                              const onMove = (ev: MouseEvent) => {
                                const dx = startX - ev.clientX; // dragging left increases width visually
                                const next = Math.max(80, Math.min(400, startW + dx / scale));
                                setTeCtaWidth(next);
                              };
                              const onUp = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
                              window.addEventListener('mousemove', onMove);
                              window.addEventListener('mouseup', onUp);
                            }}
                          />
                          {/* Right resize handle */}
                          <Box
                            sx={{ position: 'absolute', right: -10, top: '50%', transform: 'translateY(-50%)', width: 14, height: 14, borderRadius: '50%', bgcolor: 'primary.main', boxShadow: 2, cursor: 'ew-resize' }}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              const startX = e.clientX; const startW = teCtaWidth; const scale = teCtaScale || 1;
                              const onMove = (ev: MouseEvent) => {
                                const dx = ev.clientX - startX;
                                const next = Math.max(80, Math.min(400, startW + dx / scale));
                                setTeCtaWidth(next);
                              };
                              const onUp = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
                              window.addEventListener('mousemove', onMove);
                              window.addEventListener('mouseup', onUp);
                            }}
                          />
                          <Box
                            sx={{ position: 'absolute', right: -10, bottom: -10, width: 16, height: 16, borderRadius: '50%', bgcolor: 'primary.main', boxShadow: 2, cursor: 'nwse-resize' }}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              const startX = e.clientX; const startY = e.clientY;
                              const initial = teCtaScale;
                              const onMove = (ev: MouseEvent) => {
                                const dx = ev.clientX - startX; const dy = ev.clientY - startY;
                                const delta = (dx + dy) / 200; // sensitivity
                                const next = Math.max(0.5, Math.min(3, initial * (1 + delta)));
                                setTeCtaScale(next);
                              };
                              const onUp = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
                              window.addEventListener('mousemove', onMove);
                              window.addEventListener('mouseup', onUp);
                            }}
                          />
                          <Box sx={{ position: 'absolute', left: -10, top: -36 }}>
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setCtaSettingsOpen(true); }} sx={{ bgcolor: 'background.paper', boxShadow: 2 }}>
                              <SettingsIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </Box>
                    )}
                    <style>{`.cta-settings-hover:hover, .cta-settings-hover:focus-within { opacity: 1 !important; }`}</style>
                    <style>{`.MuiButton-root:hover + .cta-settings-hover { opacity: 1 !important; }`}</style>
                    <style>{`.cta-settings-hover { opacity: 0; }`}</style>
                    {/* Helper snap guide lines */}
                    {typeof teGuideX === 'number' && (
                      <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: `${teGuideX}%`, width: 0, borderLeft: '2px dashed rgba(255,255,255,0.8)', zIndex: 1 }} />
                    )}
                    {typeof teGuideY === 'number' && (
                      <Box sx={{ position: 'absolute', left: 0, right: 0, top: `${teGuideY}%`, height: 0, borderTop: '2px dashed rgba(255,255,255,0.8)', zIndex: 1 }} />
                    )}
                  </Card>
                </Box>
              );
            })()}
          </Box>
        </DialogContent>
        <DialogActions>
          {tileEditorIndex != null && (
            <Button color="error" onClick={() => { removeGridTile(tileEditorIndex); closeTileEditor(); }}>Löschen</Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button onClick={closeTileEditor}>Abbrechen</Button>
          <Button variant="contained" onClick={saveTileEditor}>Speichern</Button>
        </DialogActions>
      </Dialog>

      {/* Circles Bulk Create */}
      <Dialog open={bulkCirclesOpen} onClose={() => setBulkCirclesOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Bulk erstellen (Kreise)</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <TextField type="number" label="Anzahl" value={bulkCount} onChange={(e) => setBulkCount(Math.max(1, Number(e.target.value)||1))} sx={{ width: 140 }} />
            <TextField label="Label-Präfix" value={bulkLabelPrefix} onChange={(e) => setBulkLabelPrefix(e.target.value)} sx={{ minWidth: 220 }} />
            <TextField label="URL-Präfix (optional)" value={bulkUrlPrefix} onChange={(e) => setBulkUrlPrefix(e.target.value)} sx={{ minWidth: 280 }} />
          </Box>
          {gallery.length === 0 ? (
            <Typography color="text.secondary">Keine Galerie-Bilder vorhanden.</Typography>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(3, 1fr)', sm: 'repeat(4, 1fr)', md: 'repeat(6, 1fr)' }, gap: 1.5 }}>
              {gallery.map((p) => {
                const selected = bulkSelection.has(p);
                return (
                  <Box key={p} onClick={() => setBulkSelection(prev => { const next = new Set(prev); if (next.has(p)) next.delete(p); else next.add(p); return next; })} sx={{ position: 'relative', borderRadius: '50%', overflow: 'hidden', border: '2px solid', borderColor: selected ? 'primary.main' : 'divider', cursor: 'pointer', aspectRatio: '1 / 1' }}>
                    <Box component="img" src={toPublicUrl(p)} alt={p} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    {selected && <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(25,118,210,0.15)' }} />}
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkCirclesOpen(false)}>Abbrechen</Button>
          <Button variant="contained" onClick={() => {
            const images = Array.from(bulkSelection);
            const count = Math.max(1, bulkCount);
            const items: Array<{ label: string; url: string; image: string }> = [];
            for (let i = 0; i < count; i++) {
              const img = images.length ? images[i % images.length] : '';
              const label = `${bulkLabelPrefix} ${i+1}`.trim();
              const url = bulkUrlPrefix ? `${bulkUrlPrefix}${i+1}` : '';
              items.push({ label, url, image: img });
            }
            setCirclesItems(prev => [...prev, ...items]);
            setBulkCirclesOpen(false);
            setBulkSelection(new Set());
          }}>Hinzufügen</Button>
        </DialogActions>
      </Dialog>

      {/* Circle image picker from gallery */}
      <Dialog open={circlePickerOpen.open} onClose={closeCirclePicker} maxWidth="md" fullWidth>
        <DialogTitle>Bild aus Media Gallery wählen</DialogTitle>
        <DialogContent dividers>
          {gallery.length === 0 ? (
            <Typography color="text.secondary">Keine Galerie-Bilder vorhanden.</Typography>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
              {gallery.map((p) => (
                <Box key={p} onClick={() => { setCircleImage(p); closeCirclePicker(); }} sx={{ position: 'relative', borderRadius: '50%', overflow: 'hidden', border: '2px solid', borderColor: 'divider', cursor: 'pointer', aspectRatio: '1 / 1' }}>
                  <Box component="img" src={toPublicUrl(p)} alt={p} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCirclePicker}>Schließen</Button>
        </DialogActions>
      </Dialog>

      {/* Category Picker Dialog for linking to products page */}
      <CategoriesPickerDialog
        open={categoryPicker.open}
        onClose={closeCategoryPicker}
        onSelect={({ category, label, path }) => applyCategoryToCircle(category, label, path)}
      />

      {/* Feature pick from gallery */}
      <Dialog open={featurePicking} onClose={() => setFeaturePicking(false)} maxWidth="md" fullWidth>
        <DialogTitle>Feature-Bild aus Media Gallery wählen</DialogTitle>
        <DialogContent dividers>
          {gallery.length === 0 ? (
            <Typography color="text.secondary">Keine Galerie-Bilder vorhanden.</Typography>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
              {gallery.map((p) => (
                <Box key={p} onClick={() => { setFeatureImagePath(p); setFeaturePicking(false); }} sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', border: '2px solid', borderColor: (featureImagePath===p)?'primary.main':'divider', cursor: 'pointer', aspectRatio: '1 / 1' }}>
                  <Box component="img" src={toPublicUrl(p)} alt={p} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFeaturePicking(false)}>Schließen</Button>
          <Button variant="contained" onClick={saveFeature}>Speichern</Button>
        </DialogActions>
      </Dialog>

      {/* Promo pick from gallery (multi-select) */}
      <Dialog open={promoPicking} onClose={() => setPromoPicking(false)} maxWidth="md" fullWidth>
        <DialogTitle>Promo-Bilder aus Media Gallery wählen</DialogTitle>
        <DialogContent dividers>
          {gallery.length === 0 ? (
            <Typography color="text.secondary">Keine Galerie-Bilder vorhanden.</Typography>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
              {gallery.map((p) => {
                const selected = promoSelection.has(p);
                return (
                  <Box key={p} onClick={() => setPromoSelection(prev => { const next = new Set(prev); if (next.has(p)) next.delete(p); else next.add(p); return next; })} sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', border: '2px solid', borderColor: selected ? 'primary.main' : 'divider', cursor: 'pointer', aspectRatio: '1 / 1' }}>
                    <Box component="img" src={toPublicUrl(p)} alt={p} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    {selected && <Box sx={{ position: 'absolute', inset: 0, boxShadow: 'inset 0 0 0 3px rgba(59,130,246,0.9)' }} />}
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPromoPicking(false)}>Abbrechen</Button>
          <Button variant="contained" onClick={() => { const arr = Array.from(promoSelection); setPromoImages(arr); if (arr.length) setPromoImagePath(''); setPromoImagesLinks(prev => { const next: Record<string,string> = {}; arr.forEach(k => { if (prev[k]) next[k] = prev[k]; }); return next; }); setPromoPicking(false); }}>Übernehmen</Button>
        </DialogActions>
      </Dialog>

      {/* Slideshow Picker Dialog */}
      <Dialog open={slideshowPickerOpen} onClose={closeSlideshowPicker} maxWidth="md" fullWidth>
        <DialogTitle>Aus Media Gallery hinzufügen</DialogTitle>
        <DialogContent dividers>
          {gallery.length === 0 ? (
            <Typography color="text.secondary">Keine Galerie-Bilder vorhanden.</Typography>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
              {gallery.map((p) => {
                const selected = slideshowSelection.has(p);
                return (
                  <Box key={p} onClick={() => toggleSlideshowSelect(p)} sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', border: '2px solid', borderColor: selected ? 'primary.main' : 'divider', cursor: 'pointer', aspectRatio: '1 / 1' }}>
                    <Box component="img" src={toPublicUrl(p)} alt={p} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    {selected && (<Box sx={{ position: 'absolute', inset: 0, boxShadow: 'inset 0 0 0 3px rgba(59,130,246,0.8)' }} />)}
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSlideshowPicker}>Abbrechen</Button>
          <Button variant="contained" onClick={addSelectedToSlideshow} disabled={slideshowSelection.size === 0}>Hinzufügen</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HomepageSettingsTab;
